import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type UIEvent,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
/*
 * ⚠️ `ActivityIcon`, NOT `ActivityButton` — the same call this page's siblings
 * make (`/companies`, `/deals`, `/quotes`). `ActivityButton` fetches its own
 * task and note counts, and this page is FIVE tables at once: at fifteen rows a
 * table that is 150 extra requests for badges nobody asked for, against a per-IP
 * budget shared across the whole office. ⚠️ *SUPERSEDED: "a 120-per-60s per-IP
 * budget."* It is **12,000/60s** (corrected 2026-09-17). **The objection is the
 * SHAPE — one request per row on a paginated table — and it does not rest on the
 * arithmetic**, which was wrong by a factor of a hundred. The record pages
 * use the button because they show one record. If counts are ever wanted here,
 * `/tasks/summary` takes up to 200 ids in ONE call — one request for the page,
 * never one per row.
 */
import {
  ActivityDrawer,
  ActivityIcon,
  type ActivityTarget,
} from '@/components/tasks/ActivityDrawer';
/*
 * ⚠️ **THE FUNNEL TILES AND THE WINDOW PICKER LIVE IN `@/components/dashboards`
 * NOW — extracted on 2026-09-02, not copied.** The Management Dashboard shows
 * the same five figures for a user GROUP and for the organisation that this page
 * shows for one person, and every rule those tiles enforce is about when a
 * figure may be shown as a zero. Two copies would agree the day they were
 * written and drift on the first correction, and the drift would be silent — a
 * `0` where a `—` belongs looks exactly like a real answer.
 */
import {
  FUNNEL_TILES,
  TargetTileGrid,
  TileFailureStrip,
  tileState,
  type TileState,
} from '@/components/dashboards/TargetTiles';
import { MetricDrilldownModal } from '@/components/dashboards/drilldown/MetricDrilldownModal';
import {
  TargetWindowPicker,
  initialWindowPickerState,
  parseWindowPickerState,
  windowChoiceOf,
  type WindowPickerState,
} from '@/components/dashboards/TargetWindowPicker';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
/*
 * 🔴 **THE CURRENCY LIST AND THE DEFAULT ARE IMPORTED, NEVER RESTATED HERE —
 * owner's decision 2026-09-13 is that this page's switcher is the Management
 * Dashboard's switcher.** A second frozen `['USD','EUR','GBP']` on this page
 * would agree the day it was written and drift on the first correction, and what
 * would drift is which currencies a reader may ask a revenue figure in.
 *
 * ⚠️ **Three codes, and NOT the 164-row `f_currencies` master.** The server sums
 * against ONE base currency per request, so offering every currency would let
 * somebody ask for one in which every figure is zero and every document
 * excluded — which reads as a dead business rather than as a question nobody
 * should have been offered. The reasoning lives at the constant.
 *
 * ⚠️ **`DEFAULT_ANALYTICS_CURRENCY` is `DASHBOARD_CURRENCY` (GBP)**, the
 * fallback when the signed-in person has no reporting currency (owner
 * 2026-10-28: Targets & performance opens on theirs). It deliberately differs from the revenue
 * tabs' USD default: a TARGET is denominated, every row in `f_revenue_targets`
 * is held in GBP, and these tiles compare a target against an actual.
 */
import {
  ANALYTICS_CURRENCIES,
  ANALYTICS_CURRENCY_OPTIONS,
  DEFAULT_ANALYTICS_CURRENCY,
  DEFAULT_FX_MODE,
  FX_MODE_OPTIONS,
  parseFxMode,
} from '@/components/dashboards/financial/financial-shared';
import {
  CONVERSION_APPROXIMATE_NOTE,
  DASHBOARD_CURRENCY,
  FUNNEL_METRICS,
  conversionNote,
  excludedByCurrencyNote,
  targetMetricLabel,
  type ExcludedNote,
  type FunnelMetric,
} from '@/lib/targets';
/*
 * ⚠️ **THE ROW "+" MENU IS THE MODULE LISTS' OWN, IMPORTED — never rebuilt for
 * this page.** Same reasoning as the four create modals below: it carries a
 * portal, the `z-[60]` layer rung, full keyboard menu semantics and flip
 * handling, and it is the second consumer that made it a shared component
 * rather than a third copy. The URL conventions travel with it, so a row here
 * and a row on `/companies` or `/deals` cannot land anywhere different.
 */
import {
  COMPANY_CREATE_TARGETS,
  ConvertQuoteMenu,
  CreateForCompanyMenu,
  CreateFromDealMenu,
  DEAL_CREATE_TARGETS,
  type CompanyCreateTarget,
  type DealCreateTarget,
} from '@/components/records/RecordCreateMenu';
/*
 * ⚠️ **THE FLOW IS THE SHARED ONE, IMPORTED — never rebuilt here.** Same
 * reasoning as the row menu above and the four create modals below: it carries
 * the project-to-line seeding, the picker and the form it hands off to, and it
 * does every one of its own reads. **This page hands it `{id, name}` and
 * nothing else** — it fetches the company itself, which is what keeps a
 * dashboard of five cards from fetching anything per row.
 */
import {
  CompanyProjectLinesFlow,
  type CompanyProjectLinesKind,
  type CompanyProjectLinesRequest,
} from '@/components/records/CompanyProjectLinesFlow';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { saveBlob } from '@/lib/download';
import { denialReason, NAV_SURFACE_MODULE, type ModuleSlug } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { userReportingCurrency } from '@/lib/reporting-currency';
/*
 * ⚠️ **THE FOUR CREATE MODALS ARE THE MODULES' OWN, IMPORTED — never a form
 * written for this page.** Each one carries rules nothing here could reproduce:
 * `CompanyFormModal` holds the duplicate-name lookup and the account-lead group
 * seed, `DealFormModal` writes the Next-task note to the deal AND its company,
 * and the quote and invoice forms carry the two-phase line-item create and the
 * whole Xero tax-defaults path. A second form would agree with them on the day
 * it was written and drift on the first correction — and what would drift is
 * what reaches a customer's document.
 *
 * ⚠️ **It is a real weight**: these are four of the largest components in the
 * app. They are mounted only while open (each returns `null` on `!open`), so a
 * dashboard that nobody creates from renders none of their trees.
 */
import { CompanyFormModal } from '@/components/companies/CompanyFormModal';
import { DealFormModal } from '@/components/deals/DealFormModal';
import { QuoteFormModal } from '@/components/quotes/QuoteFormModal';
import { InvoiceFormModal } from '@/components/invoices/InvoiceFormModal';
/*
 * The quote PDF preview (owner 2026-10-25): ONE viewer host for the page, an
 * icon button on every Quotes-card row (every stage), and the deal-level
 * picker on every Deals-card row. See `PdfPreviewRow`.
 */
import {
  PreviewQuotePdfButton,
  useQuotePdfPreview,
  type QuotePdfPreviewTarget,
} from '@/components/quotes/QuotePdfPreview';
import { DealQuotesPdfMenu } from '@/components/quotes/DealQuotesPdfMenu';
import { canPreviewQuotePdf, quotePreviewHref } from '@/lib/quote-pdf-preview';
import {
  Button,
  ColumnManager,
  /*
   * ⚠️ *SUPERSEDED 2026-09-13: `Select`.* `SortControl` was this file's ONLY
   * `Select`, so the ordering dropdown becoming a searchable `Combobox` (owner's
   * decision) retired the import with it. `Field` comes along because
   * `ComboboxProps` declares no `aria-label` — the accessible name has to be a
   * real, visually-hidden `<label>`.
   */
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  PageHeader,
  Panel,
  PanelHeader,
  /*
   * The currency switcher. ⚠️ **A `SegmentedControl` and not a `Combobox`,
   * deliberately — this is the ONE dropdown-shaped control on this page that the
   * 2026-09-13 searchable-filter sweep must not convert.** The Financial
   * Analytics tab argues it at length: the currency is the UNIT of every figure,
   * and a collapsed control shows a reader which unit they are in only after they
   * think to look. Three codes visible at once is what stops a figure being
   * quoted in the wrong currency by somebody who never checked.
   */
  SegmentedControl,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  type ColumnFilterOption,
  type ColumnFilterState,
  type FilterableTHProps,
  TableWrap,
} from '@/components/ui';
/*
 * Per-table column layout — order, hidden set and the two frozen edges — stored
 * against the ACCOUNT rather than the browser.
 *
 * ⚠️ **FIVE CONTROLLERS ON THIS PAGE, ONE PER CARD, AND THAT IS THE WHOLE
 * DESIGN.** The five cards do not share a column set — Leads carries no value
 * column at all, and the `value` key means "Stage" on the two `/companies` cards
 * and money on the other three — so a single scope would let a reader hide
 * "Deal value" and silently lose the Stage column beside it. See
 * `columnLayoutScope`.
 */
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
/*
 * ⚠️ **THE TWO STATUS BADGES ARE IMPORTED, NEVER REIMPLEMENTED.** `StageBadge`
 * and `StatusBadge` are the same components `/quotes` and `/invoices` render, and
 * they are the single place a quote stage or an invoice status is turned into a
 * tone. Each reads the server's BOOLEAN FLAGS — `is_accepted` / `is_declined` on
 * `f_quote_stages`, `is_settled` / `is_paid` on `f_invoice_statuses` — and each
 * reaches its exact-name fallback only when the flag is absent.
 *
 * A second tone map here would agree with theirs the day it was written and
 * drift on the first correction, and what would drift is whether a document
 * reads as SETTLED — which is the difference between money nobody chases and a
 * false alarm somebody dismisses.
 */
import { StageBadge } from '@/components/quotes/QuoteBits';
import { StatusBadge } from '@/components/invoices/InvoiceBits';
/*
 * ⚠️ **THE SIGNATURE COLUMN IS THE `/quotes` COLUMN, IMPORTED.** `SignatureCell`
 * and the state behind it were module-local to `QuotesPage` until 2026-09-03 and
 * were EXTRACTED rather than copied, for the reason that file states at length:
 * a blank cell there asserts *nothing is awaiting signature*, which is NOT
 * *nothing was ever sent*, and a second renderer would eventually word one of
 * those as the other. Everything about the one-request-per-page shape, why the
 * query is `outstanding: true`, and why an unfetched remainder must be said out
 * loud lives in `@/lib/outstanding-envelopes`.
 */
import { SignatureCell } from '@/components/docusign/SignatureCell';
import { useOutstandingEnvelopes, type SignatureState } from '@/lib/outstanding-envelopes';
/*
 * ⚠️ **`overdueDays` is the same judgement `isOverdue()` makes on `/invoices`,
 * and they are one function.** Overdue is not a date comparison alone — it is a
 * date comparison AND a settledness test that only the `/invoice-statuses`
 * master can answer. A local `dueDate < today` here would report every paid
 * invoice as overdue on the dashboard while `/invoices` correctly reported none.
 */
import { overdueDays } from '@/lib/invoice-utils';
import { formatDateOnly, formatDateTime, orDash, pluralise, toDateInputValue } from '@/lib/format';
/*
 * ⚠️ **THE WORDING OF THE PROJECT-OF-INTEREST CELL IS IMPORTED, NEVER DERIVED
 * HERE.** `companyProjectsLabel` decides what a list of projects reads as — how
 * many it names, what it says for none, whether it counts the remainder — and
 * `companyProjectsTitle` is the full list behind it. `/companies` and the
 * company detail page render the same fact, so a second derivation on this page
 * would agree the day it was written and drift on the first correction, and what
 * would drift is which projects a reader believes an account is being pursued
 * for. `PROJECTS_OF_INTEREST_LABEL` is the heading for the same reason — see
 * `COLUMN_LABEL.projectOfInterest`.
 */
import {
  PROJECTS_OF_INTEREST_LABEL,
  /*
   * 🔴 **THE SECOND HEADING, AND IT IS WHAT KEEPS THE TWO COLUMNS APART —
   * 2026-09-14.** `lineProjects` renders through the SAME label and title
   * helpers as `projectOfInterest`, deliberately (that module records the
   * decision), so **the heading is the only thing on screen that says which
   * question the cell answers.** ⛔ Never render a document card's projects under
   * `PROJECTS_OF_INTEREST_LABEL`: a reader would reconcile it against
   * `/companies` and find a discrepancy that is not one.
   */
  PROJECTS_ON_DOCUMENT_LABEL,
  companyProjectsLabel,
  companyProjectsPills,
  companyProjectsTitle,
} from '@/lib/company-projects';
import {
  CREDIT_BALANCE_LABEL,
  CREDIT_BALANCE_NOT_VISIBLE,
  balanceLabel,
  balancePills,
  balanceTitle,
  creditHeldOf,
} from '@/lib/credit-notes';
import {
  PRODUCTS_PURCHASED_LABEL,
  PROJECTS_PARTICIPATED_LABEL,
  purchasedItemsOf,
} from '@/lib/company-purchases';
import { PurchaseHistoryCell } from '@/components/companies/PurchaseHistoryCell';
import { ValuePills } from '@/components/ui';
/*
 * 🔴 **THE EDIT HALF — owner's instruction, 2026-09-12: *"every column except the
 * record name becomes editable"*.**
 *
 * ⚠️ **`InlineField` IS THE WHOLE INTERACTION AND NOTHING HERE MAY REIMPLEMENT
 * IT.** It owns idle → editing → saving → saved/failed, the keyboard contract,
 * the focus moves, and the rule that an untouched field never fires a request —
 * which on this API is correctness rather than polish, because an empty PATCH is
 * a 422 and re-sending an unchanged picklist id can be one too. This page owns
 * only the three things the primitive deliberately knows nothing about: which
 * request key a column writes, what a refusal on that module reads as, and what
 * a 200 that changed nothing means.
 *
 * ⚠️ **`kind="picker"` is required for every dropdown here and is not a style
 * choice.** Its `Combobox` popover is portalled to `document.body` on the
 * portalled-popover rung (60), so it escapes the `overflow-x-auto` scroller these
 * tables live inside. A `<select>`-shaped control in the same cell would be
 * clipped by that scroller at exactly the widths where the card scrolls — which
 * is all five cards below ~772px.
 *
 * The idiom — one writer per row, one `gate()` spread last on every control, one
 * small component per column — is `InlineTrackerFields`', copied deliberately so
 * a reader who knows one knows the other.
 */
import { InlineField, type InlineFieldOption, type InlineSaveResult } from '@/components/ui/InlineField';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
/*
 * ⛔ **THE TWO FLAGS, NEVER A STAGE NAME.** `isStageAccepted` / `isStageDeclined`
 * read `is_accepted` / `is_declined` off the `/quote-stages` master and reach an
 * exact-name fallback only when the flag is absent. This page has the master in
 * hand (`masters.stages`), so it always passes it — and a containment match on a
 * name is what painted `"Not Accepted"` the same green as `Accepted` once
 * already.
 */
import { isStageAccepted, isStageDeclined } from '@/lib/quote-utils';
/*
 * ⚠️ **The HOOK only — `companyStageOptions` is deliberately NOT imported.** It
 * does exactly the right thing for the company-stage picker and the wrong thing
 * for the other three on this page, which are not `ReferenceRecord`s: see
 * {@link editorOptions}, which mirrors its three rules over the one shape all
 * four masters here share.
 */
import { useCompanyStages } from '@/lib/use-company-stages';
/*
 * ⚠️ **THE HOOK *AND* ITS OPTION BUILDER, which is the opposite of the line
 * directly above and is a decision rather than an inconsistency.**
 * `companyStageOptions` is left out because it takes a `ReferenceRecord[]` and
 * three of this page's four pickers are not fed one — `/countries` rows spell
 * their label `countryName` and would have to be forged into the shape. **The
 * source master IS a plain `ReferenceRecord` master**, so `companySourceOptions`
 * fits it exactly, and importing it means a correction to the retired-value
 * rules reaches this page instead of having to be carried across to
 * {@link editorOptions}'s mirror of them. One fewer copy, not one more.
 *
 * ⚠️ **ONE READ SERVES BOTH THE FUNNEL AND THE PICKER here**, unlike the country
 * pair — see `useCountryPickerMaster` for why THAT one had to be two requests
 * (the filter narrows to `hasCompanies`, a picker must not). `/company-sources`
 * offers no such narrowing, so one list is correct for both and a second hook
 * would be a second request for the same five rows.
 */
import {
  companySourceOptions,
  useCompanySources,
  type CompanySourcesState,
} from '@/lib/use-company-sources';
/*
 * ⚠️ **THE HOOK *AND* ITS OPTION BUILDER, exactly as `use-company-sources`
 * directly above** — and for the same reason: `/company-types` is a plain
 * `ReferenceRecord` master, so `companyTypeOptions` fits its rows without being
 * forged into another shape, and importing it means a correction to the
 * retired-value wording reaches this page rather than having to be carried across
 * to {@link editorOptions}'s local mirror.
 *
 * ⚠️ **ONE READ SERVES BOTH THE FUNNEL AND THE PICKER**, like the source master
 * and unlike the country pair — `/company-types` publishes no `hasCompanies`-style
 * narrowing, so one list is correct for a control that offers what can MATCH and
 * for one that offers what can be CHOSEN.
 */
import {
  companyTypeOptions,
  useCompanyTypes,
  type CompanyTypesState,
} from '@/lib/use-company-types';
/*
 * The project picker's candidates and the cycle pills behind it. ⚠️ **BOTH are
 * read ONCE AT THE PAGE and threaded down** — `/projects` and `/cycles` per
 * row-open would be one request per row, on a page whose cards fetch a hundred
 * rows each. ⚠️ *SUPERSEDED: "against a 120-per-60s budget shared by the whole
 * office."* It is **12,000/60s** (corrected 2026-09-17); the per-row shape is
 * the objection, not the arithmetic.
 */
import { useProjectOptions } from '@/lib/use-project-options';
import { useCycleScopeIds } from '@/lib/use-line-project';
import { CompanyProjectsModal } from '@/components/companies/CompanyProjectsModal';
/*
 * ⚠️ **The READ-ONLY sibling of the import above, and they are easy to confuse
 * at a call site.** `CompanyProjectsModal` edits a COMPANY's projects of
 * interest, where this one lists a DOCUMENT's line projects and writes nothing.
 */
import { DocumentProjectsModal } from '@/components/projects/DocumentProjectsModal';
/*
 * The company's contacts manager — edit, add, delete, set primary, change group,
 * and the per-row email-address button (`mailto:`; the Gmail-compose rule was
 * SUPERSEDED 2026-10-16). Owner's decision
 * 2026-10-16, replacing the read-only `CompanyContactsMailModal` (2026-09-17).
 *
 * ⚠️ **THE THIRD MODAL THIS PAGE MOUNTS OVER A COMPANY.** `CompanyProjectsModal`
 * edits a company's projects of interest, `DocumentProjectsModal` reads a
 * document's own line projects, and this one manages a company's people. It is
 * scoped by what it needs and nothing more: an id and a name. It does its own
 * reads and gates its own writes on `ability` (the server's 403 is the real
 * boundary), so the page hands it no gate.
 *
 * ⚠️ *SUPERSEDED 2026-10-16: `MailGlyph` imported from the mail modal.* The
 * trigger is now the shared people icon from the catalog.
 */
import { CompanyContactsManagerModal } from '@/components/companies/contacts-manager/CompanyContactsManagerModal';
import { UsersIcon } from '@/components/catalog/icons';
/*
 * The two COMPANY cards' account-wide note columns (2026-10-02) — the same cells
 * and headings `/companies` uses, so the two surfaces cannot word one fact twice.
 */
import {
  ACCOUNT_LAST_NOTE_AT_LABEL,
  ACCOUNT_LAST_NOTE_LABEL,
  AccountLastNoteCell,
  AccountLastNoteDateCell,
  accountLastNoteDate,
  accountLastNoteFilterText,
} from '@/components/companies/AccountLastNoteCell';
import { OpenNotesButton } from '@/components/companies/OpenNotesButton';
/*
 * The Deals card's "Quote signed" column (owner 2026-10-27) — the ONE copy of
 * its heading, wording and filter options, shared with `/deals`, the deal page
 * and the sales tracker.
 */
import {
  QUOTE_SIGNED_FILTER_OPTIONS,
  QUOTE_SIGNED_LABEL,
  quoteSignedLabel,
} from '@/lib/deal-quote-signed';
import { rangeBounds } from '@/components/ui/ColumnFilter';
/*
 * ⚠️ **The same `isRealDate` the modals pair with a native date control.** It is
 * the right rule precisely BECAUSE nothing here ever builds a `Date`: the
 * element's value is `YYYY-MM-DD` text, so `new Date('2026-05-31')` — which
 * renders as 30 May west of Greenwich — is never written.
 */
import { isRealDate } from '@/components/catalog/form-utils';
import type { XeroPushReport } from '@/lib/xero';
import { viewerZone } from '@/lib/task-time';
/*
 * The two masters the badges resolve flags against. ONE request each for the
 * whole page — not one per table, and emphatically not one per row: five tables
 * of fifteen rows would be 150 lookups for two lists that are the same on every
 * one of them.
 */
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useQuoteStageOptions } from '@/lib/use-quote-options';
import { useInvoiceStatusOptions } from '@/lib/use-invoice-options';
import type {
  Company,
  CompanyStageStatus,
  InvoiceStatus,
  MasterRef,
  ProspectFollowUpBucket,
  ProspectFollowUpSummary,
  QuoteStage,
  TargetFxMode,
} from '@/lib/types';
/*
 * The per-stage `{Stage} status` columns (owner 2026-10-25) on the Leads and
 * Companies cards. The columns, the cell rules and the shared-filter slicing
 * are the library's; this page only places, renders and writes them.
 */
import {
  columnSelection,
  isStageStatusColumnId,
  replaceColumnSelection,
  useStageStatusColumns,
  type StageStatusColumn,
} from '@/lib/stage-status-columns';
import { StageStatusColumnCell } from '@/components/companies/StageStatusColumnCell';
import {
  useStageStatusChange,
  type StageStatusChangeRequest,
  type StageStatusChangeExtra,
} from '@/lib/use-stage-status-change';
import { useMyTargets, type MyTargets, type TargetSubjectScope } from '@/lib/use-my-targets';
import type { SegmentedControlOption } from '@/components/ui/SegmentedControl';
import { useCommissionProgress } from '@/lib/use-commission-progress';
import { CommissionProgressStrip } from '@/components/dashboards/CommissionProgressStrip';
import { CommissionProgressModal } from '@/components/dashboards/CommissionProgressModal';

const TARGET_SUBJECT_OPTIONS: readonly SegmentedControlOption<TargetSubjectScope>[] = [
  { value: 'mine', label: 'My own' },
  { value: 'region', label: 'My region' },
  { value: 'overall', label: 'Overall' },
];
import { useUserOptions } from '@/lib/use-user-options';
import { MultiCombobox, type MultiComboboxOption } from '@/components/ui/MultiCombobox';
import {
  PARTNERSHIP_LEADS_PARAM,
  PARTNERSHIP_SORT_LABEL,
  allLeadScope,
  meLeadScope,
  parsePartnershipLeadScope,
  serialisePartnershipLeadScope,
  type PartnershipLeadScope,
  companyCardListParams,
  isCompanyCardKey,
  isDefaultPartnershipSort,
  parseStageStatusSort,
  partnershipSortLabel,
  resolvePartnershipDateBound,
  resolvePartnershipFilterIds,
  resolvePartnershipSort,
  stageStatusExportColumn,
  stageStatusFilterForColumns,
  stageStatusSortPair,
  statusPageFiltered,
  usePartnershipTables,
  type PartnershipColumn,
  type PartnershipCreate,
  type PartnershipFilterKind,
  type PartnershipFilters,
  type PartnershipRow,
  /*
   * ⚠️ **The row's shape for ONE project, imported rather than re-declared.** It
   * is structurally `{id, name}` and so is `CompanyProject` in
   * `company-projects.ts` — which is why the two label helpers accept either —
   * but naming the row's own type here is what the two project columns' shared
   * helpers are written against, so neither can be handed the wrong field's
   * elements if either ever gains one.
   */
  type PartnershipRowProject,
  type PartnershipSort,
  type PartnershipSorts,
  type PartnershipTableKey,
  type PartnershipTableState,
} from '@/lib/use-partnership-tables';
import { usePageLayout, type LayoutBlockDefault } from '@/lib/use-page-layout';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { StatTile } from '@/components/ui/StatTile';
import { BUCKET_LABELS } from '@/lib/stage-status-follow-up';

/*
 * The page's movable sections for the layout manager (owner 2026-10-20) — the
 * funnel panel and the five cards, in today's order (`TABLES` in
 * `use-partnership-tables`: Leads, Companies, Deals, Quotes, Invoices), each
 * full width, which is the one-card-per-row ruling of 2026-09-03.
 *
 * ⛔ The ids are stored per person in `/preferences`; never rename one. The
 * five table ids are the `PartnershipTableKey`s, so the render map is keyed by
 * `state.key` with no second mapping to drift.
 */
const PAGE_LAYOUT_DEFAULTS: readonly LayoutBlockDefault[] = [
  { id: 'targets', label: 'Targets & performance' },
  { id: 'leads', label: 'Leads' },
  { id: 'companies', label: 'Companies' },
  { id: 'deals', label: 'Deals' },
  { id: 'quotes', label: 'Quotes' },
  { id: 'invoices', label: 'Invoices' },
  /*
   * Prospect follow-ups due for the signed-in person (owner 2026-10-23).
   * Appended, so a saved layout gains it at the end; ⛔ never rename the id.
   */
  { id: 'follow-ups', label: 'Follow-ups due' },
];

/*
 * How many rows stand above the fold. The rest are reachable by scrolling
 * inside the card — see `useFiveRowCap`, which turns this into a pixel height
 * by MEASURING five rendered rows rather than assuming one.
 */
const VISIBLE_ROWS = 5;

/*
 * The skeleton draws the same five, so the card does not settle at a different
 * height than it loaded at.
 */
const SKELETON_ROWS = VISIBLE_ROWS;

/* --- how a card's table is measured -------------------------------------- */

/*
 * 🔴 **EVERY COLUMN WIDTH ON THIS PAGE, IN ONE PLACE, IN `rem`. The `<col>`
 * widths and the table's minimum width are BOTH generated from it.**
 *
 * They used to be two Tailwind literals — a `<colgroup>` of `w-[7.5rem]` /
 * `w-11` and, on the tables that needed one, a `min-w-[…]` that a person had to
 * remember to move in step. This repo already records what that costs on the
 * quote and invoice line grids, where the same arrangement has drifted more than
 * once and where the file has to carry an explicit warning that "those two lists
 * are the one thing here that can silently drift apart". Deriving the sum
 * removes the possibility rather than warning about it: add a column below and
 * the minimum moves on its own.
 *
 * ⚠️ **That is also why these are numbers rendered through `style`, not Tailwind
 * classes.** Tailwind v4 scans SOURCE TEXT for literals, so `min-w-[${n}px]`
 * emits no rule at all — the class lands in the DOM and does nothing, which
 * looks exactly like a table that simply chose not to be that wide. A width that
 * has to be computed cannot be a utility class.
 */
const COLUMN_REM = {
  /*
   * A quote stage or an invoice status badge. The widest seeded label is
   * "Awaiting Payment"; these masters are admin-editable free text, so nothing
   * caps the real maximum — `Badge` does not truncate, so an over-long label
   * wraps the row taller, which `useFiveRowCap` measures rather than assumes.
   */
  status: 9,
  /* An `EnvelopeStatusBadge`. Non-terminal words only ("Sent", "Delivered"),
   * because the query behind this column is `outstanding: true`. */
  signature: 8,
  /* "999 days", tabular. */
  overdue: 6.5,
  /*
   * The company's country of record — owner's instruction, 2026-09-03.
   *
   * ⚠️ **Sized for the LONGEST NAMES IN THE MASTER, not for the ones on screen
   * today.** `f_countries` holds 196 rows and its labels run to "United States
   * Minor Outlying Islands"; under `table-fixed` a column is a real width, so a
   * name wider than this neither widens the column nor truncates itself — it
   * wraps, which is what the cell is written to do. 9rem holds "United Kingdom"
   * and "United States" on one line, which is where the data actually sits, and
   * lets the rare long one take two lines rather than pushing the record column
   * out of view.
   *
   * ⚠️ **It costs the two `/companies` cards a horizontal scroll they did not
   * have**: they go from 39.25rem to 48.25rem of minimum width, so they scroll
   * sideways below ~772px where they previously cleared 768. That is why their
   * action column is pinned — `tableMinWidthRem` derives both, so nothing here
   * had to be moved by hand.
   */
  country: 9,
  /*
   * Where the account came from — owner's instruction, 2026-09-14, on the two
   * `/companies` cards.
   *
   * ⚠️ **9rem, the SAME as `country` directly above, and it is copied rather
   * than re-derived because the two columns are the same shape of thing**: a
   * foreign key on `d_companies`, resolved to an admin-editable master name,
   * rendered as wrapping text. The seeded sources are short ("Organic",
   * "Conferences", "Referral", "Website", "Social Media" — the longest is about
   * 6rem at `--text-sm`), so 9rem holds every one of them on one line with room
   * for a heading and for a longer name somebody adds later.
   *
   * ⛔ **DO NOT CUT IT TO THE SEEDED NAMES.** `f_company_sources` is
   * admin-editable free text with a 60-character ceiling, so the real maximum is
   * not knowable from the rows on screen today — and under `table-fixed` a
   * column is a REAL width, so a longer name neither widens the column nor
   * truncates itself: it wraps, which is what `SourceCell` is written to do. The
   * same reasoning `country` states, and the same reason it is not sized to
   * "United Kingdom".
   *
   * ⚠️ **THE COST, STATED: it is on the two `/companies` cards, so each grows by
   * 9rem** — Leads 70.25 → 79.25rem and Companies 77.75 → 86.75rem for a reader
   * who has the row "+" (5.75rem of actions), 68 → 77 and 75.5 → 84.5 for one who
   * does not. Both cards already scroll sideways at every viewport this app is
   * tested at, with the record column frozen left and the action cell pinned
   * right (see `tableMinWidthRem` and `pinActions`), so this DEEPENS an existing
   * scroll rather than creating one and no control moves out of reach. Nothing
   * had to be moved by hand: both derivations sum this object.
   */
  source: 9,
  /*
   * What KIND of institution the account is — owner's instruction, 2026-09-14,
   * on the two `/companies` cards.
   *
   * ⚠️ **9rem, the SAME as `country` and `source` above, and copied from them
   * rather than re-derived, because all three are the same shape of thing**: a
   * foreign key on `d_companies`, resolved to an admin-editable master name,
   * rendered as wrapping text. Three columns, one width, one argument.
   *
   * ⛔ **DO NOT CUT IT TO THE SEEDED NAMES.** `f_company_types` is admin-editable
   * free text, so the real maximum is not knowable from the rows on screen today
   * — and under `table-fixed` a column is a REAL width, so a longer name neither
   * widens the column nor truncates itself: it wraps, which is what
   * {@link CompanyTypeCell} is written to do. The same reasoning `source` states
   * directly above, and `country` before it.
   *
   * ⚠️ **It also has to hold the HEADING, which is the longest of the three** —
   * `Company type` against `Country` and `Source`. That is safe rather than
   * tight: headings on this table WRAP (`FilterableTH`'s `wrap`, passed on every
   * one), so a heading wider than its column costs a second header line instead
   * of painting over its neighbour — the failure `Awaiting signature` gave
   * `Your last note`. ⛔ Do not widen the column for the heading.
   *
   * ⚠️ **THE COST, STATED: it is on the two `/companies` cards, so each grows by
   * 9rem** — Leads **79.25 → 88.25rem** and Companies **86.75 → 95.75rem** for a
   * reader who has the row "+" (5.75rem of actions); 77 → 86 and 84.5 → 93.5 for
   * one who does not. Both cards already scroll sideways at every viewport this
   * app is tested at, with the record column frozen left and the action cell
   * pinned right (see `tableMinWidthRem` and `pinActions`), so this DEEPENS an
   * existing scroll rather than creating one and no control moves out of reach.
   * Nothing had to be moved by hand: both derivations sum this object.
   */
  companyType: 9,
  /*
   * The projects this company is being pursued for — owner's instruction,
   * 2026-09-12.
   *
   * 🔴 **ON ALL FIVE CARDS, not two** — it is a member of `ALL_CARD_COLUMNS`, so
   * a deal, quote and invoice row carries its COMPANY's list. ⚠️ *SUPERSEDED,
   * later the same day: "on the Leads and Companies cards".* **The arithmetic
   * below was taken against that narrower reading and is kept as the record of
   * how the 12 was chosen — do not read it as the current minimum of any card.**
   * `tableMinWidthRem` sums this object, so the real figures move on their own
   * and nothing here had to be re-cut; what is stale is the two worked examples,
   * not the number.
   *
   * ⚠️ **12rem, and it is the FIRST width on this page that could not be chosen
   * to preserve a breakpoint.** Every other number here was picked as the widest
   * that did not push a card past a viewport it currently clears. That was not
   * available:
   *
   *   Leads is `RECORD_COLUMN_MIN_REM 11 + country 9 + note 18 + actions 3.5`
   *   = 41.5rem today, so 6.5rem is all there is before it stops clearing 768px
   *   (48rem) — and 6.5rem truncates `Business School Festival - India - Summer
   *   2026` to about `Business…`, which names nothing. That is the `Awaiting s…`
   *   failure this file already records at `FilterableTH`'s `wrap` prop, and a
   *   column that cannot say WHICH project is worth less than the scroll it
   *   saves.
   *
   * **So the cost is taken and stated: Leads goes 41.5 -> 53.5rem and joins the
   * four cards that already scroll sideways at 768** (Companies was past it
   * regardless — 51.25 -> 63.25rem). Nothing had to be moved by hand for it:
   * `tableMinWidthRem` sums this object and `pinActions` is derived from its
   * answer, so both cards' action cells pin themselves.
   *
   * ⚠️ **Sized for a SUMMARY of catalogue names, not for a name and not for a
   * paragraph** — which is why it sits between `country` and `note` rather than
   * beside either. `companyProjectsLabel` is the one copy of that wording and
   * the whole list is on the cell's `title`, the same arrangement `CitiesCell`
   * on `/projects` and the line grids' inclusion count already use. If that
   * helper is ever changed to print names in full, this number is wrong and the
   * column wraps instead of truncating — re-cut it there, not here.
   *
   * ✅ **12rem SURVIVES THE 2026-09-14 SPLIT, and the heading was the thing to
   * check.** Three of the five cards now head this column
   * {@link PROJECTS_ON_DOCUMENT_LABEL} — `Projects on this document`, about
   * twice the length of `Project of interest`. **Headings on this table wrap**
   * (`FilterableTH`'s `wrap`, passed on every one), so the longer string costs a
   * second or third header line on those cards and cannot paint over its
   * neighbour — which is the failure a plain `whitespace-nowrap` gives and is
   * what `Awaiting signature` did to `Your last note`. ⛔ **Do not widen this
   * column for the heading**: the cell's budget was derived from the two-name cap
   * and widening it changes what {@link ProjectsText} truncates on all five
   * cards, including the two whose heading did not move.
   *
   */
  projectOfInterest: 12,
  /**
   * The projects a DEAL, QUOTE or INVOICE names on its own line items — owner's
   * decision, 2026-09-14. See {@link DocumentProjectsCell}.
   *
   * 🔴 **12rem, THE SAME NUMBER AS `projectOfInterest` ABOVE, AND THAT IS A
   * DECISION RATHER THAN A COPY.** `lineProjects` takes the slot
   * `projectOfInterest` held on these three cards — same position, same width,
   * same two-name cap — so the arithmetic on that constant is this one's
   * arithmetic too and neither card set's layout moved. ⛔ **Re-cut them
   * together or not at all**: the cap lives in `company-projects.ts` and was
   * derived from 12rem once, for both.
   *
   * ⚠️ **THE HEADING IS ABOUT TWICE AS LONG AND THAT IS SAFE HERE.**
   * `PROJECTS_ON_DOCUMENT_LABEL` is `Projects on this document` against
   * `Project of interest`. **Headings on this table wrap** (`FilterableTH`'s
   * `wrap`, passed on every one), so the longer string costs a second or third
   * header line on these three cards and **cannot paint over its neighbour** —
   * which is the failure a plain `whitespace-nowrap` gives, and is what
   * `Awaiting signature` did to `Your last note` (reported 2026-09-03).
   * ⛔ **Do not widen this column for the heading**: the number is the CELL's
   * budget, and widening it changes what {@link ProjectsText} truncates.
   *
   * ⚠️ **The cell got no wider and its contents got LONGER.** Measured
   * 2026-09-14: **~21% of quotes and ~12% of invoices name 3+ projects, up to
   * 8**, where a company's set maxes out at 3 — so the `+N` marker and the
   * ellipsis fire far more often here than the cap was ever exercised on the
   * company cards. That is the arrangement working, the whole list being on the
   * cell's `title`, and not a reason to re-cut this number.
   */
  lineProjects: 12,
  /*
   * "Quote signed" — the DEALS card only (owner 2026-10-27). The cell is
   * `quoteSignedLabel`, e.g. "Signed Oct 12, 2026", about 7.5rem at
   * `--text-sm`; 9rem leaves the `px-3` padding, the `lastNoteAt` arithmetic.
   * A longer locale date wraps rather than painting over a neighbour.
   *
   * ⚠️ **THE COST, STATED: the Deals card grows by 9rem and no other card
   * moves.** `PartnershipColgroup` and `tableMinWidthRem` both read this number.
   */
  quoteSigned: 9,
  /*
   * The company's credit balance — the COMPANIES card only (owner's decision
   * 2026-09-24). 9rem holds one currency's figure (`12,345.00 GBP`); a second
   * or third currency truncates and `balanceTitle` carries the whole set on the
   * cell's `title`, which is load-bearing because an ellipsis eats the tail.
   *
   * ⚠️ **THE COST, STATED: the Companies card grows by 9rem and no other card
   * moves.** `PartnershipColgroup` and `tableMinWidthRem` both read this one
   * number, so the `<col>` width and the card's minimum move together.
   */
  creditBalance: 9,
  /*
   * What the company has BOUGHT and the projects it has TAKEN PART IN, across
   * every cycle — the COMPANIES card only, read-only. 12rem each, the
   * `projectOfInterest` width, because both render through the same two-name
   * label (`companyProjectsLabel`) and that cap was derived from 12rem; the full
   * per-cycle breakdown is on the cell's `title`.
   *
   * ⚠️ **THE COST, STATED: the Companies card grows by 24rem and no other card
   * moves.** Both derivations sum this object.
   */
  productsPurchased: 12,
  projectsParticipated: 12,
  /*
   * Whether an open task sits anywhere on this account — owner's instruction,
   * 2026-09-12, on all five cards.
   *
   * ⚠️ **6.5rem, and it is sized for the HEADING rather than for the cell.** The
   * cell is `Yes` or `No`; the heading is `Open task`, which at `--text-sm` is
   * about 62px, and `TD`/`TH` carry `px-3` — 1.5rem across BOTH sides, which is
   * the arithmetic slip that produced the action column's overflow on 2026-09-04.
   * 6.5rem leaves ~80px of content box, so the heading sits on one line and does
   * not take the second header line that `TH`'s `wrap` would otherwise give it.
   * The same number as `overdue` beside it, whose heading (`Overdue by`) is a
   * shade wider and fits — so this is a measured neighbour rather than a guess.
   *
   * ⛔ **A FLOOR IS THE WRONG SHAPE HERE.** Under `table-fixed` a `<col>` width
   * is a REAL width the cell cannot exceed, so a number that "leaves room to
   * grow" does not stretch — the content spills, and with it the column beside
   * it. `PartnershipColgroup` and `tableMinWidthRem` both read this one number,
   * which is what stops those two disagreeing.
   *
   * ⚠️ **THE COST, STATED: IT IS ON ALL FIVE CARDS, so every card's minimum
   * width grows by 6.5rem** — Leads 53.5 → 60rem, Companies 63.25 → 69.75rem, and
   * the three document cards likewise. Every one of them already scrolls sideways
   * at 768px with its action cell pinned (see `tableMinWidthRem` and
   * `pinActions`), so this widens a scroll that already exists rather than
   * creating one. Nothing had to be moved by hand for it: both derivations sum
   * this object.
   */
  upcomingTask: 6.5,
  /*
   * The reader's own last note — free text a person typed.
   *
   * 🔴 **18rem, AND THE NOTE NOW WRAPS INSTEAD OF TRUNCATING — owner's
   * instruction, 2026-09-03: the note should be shown in full, and it may read
   * as a paragraph.** *SUPERSEDED: "10, chosen as the widest that does not push
   * a card past a breakpoint it currently clears, not as the widest that fits a
   * note … a recognisable opening rather than a readable note, with the whole
   * of it on the cell's `title` and the drawer one click away."* That was a
   * deliberate trade and the owner has taken the other side of it.
   *
   * ⚠️ **The number is derived, not chosen: it is the widest that leaves the
   * THREE-COLUMN cards fitting a 768px viewport without scrolling sideways.**
   * Those cards sum to `RECORD_COLUMN_MIN_REM 11 + note + 7.5 + 2.75`, so 18
   * puts them at 39.25rem. The quotes and invoices cards carry a status and a
   * signature column too and already scrolled sideways at 768 with the old 10 —
   * widening the note makes that worse, it does not create it. Every rem here
   * is still spent on all five cards at once (see `ALL_CARD_COLUMNS` —
   * ⚠️ *SUPERSEDED 2026-09-12: "see `NOTE_ONLY`"*, a constant DELETED that day;
   * a stale cross-reference to a dead symbol is this codebase's own grep-the-
   * surface trap, because searching for the name still finds this line).
   *
   * ⚠️ **`table-fixed` means this is a REAL width, not a suggestion**, so the
   * paragraph is 18rem wide on every card at every viewport above the scroll
   * point — about 45 characters a line. The record column takes the surplus at
   * wide viewports, which is why the note does not grow past this on a big
   * screen: the identity of the row is what benefits from the extra space.
   *
   * ⚠️ It also has to hold the HEADING. That used to constrain this number and
   * NO LONGER DOES: column headings on this table now wrap (`TH`'s `wrap`), so
   * a heading longer than its column costs a second header line instead of
   * painting over its neighbour, which is the defect reported the same day.
   */
  note: 18,
  /*
   * 🔴 **The two COMPANY cards' replacement for `note` — 2026-10-02, owner's
   * decision.** The account's last human note by ANYONE: author line plus the
   * body clamped to four lines (`AccountLastNoteCell`). **18rem, the width `note`
   * held in the same slot**, so the paragraph reads the same and the cards grow
   * only by the date column beside it.
   */
  lastNote: 18,
  /*
   * The date of that note — `formatDateTime`, e.g. "Oct 2, 2026, 2:05 PM",
   * about 8rem at `--text-sm`. 9rem leaves the `px-3` padding; a longer locale
   * format wraps to a second line rather than painting over a neighbour, and the
   * heading wraps too (`FilterableTH`'s `wrap`).
   *
   * ⚠️ **THE COST, STATED: Leads and Companies each grow by 9rem** (the note
   * column itself is a like-for-like swap). Both already scroll sideways at every
   * tested viewport with the record column frozen and the action cell pinned, so
   * this deepens an existing scroll. The `<col>` width and the table minimum both
   * read this number, so nothing else moved by hand.
   */
  lastNoteAt: 9,
  /*
   * ⚠️ **A SLOT, never rendered** (owner 2026-10-25) — the hook's marker for
   * where the per-stage status columns go. The panel removes it from the column
   * list and appends one `stageStatus:<id>` key per stage after the Stage column
   * instead, each `STAGE_STATUS_COLUMN_REM` wide. The entry exists only because
   * this record is exhaustive; it is the same width so nothing reads as 0.
   */
  stageStatuses: 11,
  /*
   * The record's CURRENT resolved account lead (owner 2026-10-27) — its own
   * override, else the company's lead today — on all five cards. A staff full
   * name; a longer one wraps rather than painting over a neighbour.
   */
  accountLead: 10,
} as const satisfies Record<PartnershipColumn, number>;

/** Money with its code — "100,000.00 GBP". */
const VALUE_COLUMN_REM = 7.5;

/**
 * One per-stage `{Stage} status` column (owner 2026-10-25), keyed
 * `stageStatus:<stageId>` — see `@/lib/stage-status-columns`. The keys are
 * DYNAMIC (one per stage the card holds), so they are not members of
 * `COLUMN_REM`; `columnWidthRem` and `tableMinWidthRem` read this instead.
 * The editable cell's select has a 9rem floor, plus the `TD`'s `px-3` twice.
 */
const STAGE_STATUS_COLUMN_REM = 11;

/* --- the ONE ordered column list ----------------------------------------- *
 *
 * 🔴 **THE COLGROUP, THE HEADER, THE BODY CELLS AND THE SKELETON ALL WALK ONE
 * LIST NOW, AND UNTIL 2026-09-12 THEY WALKED FOUR.**
 *
 * ⚠️ *SUPERSEDED, and it was true of every one of them:* `columnKeys` drove the
 * head; `PartnershipColgroup`, `ExtraCells` and `RowsSkeleton` each re-derived
 * their own sequence from `state.extraColumns`; and the record cell and the
 * value cell were hard-coded first and second-last in three of those four
 * places. Four derivations of one layout agreed only because nothing could
 * reorder them — which is exactly what the column manager now does.
 *
 * ⚠️ **Under `table-fixed` a disagreement between any two of them does NOT fail
 * loudly.** A head cell with no matching `<col>` shifts every column after it
 * and the browser pads the shortfall at the end: the table renders, nothing
 * errors, and the headings simply sit over the wrong cells. This file already
 * carried that warning twice — at `PartnershipColgroup` and at
 * `PartnershipHead` — which is the tell that a warning was standing in for a
 * structure.
 *
 * So there are exactly three kinds of key, `COLUMN_REM` is the register of the
 * middle one, and {@link columnWidthRem} is the single place a key becomes a
 * width.
 */

/** The row's identity. LOCKED — see {@link LOCKED_COLUMN_KEYS}. */
const RECORD_KEY = 'record';
/**
 * The per-card value column, whose HEADING is `state.valueLabel` and whose
 * meaning is not the same on all five cards — money on deals, quotes and
 * invoices, a stage name on leads and companies, and absent on Leads entirely.
 * That is why the stored layout is scoped per card and never per page.
 */
const VALUE_KEY = 'value';
/**
 * The tasks-and-notes lane.
 *
 * ⛔ **DELIBERATELY NOT A MEMBER OF THE MANAGED LIST**, unlike `/deals` and
 * `/quotes`, which do carry theirs. Three reasons, and the third is the one that
 * bites: it has no heading to name in the manager; it is the only column here
 * whose width is derived from which CONTROLS render rather than from
 * `COLUMN_REM`; and it is `pinned`, which is `sticky right-0` and can therefore
 * only ever be the LAST column — a manager entry that let anything move past it
 * would silently unpin the one control this page has already had reported as
 * unreachable. It is appended after the managed keys everywhere, exactly as it
 * was before.
 *
 * It is still counted by {@link useFrozenOffsets} as a right-frozen key, because
 * its rendered width has to be counted into the offset of any OTHER column
 * frozen to that edge. See {@link PINNED_COLUMN_KEYS}.
 */
const ACTIONS_KEY = 'actions';

/**
 * Keys this page renders with `pinned` — the actions lane, and only it.
 *
 * ✅ *SUPERSEDED 2026-09-12: a local `measuredSideOf` callback that answered
 * `'right'` for `ACTIONS_KEY` and delegated everything else to the controller,
 * memoised on `frozenSideOf` so the offsets hook's `ResizeObserver` was not
 * rebuilt every render.* **{@link useFrozenOffsets} takes the pinned set
 * directly now**, so the page hands it this array and the hook does the same
 * substitution internally. One less function whose identity had to be nursed.
 *
 * ⛔ **NOT passed to `useColumnLayout` or `ColumnManager`, and that is not an
 * omission.** Both of those only ever read a key that is IN the managed list,
 * and `ACTIONS_KEY` deliberately is not one — see above. A `pinnedKeys` naming
 * it there would be a prop that can never fire, which reads to the next person
 * as a guard that is doing something.
 *
 * ⚠️ **Module-level so the reference is stable**, for the same reason
 * {@link LOCKED_COLUMN_KEYS} is: the hook memoises a `Set` on this array's
 * identity, and a fresh literal per render would rebuild it — and with it the
 * measuring effect's `sideOf` — on every keystroke in a funnel.
 */
const PINNED_COLUMN_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * Keys the reader may neither hide nor move.
 *
 * ⚠️ **`record` ALONE, and the reason is structural rather than editorial.** It
 * identifies the row — so hiding it leaves five cards of anonymous figures — and
 * it is the one `<col>` carrying NO width, which under `table-fixed` is what
 * lets it absorb the surplus and truncate instead of pushing the action cell off
 * the right edge. Moving it out of first position, or hiding it, would need a
 * real width chosen for it first.
 *
 * ⚠️ **Module-level so the reference is stable** — `useColumnLayout` memoises
 * its locked set on this array's identity, and a fresh literal per render would
 * rebuild it on every keystroke in a funnel.
 *
 * ✅ *SUPERSEDED 2026-09-12: "`ColumnManager`'s disabled `title` on a locked key
 * reads 'This column holds the row actions and cannot be hidden', which is the
 * wrong sentence for this one. That string is baked into the shared component;
 * it is reported, not worked around here."* **It takes a `lockReasonOf` now** —
 * see {@link columnLockReason}.
 */
const LOCKED_COLUMN_KEYS: readonly string[] = [RECORD_KEY];

/**
 * Keys whose EDGE this page decides, so the column manager offers no freeze
 * control for them.
 *
 * 🔴 **`record` ALONE, and it is frozen LEFT on every card — owner's
 * instruction 2026-09-13.** The record column names the row, and these five
 * tables scroll sideways at every width a person reads them at (Quotes' minimum
 * is ~1372px against a content area of roughly 1184px at 1440, and the cards are
 * 2-up from `lg`), so every column past the first was being read against a name
 * that had already scrolled away.
 *
 * ⛔ **WHY THE READER MAY NO LONGER CHOOSE THIS ONE COLUMN'S EDGE, rather than
 * it merely defaulting to left.** `useColumnLayout` stores the freeze lists and
 * nothing else, so "never chosen" and "chosen: neither edge" are the SAME stored
 * state — an empty pair of arrays. A default applied on top of that is
 * unclearable: `ColumnManager`'s freeze-left control calls
 * `setFrozen(key, undefined)` when the column already reads as left, that writes
 * the same empty state back, and the default re-applies. **The button would read
 * as pressed while nothing moves**, which is exactly the affordance-with-one-
 * outcome this repo refuses everywhere else. Making the edge the page's — the
 * standing the actions lane already has — is the one shape that stays honest
 * without a `defaultLayout` on the shared hook.
 *
 * ⚠️ **NOT the same list as {@link PINNED_COLUMN_KEYS}, and the two must not be
 * merged.** That one goes to `useFrozenOffsets`, which substitutes **`'right'`**
 * for every key in it — correct for the actions lane and the exact opposite of
 * what this column needs. `ACTIONS_KEY` is deliberately absent from THIS list
 * too: it is not in the managed set, so the manager never renders a row for it
 * and would have nothing to omit — a prop entry that can never fire reads to the
 * next person as a guard that is doing something.
 *
 * ⚠️ **Module-level for a stable reference**, like its two neighbours.
 */
const PAGE_FROZEN_COLUMN_KEYS: readonly string[] = [RECORD_KEY];

/**
 * Why a locked column is locked, in the reader's own terms.
 *
 * ⚠️ **It names the RECORD column, not the actions lane, and the difference is
 * the whole reason this function exists.** Every other list page here locks its
 * actions lane, so the shared component's fallback sentence is about row
 * controls — and pasted onto this page it would tell the reader that the column
 * carrying the school's name holds the row actions, which is both false and
 * unactionable.
 *
 * ⚠️ **A why-this-is-disabled sentence, which is one of the four kinds the
 * no-helper-text rule explicitly keeps.** A bare `disabled` switch makes a
 * deliberately locked column and a broken control identical.
 *
 * Returns `undefined` for anything else, so the shared fallback still covers a
 * key added to {@link LOCKED_COLUMN_KEYS} without a sentence being written for
 * it — a general sentence beats no sentence, and a wrong one beats neither.
 */
function columnLockReason(key: string): string | undefined {
  return key === RECORD_KEY
    ? 'This column names the record, so the rest of the row has nothing to describe without it'
    : undefined;
}

/**
 * Where one card's stored column layout lives.
 *
 * 🔴 **ONE SCOPE PER CARD, KEYED BY THE CARD'S OWN `state.key`, AND A SINGLE
 * PAGE-WIDE SCOPE WOULD BE A DEFECT RATHER THAN A SIMPLIFICATION.** The five
 * cards do not render the same columns: Leads carries no `value` column at all,
 * only the two `/companies` cards carry `country`, only Quotes carries
 * `signature` and only Invoices carries `overdue`. Worse, `value` is not one
 * column wearing five hats — its heading is `state.valueLabel`, which is
 * **"Stage" on leads and companies and money on the other three**. A reader
 * hiding "Invoice value" under a shared scope would find the Stage column gone
 * from Companies, with nothing on either card saying why.
 *
 * ⚠️ **`up.` prefixes the five keys** because the scope namespace is the whole
 * account's, shared with every other table in this app — `leads`, `deals`,
 * `quotes` and `invoices` are all names a list page could reasonably want for
 * itself, and `/deals` and `/quotes` have already claimed two of them.
 *
 * ⚠️ **The stored branch can only ever SUBTRACT** — `useColumnLayout` stores the
 * HIDDEN keys, never the visible ones — so a column this page gains later
 * appears for everybody, including readers who have already arranged a card.
 */
function columnLayoutScope(key: PartnershipTableKey): string {
  return `up.${key}`;
}

/**
 * Whether a layout key is one of the per-card extra columns.
 *
 * ⚠️ **Tested against `COLUMN_REM`, never against a list written here.** That
 * object is `satisfies Record<PartnershipColumn, number>`, so a sixth column
 * added to the union cannot compile without a width — and therefore cannot be
 * missed by this predicate. A hand-kept list, or a negative test against
 * `record`/`value`/`actions`, would silently answer `false` for it and drop the
 * column from the widths, the minimum and the body at once.
 */
function isPartnershipColumn(key: string): key is PartnershipColumn {
  return Object.hasOwn(COLUMN_REM, key);
}

/**
 * One key's `<col>` width in `rem`, or `null` for the column that takes what is
 * left.
 *
 * ⚠️ **`null` is NOT "unknown" and must not be coalesced to a number.** It is
 * the record column's declared behaviour: a `<col>` with no width under
 * `table-fixed` absorbs the surplus, which is what makes a long school name
 * truncate rather than widen the table.
 */
function columnWidthRem(key: string): number | null {
  if (key === VALUE_KEY) return VALUE_COLUMN_REM;
  if (isStageStatusColumnId(key)) return STAGE_STATUS_COLUMN_REM;
  return isPartnershipColumn(key) ? COLUMN_REM[key] : null;
}

/*
 * The action cell measured from the primitives it actually contains, rather
 * than estimated. Each is read off the component, so a change there is a
 * change here and not a number that silently stops matching:
 *
 *   Button size="sm" iconOnly -> `h-8 w-8`   (Button.tsx ICON_SIZES.sm)
 *   the wrapper                -> `gap-1`     (0.25rem)
 *   TD                         -> `px-3`      (0.75rem EACH SIDE)
 *
 * ⚠️ `ACTION_CELL_PADDING_REM` is BOTH sides. Counting one is the arithmetic
 * slip that produced the overflow these replaced.
 */
const ACTION_BUTTON_REM = 2;
const ACTION_BUTTON_GAP_REM = 0.25;
const ACTION_CELL_PADDING_REM = 1.5;

/**
 * What the action cell must be drawn at to hold `buttons` icon buttons.
 *
 * 🔴 **A COUNT SINCE 2026-09-17, WHERE IT WAS A BOOLEAN — because the cell can
 * now hold THREE.** *SUPERSEDED: two constants, `ACTION_COLUMN_REM` (the
 * tasks-and-notes button alone) and `ACTION_COLUMN_WITH_CREATE_REM` (that
 * button and the row "+"), chosen between by a `hasRowCreate` boolean at both
 * consumers.* The Leads and Companies cards carry a contacts button (a mail
 * button until 2026-10-16; wider, see `CONTACTS_BUTTON_EXTRA_REM`) as well, and
 * the "+" is offered per module permission — so the four combinations of
 * {create?} × {mail?} produce one, two or three buttons and there is no boolean
 * that names the answer.
 *
 * ⚠️ **BOTH OLD FIGURES ARE REPRODUCED EXACTLY, which is what makes this
 * change auditable**: `actionColumnRem(1)` is 3.5 and `actionColumnRem(2)` is
 * 5.75, the two numbers that were declared before. **Only a card that gains the
 * third button moves**, to 8.
 *
 * 🔴 **THE FIGURES WERE WRONG UNTIL 2026-09-04, and the "+" is what made it
 * visible.** *SUPERSEDED: `ACTION_COLUMN_REM = 2.75` and
 * `ACTION_COLUMN_WITH_CREATE_REM = ACTION_COLUMN_REM + 2.25`, described as "a
 * FLOOR rather than an exact sum — neither counts the `TD`'s own `px-3`".*
 *
 * ⚠️ **A FLOOR IS THE WRONG SHAPE FOR A `<col>` WIDTH.** This number feeds
 * `tableMinWidthRem`, where under-counting only costs a slightly narrow table —
 * AND `PartnershipColgroup`, where under `table-fixed` a `<col>` width is a
 * REAL width the cell cannot exceed. The padding is INSIDE that box, so a
 * figure that omits it is not conservative, it overflows: with `justify-end`
 * the surplus spills LEFT, and the "+" landed under the value column. Reported
 * on the Quotes card. **That is the defect a third button would have
 * reproduced had this stayed a boolean** — nothing would have failed, and the
 * mail button would simply have sat under the Note column.
 *
 * ⚠️ **ONE function rather than a constant per shape, and that is the point.**
 * The two consumers are required to agree — the colgroup declares the width and
 * the minimum reserves it — so a third constant would have been a third place
 * for the arithmetic to drift. **`n - 1` gaps, never `n`**: the wrapper's
 * `gap-1` sits BETWEEN buttons, so counting one per button over-reserves at
 * every count and silently hands the surplus to the record column.
 *
 * ⚠️ **The `max-sm` shortfall is UNCHANGED and still deliberate, and it now
 * runs one button deeper.** The primitives take their touch bump to `w-10`
 * there, so two buttons want 6.75rem against 5.75 and three want 9.5 against 8.
 * Sizing every desktop card for a phone would waste a column on all five; the
 * action cell is PINNED on every card that scrolls, so the shortfall costs a
 * little overlap at the right edge and never an unreachable control. That was
 * the right call and it survives.
 */
function actionColumnRem(buttons: number, extraRem: number): number {
  return (
    ACTION_BUTTON_REM * buttons +
    ACTION_BUTTON_GAP_REM * (buttons - 1) +
    ACTION_CELL_PADDING_REM +
    extraRem
  );
}

/**
 * How much wider than an icon button the contacts button is drawn — it carries
 * the contact count beside the people icon (owner, 2026-10-16), at a fixed
 * `w-14` (3.5rem) against an icon button's 2rem.
 *
 * ⚠️ **Required at every consumer, never defaulted** (`actionColumnRem`'s
 * second parameter). The colgroup and the table minimum must reserve the same
 * width; a default would let one of them forget it and the button would spill
 * left under the neighbouring column — the 2026-09-04 overflow again.
 */
const CONTACTS_BUTTON_EXTRA_REM = 1.5;
/**
 * What the record column may be squeezed to before the table starts scrolling
 * sideways instead.
 *
 * ⚠️ Only consulted on a card that HAS extra columns.
 *
 * 🔴 **AS OF 2026-09-03 THAT IS ALL FIVE, AND THE "no minimum" CARD NO LONGER
 * EXISTS.** *SUPERSEDED: "A three-column card is `w-full table-fixed` with no
 * minimum at all, exactly as before — its record column absorbs whatever is left
 * and truncates, and there is nothing to scroll."* The note column is on every
 * card (see `ALL_CARD_COLUMNS` in `use-partnership-tables.ts` — ⚠️ *SUPERSEDED
 * 2026-09-12: "see `NOTE_ONLY`"*, deleted that day), so leads, companies and
 * deals now declare a minimum and scroll sideways below it, with their action
 * column pinned for it.
 *
 * 🔴 **AND THE MINIMUM GREW AGAIN ON 2026-09-12: `projectOfInterest` and
 * `upcomingTask` joined `note` on all five cards.** Three universal columns
 * now, not one — so every card scrolls sideways sooner, and the two company
 * cards carry `country` on top of that. The arithmetic is DERIVED
 * (`tableMinWidthRem` sums `COLUMN_REM`), so it moved on its own and no figure
 * here had to be re-typed; that is the whole reason it is derived.
 *
 * ⚠️ **The `null` branch below is KEPT rather than deleted.** It is what a
 * future card with no extra columns would take, and removing it would make
 * adding one a change to this function rather than a line in a spec — the same
 * reason `PartnershipColumn` is a union and not a boolean.
 */
/*
 * 🔴 **RAISED 11 → 19 ON 2026-09-13, ON THE OWNER'S INSTRUCTION: "the record
 * column width should be like deals in both quotes and invoices".**
 *
 * ⚠️ **MEASURED, NOT CHOSEN.** At 1440 the record column was **306px on Deals
 * and 176px on Quotes and Invoices** — and 176px IS 11rem, i.e. those two cards
 * were sitting exactly on this floor while Deals floated well above it. 19rem is
 * 304px, which puts all three within two pixels of each other.
 *
 * ⚠️ **WHY THE TWO DIVERGED, because the number is a symptom and the cause is
 * the layout.** The record `<col>` is the ONE column with no declared width — it
 * absorbs whatever the others leave (see `columnWidthRem`, and the `null` branch
 * in `PartnershipColgroup`). Deals carries four extra columns, Quotes carries
 * six; the surplus Deals hands its record column is the width Quotes has already
 * spent on Status and Awaiting signature. So the gap was never a bug in either
 * card — it is what "absorb the remainder" MEANS once two cards stop carrying
 * the same columns, and it widened on 2026-09-12 when two more universal columns
 * landed.
 *
 * ⚠️ **THE COST, STATED RATHER THAN DISCOVERED: Quotes and Invoices scroll
 * sideways further.** `tableMinWidthRem` sums this floor, so their minimum grows
 * by 8rem — Quotes goes from 1244px to ~1372px against a content area of roughly
 * 1184px at 1440. They ALREADY scrolled at that width (1244 > 1184), so this
 * deepens an existing scroll rather than introducing one, and the action column
 * is pinned on exactly the cards that scroll, so no control moves out of reach.
 * The owner was shown both figures and asked for parity anyway.
 *
 * ⛔ **AND DEALS IS UNAFFECTED, WHICH IS THE POINT OF RAISING THE FLOOR RATHER
 * THAN DECLARING A WIDTH.** 19rem is below the 306px Deals already yields, so
 * that card keeps absorbing and nothing about it moves. Declaring a real width
 * on the record `<col>` would have pinned all five cards to one number and taken
 * the surplus away from Deals — fixing the complaint by making the good case
 * worse. A floor only ever raises the short ones.
 *
 * ⚠️ It is a MINIMUM and the record column still truncates rather than wrapping,
 * so a long school name is still cut — this buys ~130px more of it on two cards,
 * not an end to truncation.
 */
const RECORD_COLUMN_MIN_REM = 19;

/**
 * The narrowest this card's table may be drawn, or `null` for "no minimum —
 * fit the card".
 *
 * ⚠️ **A minimum means the table SCROLLS SIDEWAYS below that width, and this
 * page has that defect on record**: the table once carried a 520px minimum,
 * wider than the card, so the action cell sat past the right edge and could
 * only be reached by swiping — "a control you have to discover by swiping is
 * not a control". The cure is not to go back to no minimum, which is what put
 * the status under the record name in the first place; it is to PIN the action
 * column (see `pinActions`), which is the one thing that column is eligible for
 * — `TD`'s `pinned` is `sticky right-0` and can only ever hold the last column.
 */
function tableMinWidthRem(
  /**
   * 🔴 **THE VISIBLE, ORDERED KEYS — not the card's declared column set.**
   * *SUPERSEDED 2026-09-12: two parameters, `extraColumns` and a separate
   * `hasValueColumn` boolean, each of which every call site had to keep in step
   * with the one handed to `PartnershipColgroup`.* Both now read the same list
   * the header and the body walk, so the "MUST be the same expression" warning
   * that used to sit on the second parameter has nothing left to warn about.
   *
   * ⚠️ **It has to follow HIDING, and this is where the consequence lands.**
   * `pinActions` is derived from whether this returns a number, so a reader who
   * hides every extra column takes this to `null` and unpins the action cell —
   * which is correct, because a table with no minimum cannot scroll sideways and
   * a pinned cell there draws a hairline down the middle of the card for no
   * reason. It is only consistent while both read THIS list; a minimum that
   * followed the visible set beside a `pinActions` that followed the declared
   * one would pin a cell on a table with nothing to scroll.
   */
  columnKeys: readonly string[],
  /**
   * How many icon buttons this card's rows actually render in the action cell.
   *
   * 🔴 **A COUNT SINCE 2026-09-17.** *SUPERSEDED: `hasRowCreate: boolean`,
   * "whether this card's rows carry the '+' menu as well as the activity
   * button".* The Leads and Companies cards carry a contacts button too, so there
   * are three possible widths and no boolean that names them.
   *
   * ⚠️ **Passed rather than derived from the card's key, and BOTH reasons are
   * live.** The "+" is offered per MODULE permission, so a reader who may
   * create nothing gets one button fewer than the card normally shows; and the
   * contacts button is offered on two cards of the five. **The minimum has to
   * follow what actually renders**, not what the card declares — which is the
   * same rule the `columnKeys` parameter above already states for hiding.
   */
  actionButtons: number,
  /** Width beyond `actionButtons` icon buttons — see `CONTACTS_BUTTON_EXTRA_REM`. */
  actionExtraRem: number,
): number | null {
  const extras = columnKeys.filter(isPartnershipColumn);
  // The per-stage status columns count as extras too (dynamic keys, one width).
  const stageStatusCount = columnKeys.filter(isStageStatusColumnId).length;
  if (extras.length === 0 && stageStatusCount === 0) return null;
  const extrasRem =
    extras.reduce((total, column) => total + COLUMN_REM[column], 0) +
    stageStatusCount * STAGE_STATUS_COLUMN_REM;
  const actions = actionColumnRem(actionButtons, actionExtraRem);
  const value = columnKeys.includes(VALUE_KEY) ? VALUE_COLUMN_REM : 0;
  return RECORD_COLUMN_MIN_REM + extrasRem + value + actions;
}

/**
 * Which edge a key is frozen to, and how far in from it — as the CELLS need it.
 *
 * ⚠️ **THIS IS NOT THE CONTROLLER'S `frozenSideOf`, and handing one function to
 * both the cells and the measurer gets one of them wrong.** The actions lane
 * reports as UNFROZEN here, because it renders `pinned` — which is
 * `frozen="right"` at offset zero, is what fifteen pages already say, and whose
 * `box-shadow` literal cannot merge with the frozen one on a single cell. It
 * counts as right-FROZEN inside {@link useFrozenOffsets}, which is handed
 * {@link PINNED_COLUMN_KEYS} for exactly that, so its rendered width is counted
 * into the offset of any other column frozen to that edge; without that, a
 * second right-frozen column would sit underneath it.
 */
interface FrozenProps {
  frozen?: 'left' | 'right';
  frozenOffset?: number;
}

/**
 * What every part of the table needs to place a frozen column: the controller's
 * own `frozenSideOf`, and the MEASURED offsets.
 *
 * ⚠️ **Threaded as a pair, and to all four of the colgroup's fellow travellers
 * — the head, the body and the skeleton.** A frozen column that sticks on the
 * loaded table and not on the skeleton, or on the body and not on its own
 * heading, is not a half-built feature: it is a column that slides out from
 * under its own name as the card scrolls sideways, with nothing erroring.
 */
interface FreezeProps {
  frozenSideOf: (key: string) => 'left' | 'right' | undefined;
  frozenOffsets: Record<string, number>;
}

function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => 'left' | 'right' | undefined,
  offsets: Record<string, number>,
): FrozenProps {
  if (key === ACTIONS_KEY) return {};
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

/* --- icons --------------------------------------------------------------- */

interface IconProps {
  className?: string;
}

function iconProps(className?: string) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className: cx('shrink-0', className ?? 'h-3.5 w-3.5'),
  };
}



function InboxIcon({ className }: IconProps) {
  return (
    <svg {...iconProps(className)}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

/* --- the follow-ups tile ------------------------------------------------- */

/*
 * The three buckets the tile counts, each mapped to its summary field. Labels
 * come from `BUCKET_LABELS` (one copy); the link keys are the server's bucket
 * keys, which `/prospect-follow-ups` reads from `?bucket=`.
 */
const FOLLOW_UP_TILES: readonly {
  bucket: ProspectFollowUpBucket;
  field: keyof ProspectFollowUpSummary;
}[] = [
  { bucket: 'overdue', field: 'overdue' },
  { bucket: 'due_soon', field: 'dueSoon' },
  { bucket: 'resurfacing', field: 'resurfacing' },
];

type FollowUpSummaryState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; summary: ProspectFollowUpSummary };

/**
 * "Follow-ups due" — the signed-in person's own Prospect follow-up counts
 * (`mine: true`), each tile a real link into the tracker filtered to it.
 * Rendered only for a reader with `trackers-prospect-follow-ups` AND
 * `companies` read (the server's real guard); the caller gates it, so the
 * read never starts for anybody else.
 *
 * ⚠️ A count the server did not send (an older API) renders `—`, never 0.
 */
function FollowUpsPanel() {
  const headingId = useId();
  const [state, setState] = useState<FollowUpSummaryState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    api
      .getProspectFollowUpSummary({ mine: true }, signal)
      .then((response) => {
        if (signal.aborted) return;
        setState({ kind: 'ready', summary: response.data });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        setState({
          kind: 'error',
          message:
            caught instanceof ApiError || caught instanceof Error
              ? caught.message
              : 'Couldn’t read your follow-ups.',
        });
      });
    return () => controller.abort();
  }, []);

  return (
    <Panel className="overflow-hidden">
      <PanelHeader id={headingId} title="Follow-ups due" />
      {state.kind === 'error' ? (
        <div className="p-4">
          <Notice tone="danger">{state.message}</Notice>
        </div>
      ) : (
        <ul
          aria-labelledby={headingId}
          className="m-0 grid list-none grid-cols-1 gap-3 p-4 sm:grid-cols-3"
        >
          {FOLLOW_UP_TILES.map(({ bucket, field }) => {
            const count = state.kind === 'ready' ? state.summary[field] : undefined;
            const danger = bucket === 'overdue' && typeof count === 'number' && count > 0;
            return (
              <li key={bucket} className="min-w-0">
                <Link
                  to={`/prospect-follow-ups?mine=1&bucket=${bucket}`}
                  className="block rounded-md outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                >
                  <StatTile
                    label={BUCKET_LABELS[bucket]}
                    className={cx(
                      'transition-colors hover:bg-surface-hover',
                      danger && 'border-danger-border',
                    )}
                    value={
                      state.kind === 'loading' ? (
                        <Skeleton className="h-8 w-12" />
                      ) : typeof count === 'number' ? (
                        <span className={cx(danger && 'text-danger')}>{count}</span>
                      ) : (
                        '—'
                      )
                    }
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

/* --- the funnel tiles ---------------------------------------------------- */

/**
 * What the WHOLE panel is doing, resolved once.
 *
 * ⚠️ **`TileState` AND `tileState` MOVED TO `@/components/dashboards/TargetTiles`
 * ON 2026-09-02** so the Management Dashboard reports the same five states about
 * a group that this page reports about a person. The reasoning travelled with
 * them and is on the type; what stays here is only the WORDING, because these
 * sentences are about *the accounts you lead* and the Management Dashboard's are
 * about a team.
 *
 * ⚠️ **`tileState` gained a `refused` member for the Management Dashboard's 403,
 * and this page can never produce one** — it asks about the signed-in user and
 * nobody else, which is the one subject `/targets/actuals` never refuses. The
 * branch is unreachable from here and is deliberately still handled below:
 * falling through to "Couldn't read your figures" would be a claim about the
 * server this page cannot support.
 */
type FunnelState = TileState;

/*
 * ⚠️ **`allZero` READ `Number(row.actual) === 0` UNTIL THE EXTRACTION, AND
 * `Number(null)` IS `0`.** It could not fire here — this page never sees a null
 * actual — but the shared version does, so the null test went in with it. Left
 * recorded because the shape is the point: a coalesce nobody wrote, supplied by
 * the language, turning "cannot be measured" into "achieved nothing" on the one
 * subject the whole `actual: null` contract exists for.
 */
function funnelState(targets: MyTargets, blocked: boolean): FunnelState {
  return tileState({
    blocked,
    loading: targets.loading,
    error: targets.error,
    noPeriod: targets.noPeriod,
    byMetric: targets.byMetric,
  });
}

/**
 * The one thing that is true of every tile, said once, above them.
 *
 * ⚠️ **ONLY A SERVER FAILURE OR A REFUSAL IS SAID HERE** — owner's ruling,
 * 2026-09-05. The `no-period` and `noTargetsSet` strips this used to render were
 * explanations rather than refusals, and they went with the rest of the app's
 * helper text. The states themselves are untouched: the tiles below still show a
 * dash and their own per-metric note.
 */
function FunnelNote({ state, isSelf }: { state: FunnelState; isSelf: boolean }) {
  if (state.kind === 'error') {
    return (
      <TileFailureStrip
        headline={isSelf ? 'Couldn’t read your figures.' : 'Couldn’t read these figures.'}
        message={state.message}
      />
    );
  }

  /*
   * ⚠️ **UNREACHABLE FROM THIS PAGE AND DELIBERATELY HANDLED.** A `refused` is a
   * 403 from `/targets/actuals`, which is raised only when a caller asks about
   * somebody OTHER than themselves without the audit grant — and this page asks
   * about the signed-in user and nobody else. Falling through to the error strip
   * above would tell a reader the server failed when it declined, so the branch
   * stays rather than being deleted as dead: the day this page grows a person
   * picker it is already right.
   */
  if (state.kind === 'refused') {
    return (
      <TileFailureStrip
        headline="You may not read these figures."
        message={state.message}
        tone="warning"
      />
    );
  }

  return null;
}

/* --- the funnel panel ---------------------------------------------------- */

/**
 * The five figures for the signed-in person, over a window they choose.
 *
 * ⚠️ **The tiles, the window picker and — since 2026-09-13 — the currency
 * switcher's option list and default are ALL shared with the Management
 * Dashboard** (`@/components/dashboards/`). What stays here is the WORDING:
 * every sentence in `FunnelNote` is about *the accounts you lead*, which is a
 * claim only this page may make.
 *
 * ⚠️ **The two strips below the tiles are this page's own and are NOT wording
 * about the accounts you lead** — they report what the server measured in and
 * what it left out, which is a fact about the RESPONSE. Both are duplicated on
 * the Financial Analytics tab because neither can be rendered from inside
 * `TargetTiles`; if that component ever grows them, delete both copies rather
 * than one.
 */
function FunnelPanel({
  userId,
  isSelf,
  organisation,
}: {
  /**
   * Whose figures. On the organisation path it is still the signed-in person,
   * because `useMyTargets` holds on a null id; the subject is what changes.
   */
  userId: number | null;
  /** `userId` is the signed-in person (the page's default "me" scope). */
  isSelf: boolean;
  /**
   * The page-level Account lead control names several leads, Unassigned or All
   * (owner 2026-10-27): organisation figures, no subject toggle, no commission.
   */
  organisation: boolean;
}) {
  const headingId = useId();

  /*
   * ⚠️ **The picker's state lives HERE and the control is presentational**, so
   * the request and the control are built from one value. A picker owning its
   * own state would make the two reachable only through a callback, which is how
   * a request goes out for one window while the control shows another.
   */
  const [picker, setPicker] = useRememberedState<WindowPickerState>(
    'funnel-window',
    initialWindowPickerState,
    parseWindowPickerState,
  );
  const choice = windowChoiceOf(picker);

  /*
   * 🔴 **WHICH CURRENCY TO SUM IN — new 2026-09-13, owner's decision, and it is
   * a fix rather than a feature.** Until today this panel mounted the shared
   * tiles with no currency at all, so they fell back to `DASHBOARD_CURRENCY` and
   * the hook sent no `baseCurrency` — a GBP-only page with no unit printed on
   * any figure. Measured on production the same day: **651 USD invoices, 89 EUR
   * and 56 GBP**, so an account lead was reading roughly one invoice in fourteen
   * and nothing on screen said the other thirteen existed.
   *
   * ⚠️ **Opens on the signed-in person's reporting currency (owner 2026-10-28;
   * `User.reportingCurrency`), else `DEFAULT_ANALYTICS_CURRENCY`.** A choice
   * already made on the control (the remembered state) still wins.
   *
   * ⚠️ **The remembered state holds `null` until the person picks**, and the
   * default is resolved per render rather than read once: `useRememberedState`
   * freezes its `initial` at mount, so passing the user's code there would keep
   * the wrong one when the user object lands or changes (a support session)
   * after mount. A pick stores a code, exactly as before, so older memory reads
   * back unchanged.
   */
  const { user: viewer } = useAuth();
  const [pickedCurrency, setCurrency] = useRememberedState<string | null>(
    'funnel-currency',
    null,
    (raw) => (typeof raw === 'string' && ANALYTICS_CURRENCIES.includes(raw) ? raw : undefined),
  );
  const currency =
    pickedCurrency ?? userReportingCurrency(viewer, ANALYTICS_CURRENCIES) ?? DEFAULT_ANALYTICS_CURRENCY;

  /*
   * Which rate converts other currencies (owner 2026-10-28), remembered like the
   * currency. Opens on `DEFAULT_FX_MODE` (Period end, owner 2026-10-28); a
   * remembered Document date still wins and is sent as an absent key.
   */
  const [fxMode, setFxMode] = useRememberedState<TargetFxMode>(
    'up-funnel-fx-mode',
    DEFAULT_FX_MODE,
    parseFxMode,
  );

  /*
   * Whose figures: the signed-in person's (the default, and what this panel has
   * always shown) or the whole business's. Owner's request, 2026-10-11.
   * "My region" (owner, 2026-10-23): every company in the billing regions that
   * name this person as default account lead. Offered only to somebody who
   * leads at least one (`targets.ledRegions`, one read per user in the hook).
   */
  const [subject, setSubject] = useRememberedState<TargetSubjectScope>(
    'funnel-subject',
    'mine',
    (raw) => (raw === 'mine' || raw === 'region' || raw === 'overall' ? raw : undefined),
  );

  /*
   * 2026-10-27: the page's Account lead control decides the subject before the
   * remembered toggle does. Several leads, Unassigned or All → the organisation.
   * Another single lead → that person, never "My region": `scope=my_region` is
   * resolved from the CALLER on the server, so it would show the viewer's
   * regions under the other lead's name. The remembered value is left alone.
   */
  const requestedSubject: TargetSubjectScope = organisation
    ? 'overall'
    : !isSelf && subject === 'region'
      ? 'mine'
      : subject;
  const targets = useMyTargets(userId, choice, currency, undefined, requestedSubject, fxMode);
  /*
   * ⚠️ The toggle shows the subject the HOOK asked for, so a remembered
   * "My region" for somebody who leads none reads as "My own" rather than
   * selecting a segment that is not there.
   */
  const shownSubject = targets.subject;
  const offersRegion =
    isSelf &&
    ((targets.ledRegions !== null && targets.ledRegions.length > 0) || shownSubject === 'region');
  const subjectOptions = (
    offersRegion
      ? TARGET_SUBJECT_OPTIONS
      : TARGET_SUBJECT_OPTIONS.filter((option) => option.value !== 'region')
  ).map((option) =>
    !isSelf && option.value === 'mine' ? { ...option, label: 'Lead' } : option,
  );
  /*
   * The region names for the facts line: the server's echo where it sent one,
   * else the billing-region master's answer. Names are display only.
   */
  const regionNames = (targets.regions ?? targets.ledRegions ?? [])
    .map((region) => region.name)
    .join(', ');
  const period = targets.period;
  const blocked = choice.kind === 'none';
  /*
   * Resolved once for the whole panel, so the note above the tiles and the
   * tiles themselves cannot disagree about which state the panel is in.
   */
  const state = funnelState(targets, blocked);

  /*
   * Commission progress strip under the Invoiced tile (owner, 2026-10-25).
   * Always about this dashboard's person, whatever "Show" says. It follows the
   * chosen cycle only to pick the assignment, never the Report-by subdivision:
   * the strip always measures the assignment's full period. Hidden when there
   * is no assignment, on a 403 and on any error.
   */
  // 2026-10-27: no commission strip on the organisation path (`null` fires nothing).
  const progress = useCommissionProgress(
    organisation ? null : userId,
    picker.period?.id ?? targets.period?.id ?? null,
  );
  const [progressOpen, setProgressOpen] = useState(false);

  // The tile drill-down (owner 2026-10-28): ONE modal for the panel.
  const [drillMetric, setDrillMetric] = useState<FunnelMetric | null>(null);
  // The exact query the tiles were measured with, plus the metric: one copy of the rule.
  const drillParams =
    drillMetric === null || targets.actualsQuery === null
      ? null
      : { ...targets.actualsQuery, metric: drillMetric };
  const drillLabel =
    drillMetric === null
      ? ''
      : (FUNNEL_TILES.find((tile) => tile.metric === drillMetric)?.label ??
        targetMetricLabel(drillMetric));
  const drillTitle =
    targets.window === null ? drillLabel : `${drillLabel} · ${targets.window.name}`;

  /*
   * 🔴 **THE CURRENCY THE SERVER SAYS IT SUMMED IN — NEVER `currency`, WHICH IS
   * ONLY WHAT WAS ASKED FOR.** The two come apart against an API predating the
   * `baseCurrency` parameter: it ignores the key and sums in GBP, which is all
   * it could ever do. Labelling those GBP amounts `USD` because the picker says
   * USD is a wrong number wearing a plausible unit — strictly worse than the
   * understatement this whole change exists to fix, because nothing about it
   * looks wrong and nobody would report it.
   *
   * ⚠️ **`row.baseCurrency` is the WRONG field and it looks like the right one.**
   * That is the stored TARGET's currency — a different fact, `null` on a count
   * and on any metric nobody set a target for — so it diverges in exactly the
   * case a multi-currency dashboard exists to describe.
   *
   * ⚠️ **The `?? DASHBOARD_CURRENCY` fallback is not a guess**: an absent field
   * means a server from before the parameter existed, and that server summed in
   * GBP by construction.
   */
  const figuresCurrency = targets.measuredInCurrency ?? DASHBOARD_CURRENCY;

  /*
   * ⚠️ **NO WINDOW NOTE ON THE HEADER — removed 2026-09-05 with the rest of this
   * app's descriptions.** It named the window from `targets.window` (what was
   * MEASURED) rather than from the picker, and if one is ever restored it must
   * be read from the response again: the two disagree for as long as a request
   * is in flight.
   */
  return (
    <Panel className="overflow-hidden">
      {/*
        * ✅ **WHOSE FIGURES AND THE CURRENCY SIT IN THE HEADER — owner's
        * decision 2026-10-11**, as two unlabelled toggles beside the title.
        * Their options name themselves (My own / Overall, USD / EUR / GBP), so
        * a label above each only repeated them and made the row read as five
        * unrelated fields. The currency is still always visible, which is the
        * rule that matters: it is the unit of every money figure here and may
        * never move behind a disclosure.
        *
        * ⚠️ The wrapper wraps, so at 320 the toggles stack rather than
        * overflowing the header's `shrink-0` actions slot.
        *
        * 2026-10-28 (owner): a third toggle, the conversion rate (Document date /
        * Period end), beside the currency it qualifies.
        */}
      <PanelHeader
        id={headingId}
        title="Targets & performance"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {!organisation && (
              <SegmentedControl
                label="Show"
                value={shownSubject}
                options={subjectOptions}
                onChange={setSubject}
              />
            )}
            <SegmentedControl
              label="Currency"
              value={currency}
              options={ANALYTICS_CURRENCY_OPTIONS}
              onChange={setCurrency}
            />
            <SegmentedControl
              label="Rates"
              value={fxMode}
              options={FX_MODE_OPTIONS}
              onChange={setFxMode}
            />
          </div>
        }
      />

      {/*
        * ⚠️ **ONE field row, and the picker renders `inline` INTO it rather than
        * bringing a bordered strip of its own** (owner, 2026-09-02). Same
        * filter-row idiom as `PartnershipTablePanel` below, and it wraps.
        *
        * ✅ **THE CONTROL THIS PREDICTED ARRIVED — 2026-09-13, and `inline` did
        * exactly what it was kept for.** *SUPERSEDED: "This page has no subject
        * or account-lead control — every figure on it is the signed-in person's
        * — so the window fields are the ONLY THING IN THE ROW; the point of
        * `inline` is that when a control IS added here it lands beside them
        * instead of stacking a second bordered strip that asks half of one
        * question."* The currency switcher landed beside them and no second
        * strip was needed. **Both halves of that sentence were still true of
        * the design and the first half stopped being true of the row**, which
        * is the artefact-falsified-elsewhere shape: the reasoning survives, the
        * count of controls does not. Do not restate a count here.
        */}
      <div className="flex flex-wrap items-start gap-3 border-b border-border px-4 py-3">
        {/*
          ⚠️ **`periods` is the hook's OWN list, never a second read.**
          `useMyTargets` has already fetched every active period to resolve the
          default; a picker fetching it again would be a second request per
          mount against a per-IP budget shared across the office, and two copies
          of one list can disagree about which cycles exist while both look
          right — which is the half that matters and costs nothing to avoid.
          ⚠️ *SUPERSEDED: "a 120-per-60s per-IP budget."* It is **12,000/60s**
          (corrected 2026-09-17).

          ⚠️ **`noPeriod` needs NOTHING here, unlike the Financial Analytics
          tab.** `useMyTargets` derives its flag from the window it actually
          resolved rather than from whatever covers today, so a cycle chosen at
          step one already reports correctly. Re-deriving it at this call site
          would be a second fix for a solved problem, in the direction of
          blanking a working panel.
        */}
        <TargetWindowPicker
          period={period}
          periods={targets.periods}
          state={picker}
          onChange={setPicker}
          inline
          cycleToggleBelow
        />
      </div>

      {/*
        * ⚠️ **A FACT ABOUT THE DATA, not helper text** (owner 2026-10-23): which
        * billing regions "My region" covers. Do not sweep it.
        */}
      {shownSubject === 'region' && regionNames !== '' && (
        <p className="m-0 px-4 pt-3 text-xs text-fg-muted">{regionNames}</p>
      )}

      <FunnelNote state={state} isSelf={isSelf && !organisation} />

      {/*
        * 🔴 **WHAT WAS MEASURED, WHERE IT IS NOT WHAT WAS ASKED FOR.**
        *
        * ⚠️ **Compared against the DATA, never against the picker's own state.**
        * That is the rule the window already keeps: the two disagree for as long
        * as a request is in flight, and where the server answers in a different
        * currency the picker is simply wrong about what is on screen.
        *
        * ⚠️ **`warning`, and never `danger`.** Nothing is broken — the figures
        * are real and correctly labelled; they are just not in the currency the
        * reader asked for, which is a fact they need rather than a fault to fix.
        * The danger register would send somebody to repair a server that is
        * answering exactly as it was built to.
        *
        * ⚠️ **Suppressed unless the panel actually holds figures**, so a
        * loading, blocked, refused or failed panel makes no claim about a
        * currency nothing was summed in. Same suppression, and the same reason,
        * as the exclusion strip below.
        *
        * ⚠️ **NOT helper text.** It names a state the reader cannot otherwise
        * see — the wrong conclusion it prevents is "these are my dollar figures"
        * — which is the documented exception, and a sweep must not take it.
        */}
      {state.kind === 'figures' && figuresCurrency !== currency && (
        <TileFailureStrip
          headline={`Showing figures in ${figuresCurrency}.`}
          message={`These were measured in ${figuresCurrency}, not the ${currency} you chose.`}
          tone="warning"
        />
      )}

      {/*
        ⚠️ **The currency passed is the one the SERVER MEASURED IN, never the one
        the picker is set to** — see `figuresCurrency` above.

        ⚠️ **`won_deals` does not acquire a symbol by being handed a code.**
        `compactFigureParts` drops the currency by the METRIC, so the one COUNT
        tile is unaffected either way; do not defeat that from here.
      */}
      <TargetTileGrid
        state={state}
        labelledBy={headingId}
        currency={figuresCurrency}
        onOpenMetric={setDrillMetric}
        below={
          progress.data
            ? {
                invoiced: (
                  <CommissionProgressStrip
                    progress={progress.data}
                    onOpen={() => setProgressOpen(true)}
                  />
                ),
              }
            : undefined
        }
      />
      {progress.data && (
        <CommissionProgressModal
          open={progressOpen}
          onClose={() => setProgressOpen(false)}
          progress={progress.data}
        />
      )}
      <MetricDrilldownModal
        open={drillParams !== null}
        onClose={() => setDrillMetric(null)}
        params={drillParams}
        title={drillTitle}
      />

      {/*
        * HOW THESE FIGURES WERE ARRIVED AT. One line, wording owned by
        * `CONVERSION_APPROXIMATE_NOTE` — see it for the full reasoning. **One
        * copy, shared with the Financial Analytics tab**, because both render
        * `/targets/actuals` and a second wording of how money was derived would
        * drift on the first correction.
        *
        * ⚠️ **NEITHER `warning` NOR A STRIP.** Nothing is wrong — the figures
        * are correct and correctly labelled, and the failure family would put a
        * fault register around an ordinary property of the arithmetic. This is
        * a footnote qualifying a figure, so it takes the `text-2xs
        * text-fg-subtle` register used for footnotes under a figure elsewhere
        * in this app, with the full statement on `title`.
        *
        * ⚠️ **Above the exclusion strip deliberately**: this always applies
        * wherever figures exist and frames every number in the grid, where the
        * strip below names specific counts that were dropped.
        *
        * ⚠️ **Suppressed unless the panel actually holds figures**, the same
        * gate and the same reason as the measured-in-currency strip above — a
        * disclaimer over a loading, blocked, refused or failed panel is a claim
        * about a conversion that never happened.
        *
        * ⚠️ **NOT helper text, and a sweep must not take it.** It states a fact
        * about the data the reader cannot otherwise see — nothing on screen
        * distinguishes a natively-summed total from one restated through a
        * reference rate — which is the documented exception, the same class as
        * the kanban board's "Indicative — mixed GBP, USD" and the revenue
        * tracker's "Mixed currencies". The wrong conclusion it prevents is
        * "this total is what the accounts will show".
        *
        * 2026-10-28 (owner): worded by `conversionNote` from the mode and rate
        * the SERVER echoed, never the picker. The `title` describes per-document
        * conversion, so it rides only on a document-mode answer.
        */}
      {state.kind === 'figures' && (
        <p
          className="m-0 px-4 pt-2 text-2xs text-fg-subtle"
          {...(targets.fxMode === 'period_end' && targets.translation !== null
            ? {}
            : { title: CONVERSION_APPROXIMATE_NOTE.detail })}
        >
          {conversionNote({
            fxMode: targets.fxMode,
            translation: targets.translation,
            currency: figuresCurrency,
          })}
        </p>
      )}

      <FunnelExclusionStrip state={state} />
    </Panel>
  );
}

/**
 * WHAT THE FIVE FIGURES LEFT OUT, once, under the tiles.
 *
 * 🔴 **THIS PANEL RENDERED NOTHING AT ALL UNTIL 2026-09-13, WHILE EVERY ROW ON
 * THE WIRE CARRIED `excludedByCurrency`.** With the page hardcoded to GBP the
 * count was the difference between the figure on screen and the business, and it
 * was not on screen: measured on production, the collected figure came back with
 * documents dropped against 651 USD and 89 EUR invoices, and it read as a quiet
 * quarter rather than as the wrong unit. **This strip is what stops the switcher
 * above being set to the wrong currency in silence.**
 *
 * 🔴 **IT EXISTS BECAUSE THE COUNT CANNOT GO INSIDE A TILE FROM HERE.**
 * `TileFigures` owns the inside of a tile and renders no exclusion line, and
 * `TargetTiles` is outside this page. Beside the grid is the closest a caller
 * can get, and the Financial Analytics tab reached the same conclusion and has
 * the same strip. **When `TileFigures` grows a per-tile line, delete both** —
 * saying it twice is how a reader learns to stop reading it.
 *
 * ⚠️ **THE METRIC IS NAMED ON EVERY ITEM.** A bare "2 in another currency" under
 * five tiles cannot say which figure it qualifies, and the whole point is that a
 * reader can tell how much of WHICH number is missing.
 *
 * ✅ **`excludedByCurrencyNote` in `@/lib/targets` is the ONE copy of the
 * sentence and this deliberately does not write its own — AND THAT PAID OFF ON
 * 2026-09-13.** *SUPERSEDED: "the backend is concurrently moving what the count
 * MEANS … the item sentence is the library's to re-word when that lands, and it
 * must not be forked here to get ahead of it."* **It landed** (migration 178),
 * the library's sentence moved from *"N in another currency"* to *"N could not
 * be converted"*, and **this file needed no edit at all** — which is the whole
 * return on not forking it. The same change reached the Financial Analytics tab
 * in the same edit, and its local duplicate was deleted rather than re-worded
 * twice.
 *
 * ⚠️ **NOTHING RENDERS AT `0` AND NOTHING RENDERS AT `null`**, and the library
 * function is the single place that decides. `0` is a measurement — nothing was
 * excluded, so there is nothing to say; `null` is *the server did not compute
 * one*, and a sentence built from an absence is a claim nobody made. An
 * "0 excluded" line on five tiles on every window is the noise that teaches
 * somebody to stop reading the small print, at which point the one time it says
 * 234 is the one time it is not read.
 *
 * ⚠️ **Only ever rendered over real figures.** On a loading, blocked, refused or
 * failed panel there is no `byMetric` to read and no exclusion to report, and a
 * strip there would qualify figures that do not exist.
 */
function FunnelExclusionStrip({ state }: { state: FunnelState }) {
  if (state.kind !== 'figures') return null;

  const items: { metric: FunnelMetric; note: ExcludedNote }[] = [];
  for (const metric of FUNNEL_METRICS) {
    /*
     * ⚠️ **`?? null` preserves a distinction rather than defaulting.** An absent
     * ROW is a metric the server did not report at all — the live deploy-order
     * state for `invoiced` — and a missing KEY is a server predating the field.
     * Neither is a zero, and both are `null`, which the note declines to speak
     * about. Never `?? 0`: that would turn "we were not told" into "nothing was
     * left out", which is a positive claim about somebody's revenue.
     */
    const note = excludedByCurrencyNote(state.byMetric[metric]?.excludedByCurrency ?? null);
    if (note !== null) items.push({ metric, note });
  }

  if (items.length === 0) return null;

  return (
    /*
     * ⚠️ **THE LIST IS LED BY WHAT IT IS.** Five bare items reading "Collected
     * revenue: 14 in another currency" sit under a grid of five figures with
     * nothing saying whether they are part of those figures or missing from
     * them — which is the entire point. The term is a heading for the list, not
     * advice: it answers *what am I looking at*, which the no-helper-text rule
     * already exempts.
     *
     * ✅ **"Left out of these figures" is deliberately NEUTRAL about WHY, AND THE
     * CHANGE IT WAS HEDGING AGAINST HAS NOW HAPPENED.** The count moved from
     * *billed in another currency* to *could not be converted* on 2026-09-13,
     * and this lead-in was true before and after — **it needed no second pass,
     * exactly as written.** Keep it neutral: the reason belongs in the item's own
     * sentence, which is the library's.
     *
     * The exact statement rides on each item's `title`, the same rule the
     * figures themselves keep: the dense form is on screen and the full one is
     * on the element.
     */
    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-t border-border px-4 py-2.5 text-xs text-fg-subtle">
      <span className="font-medium text-fg-muted">Left out of these figures</span>
      <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-1 p-0">
        {items.map(({ metric, note }) => (
          <li key={metric} title={note.detail}>
            <span className="font-medium text-fg-muted">{targetMetricLabel(metric)}</span>:{' '}
            {note.short}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* --- what each table is, said honestly ----------------------------------- */

/*
 * ⚠️ Driven by `state.scopedBy`, never hard-coded per table — a wrong title
 * typechecks and renders fine, so it must track the hook's actual scope. Every
 * table is scoped by `accountLeadId` today; the `ownerId` branch renders for
 * nothing but stays, since a future table may use it.
 */
const EMPTY_TITLE: Record<PartnershipTableState['scopedBy'], (what: string) => string> = {
  accountLeadId: (what) => `No ${what} on your accounts`,
  ownerId: (what) => `No ${what} name you as owner`,
};

/* --- the three states ---------------------------------------------------- */

function RowsSkeleton({
  /**
   * 🔴 **THE VISIBLE, ORDERED KEYS.** *SUPERSEDED 2026-09-12: `extraColumns`
   * plus `hasValueColumn`, rebuilt here into record / extras / value / actions —
   * the fourth of the four re-derivations.* The half of the old doc that
   * survives is the reason this component exists at all, and it now applies to
   * ORDER as well as to count: **the skeleton has to match the LOADED row, not
   * just the colgroup.** A short or differently-ordered row under `table-fixed`
   * is not merely untidy — the browser pads the shortfall at the end, so the
   * columns land in different places before and after the rows arrive and the
   * card visibly re-flows.
   */
  columnKeys,
  pinActions,
  frozenSideOf,
  frozenOffsets,
}: {
  columnKeys: readonly string[];
  pinActions: boolean;
} & FreezeProps) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columnKeys.map((key) => (
            /*
             * ⚠️ **Frozen here too, and not only on the loaded table.** The
             * skeleton is inside the same horizontal scroller; a frozen column
             * that only starts sticking once the rows arrive shifts under the
             * reader's eye at exactly the moment the card settles.
             */
            <TD key={key} {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}>
              {key === RECORD_KEY ? (
                <div className="grid gap-1.5">
                  <Skeleton className="h-3 w-48" />
                  <Skeleton className="h-2.5 w-28" />
                </div>
              ) : key === VALUE_KEY ? (
                <Skeleton className="h-3 w-24" />
              ) : (
                <Skeleton className="h-4 w-16" />
              )}
            </TD>
          ))}
          {/* The action cell, at the size of the control that lands in it, so
            * the column does not resize when the rows arrive. Outside the
            * managed list — see `ACTIONS_KEY`. */}
          <TD className="w-px" pinned={pinActions}>
            <Skeleton className="h-8 w-8" />
          </TD>
        </TR>
      ))}
    </TBody>
  );
}

// An error is a different fact from "there is nothing here" — collapsing the
// two is how a dead endpoint renders as a calm empty state. Named separately,
// in its own danger-toned block, and never substituted for the empty state.
function LoadFailure({ title, message }: { title: string; message: string }) {
  return (
    <Notice
      tone="danger"
      role="alert"
      className="m-3"
      title={<>Couldn&rsquo;t load {title}.</>}
    >
      <span className="min-w-0 [overflow-wrap:anywhere]">{message}</span>
    </Notice>
  );
}

/*
 * One definition of what a row is CALLED, read by the link and by the activity
 * sheet's heading. Two copies would agree today and drift the first time the
 * fallback changes, and the sheet would then be titled for a record the table
 * names differently.
 */
function recordLabelOf(row: PartnershipRow): string {
  return row.primary.trim() || `Record #${row.id}`;
}

/**
 * The two reference masters the status badges resolve their flags against.
 *
 * Threaded down as one prop rather than two, so a card cannot be given the quote
 * master and not the invoice one.
 *
 * ⚠️ **An EMPTY array is what a failed master fetch leaves, and it degrades
 * exactly as an absent one does** — both badges look the row up by id, find
 * nothing, and fall back to matching the stage or status name EXACTLY (never by
 * containment). So a `/quote-stages` outage costs the correctness of a COLOUR
 * and never the label: the name is always the server's own, so the text on the
 * badge is right whatever happens to the master. That is why nothing here waits
 * on `loading` or renders anything for `failed` — a spinner or an error strip
 * for a tone would be louder than the thing it is reporting.
 */
interface StatusMasters {
  stages: readonly QuoteStage[];
  statuses: readonly InvoiceStatus[];
}

/**
 * A quote's stage or an invoice's status, on the row.
 *
 * ⚠️ **Switched on the hook's `kind` TAG, never on the table's key or title.**
 * The two badges read different flags off different masters, and a `{id, name}`
 * from either one compiles against the other — so the tag is what makes handing
 * an invoice status to `StageBadge` a `tsc` error rather than a badge that
 * silently resolves against the wrong list and paints "Draft" from whichever
 * master happened to have a row with that id.
 *
 * The `default: null` arm is a real state, not a guard: three of the five tables
 * carry no status at all.
 */
function RowStatusBadge({ status, masters }: { status: PartnershipRow['status']; masters: StatusMasters }) {
  /*
   * ⚠️ A REAL STATE, not a guard, and it renders an em dash rather than
   * nothing now that this sits in a fixed column: a hole in a column reads as a
   * rendering fault, where a dash reads as an absent value. It is reachable —
   * convert resolves the Draft status and the Accepted stage BY NAME and leaves
   * the column null when no such row exists.
   */
  if (status === null) {
    return (
      <span className="text-xs text-fg-subtle">
        <span aria-hidden="true">—</span>
        <span className="sr-only">No status</span>
      </span>
    );
  }
  return status.kind === 'quote-stage' ? (
    <StageBadge stage={status.ref} stages={masters.stages} />
  ) : (
    <StatusBadge status={status.ref} statuses={masters.statuses} />
  );
}

/**
 * How late an invoice is, in its own column.
 *
 * ⚠️ **The judgement is `overdueDays`, shared with `/invoices`, and it is NOT a
 * date comparison.** An invoice is overdue only if it is also still owed, which
 * is `is_settled` on the `/invoice-statuses` master — so this cell and the
 * status badge one column to its left read the same master through the same
 * function and cannot end up contradicting each other. That contradiction is a
 * defect this repo has actually shipped: a green settled badge beside an Overdue
 * marker, which a reader resolves by trusting whichever they notice first.
 *
 * ⚠️ **Every unknown resolves to NOT OVERDUE and renders a dash** — no due date,
 * a status the master could not classify, a master that failed to load. That is
 * the same asymmetry `isOverdue()` documents, arriving here unchanged: an
 * invoice wrongly shown overdue is a false alarm somebody dismisses, and one
 * wrongly shown clear is money nobody chases. Noise is visible; silence is not.
 * The dash is honest about which of the two this is, because the column can only
 * ever say "late by this much".
 *
 * The FIGURE carries the meaning and the colour is a second signal agreeing with
 * it — never colour alone, per house style.
 */
function OverdueCell({
  row,
  masters,
  extraTitle,
}: {
  row: PartnershipRow;
  masters: StatusMasters;
  /**
   * Appended to whichever `title` this cell builds.
   *
   * ⚠️ **It exists so {@link DueDateCell} can say WHY it cannot be edited without
   * printing a line inside a grid cell** — see {@link CellGate.reason}. Taking it
   * here rather than wrapping the cell in a titled `<span>` keeps ONE `title` on
   * the element: nested titles are resolved by whichever the pointer is actually
   * over, so a wrapper's sentence would appear in the cell's padding and vanish
   * over its text.
   */
  extraTitle?: string;
}) {
  /*
   * `row.status` is a tagged union covering three masters, so the invoice arm is
   * narrowed rather than assumed. A quote stage handed to `overdueDays` would
   * be resolved against the invoice-status master by id and could answer
   * "settled" from a completely unrelated row — the exact substitution the tag
   * exists to make impossible.
   */
  const status = row.status?.kind === 'invoice-status' ? row.status.ref : null;
  const days = overdueDays(row.dueDate, status, masters.statuses);

  if (days === null) {
    return (
      /*
       * ⚠️ **The `title` NAMES THE STORED DUE DATE when there is one — added
       * 2026-09-12 with the editor, and it is a fact rather than a hint.** This
       * cell is now the trigger that edits `dueDate` (see {@link DueDateCell}),
       * and the dash covers three states at once: no due date, a settled invoice,
       * and a status the master could not classify. Without this the only way to
       * learn which is to open the editor. ⛔ It still says nothing about
       * overdueness — the dash is the whole of that claim.
       */
      <span
        className="text-xs text-fg-subtle"
        title={cellTitle(
          row.dueDate === null
            ? 'No due date recorded on this invoice.'
            : `Due ${formatDateOnly(row.dueDate)}. Not overdue.`,
          extraTitle,
        )}
      >
        <span aria-hidden="true">—</span>
        <span className="sr-only">Not overdue</span>
      </span>
    );
  }

  return (
    <span
      className="tabular text-xs font-medium whitespace-nowrap text-danger"
      /* `formatDateOnly`, never the raw column: a plain `YYYY-MM-DD` must not be
       * put through `new Date()` to be displayed, which renders as the previous
       * day west of Greenwich. */
      title={cellTitle(`Due ${formatDateOnly(row.dueDate)}, and not settled.`, extraTitle)}
    >
      {pluralise(days, 'day')}
    </span>
  );
}

function RowIdentity({ row }: { row: PartnershipRow }) {
  /*
   * ⚠️ Tested for CONTENT, not truthiness, and trimmed first.
   *
   * `secondary` is `null` on the Companies and Leads cards — it held the
   * account lead, which is the same person on every row of a page scoped to
   * that person. A whitespace-only string is falsy nowhere, so a bare
   * `row.secondary &&` would render an empty `<p>` that still carries
   * `mt-0.5` and a line box: a gap under one row and not the next, which
   * reads as a missing value rather than as an absent field.
   */
  const secondary = row.secondary?.trim() ?? '';

  return (
    <div className="min-w-0">
      <Link
        to={row.href}
        /*
         * ⚠️ `truncate`, NOT `[overflow-wrap:anywhere]`. Wrapping was right
         * while the table sized itself to its content; under `table-fixed` a
         * long name would instead grow the row to three or four lines, and five
         * such rows make a card twice the height it settled at while loading.
         * The full name is on the `title`, and the record is one click away.
         *
         * (This said "twice the height of its NEIGHBOUR" while the cards were
         * two-up. There is no side-by-side neighbour any more, and the reason
         * survives the layout: the skeleton draws five rows and the card must
         * not jump when the real ones land.)
         */
        className="block truncate rounded-sm text-sm font-medium text-fg underline-offset-2 hover:underline"
        title={recordLabelOf(row)}
      >
        {recordLabelOf(row)}
      </Link>
      {/*
        * 🔴 **THE STATUS HAS LEFT THIS CELL — owner's instruction, 2026-09-03:
        * *"i want the status as other column and not below in each row"*.**
        *
        * ⚠️ *SUPERSEDED, at length: "**THE STATUS SITS IN THE RECORD CELL, NOT
        * IN A COLUMN OF ITS OWN, AND THAT IS A MEASUREMENT RATHER THAN A
        * PREFERENCE** … At 320px the card is the full content width, about
        * 288px, so the record column already has roughly 124px. A fourth column
        * of any usable width leaves it near nothing, and under `table-fixed` the
        * table cannot grow to absorb it: it either crushes the only thing
        * identifying the row, or it overflows and pushes the action cell off the
        * right edge."*
        *
        * ⚠️ **THAT MEASUREMENT WAS RIGHT AND IS STILL RIGHT — the extra columns
        * do not fit at 320px, and nothing about the owner's instruction changes
        * the arithmetic.** What changed is which of the two costs is paid: these
        * cards now declare a table MINIMUM WIDTH (`tableMinWidthRem`) and scroll
        * sideways below it rather than crushing the record column. The old
        * paragraph named that outcome as the disqualifying one, because of a
        * reported defect where the action cell became unreachable — so the
        * action column is now PINNED (`sticky right-0`) on exactly the cards
        * that can scroll, which is what makes the trade available at all. It was
        * not available when that paragraph was written.
        *
        * **The half that is untouched, and still binds:** a column that appears
        * only above a breakpoint is worse than either option, because a status
        * absent at 320px is how somebody on a phone concludes these documents
        * carry no status at all. The columns are present at EVERY width.
        */}
      {secondary !== '' && (
        <p className="m-0 mt-0.5 truncate text-xs text-fg-muted" title={secondary}>
          {secondary}
        </p>
      )}
    </div>
  );
}

/* --- the per-card column set --------------------------------------------- */

/**
 * The `<col>` widths, generated from {@link COLUMN_REM} so they cannot disagree
 * with the table minimum computed from the same numbers.
 *
 * ⚠️ **ONE component, rendered by BOTH the skeleton table and the loaded one.**
 * They were two hand-written copies of the same three `<col>`s, each with the
 * same forty-line comment above it, and the columns had to match or the card
 * re-flowed the moment its rows arrived. Two copies of a layout that must agree
 * is the arrangement this file's own comments describe as the one thing that
 * silently drifts.
 *
 * The RECORD `<col>` carries no width on purpose: under `table-fixed` it takes
 * whatever is left, which is what lets a long record name truncate instead of
 * widening the table and pushing the action column out of view. `auto` layout
 * cannot do that — it sizes columns to their CONTENT.
 *
 * ⚠️ *SUPERSEDED 2026-09-12: "The FIRST `<col>`".* It is still first — `record`
 * is locked, so nothing can move ahead of it — but the width now follows the KEY
 * (see `columnWidthRem`) rather than the position, which is what a reordered
 * table needs. **A `?? 0` on that `null` would give the record column a real
 * width and hand the surplus to whichever column came last.**
 */
function PartnershipColgroup({
  /**
   * 🔴 **THE VISIBLE, ORDERED KEYS — the same list the header, the body and the
   * skeleton walk.** *SUPERSEDED 2026-09-12: `extraColumns` plus a
   * `hasValueColumn` boolean, from which this component rebuilt the sequence
   * `<col/>`, extras, value, actions on its own.* That rebuild was one of four,
   * and it hard-coded the record column first and the value column second-last —
   * neither of which survives a reader reordering the table.
   */
  columnKeys,
  /**
   * How many icon buttons this card's rows render in the action cell.
   *
   * 🔴 **A COUNT SINCE 2026-09-17**, when the Leads and Companies cards gained
   * a third. *SUPERSEDED: `hasRowCreate: boolean`, "whether this card's rows
   * carry the '+' as well as the activity button".*
   *
   * 🔴 **THIS PARAMETER IS THE FIX FOR A REPORTED DEFECT, 2026-09-04.** The
   * action `<col>` was hard-wired to the ONE-button width while
   * `tableMinWidthRem` had already been taught to switch to the two-button one.
   * So the table reserved room for two buttons and then declared a column that
   * could only hold one.
   *
   * ⚠️ **Under `table-fixed` that is not a squeeze, it is an OVERFLOW**, and
   * `justify-end` sends the surplus LEFT: the "+" drew on top of the value
   * column, under the amount. Nothing errored and no layout warning exists for
   * it. **A third button added without widening this is that defect again**,
   * and it is why the mail control could not be a purely additive change.
   *
   * ⚠️ **It must stay the SAME VALUE as the one `tableMinWidthRem` is given.**
   * The doc above promises these widths "cannot disagree with the table minimum
   * computed from the same numbers" — that promise held only while both read
   * one constant, and adding the "+" is exactly what broke it. It is kept now
   * by the card computing the count ONCE (`actionButtons`) and handing the same
   * variable to all three consumers, rather than three call sites each spelling
   * a predicate that has to match.
   */
  actionButtons,
  actionExtraRem,
}: {
  columnKeys: readonly string[];
  actionButtons: number;
  /** The same value `tableMinWidthRem` is given — see `CONTACTS_BUTTON_EXTRA_REM`. */
  actionExtraRem: number;
}) {
  return (
    <colgroup>
      {columnKeys.map((key) => {
        const rem = columnWidthRem(key);
        /*
         * ⚠️ **A `<col>` with NO width is the record column's whole behaviour,
         * not a missing value** — see `columnWidthRem`. It is emitted as a bare
         * element rather than `width: auto`, which is what it was before.
         */
        return rem === null ? <col key={key} /> : <col key={key} style={{ width: `${rem}rem` }} />;
      })}
      {/* The actions lane is outside the managed list — see `ACTIONS_KEY`. */}
      <col style={{ width: `${actionColumnRem(actionButtons, actionExtraRem)}rem` }} />
    </colgroup>
  );
}

/**
 * Holds a table open to its computed minimum, so it scrolls sideways inside the
 * card rather than crushing its own record column.
 *
 * ✅ **`MinTableWidth` WAS HERE AND IS DELETED — 2026-09-12.** It was a wrapper
 * `<div>` carrying `min-width` "because `Table` takes only `className` and this
 * width is COMPUTED" — see `COLUMN_REM` for why a computed width cannot be a
 * Tailwind utility. Its own note called itself a knowing exception to the house
 * rule, named widening `Table` to take a `style` as the right change, and said
 * that change was out of scope. **It has landed**, so the style goes straight on
 * the table and there is no spacer element at all.
 *
 * ⚠️ **`null` must stay `undefined` rather than `{ minWidth: 0 }`.** A card with
 * no minimum has to be free to shrink; a zero would read the same to a human and
 * pin the table's floor to nothing, which is not the same as declining to set
 * one. No card takes that branch today — every one carries the note column — and
 * it is kept for a future one, see `RECORD_COLUMN_MIN_REM`.
 */
function minTableWidthStyle(rem: number | null): CSSProperties | undefined {
  return rem === null ? undefined : { minWidth: `${rem}rem` };
}

/** What each extra column is called. */
const COLUMN_LABEL: Record<PartnershipColumn, string> = {
  status: 'Status',
  /*
   * ⚠️ **"Awaiting", never "Signature", and the word is load-bearing.** The one
   * request behind this column asks for `outstanding: true`, so a quote whose
   * envelope completed, was declined or was voided is deliberately absent from
   * it and shows a dash. Under a heading reading "Signature" that dash would say
   * *never sent*. `/quotes` names its column the same way for the same reason.
   */
  signature: 'Awaiting signature',
  overdue: 'Overdue by',
  /*
   * ⚠️ **The country OF RECORD — `d_companies.country_id`, a real foreign key —
   * and NOT the free-text country line inside the billing address.** The two are
   * separate fields that will disagree, and this heading sits directly above the
   * cell the Country FILTER narrows, which is what makes the pairing honest:
   * `src/lib/types.ts` warns at `CompanyListParams.countryId` that a UI must
   * never hang that filter off a column showing `billingCountry`, because it
   * would narrow on one field while displaying another.
   *
   * ⚠️ **Not "Billing country"**, which would name the other field, and not
   * "Region" — the billing REGION is derived from this country and is a third
   * thing again.
   */
  country: 'Country',
  /*
   * ⚠️ **"Source", not "Company source", and the shorter word is the more exact
   * one HERE.** Both of the cards that render this column are cards of companies
   * — the row IS a company — so "Company" would repeat what the heading above the
   * card already says and spend a column whose width is the thing under
   * pressure. The qualifier earns its place on a screen where a source could
   * belong to something else; on these two it cannot.
   *
   * ⛔ **NOT "Lead source", which names a DIFFERENT MASTER.**
   * `Contact.leadSourceRef` is `d_lead_sources` — where a deal came from — and
   * this column is `f_company_sources`, where an ACCOUNT came from. Both resolve
   * to `{id, name}` and both would read plausibly under either heading, which is
   * exactly why the heading must not blur them: a reader reconciling this column
   * against a contact's lead source would be comparing two different facts and
   * finding a discrepancy that is not one.
   *
   * ⚠️ **NOT "Origin" or "Channel".** Those are words this app does not use
   * anywhere else, and the master, the filter key and the write key all say
   * `source`; a heading that renames the field is how a reader comes to look for
   * a control that is not there.
   */
  source: 'Source',
  /*
   * ⚠️ **"Company type", BOTH WORDS, where `source` one column over is the bare
   * "Source" — and the asymmetry is deliberate rather than an inconsistency.**
   * The short word earns its place there because a source on a card of companies
   * can belong to nothing else. "Type" cannot: this app already names a DEAL type
   * (`d_deal_types`) and a document's line types, and a bare "Type" heading on a
   * dashboard carrying deal, quote and invoice cards beside this one would be
   * read against whichever the reader was last looking at.
   *
   * ⛔ **NOT "Category", "Segment" or "Institution type".** The master, the filter
   * key and the write key all say `companyType`; a heading that renames the field
   * is how a reader comes to look for a control that is not there — and the first
   * two are words this app does not use anywhere.
   *
   * ⚠️ **It is the longest of the three master-name headings on these cards** and
   * that is safe: headings here wrap, so it costs a second header line at worst.
   * See `COLUMN_REM.companyType`, which says why the column is NOT widened for it.
   */
  companyType: 'Company type',
  /*
   * ⚠️ **`PROJECTS_OF_INTEREST_LABEL`, not a second string literal** — the same
   * heading is rendered on `/companies` and on the company detail page, and this
   * is the only one of the three that could drift without anybody noticing: a
   * dashboard column headed one way beside a list page headed another reads as
   * two different fields rather than as one field named twice.
   *
   * ⚠️ **It is a PLURAL fact under a SINGULAR-sounding heading, deliberately.** A
   * company may be pursued for several projects at once, and the constant is the
   * one place that wording is decided — ⛔ do not "correct" it here to match what
   * a given cell happens to render today.
   *
   * 🔴 **THIS IS THE HEADING ON THE TWO COMPANY CARDS ONLY SINCE 2026-09-14**,
   * because the column itself is now declared on those two alone
   * (`COMPANY_CARD_COLUMNS`). Deals, Quotes and Invoices carry `lineProjects`
   * below, a different column key over a different field.
   *
   * ✅ **THAT IS WHY THIS MAP STAYED PER-COLUMN.** A per-CARD lookup on one entry
   * was the alternative and would have made this map the second place the
   * column-to-card decision lived — drifting against the two arrays that already
   * own it, and inviting the next column to carry a card dimension it does not
   * need. **The heading follows the KEY; the key follows the card.**
   */
  projectOfInterest: PROJECTS_OF_INTEREST_LABEL,
  /**
   * The three DOCUMENT cards' projects column — owner's decision, 2026-09-14.
   *
   * ⚠️ **`PROJECTS_ON_DOCUMENT_LABEL`, not a second string literal**, for the
   * same reason `projectOfInterest` above reads from a constant: this heading is
   * the ONLY thing on screen distinguishing a document's own scope from its
   * account's projects of interest, and the two cells are otherwise identical down to the
   * label helper and the column width.
   *
   * ⛔ **NOT "Projects", and not "Project".** A bare noun is exactly what both
   * columns would be called, on one page, under two different meanings — and a
   * reader comparing a quote row against the company row above it would take the
   * two cells for one fact that disagrees with itself.
   */
  lineProjects: PROJECTS_ON_DOCUMENT_LABEL,
  // The ONE copy of the heading, shared with the deals list and deal detail.
  quoteSigned: QUOTE_SIGNED_LABEL,
  /*
   * The Companies card's credit column — the ONE copy of the heading, shared
   * with `/companies` and the company record through `@/lib/credit-notes`.
   * Unitless on purpose: the cell is one figure PER CURRENCY.
   */
  creditBalance: CREDIT_BALANCE_LABEL,
  // The resolved lead the page-level Account lead filter matches (2026-10-27).
  accountLead: 'Account lead',
  /* Purchase history headings — the ONE copy, in `@/lib/company-purchases`. */
  productsPurchased: PRODUCTS_PURCHASED_LABEL,
  projectsParticipated: PROJECTS_PARTICIPATED_LABEL,
  /*
   * 🔴 **"OPEN TASK", AND THE OWNER'S WORD FOR IT WAS "UPCOMING TASK".** The key
   * keeps the word they asked for; the HEADING says what the flag measures,
   * because the two are not the same fact and this column's whole risk is being
   * read as the one it is not.
   *
   * ⛔ **`hasOpenTask` DOES NOT TEST A DUE DATE.** A task with no date at all and
   * a task three weeks OVERDUE both read `Yes`. So a heading reading "Upcoming
   * task", "Next task" or "Task due" would promise a date in the future that
   * nothing behind this column has looked at — and a reader planning their week
   * off it would skip exactly the accounts carrying the oldest unfinished work.
   * That is the `COLUMN_LABEL.note` failure in the other direction: a heading
   * claiming more than the field knows.
   *
   * ⚠️ **It is ACCOUNT-LEVEL IN BOTH DIRECTIONS, which no heading of a sensible
   * length can say.** A `Yes` on a quote row means *a task is open somewhere on
   * this account*, not on that quote; and the per-row `title` is where that is
   * said, because a heading is read once and a cell is read per row.
   *
   * ✅ **VIEWER-INDEPENDENT, which is what lets it be a bare Yes/No at all** —
   * unlike `note` beside it, whose `null` is scoped to the reader and needs the
   * word "Your" to be sayable. Every reader gets the same answer here, so there
   * is nothing to under-claim and the two columns are not the same problem.
   *
   * ⛔ **Not "Tasks"**, which would name a count this column does not carry, and
   * not "Has task", which reads as a field on the record rather than a state of
   * the account.
   */
  upcomingTask: 'Open task',
  /*
   * 🔴 **"YOUR", AND THAT WORD IS THE WHOLE MITIGATION FOR THIS COLUMN.**
   *
   * `latestNote` runs through the same visibility clause `GET /notes` does, so
   * without the notes audit grant — which most staff do not hold — it is the
   * newest note **the reader wrote**, and an empty cell means *you have not
   * written one here*, never *nothing has happened on this account*.
   *
   * ⚠️ **A heading reading "Notes" or "Last note" over this field is not a
   * shorter version of this one — it is a defect this page has already shipped
   * and had removed.** The page ordered five tables by `noteSummary.lastNoteAt`
   * under a heading calling it activity: the tables ordered differently for
   * different people and read blank on every account the reader had not
   * personally written on, with nothing on screen saying so. `PartnershipRow`'s
   * `latestNote` and `src/lib/types.ts`'s `ViewerLatestNote` both carry the
   * history. ⛔ Do not shorten this heading.
   *
   * **Why "Your" and not "My"**: every other sentence on this page is second
   * person — "No leads on your accounts", "Couldn't read your figures", "You may
   * not read these figures" — and one first-person heading among them reads as a
   * different voice rather than as a stronger claim. Both words do the same job;
   * this one matches the page.
   *
   * ✅ **It UNDER-claims for a reader who DOES hold the audit grant**, for whom
   * the field really is the account's latest note whoever wrote it. That is the
   * safe direction and it is deliberate: one heading has to be true for both
   * kinds of reader, and there is no honest client-side test for which is
   * looking (`ability.isAdmin` answers a different question). Understating
   * visibility costs a grant holder nothing; overstating it is the removed bug.
   */
  note: 'Your last note',
  /*
   * 🔴 **The two COMPANY cards' account-wide pair, 2026-10-02.** NOT "Your" —
   * these are ANYONE's last human note on the account, the same for every
   * reader, which is exactly what the possessive above must not be stretched to
   * cover. The constants are the ones `/companies` renders, so the two surfaces
   * head one fact identically.
   */
  lastNote: ACCOUNT_LAST_NOTE_LABEL,
  lastNoteAt: ACCOUNT_LAST_NOTE_AT_LABEL,
  /*
   * The SLOT's name, never a heading — the slot is replaced by one column per
   * stage, each headed `{Stage} status` from its `StageStatusColumn.label`.
   */
  stageStatuses: 'Stage status',
};

/* --- editing a cell in place --------------------------------------------- *
 *
 * 🔴 **THIS PAGE WRITES NOW — owner's instruction, 2026-09-12: every column
 * except the record's NAME becomes editable.** It was five read-only tables
 * until today, which is why none of what follows had a home here and why the
 * whole of it is new rather than adapted.
 *
 * ⛔ **FIVE THINGS STAY READ-ONLY, AND EACH FOR ITS OWN REASON — none of them is
 * "not built yet", and a later pass must not close them by symmetry:**
 *
 *  - **the record's NAME**, which is the owner's own exception;
 *  - **the VALUE column on deals, quotes and invoices**, which is MONEY. A
 *    document's total is the sum of its LINE ITEMS — `subTotal`, `discountTotal`,
 *    `taxTotal` and `grandTotal` have two competing writers, and a header PATCH
 *    writes exactly what it is given and never re-derives. One key per PATCH
 *    means four writes and three durable states each visible to a `GET`, copied
 *    by convert and read by Xero. `InlineInvoiceFields` refuses the same four
 *    columns for the same reason and says so at length;
 *  - **Awaiting signature**, which is not a column on any endpoint at all — it is
 *    a separate `/docusign/envelopes?outstanding=true` read joined to the rows in
 *    the browser, so there is nothing to write to;
 *  - **Your last note**, which is a note. Notes are created in the drawer the
 *    cell already opens; typing over this cell would silently EDIT somebody's
 *    timeline entry rather than adding one;
 *  - **Open task**, which is derived — see {@link UpcomingTaskCell}.
 *
 * ⚠️ **The Companies card's VALUE column is the company STAGE, not money**, and
 * that one IS editable. The money rule above bites on deals, quotes and invoices
 * alone — see {@link VALUE_COLUMN_KIND}, which is why the two are told apart by a
 * declared map and never by `state.valueLabel`, an admin-facing string.
 */

/**
 * Which module's `update` permission each card's OWN records need.
 *
 * ⚠️ **NOT the module a given CELL writes.** Three of the editable columns write
 * a COMPANY however they are reached — a deal row's projects of interest are its
 * company's — so those gate on `companies` on all five cards. This map answers
 * the other question: what a quote stage, an invoice status or an invoice due
 * date needs, which really is the card's own module.
 *
 * ⚠️ **The two coincide on the `/companies` cards and that is a coincidence, not
 * a reason to collapse them.** A permission set can grant companies and refuse
 * invoices, which is exactly the case a single page-level test gets wrong.
 *
 * Exhaustive, so a sixth card cannot arrive without declaring what it writes.
 */
const EDIT_MODULE: Record<PartnershipTableKey, ModuleSlug> = {
  leads: 'companies',
  companies: 'companies',
  deals: 'deals',
  quotes: 'quotes',
  invoices: 'invoices',
};

/**
 * Which COMPANY a row's company-owned columns write to.
 *
 * 🔴 **THE TWO `/companies` CARDS ANSWER FROM `row.id`, AND THAT IS NOT A
 * FALLBACK — IT IS WHAT THE ROW IS.** A Leads or Companies row IS a company;
 * `RowCreateMenu` has passed `{ id: row.id }` as the company to
 * `CreateForCompanyMenu` since that menu landed, so this page already depends on
 * it. The other three cards carry a deal, a quote or an invoice, and
 * `row.companyId` — the company the record hangs off — is the only answer
 * available there.
 *
 * ⚠️ **Reading `row.companyId` on all five would be shorter and is not obviously
 * safe**: it is a field added for the three document cards, and if it ever came
 * back `null` on a company row (because a company has no "company" to point at)
 * every editable column on the two cards this page is named for would silently go
 * read-only, with an honest-looking sentence under it. Deriving the two cases
 * separately cannot be wrong in that direction.
 */
function companyIdOf(row: PartnershipRow, tableKey: PartnershipTableKey): number | null {
  return tableKey === 'leads' || tableKey === 'companies' ? row.id : row.companyId;
}

/**
 * The whole of what {@link CompanyContactsManagerModal} needs to name a company.
 *
 * ⚠️ **An id and a label, and deliberately not a row.** The dialog reads that
 * company's contacts itself; handing it a `PartnershipRow` would give it a deal's
 * status, a quote's signature state and three dozen other fields it has no
 * business consulting, and the day one of them looked useful there would be two
 * screens disagreeing about what a row means.
 */
type ContactsCompany = { id: number; name: string };

/**
 * The cards on which a row carries the contacts button (the mail button until
 * 2026-10-16; same cards).
 *
 * 🔴 **THE OWNER NAMED THESE TWO, 2026-09-17**, and the scope is declared rather
 * than derived. {@link companyIdOf} answers a non-`null` id on all five cards —
 * a deal, a quote and an invoice each hang off a company — so "does this row
 * have a company" would silently put the control on every card, which is not
 * what was asked for.
 *
 * ⚠️ **The two here are the cards where a row IS a company**, which is also why
 * they are the two {@link companyIdOf} answers `row.id` for. That agreement is a
 * reason the scope is coherent, **not the mechanism that produces it** — a later
 * card where a row is a company would have to be added here on purpose.
 *
 * ⚠️ **A case could be made for the three document cards** — mailing the
 * customer while looking at their unpaid invoice is an obvious thing to want —
 * but it was not asked for and it is a different question: the company a
 * document hangs off is not always the party a person means to write to. Raise
 * it; do not widen this list to answer it.
 */
const CONTACTS_CARD_KEYS: readonly PartnershipTableKey[] = ['leads', 'companies'];

/**
 * What the per-card VALUE column actually holds.
 *
 * 🔴 **DECLARED PER CARD, NEVER DERIVED FROM `state.valueLabel`.** That string is
 * the heading a reader sees — "Stage" on the two `/companies` cards, a money
 * label on the other three — and this codebase has been bitten three times by a
 * client deciding what a record MEANS from a name an administrator can change.
 * Matching it here would make a renamed heading silently turn a stage picker into
 * a read-only amount, or worse, offer to edit a grand total.
 *
 * ⚠️ **`leads` is declared `company-stage` even though the Leads card renders NO
 * value column** (`state.valueLabel` is `null` there, so `VALUE_KEY` is never in
 * its `columnKeys`). That is honest rather than dead: if it ever gains one it is
 * a stage and not money, and declaring it `money` to mean "unreachable" would be
 * a wrong answer waiting for the column to arrive.
 */
const VALUE_COLUMN_KIND: Record<PartnershipTableKey, 'company-stage' | 'money'> = {
  leads: 'company-stage',
  companies: 'company-stage',
  deals: 'money',
  quotes: 'money',
  invoices: 'money',
};

/**
 * 🔴 **THE PROJECT COLUMN IS TWO COLUMNS SINCE 2026-09-14 — owner's decision,
 * and it is a DIFFERENT COLUMN KEY rather than one column read two ways.**
 * `projectOfInterest` is declared on the two company cards and carries the
 * ACCOUNT's projects of interest; **`lineProjects`** is declared on Deals, Quotes and
 * Invoices and carries **the projects that document's own line items name**.
 * `COMPANY_CARD_COLUMNS` and `DOCUMENT_CARD_COLUMNS` in
 * `use-partnership-tables.ts` are the one place that is decided.
 *
 * ## ⚠️ THIS PARTLY REVERSES THE 2026-09-12 RULING — record it, do not read past it
 *
 * *SUPERSEDED, and it is written up at length on {@link ProjectOfInterestCell},
 * on `ALL_CARD_COLUMNS` and in `company-projects.ts`: "**Projects of interest
 * reached ALL FIVE cards** … on deals, quotes and invoices the cell is the row's
 * **COMPANY's** list, riding on a `company` embed those three list endpoints now
 * return", **editable on every one of them through `CompanyProjectsModal`**.*
 *
 * **The READ half was answering a question nobody asked of a document row.** A
 * quote's line items name the projects that quote is FOR; its company's projects
 * of interest are what the account is being chased for, and the two routinely
 * disagree — which `company-projects.ts` said in terms while the old rendering
 * stood ("⛔ **IT IS THE ACCOUNT'S LIST, NEVER THE DOCUMENT'S SCOPE** … rendering
 * it as though it described the document produces a **confident wrong answer**
 * rather than a blank"). That warning was obeyed by wording the heading
 * carefully; it is now answered by showing the document's own projects.
 *
 * **And the WRITE half had become unsafe** — see {@link DocumentProjectsCell}.
 *
 * ## ⛔ THE TWO FIELDS ARE INTERCHANGEABLE IN CODE AND NOT IN MEANING
 *
 * Both are `readonly PartnershipRowProject[] | null`, both render through the
 * same label helper, and both sit in the same 12rem slot — **so swapping them
 * compiles and prints a plausible list under the wrong heading.** Nothing
 * typechecks which question a column asks. What keeps them apart is the column
 * KEY, the field NAME and the two heading constants, which is why each arm below
 * names its field exactly once, in an accessor its own readers share.
 *
 * This block is the page's copy of that rule; the contract is in
 * `use-partnership-tables.ts` on `PartnershipColumn` and on
 * `PartnershipRow.lineProjects`. Read those before changing either arm.
 */

/**
 * Which master the STATUS column's picker writes to, per card.
 *
 * 🔴 **BY THE CARD, NOT BY `row.status?.kind`, AND THE DIFFERENCE IS A ROW WITH
 * NO STATUS.** The row's tag is the right discriminator for RENDERING a badge —
 * see `RowStatusBadge`, which cannot draw anything without one. It is the wrong
 * one for OFFERING a picker: a quote whose stage is `null` is exactly the row
 * somebody opens this control to fix, and keying off the absent tag would leave
 * it the one row that cannot be edited.
 *
 * ⚠️ **`null` on the other three cards is a real answer, not a guard.** Deals,
 * leads and companies declare no `status` column at all; a deal's stage is a
 * THIRD master with `isWon`/`isLost` and no endpoint here would take a `stageId`
 * meant for it. Same reasoning as `masterFor` above.
 */
const STATUS_COLUMN_KIND: Record<PartnershipTableKey, 'quote-stage' | 'invoice-status' | null> = {
  leads: null,
  companies: null,
  deals: null,
  quotes: 'quote-stage',
  invoices: 'invoice-status',
};

/**
 * One module's write permission, resolved at the page and threaded down.
 *
 * ⚠️ **`ability` IS IN SCOPE ONLY AT THE PAGE COMPONENT, deliberately.** Every
 * other gate on this page is resolved there and passed as a boolean — `canCreate`
 * has been for as long as the card has had a "+" — so a cell cannot reach for
 * `useAuth()` and quietly answer a different question from its neighbour.
 */
interface EditGate {
  canEdit: boolean;
  /** Why not, in one sentence, when they may not. `denialReason`'s words. */
  disabledReason: string | undefined;
}

/**
 * A failed Xero push, in the words the reader needs.
 *
 * ⚠️ **It is NOT an `InlineSaveResult`, and that is the whole point of the type.**
 * A landed write bumps the card's reload token, which takes the card back to its
 * skeleton — so any message the `InlineField` was holding is unmounted with the
 * row before anybody reads it. A push failure has to outlive that, so it goes to
 * the PANEL, which does not unmount.
 */
type WriteNotice = string;

/**
 * A quote stage change that has to be confirmed before it is sent.
 *
 * 🔴 **OWNER'S DECISION, 2026-09-12: an ACCEPTING or DECLINING stage is
 * confirmed first.** Both are one-way in a system this page cannot see — see
 * {@link StatusCell} for what actually happens and why it cannot be undone from
 * here.
 *
 * ⚠️ **`resolve` is how the confirmation reaches the `InlineField` that asked for
 * it, and it is the only mechanism available.** `InlineField.onSave` returns ONE
 * of four TERMINAL outcomes — there is no "suspended" member and adding one would
 * change a primitive fifteen surfaces share. So the save's promise simply stays
 * pending while the dialog is open and is settled by the reader's answer.
 *
 * ⚠️ **THE COST, STATED: the field reads "Saving…" while the dialog is open**,
 * which is not true — nothing is in flight yet. It is accepted rather than
 * hidden: the dialog holds focus and is the only thing the reader is looking at,
 * and the alternative (a fifth outcome, or a bespoke cell that does not use
 * `InlineField` at all) costs either a shared primitive or the whole keyboard and
 * focus contract this cell gets for free.
 *
 * ⚠️ **A CANCEL resolves `false` and the save reports `warning`, never
 * `saved`.** Without that, Save-then-Cancel reads as a broken button — which is
 * one of the four sentence kinds the no-helper-text rule explicitly keeps.
 */
interface QuoteStageConfirm {
  /** The quote, as the row names it. */
  quoteLabel: string;
  /** The stage being moved to, in the master's own words. */
  stageName: string;
  /** Which one-way outcome this is. Read from the FLAGS, never a stage name. */
  outcome: 'accepted' | 'declined';
  resolve: (confirmed: boolean) => void;
}

/**
 * Everything a cell needs to write, resolved per card and per row.
 *
 * ⚠️ **TWO GATES, not one.** `company` is `companies.update` and governs the
 * country, the company stage and the projects of interest — the three columns
 * that write a COMPANY whichever card they are reached from. `record` is the
 * card's own module and governs the quote stage, the invoice status and the
 * invoice due date. See {@link EDIT_MODULE}.
 */
interface PartnershipEditing {
  company: EditGate;
  record: EditGate;
  /** The whole `/countries` master, for the Country picker — see `useCountryPickerMaster`. */
  countries: FilterMaster;
  /** The `/company-stages` master, for the Stage picker on the two `/companies` cards. */
  companyStages: FilterMaster;
  /**
   * The `/company-sources` master, for the Source picker on the two `/companies`
   * cards.
   *
   * 🔴 **A `ReferenceRecord[]` RATHER THAN A {@link FilterMaster}, and the
   * difference is which option builder it can feed.** `companySourceOptions`
   * takes the master's own rows and owns the retired-value wording for this
   * column everywhere in the app; `FilterMaster.rows` is a lossy
   * `{id, name, isActive}` projection that only {@link editorOptions}'s local
   * mirror of those rules can read. Carrying the real rows means the picker on
   * this page and the picker on `/companies` cannot word a retired source
   * differently.
   *
   * ⚠️ **`failed` is threaded for the reason {@link FilterMaster} states about
   * itself: an unread master and an empty one produce the same empty dropdown
   * and mean opposite things.** It is what puts {@link MASTER_UNREAD} on the
   * gate rather than offering a picker with nothing in it, which reads as "there
   * are no sources" and invites clearing a real value.
   *
   * ⚠️ **`reload` is deliberately NOT carried.** The hook offers one and there is
   * nowhere on a dense grid cell to put a retry control without helper text; the
   * page's own refresh is the way back. Narrowing to these three keys is what
   * says so rather than leaving a callback nothing calls.
   */
  companySources: Pick<CompanySourcesState, 'records' | 'loading' | 'failed'>;
  /**
   * The `/company-types` master, for the Company type picker on the two
   * `/companies` cards.
   *
   * 🔴 **A `ReferenceRecord[]` rather than a {@link FilterMaster}, for the reason
   * `companySources` directly above states in full** — `companyTypeOptions` reads
   * the master's own rows and owns the retired-value wording for this column
   * everywhere in the app, where `FilterMaster.rows` is a lossy
   * `{id, name, isActive}` projection only {@link editorOptions}'s local mirror
   * can read.
   *
   * ⚠️ **`failed` is threaded, `reload` is not** — the same two decisions, for the
   * same two reasons: an unread master and an empty one produce the same empty
   * dropdown and mean opposite things, and there is nowhere on a dense grid cell
   * to put a retry control without helper text.
   */
  companyTypes: Pick<CompanyTypesState, 'records' | 'loading' | 'failed'>;
  /**
   * The per-stage status columns (owner 2026-10-25): every column the masters
   * produce, the whole status master (retired rows included — the cell keeps a
   * company's retired status), and whether either master failed. Read ONCE at
   * the page; each card narrows `columns` to the stages it holds.
   */
  stageStatus: {
    columns: readonly StageStatusColumn[];
    statuses: readonly CompanyStageStatus[];
    failed: boolean;
  };
  /**
   * The page's ONE `useStageStatusChange` (its dialog is mounted once at the
   * page): asks for a follow-up date or a reason before a status is sent.
   * Resolves `null` on cancel.
   */
  requestStageStatusChange: (
    input: StageStatusChangeRequest,
  ) => Promise<StageStatusChangeExtra | null>;
  /**
   * Refetch this card after a LANDED write, so the row re-ranks.
   *
   * 🔴 **THE SORT IS SERVER-SIDE AND THIS PAGE HOLDS PURE SERVER STATE.** There
   * is no local patch layer to nudge, and the backend folds a record's own
   * `updated_at` into `latest_activity_at` — so an edit moves the row under the
   * default "Recent activity" ordering and only a refetch can show that.
   *
   * ⚠️ **THE ACCEPTED COST, STATED: the "Saved." acknowledgement does not
   * survive it.** `usePartnershipTables` answers `PENDING` the moment its stamp
   * changes, so the card goes back to its skeleton and the `InlineField` holding
   * the notice is unmounted with the row. The reload IS the acknowledgement —
   * the value visibly changes — and it is the same behaviour a create from this
   * card has always had. Anything that genuinely must outlive the reload goes to
   * {@link PartnershipEditing.onWriteNotice} instead.
   *
   * ⚠️ **Called ONLY when the write landed.** A refused or unapplied write leaves
   * the card alone, so the field keeps its message and the reader keeps their
   * draft.
   */
  onSaved: () => void;
  /** A message that must survive the reload above. Today: a failed Xero push. */
  onWriteNotice: (notice: WriteNotice) => void;
  /**
   * Every settled save, landed or not.
   *
   * ⚠️ **It re-measures the five-row cap, and it is not belt-and-braces.**
   * `useFiveRowCap` observes the TABLE, so a row that changes height while the
   * table's total does not — one row gaining a status line as another's fades —
   * fires nothing, and the fifth row is then cut through its own text. That reads
   * as a rendering fault rather than as a message somebody just dismissed.
   */
  onSettled: () => void;
  /** Opens the ONE projects-of-interest modal, mounted at the page. */
  onOpenProjects: (row: PartnershipRow) => void;
  /**
   * Asks the reader to confirm an accepting or declining quote stage, and
   * resolves `true` only if they said yes — see {@link QuoteStageConfirm}.
   */
  confirmQuoteStage: (request: Omit<QuoteStageConfirm, 'resolve'>) => Promise<boolean>;
}

/**
 * One row's writer: the gate, and the write itself.
 *
 * Built per cell from a {@link PartnershipEditing} and the gate that column
 * answers to, so no cell can gate itself differently from its neighbour.
 */
interface PartnershipRowWriter {
  canEdit: boolean;
  disabledReason: string | undefined;
  save: <T>(spec: InlineWriteSpec<T>) => Promise<InlineSaveResult>;
  /**
   * {@link PartnershipEditing.onSettled}, forwarded.
   *
   * ⚠️ **A cell that returns an outcome WITHOUT calling {@link save} has to call
   * this itself** — a cancelled confirmation, a refused ordering, a bill date
   * that could not be read. Each of those puts a message line under a cell, which
   * makes that row taller, and the five-row cap is measured from the TABLE: if
   * another row shrinks by the same line in the same frame the observer sees no
   * change at all and the fifth row is left cut through its own text.
   */
  onSettled: () => void;
}

/** One column's write, as {@link PartnershipRowWriter.save} needs it. */
interface InlineWriteSpec<T> {
  /**
   * The ONE request key this write sends.
   *
   * ⚠️ **Used to ROUTE a 422 and nothing else** — `routeFieldErrors` partitions
   * the server's details on the form's own write-key list, so a path naming a key
   * no control here renders lands in `unroutable` and is reported as prose rather
   * than marking a control that is not on screen.
   */
  requestKey: string;
  /** Performs the PATCH. ⛔ ONE explicit literal key — never a spread. */
  write: () => Promise<T>;
  /**
   * Did the response come back carrying what was asked for?
   *
   * 🔴 **A `200` IS NOT EVIDENCE THE CHANGE HAPPENED**, and this is the second of
   * the two defences `CompaniesPage.changeStage` documents. Strictness answers
   * *the key was not recognised*; this answers the different question strictness
   * never could — *the key was recognised and NOT APPLIED*, by a guard that
   * ignored it or a write that landed elsewhere.
   */
  applied: (data: T) => boolean;
  /** What to say when it did not. Names the column, never "something went wrong". */
  notApplied: string;
  /**
   * The Xero push report, where the endpoint carries one.
   *
   * 🔴 **A QUOTE OR INVOICE PATCH REACHES XERO.** `autoPushAndRefresh` in those
   * controllers calls `autoPushDocument`, so a document with line items on a
   * Xero-linked company is created or overwritten in the accountant's system on
   * save. Omitted on `/companies`, whose response carries no such key —
   * `updateCompany` returns a bare `Envelope`, not a `SaveEnvelope`.
   */
  push?: (data: T) => XeroPushReport | undefined;
  /** How a failed push names the document, e.g. `Quote QU-0479`. */
  pushSubject?: string;
}

/**
 * Builds a row's writer for one of the two gates.
 *
 * ⚠️ **A plain function, not a hook.** It is called inside `RowCells` once per
 * cell and closes over the row; memoising it would assert a stability nothing
 * depends on and would need `row` in a dependency list where a stale one writes
 * to the wrong record.
 */
function rowWriter(editing: PartnershipEditing, gateFor: EditGate): PartnershipRowWriter {
  const save = async <T,>(spec: InlineWriteSpec<T>): Promise<InlineSaveResult> => {
    try {
      const data = await spec.write();

      if (!spec.applied(data)) {
        /*
         * The write is OVER and the value did not take, so the editor closes and
         * the message stays: `warning`, never `error`. Marking the draft invalid
         * would state something false about what the reader typed, and holding
         * the editor open invites a retype that would be ignored identically.
         * ⚠️ **No reload** — the card still shows the stored value, which is the
         * truth, and blanking it would take this message with it.
         */
        editing.onSettled();
        return { status: 'warning', message: spec.notApplied };
      }

      /*
       * ⛔ **`skipped` IS THE ORDINARY OUTCOME AND MUST NEVER READ AS A
       * FAILURE.** A line-less document and a company with no Xero link both
       * skip, and three "already acted on in Xero" codes are downgraded to it
       * deliberately — an approved invoice is not a fault. Only `failed` is worth
       * a word, and it goes to the PANEL because the reload below would unmount
       * anything the field was holding.
       */
      const push = spec.push?.(data);
      if (push?.status === 'failed' && spec.pushSubject !== undefined) {
        editing.onWriteNotice(
          `${spec.pushSubject} saved, but it could not be sent to Xero. ${push.message}`,
        );
      }

      editing.onSaved();
      editing.onSettled();
      return { status: 'saved' };
    } catch (caught) {
      editing.onSettled();
      if (caught instanceof ApiError) {
        /*
         * ⚠️ **A 403 carries NO `details` at all**, so it can never be routed to
         * a field by path — it is rendered as prose beside the control, which is
         * the one message slot this primitive has. It can arrive even though the
         * gate said otherwise, because a role can change under an access token
         * that lasts over five days: the gate is the optimisation, this branch is
         * the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              gateFor.disabledReason ?? 'Your account no longer has permission to change this.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This record is no longer there, so the change could not be saved.',
          };
        }
        /*
         * Split against the ONE key this save sent. A 422 whose path names it
         * belongs on this control; a `.strict()` rejection (`path: ''` plus a
         * `key`), the at-least-one-field refusal, and a path naming a key with no
         * control here are all unroutable and are reported as prose.
         */
        const { fields, unroutable } = caught.routeFieldErrors([spec.requestKey]);
        const fieldText = Object.values(fields).join(' ');
        if (fieldText !== '') return { status: 'error', message: fieldText };
        if (unroutable.length > 0) {
          return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
        }
        /*
         * `formMessage` before `message`: on a 422 `message` is only "Validation
         * failed", and the sentence naming what was refused lives on the former.
         */
        return { status: 'error', message: caught.formMessage ?? caught.message };
      }
      if (caught instanceof Error && caught.message !== '') {
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  return {
    canEdit: gateFor.canEdit,
    disabledReason: gateFor.disabledReason,
    save,
    onSettled: editing.onSettled,
  };
}

/**
 * Whether one cell may be edited, and why not.
 *
 * ⚠️ **`rowRefusal` is the PER-ROW half and the permission refusal outranks it.**
 * A reader who may not write anything should be told that once, in the same words
 * on every cell, rather than meeting a sentence about this particular row that
 * they could not act on even if it were fixed.
 */
interface CellGate {
  disabled: boolean;
  /**
   * ⛔ **FOR A `title`, AND NEVER FOR `InlineField`'s `disabledReason`.**
   *
   * That prop renders a VISIBLE LINE under the value for as long as the field is
   * disabled — which is right on a record page, where `InlineTrackerFields` and
   * the three document editors use it, and wrong here. ⛔ **Nothing explanatory
   * may render inside a desktop grid cell**, and the arithmetic says why rather
   * than the rule alone: a `viewer` meets four or five disabled cells on every
   * row, so one sentence per cell is four or five repetitions of the same words
   * per row, twenty-five per card, on five cards — and `useFiveRowCap` sizes each
   * card from its first five rows, so the page would be several screens of the
   * same refusal.
   *
   * The sentence is NOT dropped: it goes to the cell's `title`, which is the
   * channel this app's own grids already use for anything that cannot render
   * beside a control. A why-this-is-disabled sentence is one of the four kinds
   * the no-helper-text rule keeps — it has to survive, it does not have to be
   * printed.
   */
  reason: string | undefined;
}

function cellGate(writer: PartnershipRowWriter, rowRefusal?: string): CellGate {
  return {
    disabled: !writer.canEdit || rowRefusal !== undefined,
    reason: writer.canEdit ? rowRefusal : writer.disabledReason,
  };
}

/**
 * A cell's `title`: what it would have said, plus why it cannot be edited.
 *
 * ⚠️ **`undefined` rather than `''` when there is nothing to say**, so React
 * omits the attribute — an empty `title` renders a blank tooltip box on hover and
 * reads as a broken one. Same spelling as `companyProjectsTitle`'s own contract.
 */
function cellTitle(...parts: (string | undefined)[]): string | undefined {
  const text = parts.filter((part) => part !== undefined && part !== '').join('\n\n');
  return text === '' ? undefined : text;
}

/**
 * A picklist's options: every ACTIVE row, plus the stored one when it is not.
 *
 * ⚠️ **A deliberate mirror of `companyStageOptions` in `@/lib/use-company-stages`,
 * and the three rules are ITS rules — carry a correction there across to here.**
 * It is not imported because it takes a `ReferenceRecord[]`, and the four masters
 * behind this page's pickers do not share that type: `/countries` rows spell their
 * label `countryName` and would have to be forged into a `ReferenceRecord` with
 * invented `sortOrder` and timestamps to fit. They DO all agree on
 * {@link FilterOption}, which this page already declares for its funnels, so one
 * local helper over that shape beats four call sites each mapping differently.
 *
 * The three rules, restated because they are each a defect somebody has shipped:
 *
 *  - **"(retired)"** means the master still holds the row, so its real name is
 *    available. Reads never filter on `isActive`, so records genuinely sit on
 *    retired values and dropping one would leave the record unable to show what
 *    it holds.
 *  - **"(no longer offered)"** means the row is not in the list at all, so the
 *    only name available is the stale one stored on the record. It is `unshift`ed
 *    rather than appended, so a value the record really holds is never below the
 *    fold of its own picker.
 *  - **An EMPTY `rows` gets NEITHER label.** Empty means "still loading" or
 *    "failed to load", never "every option was retired" — labelling then would
 *    accuse a perfectly live value of being retired for a frame or two.
 */
function editorOptions(
  rows: readonly FilterOption[],
  current: MasterRef | null,
): InlineFieldOption[] {
  const labelFor = (id: number, name: string) => {
    const trimmed = name.trim();
    /*
     * A blank name would render an invisible option and an empty cell. These
     * masters trim to a non-empty string so it should not happen — but a name is
     * user-entered data, and "should not happen" is not a rendering strategy.
     */
    return trimmed === '' ? `#${id}` : trimmed;
  };

  const options = rows
    .filter((row) => row.isActive || row.id === current?.id)
    .map((row) => ({
      value: String(row.id),
      label: row.isActive ? labelFor(row.id, row.name) : `${labelFor(row.id, row.name)} (retired)`,
    }));

  if (current && !options.some((option) => option.value === String(current.id))) {
    options.unshift({
      value: String(current.id),
      label:
        rows.length > 0
          ? `${labelFor(current.id, current.name)} (no longer offered)`
          : labelFor(current.id, current.name),
    });
  }

  return options;
}

/**
 * How an unread master reads in a picker that is otherwise enabled.
 *
 * ⚠️ **An unread list and an empty one are different facts and must not look
 * alike.** A picker offering nothing because the request failed reads exactly
 * like a master with no rows in it, and the reader's next act differs completely:
 * one is "reload", the other is "ask an administrator to add one". `InlineField`
 * has no failure slot of its own, so the refusal goes on the gate.
 */
const MASTER_UNREAD = 'The list could not be read, so this cannot be changed right now.';

/**
 * The reader's own last note on the record, and a second way into the drawer.
 *
 * 🔴 **THE EMPTY CELL IS THE HARD HALF, AND IT IS NOT A DASH.** `latestNote` is
 * VIEWER-SCOPED — see `COLUMN_LABEL.note` above and `ViewerLatestNote` in
 * `src/lib/types.ts` — so `null` means *no note you may see*. On this page that
 * is, for most staff on most rows, *you have not written one here*. An em dash
 * would say what every other empty cell on this table says, which is "this
 * record has no such value", and that is the one reading this cell must not
 * have: it would restore the exact defect that had `noteSummary.lastNoteAt`
 * removed from this page.
 *
 * ✅ **"None from you" is true for EVERY reader**, which is what makes it
 * sayable at all: your own notes are visible to you whether or not you hold the
 * notes audit grant, so `null` always implies you wrote none here. For a grant
 * holder it means nobody did, which this under-claims — the safe direction, and
 * the same choice the heading makes.
 *
 * ⚠️ **The empty cell stays CLICKABLE**, and that is the point of wording it
 * rather than blanking it: the drawer behind it is where a colleague's note
 * would be, and where this reader writes one. A cell that says "nothing here" by
 * being empty and cannot be opened is a dead end on the one row where the reader
 * most needs the sheet.
 *
 * ⚠️ **A plain `<button>`, and a KNOWING exception to "build from the
 * primitives" rather than an oversight** — the same standing as the scroller
 * `<div>` above, which is documented the same way. ⚠️ *SUPERSEDED 2026-09-12:
 * this named `MinTableWidth` as the second such exception; **that one is closed**
 * — `Table` takes a `style` now and the wrapper is gone, which is what an
 * exception of this kind is supposed to end in.* `Button`
 * bakes in `justify-center`, and `justify-start` cannot override it from a
 * `className`: both are `justify-content` utilities, so the emitted stylesheet's
 * own order decides and `center` wins whatever the class string says. A
 * centre-aligned truncated note is not the control. Widening the primitive is
 * the right fix and is out of this change's scope. Everything else is the house
 * idiom, copied from `RowIdentity`'s link one cell to the left; the global
 * `:focus-visible` rule in `index.css` matches `button` as well as `a`, so the
 * focus ring needs nothing here.
 */
function NoteCell({ row, onOpenActivity }: { row: PartnershipRow; onOpenActivity: () => void }) {
  const note = row.latestNote;

  return (
    <button
      type="button"
      onClick={onOpenActivity}
      /*
       * 🔴 **The accessible name says what the control DOES — owner's decision
       * 2026-10-04: this cell opens the drawer scrolled to the notes.**
       * *SUPERSEDED: the visible note text as the name, with " (open tasks and
       * notes)" appended in an `sr-only` span.* The note itself is still
       * exposed: with an `aria-label` present the `title` below is no longer
       * used as the name and becomes the accessible DESCRIPTION, so a screen
       * reader hears the body after the action.
       */
      aria-label={`Open notes for ${recordLabelOf(row)}`}
      /*
       * The full body, which is the whole reason one line of it may be
       * truncated, plus WHEN — a real instant, so `formatDateTime` and not
       * `formatDateOnly`. On the empty cell it says the same thing the visible
       * text does, in the second person, so a hover cannot arrive at a broader
       * reading than the cell.
       */
      title={
        note === null
          ? 'You have not written a note on this record.'
          : `${note.body}\n\n${formatDateTime(note.createdAt)}`
      }
      className={cx(
        'block w-full rounded-sm py-1 text-left text-xs',
        /*
         * 🔴 **THE NOTE READS AS A PARAGRAPH — owner's instruction,
         * 2026-09-03.** *SUPERSEDED: `truncate`,* which cut every note to one
         * line at about twenty characters and put the rest on the `title`.
         *
         * `whitespace-pre-line` rather than plain wrapping, because **`body` is
         * newline-bearing** — `ViewerLatestNote` says so at the field — so a
         * note somebody typed as three short lines reads as three short lines
         * rather than as one run-on sentence. `[overflow-wrap:anywhere]` is for
         * the other end of the same problem: a pasted URL or reference with no
         * spaces in it has nowhere to break, and under `table-fixed` a word
         * wider than its column does not widen the column — it spills over the
         * cell beside it.
         *
         * ⚠️ **`line-clamp-10` IS A CEILING ON THE LAYOUT, NOT A RETURN TO
         * TRUNCATION, and it is here because of a measurement**: the server
         * accepts a note body of **20,000 characters** (`notes.schema.ts`).
         * Unclamped, one pasted email would be some 440 lines in this cell —
         * and `useFiveRowCap` sizes the card from the height of its first five
         * rows, so a single such note would make the card thousands of pixels
         * tall and push the four cards below it off the screen. Ten lines is
         * about 450 characters at this width, which is far past any note anyone
         * writes on an account; a note that does run longer keeps its full text
         * on the `title` and is one click from the drawer, which is where a
         * note that long is worth reading anyway.
         */
        'whitespace-pre-line [overflow-wrap:anywhere] line-clamp-10',
        'transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none',
        note === null ? 'text-fg-subtle' : 'text-fg-secondary',
      )}
    >
      {note === null ? 'None from you' : note.body}
    </button>
  );
}

/**
 * The company's country of record.
 *
 * 🔴 **THE EMPTY CELL MEANS "NOBODY HAS RECORDED ONE", AND IT IS AN EM DASH
 * BECAUSE THAT IS WHAT IT MEANS.** Unlike the note column beside it — whose
 * blank is viewer-scoped and therefore worded — `country` is the same answer for
 * every reader: `d_companies.country_id` is nullable and a company nobody has
 * classified has none. An em dash is exactly this table's "this record has no
 * such value", which is the truth here.
 *
 * ⚠️ **A row with no country is unreachable by the Country FILTER**, because the
 * server renders `country_id IN (...)` and that is UNKNOWN for a NULL — there is
 * deliberately no way to ask for "unclassified". So filtering by any country
 * hides every blank row, which is the contract rather than a fault; the panel's
 * own filter control is where that is said, not here.
 *
 * ⚠️ **`wrap`, not `truncate`.** Under `table-fixed` this column is a real 9rem
 * and the master holds names far longer than that; truncating would cut
 * "United States Minor Outlying Islands" to something that reads like a
 * different country, where wrapping costs one extra line on the rare long row.
 */
function CountryCell({
  row,
  companyId,
  writer,
  master,
}: {
  row: PartnershipRow;
  companyId: number | null;
  writer: PartnershipRowWriter;
  master: FilterMaster;
}) {
  const g = cellGate(
    writer,
    companyId === null
      ? 'This row names no company, so there is no country of record to set.'
      : master.failed
        ? MASTER_UNREAD
        : undefined,
  );

  /*
   * ⚠️ **The read half is unchanged and is still the whole of what a reader who
   * may not edit sees.** `disabled` renders this as plain text with no trigger
   * and no glyph, which is why the control is mounted unconditionally: hiding it
   * would make a read-only account look like a company with no country recorded.
   *
   * ⚠️ **The refusal joins the `title` rather than taking a line of its own** —
   * see {@link CellGate.reason}.
   */
  const display =
    row.country === null ? (
      <span
        className="text-fg-subtle"
        title={cellTitle('No country recorded on this company.', g.reason)}
      >
        {orDash(null)}
      </span>
    ) : (
      /* `title={undefined}` is omitted by React — the same spelling the value
       * cell in `RowCells` already uses — so there is no blank tooltip box. */
      <span className="text-xs text-fg-secondary" title={cellTitle(row.country, g.reason)}>
        {row.country}
      </span>
    );

  /*
   * ⚠️ **The row carries the country's NAME and the picker writes its ID**, which
   * is exactly why `row.countryId` had to be added to `PartnershipRow` rather
   * than resolved here from the name. Two countries may be renamed into each
   * other's spelling and an id is the only thing that cannot be.
   */
  const current: MasterRef | null =
    row.countryId === null ? null : { id: row.countryId, name: row.country ?? '' };

  return (
    <InlineField
      label="Country"
      kind="picker"
      value={row.countryId === null ? '' : String(row.countryId)}
      display={display}
      options={editorOptions(master.rows, current)}
      optionsLoading={master.loading}
      /*
       * ⚠️ **It says what CHOOSING it does, not what the row currently is.**
       * Clearing is real here — `countryId` is nullable and `null` is the clear —
       * and the consequence is invisible from the cell: the server derives the
       * billing region, and therefore the currency and the Xero tax and branding
       * defaults, from this column. A bare dash would read as "unknown".
       */
      emptyOptionLabel="No country of record"
      onSave={(next) => {
        /*
         * ⚠️ **Refused rather than defaulted.** The gate below already disables
         * the control on a row with no company, so this is unreachable — and a
         * `?? 0` here would make it reachable by writing to company zero, which
         * is a request nobody can undo. A guard someone can forget is not a
         * guard; one that cannot express the bad value is.
         */
        if (companyId === null) {
          writer.onSettled();
          return Promise.resolve<InlineSaveResult>({
            status: 'error',
            message: 'This row names no company, so nothing was saved.',
          });
        }
        const countryId = next === '' ? null : Number(next);
        return writer.save({
          requestKey: 'countryId',
          // ⛔ ONE explicit literal key. A computed or spread key is exactly what
          // a misspelling survives, and a stripped key would answer 200.
          write: () => api.updateCompany(companyId, { countryId }),
          applied: ({ data }) => (data.country?.id ?? null) === countryId,
          notApplied: 'Country not saved — the server didn’t apply the change. Try again.',
        });
      }}
      disabled={g.disabled}
    />
  );
}

/**
 * Where the account came from — owner's instruction, 2026-09-14.
 *
 * 🔴 **THE EMPTY CELL IS THE MAJORITY, AND IT IS AN EM DASH BECAUSE THAT IS WHAT
 * IT MEANS.** `d_companies.source_id` is nullable and OPTIONAL: every company
 * that predates the column has none, so a card reading as a blank Source column
 * all the way down is **the data**, not a failed read. Like `country` beside it
 * and unlike `note`, the answer is the same for every reader, so the em dash —
 * this table's "this record has no such value" — is exactly true here.
 *
 * ⛔ **NOTHING MAY BE INVENTED FOR THE BLANK.** "Unknown", "Direct" or "Organic"
 * in place of a dash would be this client asserting a provenance for an account
 * that nobody recorded — a claim somebody would act on, on a column whose whole
 * job is to say where business came from. The same `?? 0`-shaped failure the row
 * contract refuses at the wire.
 *
 * ⚠️ **A row with no source is unreachable by the Source FILTER**, because the
 * server renders `source_id IN (...)` and that is UNKNOWN for a NULL — there is
 * deliberately no way to ask for "not recorded". So filtering by any source
 * hides every blank row, which on THIS column is most of them. That is the
 * contract rather than a fault, and it is the funnel's own option counts that
 * carry it on screen, exactly as they do for the two page-scoped funnels.
 *
 * ⚠️ **`wrap`, not `truncate`**, for the reason `CountryCell` gives: the master
 * is admin-editable free text to 60 characters against a real 9rem column, and a
 * truncated source name reads like a different source.
 *
 * ⚠️ **It mirrors `CountryCell` line for line and is NOT folded into it.** The
 * two write different keys to different masters and their empty cells mean
 * different things — one is a company nobody has classified, the other is the
 * ordinary state of nearly every row — so a shared component would need a
 * parameter for the key, the label, the refusal sentence, the empty-option
 * wording and the `applied` test, which is every line of it.
 */
function SourceCell({
  row,
  companyId,
  writer,
  master,
}: {
  row: PartnershipRow;
  companyId: number | null;
  writer: PartnershipRowWriter;
  master: Pick<CompanySourcesState, 'records' | 'loading' | 'failed'>;
}) {
  const g = cellGate(
    writer,
    companyId === null
      ? 'This row names no company, so there is no source to set.'
      : master.failed
        ? MASTER_UNREAD
        : undefined,
  );

  /*
   * ⚠️ **The read half is the whole of what a reader who may not edit sees.**
   * `disabled` renders this as plain text with no trigger and no glyph, which is
   * why the control is mounted unconditionally: hiding it would make a read-only
   * account look like a company with no source recorded — and on this column that
   * is the state nearly every row is genuinely in, so the two would be
   * indistinguishable.
   *
   * ⚠️ **The refusal joins the `title` rather than taking a line of its own** —
   * see {@link CellGate.reason}.
   */
  const display =
    row.source === null ? (
      <span
        className="text-fg-subtle"
        title={cellTitle('No source recorded on this company.', g.reason)}
      >
        {orDash(null)}
      </span>
    ) : (
      <span className="text-xs text-fg-secondary" title={cellTitle(row.source, g.reason)}>
        {row.source}
      </span>
    );

  /*
   * ⚠️ **The row carries the source's NAME and the picker writes its ID**, which
   * is why `row.sourceId` is a field on `PartnershipRow` rather than resolved
   * here from the name. Two sources may be renamed into each other's spelling and
   * an id is the only thing that cannot be — the same reasoning `CountryCell`
   * states, and the reason this master's admin-editable labels are safe to print
   * and unsafe to match.
   */
  const current: MasterRef | null =
    row.sourceId === null ? null : { id: row.sourceId, name: row.source ?? '' };

  return (
    <InlineField
      label="Source"
      kind="picker"
      value={row.sourceId === null ? '' : String(row.sourceId)}
      display={display}
      /*
       * ⚠️ **The master's OWN option builder**, not this page's local
       * {@link editorOptions} mirror — see the import note. It owns the two
       * retired wordings and the rule that an EMPTY list gets neither of them,
       * so this picker and the one on `/companies` cannot come to label a
       * retired source differently.
       */
      options={companySourceOptions(master.records, current)}
      optionsLoading={master.loading}
      /*
       * ⚠️ **It says what CHOOSING it does, not what the row currently is.**
       * Clearing is real — `sourceId` is nullable and `null` is the clear — and
       * `companySourceOptions` deliberately supplies no empty row of its own,
       * because only the control knows whether absence means "clear it" (here)
       * or "any" (the funnel).
       */
      emptyOptionLabel="No source recorded"
      onSave={(next) => {
        /*
         * ⚠️ **Refused rather than defaulted** — see `CountryCell`, where the
         * same guard is written out and the reason is given in full. Unreachable
         * on these two cards, where the row IS the company; kept because a guard
         * that cannot express the bad value is the only kind that holds.
         */
        if (companyId === null) {
          writer.onSettled();
          return Promise.resolve<InlineSaveResult>({
            status: 'error',
            message: 'This row names no company, so nothing was saved.',
          });
        }
        const sourceId = next === '' ? null : Number(next);
        return writer.save({
          requestKey: 'sourceId',
          // ⛔ ONE explicit literal key. A computed or spread key is exactly what
          // a misspelling survives, and a stripped key would answer 200.
          write: () => api.updateCompany(companyId, { sourceId }),
          /*
           * ⚠️ **`data.source`, the READ key — `sourceId` is the WRITE key and
           * the response does not carry it.** Read key ≠ write key on this
           * column, so testing `data.sourceId` would compare `undefined` against
           * a number and report every landed write as unapplied.
           */
          applied: ({ data }) => (data.source?.id ?? null) === sourceId,
          notApplied: 'Source not saved — the server didn’t apply the change. Try again.',
        });
      }}
      disabled={g.disabled}
    />
  );
}

/**
 * What KIND of institution the account is — owner's instruction, 2026-09-14, on
 * the two `/companies` cards.
 *
 * ⛔ **`null` IS "NOBODY HAS CLASSIFIED THIS ACCOUNT" AND MUST NEVER BE WORDED AS
 * A KIND.** An empty cell here is the ordinary state — the column is optional and
 * blank on every company that predates it — and printing "Other", "Unknown" or
 * "University" in its place would be this client asserting what an institution
 * IS. That is a claim somebody acts on, on a column that decides how an account
 * is approached, and the record does not make it. The same `?? 0`-shaped failure
 * the row contract refuses at the wire.
 *
 * ⚠️ **A row with no type is unreachable by the Company type FILTER**, because
 * the server renders `company_type_id IN (...)` and that is UNKNOWN for a NULL —
 * there is deliberately no way to ask for "not classified". So filtering by any
 * type hides every blank row, which on THIS column is most of them. That is the
 * contract rather than a fault, and the funnel's own option counts are what carry
 * it on screen.
 *
 * ⚠️ **`wrap`, not `truncate`**, for the reason `CountryCell` and `SourceCell`
 * both give: the master is admin-editable free text against a real 9rem column,
 * and a truncated type name reads like a different type.
 *
 * ⚠️ **It mirrors `SourceCell` line for line and is NOT folded into it — the
 * THIRD such component on this page, which is the strongest case yet for
 * merging and is still refused.** The three write different keys to different
 * masters: a shared component would need a parameter for the request key, the
 * label, the two refusal sentences, the empty-option wording, the option builder
 * and the `applied` test, which is every line of it — and the `applied` test is
 * the one that matters, because read key ≠ write key differently on each. What a
 * merge would actually share is the JSX skeleton, and what it would risk is a
 * `sourceId` reaching `PATCH /companies` under a type picker: a `200`, the wrong
 * column written, and nothing on screen to say so.
 */
function CompanyTypeCell({
  row,
  companyId,
  writer,
  master,
}: {
  row: PartnershipRow;
  companyId: number | null;
  writer: PartnershipRowWriter;
  master: Pick<CompanyTypesState, 'records' | 'loading' | 'failed'>;
}) {
  const g = cellGate(
    writer,
    companyId === null
      ? 'This row names no company, so there is no type to set.'
      : master.failed
        ? MASTER_UNREAD
        : undefined,
  );

  /*
   * ⚠️ **The read half is the whole of what a reader who may not edit sees**, so
   * the control is mounted unconditionally — see `SourceCell`, where the reason is
   * written out: hiding it would make a read-only account look like a company with
   * no type recorded, and on this column that is the state nearly every row is
   * genuinely in, so the two would be indistinguishable.
   */
  const display =
    row.companyType === null ? (
      <span
        className="text-fg-subtle"
        title={cellTitle('No type recorded on this company.', g.reason)}
      >
        {orDash(null)}
      </span>
    ) : (
      <span className="text-xs text-fg-secondary" title={cellTitle(row.companyType, g.reason)}>
        {row.companyType}
      </span>
    );

  /*
   * ⚠️ **The row carries the type's NAME and the picker writes its ID**, which is
   * why `row.companyTypeId` is a field on `PartnershipRow` rather than resolved
   * here from the name. Two types may be renamed into each other's spelling and an
   * id is the only thing that cannot be — the reasoning `CountryCell` states, and
   * the reason this master's admin-editable labels are safe to print and unsafe to
   * match.
   */
  const current: MasterRef | null =
    row.companyTypeId === null ? null : { id: row.companyTypeId, name: row.companyType ?? '' };

  return (
    <InlineField
      label="Company type"
      kind="picker"
      value={row.companyTypeId === null ? '' : String(row.companyTypeId)}
      display={display}
      /*
       * ⚠️ **The master's OWN option builder**, not this page's local
       * {@link editorOptions} mirror — see the import note. It owns the two retired
       * wordings and the rule that an EMPTY list gets neither of them, so this
       * picker and the one on `/companies` cannot come to label a retired type
       * differently.
       */
      options={companyTypeOptions(master.records, current)}
      optionsLoading={master.loading}
      /*
       * ⚠️ **It says what CHOOSING it does, not what the row currently is.**
       * Clearing is real — `companyTypeId` is nullable and `null` is the clear —
       * and `companyTypeOptions` deliberately supplies no empty row of its own,
       * because only the control knows whether absence means "clear it" (here) or
       * "any" (the funnel).
       */
      emptyOptionLabel="No type recorded"
      onSave={(next) => {
        /*
         * ⚠️ **Refused rather than defaulted** — see `CountryCell`, where the same
         * guard is written out in full. Unreachable on these two cards, where the
         * row IS the company; kept because a guard that cannot express the bad
         * value is the only kind that holds.
         */
        if (companyId === null) {
          writer.onSettled();
          return Promise.resolve<InlineSaveResult>({
            status: 'error',
            message: 'This row names no company, so nothing was saved.',
          });
        }
        const companyTypeId = next === '' ? null : Number(next);
        return writer.save({
          requestKey: 'companyTypeId',
          // ⛔ ONE explicit literal key. A computed or spread key is exactly what
          // a misspelling survives, and a stripped key would answer 200.
          write: () => api.updateCompany(companyId, { companyTypeId }),
          /*
           * ⚠️ **`data.companyType`, the READ key — `companyTypeId` is the WRITE
           * key and the response does not carry it.** Read key ≠ write key on this
           * column, so testing `data.companyTypeId` would compare `undefined`
           * against a number and report every landed write as unapplied.
           *
           * ⛔ **And it is `.companyType`, never `.type`** — the long word is the
           * field, which `types.ts` states at {@link Company.companyType}. Reading
           * `.type` compiles to `undefined` and would report the same false
           * failure by a different route.
           */
          applied: ({ data }) => (data.companyType?.id ?? null) === companyTypeId,
          notApplied: 'Company type not saved — the server didn’t apply the change. Try again.',
        });
      }}
      disabled={g.disabled}
    />
  );
}

/**
 * The company's stage, in the VALUE column of the two `/companies` cards.
 *
 * ⚠️ **It is the value column and it is NOT money** — see
 * {@link VALUE_COLUMN_KIND}, which is what tells this apart from the deals,
 * quotes and invoices cards without reading `state.valueLabel`, an admin-facing
 * string.
 *
 * 🔴 **CHANGING IT MOVES THE ROW BETWEEN THE LEADS AND COMPANIES CARDS, and the
 * caller is what knows that.** Those two are a PARTITION on `stageId` —
 * `nonLeadStageFilter` derives one from the other — so a row that stops being a
 * lead has to leave one card and arrive on the other. The page reloads BOTH; see
 * `bumpReload`.
 */
function CompanyStageCell({
  row,
  companyId,
  writer,
  master,
}: {
  row: PartnershipRow;
  companyId: number | null;
  writer: PartnershipRowWriter;
  master: FilterMaster;
}) {
  const stage = row.companyStage;
  const g = cellGate(
    writer,
    companyId === null
      ? 'This row names no company, so there is no stage to set.'
      : master.failed
        ? MASTER_UNREAD
        : undefined,
  );

  return (
    <InlineField
      label="Stage"
      kind="picker"
      value={stage === null ? '' : String(stage.id)}
      /*
       * ⚠️ **`row.meta`, NOT `stage.name`, and the difference is not cosmetic.**
       * `meta` is what the value column has always rendered on these two cards
       * and is what the column's own heading describes; reading the embedded
       * object instead would be a SECOND derivation of one cell's text, which is
       * the arrangement this file's own comments describe as what silently
       * drifts. The picker's VALUE is the id, which is the half that must be
       * exact.
       *
       * ⚠️ **`block` is what makes `truncate` apply to a `<span>`.** The utility
       * is `overflow:hidden` + `text-overflow:ellipsis` + `white-space:nowrap`,
       * and the first two do nothing on an inline box — so a bare `truncate`
       * would neither ellipsise nor widen the column, it would paint over the
       * cell beside it. That is what `Awaiting signature` did to `Your last note`
       * once already (reported 2026-09-03).
       */
      display={
        <span className="block truncate text-xs" title={cellTitle(row.meta ?? undefined, g.reason)}>
          {orDash(row.meta)}
        </span>
      }
      options={editorOptions(master.rows, stage)}
      optionsLoading={master.loading}
      /*
       * ⚠️ **"Unclassified" is a real, reachable state** — `stageId` is nullable
       * and `null` clears it — and the two `/companies` cards partition on the
       * LEAD stage, so a company with no stage at all falls out of Leads without
       * arriving anywhere. That is the contract rather than a fault; it is said
       * here because nothing else on the card can.
       */
      emptyOptionLabel="No stage (unclassified)"
      onSave={(next) => {
        // Refused rather than defaulted — see `CountryCell`, where the same
        // guard is written out and the reason is given in full.
        if (companyId === null) {
          writer.onSettled();
          return Promise.resolve<InlineSaveResult>({
            status: 'error',
            message: 'This row names no company, so nothing was saved.',
          });
        }
        const stageId = next === '' ? null : Number(next);
        return writer.save({
          requestKey: 'stageId',
          write: () => api.updateCompany(companyId, { stageId }),
          applied: ({ data }) => (data.stage?.id ?? null) === stageId,
          notApplied: 'Stage not saved — the server didn’t apply the change. Try again.',
        });
      }}
      disabled={g.disabled}
    />
  );
}

/**
 * A quote's stage or an invoice's status, edited in the column that reads it.
 *
 * 🔴 **THE PICKER WRITES A DIFFERENT ENDPOINT ON EACH CARD, AND THE TWO MASTERS'
 * IDS ARE BOTH PLAIN INTEGERS.** A quote stage id resolves against the
 * invoice-status master without complaint, so which card this is on is read from
 * {@link STATUS_COLUMN_KIND} — a declared map — and never from a badge, a label
 * or the row's own tag, which is absent on exactly the row somebody opens this
 * control to fix.
 *
 * 🔴 **ACCEPTING OR DECLINING A QUOTE IS CONFIRMED FIRST — owner's decision,
 * 2026-09-12.** Both are read from the FLAGS (`isStageAccepted` /
 * `isStageDeclined` against the `/quote-stages` master this page already holds),
 * never from a stage NAME: `"Not Accepted"` contains `"accept"`, and this
 * codebase has painted it the same green as `Accepted` once already.
 *
 * ⚠️ **`is_accepted` is seeded on BOTH `Accepted` AND `Invoiced`, and
 * `is_declined` is NOT on `Expired`.** So the confirmation fires on two stages
 * rather than one, and does not fire on a quote quietly expiring — which is right
 * in both directions and is invisible from any list of stage names.
 *
 * ⚠️ **An INVOICE STATUS is not confirmed.** Nothing about it is one-way: an
 * invoice's sales-tracker card moves on `is_awaiting_payment` / `is_paid`, and a
 * status set in error is set again. Confirming it would train the reader to
 * dismiss the dialog that does matter.
 */
function StatusCell({
  row,
  kind,
  writer,
  masters,
  master,
  confirmQuoteStage,
}: {
  row: PartnershipRow;
  kind: 'quote-stage' | 'invoice-status';
  writer: PartnershipRowWriter;
  /** For the badge's TONE and for the accepted/declined FLAGS. */
  masters: StatusMasters;
  /**
   * The same master, with its load state.
   *
   * ⚠️ **TWO VIEWS OF ONE LIST, and both are needed.** `StatusMasters` carries
   * the flag-bearing rows and deliberately drops `loading`/`failed`, because a
   * failed master costs a BADGE its tone and never its label. A PICKER cannot
   * degrade that quietly: an empty dropdown while the list is in flight and one
   * that could not be read look identical and mean opposite things. The panel
   * hands both from the same source, so they cannot describe different lists.
   */
  master: FilterMaster;
  confirmQuoteStage: PartnershipEditing['confirmQuoteStage'];
}) {
  const isQuote = kind === 'quote-stage';
  const rows: readonly FilterOption[] = isQuote ? masters.stages : masters.statuses;
  const current = row.status?.ref ?? null;
  const g = cellGate(writer, master.failed ? MASTER_UNREAD : undefined);

  return (
    <InlineField
      label={isQuote ? 'Stage' : 'Status'}
      kind="picker"
      value={current === null ? '' : String(current.id)}
      /*
       * ⚠️ **The badge is the display, so the tone a reader knows survives the
       * cell becoming editable.** `RowStatusBadge` is the one place a flag
       * becomes a colour; rendering the bare name here instead would be a second
       * answer to "is this settled", which is the difference between money nobody
       * chases and a false alarm somebody dismisses.
       */
      /*
       * ⚠️ **Wrapped so the refusal has somewhere to live.** `Badge` carries no
       * `title` of its own, so the wrapper is the only place a sentence can go —
       * and it must be a sentence on a `title`, never a line under the badge.
       */
      display={
        <span title={g.reason}>
          <RowStatusBadge status={row.status} masters={masters} />
        </span>
      }
      options={editorOptions(rows, current)}
      optionsLoading={master.loading}
      emptyOptionLabel={isQuote ? 'No stage' : 'No status'}
      onSave={async (next) => {
        const id = next === '' ? null : Number(next);

        if (isQuote && id !== null) {
          /*
           * ⚠️ **The stage is looked up in the MASTER because the flags live
           * there and not on the row.** `row.status.ref` is a bare `{id, name}`
           * with no `isAccepted` on it — which is the whole reason
           * `RowStatusBadge` is handed the masters too.
           *
           * ⚠️ **A row the master does not hold answers `null` and the write
           * proceeds UNCONFIRMED.** The gate below already disables this control
           * when the master `failed`, so that is defence in depth rather than a
           * live path — and it fails in the right direction either way: a missing
           * dialog costs a confirmation, where a dialog synthesised from a NAME
           * would make a claim about what the stage does. ⛔ Never fall back to
           * matching the name here; `isStageAccepted` already has its own
           * exact-name fallback and reaches it only when the FLAG is absent.
           */
          const chosen = masters.stages.find((stage) => stage.id === id) ?? null;
          const asRef: MasterRef | null = chosen && { id: chosen.id, name: chosen.name };
          const outcome = isStageAccepted(asRef, masters.stages)
            ? ('accepted' as const)
            : isStageDeclined(asRef, masters.stages)
              ? ('declined' as const)
              : null;

          if (outcome !== null && chosen !== null) {
            const confirmed = await confirmQuoteStage({
              quoteLabel: recordLabelOf(row),
              stageName: chosen.name,
              outcome,
            });
            if (!confirmed) {
              /*
               * ⚠️ **A DECLINED CONFIRMATION, which is one of the four sentence
               * kinds the no-helper-text rule keeps.** `warning` closes the
               * editor and holds the line: without it, Save-then-Cancel reads as
               * a button that did nothing.
               */
              writer.onSettled();
              return { status: 'warning', message: 'Stage not changed — you cancelled.' };
            }
          }
        }

        return isQuote
          ? writer.save({
              requestKey: 'stageId',
              write: () => api.updateQuote(row.id, { stageId: id }),
              applied: ({ data }) => (data.stage?.id ?? null) === id,
              notApplied: 'Stage not saved — the server didn’t apply the change. Try again.',
              push: (envelope) => envelope.xeroPush,
              pushSubject: `Quote ${recordLabelOf(row)}`,
            })
          : writer.save({
              requestKey: 'statusId',
              write: () => api.updateInvoice(row.id, { statusId: id }),
              applied: ({ data }) => (data.status?.id ?? null) === id,
              notApplied: 'Status not saved — the server didn’t apply the change. Try again.',
              push: (envelope) => envelope.xeroPush,
              pushSubject: `Invoice ${recordLabelOf(row)}`,
            });
      }}
      disabled={g.disabled}
    />
  );
}

/**
 * The projects this company is being pursued for.
 *
 * 🔴 **IT IS EDITABLE NOW — owner's instruction, 2026-09-12 — AND THE WHOLE CELL
 * IS THE TRIGGER.** ⚠️ *SUPERSEDED, and every clause of it was true when
 * written:* "⛔ **READ-ONLY, AND THAT IS A DECISION RATHER THAN AN UNFINISHED
 * CELL.** Editing this lives on `/companies` and on the company detail page,
 * which is where the record is. A dashboard is five tables of up to a hundred
 * rows each over four endpoints; an editor here would be a fifth writer of a
 * field whose other three already agree, on the one surface that cannot show the
 * reader what else is on the record they are changing. The row carries no company
 * id for three of the five cards either — `row.id` is a deal, a quote or an
 * invoice there — so the control could not even be offered consistently."
 *
 * ⚠️ **Note WHICH half of that expired.** The *fifth writer* objection is spent
 * by an instruction, not by an argument: the owner asked for it, and a fourth
 * writer of one field is a decision they are entitled to take. The *no company
 * id* objection expired by a FACT — `PartnershipRow` carries `companyId` now, so
 * the control really can be offered consistently, which is what
 * {@link companyIdOf} resolves. ⛔ Neither is a reason to widen the editor's
 * SCOPE: it writes the one field, through the one modal, and the record pages
 * remain where a company is edited as a whole.
 *
 * 🔴 **IT IS ON THE TWO COMPANY CARDS ONLY SINCE 2026-09-14 — owner's decision.**
 * ⚠️ *SUPERSEDED: "**It is on ALL FIVE CARDS** (`ALL_CARD_COLUMNS`), so this cell
 * runs against a deal, quote and invoice row as well as a company one — which is
 * why it reads {@link companyIdOf} rather than assuming `row.id`."* The
 * `projectOfInterest` COLUMN is declared by `COMPANY_CARD_COLUMNS` now; Deals,
 * Quotes and Invoices carry `lineProjects` and {@link DocumentProjectsCell}
 * instead, showing the document's own line projects and writing nothing.
 *
 * ⚠️ **{@link companyIdOf} STAYS, and the reason it was reached for has NOT
 * expired.** Leads and Companies answer from `row.id`, which is what that helper
 * decides; the clause above about deal rows is what went. ⛔ Do not "simplify" it
 * to `row.id` here on the strength of this cell now being company-only — the
 * helper is the one place that derivation lives, and inlining half of it is how
 * the two cards come to resolve a company differently.
 *
 * ⚠️ **THE WRITE IS WHAT CONFINED IT, more than the read.** This cell opens
 * `CompanyProjectsModal`, which writes the COMPANY's whole set. On a document
 * card the cell would have shown one list and edited another, so the reader's
 * untick would land on a record they were not looking at. A read-only cell was chosen
 * over a matching-subject editor because **a document has no such field to
 * write**: line projects live on the line items.
 *
 * ⚠️ **The wording is `companyProjectsLabel`'s and the full list is
 * `companyProjectsTitle`'s** — see the import. This cell decides neither, which
 * is what lets the column be re-worded in one place. In particular the EMPTY
 * case is the helper's to word: a company with no projects recorded is an
 * ordinary state on a Lead, and whether that reads as a dash or as a sentence is
 * a decision about the whole app rather than about this table.
 *
 * ⚠️ **The ONE thing this cell does decide is the `null` row**, because the
 * helpers take a non-nullable list and are right to: an unread list is not a
 * short one. That branch is `CountryCell`'s null, reused rather than re-spelled,
 * and the reasoning is in the function.
 *
 * ⚠️ **`truncate`, which is the SAFE half of the pair, not the silent one.** It
 * carries `overflow: hidden` and `text-overflow: ellipsis` as well as
 * `white-space: nowrap`, so an over-long label is cut with an ellipsis inside a
 * `table-fixed` column. A bare `whitespace-nowrap` is the one that breaks
 * silently — it neither truncates nor widens, it paints over the next column,
 * which is exactly what `Awaiting signature` did to `Your last note` (reported
 * 2026-09-03). `block` is what makes the ellipsis apply to a `<span>`.
 */
function ProjectOfInterestCell({
  row,
  companyId,
  writer,
  onOpen,
}: {
  row: PartnershipRow;
  companyId: number | null;
  writer: PartnershipRowWriter;
  onOpen: () => void;
}) {
  /*
   * ⛔ **THE `null` BRANCH COMES FIRST, AND `?? []` IS THE ONE EDIT THAT MUST
   * NEVER BE MADE HERE.** The two helpers take a non-nullable list, so a `?? []`
   * at the call below is the shortest way to make this compile and it is a
   * defect: `companyProjectsLabel([])` renders an em dash meaning *this company
   * is being pursued for nothing* — a real, ordinary state on a Lead. `null` is
   * a different fact entirely. On the two `/companies` cards it is *the response
   * carried no such key*, which happens whenever this app is deployed ahead of
   * the API; on a deal, quote or invoice row it is *the concept does not apply
   * to this record*. Coalescing turns either into a confident claim of none, on
   * the one column whose whole job is to say what is being pursued — and the
   * claim is then indistinguishable from the truth, with nothing failing. The
   * field's own doc on `PartnershipRow` and the header on `companyProjectsLabel`
   * both say so in terms.
   *
   * ⚠️ **`orDash(null)` in `text-fg-subtle` is `CountryCell`'s null exactly** —
   * same glyph, from the one copy in `format.ts`, and the same muted tone.
   * Two absent cells on one row spelled two ways read as two different states.
   *
   * ⚠️ **"read", NOT "recorded", and that verb is the whole distinction.**
   * `CountryCell` may say *recorded* because its `null` has one meaning. Ours
   * has two, and this cell can tell them apart from neither the row nor the
   * card it is on, so the sentence says only what is true of both: nothing
   * arrived. ⛔ Do not harmonise it to `CountryCell`'s wording — that is the
   * `?? []` bug written in prose rather than in code, and it would read as a
   * tidy-up.
   */
  if (row.projectsOfInterest === null) {
    return (
      <span className="text-fg-subtle" title="No projects of interest were read for this row.">
        {orDash(null)}
      </span>
    );
  }

  /*
   * ⚠️ **The full list on `title` is the whole recovery for the truncation
   * above**, and the attribute is SPREAD CONDITIONALLY rather than passed as
   * `title={undefined}`. `companyProjectsTitle` returns `undefined` on an empty
   * set precisely so React omits the attribute, where an empty `title` renders a
   * blank tooltip box on hover and reads as a broken one. Same spelling as the
   * `/companies` cell, which is the other caller of this pair.
   *
   * ⚠️ **The EMPTY case is still the helper's to word, not this cell's.** No
   * `length === 0` branch here: whether a company with nothing recorded reads as
   * a dash or as a sentence is a decision about the whole app, and it is made
   * once in `companyProjectsLabel`.
   */
  const projects = row.projectsOfInterest;
  const title = companyProjectsTitle(projects);
  const label = companyProjectsLabel(projects);

  /*
   * 🔴 **WHY THIS ROW MAY NOT BE EDITED, and the FIRST branch is the one that
   * matters.** `projectIdsOfInterest` is a WHOLE-SET REPLACE — `[]` clears — so
   * an editor opened over a list this page never read would show nothing ticked
   * and, on save, would destroy every project the company holds with a `200` and
   * nothing to restore from. `null` is exactly that state: *the response carried
   * no such key*, which happens whenever this app is deployed ahead of the API.
   * **The em dash must not become a way in.**
   *
   * ⚠️ The `null` branch above returns before this, so the check reads as
   * belt-and-braces and is not: it is what stops a later refactor collapsing the
   * two renders into one and handing the modal an unread list.
   */
  const g = cellGate(
    writer,
    projects === null
      ? 'No projects of interest were read for this row, so they cannot be changed here.'
      : companyId === null
        ? 'This row names no company, so there are no projects of interest to set.'
        : undefined,
  );
  /*
   * ⚠️ **The refusal goes on the `title`, which is where a sentence belongs in a
   * desktop grid cell** — see {@link CellGate.reason}. The full list is already
   * here for the truncation, so the sentence joins it rather than taking a line
   * of its own under every row of five cards.
   */
  const fullTitle = cellTitle(title, g.reason);

  /*
   * ⛔ **READ-ONLY READS EXACTLY AS IT ALWAYS DID — a value, not an inert
   * control.** `InlineField` renders its `disabled` state as plain text with no
   * trigger and no glyph for the same reason: an affordance whose only outcome is
   * nothing is worse than no affordance, and this page has had a menu reported as
   * a DELETED FEATURE for being merely unreachable.
   */
  if (g.disabled) {
    return <ProjectsText projects={projects} label={label} title={fullTitle} />;
  }

  /*
   * ⚠️ **THE WHOLE CELL IS THE TRIGGER — owner's instruction, 2026-09-12** — and
   * it opens the ONE modal mounted at the page. Never a modal per row: five cards
   * of a hundred rows would be five hundred mounted dialogs, each with its own
   * focus trap, which is the same reasoning that keeps `ActivityDrawer` at the
   * page.
   *
   * ⚠️ **A plain `<button>`, the same knowing exception `NoteCell` two cells over
   * documents**: `Button` bakes in `justify-center`, which a `className` cannot
   * override — both are `justify-content` utilities, so the emitted stylesheet's
   * own order decides and `center` wins whatever the class string says. A
   * centre-aligned truncated list is not the control.
   */
  return (
    <button
      type="button"
      onClick={onOpen}
      title={fullTitle}
      className={cx(
        'block w-full rounded-sm py-1 text-left text-xs text-fg-secondary',
        'transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none',
      )}
    >
      {/*
        * Every project as a pill, wrapping — owner's decision 2026-09-25, no cap
        * and no `+N`. The empty set keeps the helper's dash, unchanged.
        */}
      {projects.length === 0 ? label : <ValuePills items={companyProjectsPills(projects)} />}
      {/*
        * What the control DOES, for a reader who cannot see that it is a button.
        * The visible text stays the accessible name — the projects are the
        * answer — so this is appended rather than replacing it.
        */}
      <span className="sr-only"> (change the projects of interest)</span>
    </button>
  );
}

/**
 * A list of project names in a 12rem cell: truncated, with the whole list on the
 * `title`.
 *
 * 🔴 **ONE COPY BECAUSE THE COLUMN IS NO LONGER UNIFORM.** Since 2026-09-14 this
 * heading sits over two different cells — {@link ProjectOfInterestCell} on the
 * two company cards and {@link DocumentProjectsCell} on the three document ones
 * (`COMPANY_CARD_COLUMNS` and `DOCUMENT_CARD_COLUMNS`). Two copies of one class
 * string would agree the day they were written and drift on the first restyle,
 * in a column where the drift reads as two different facts about one page.
 *
 * ⚠️ **EXACTLY TWO CALL SITES, and the list this used to give was wrong in both
 * directions — corrected 2026-09-14.** *SUPERSEDED, written hours earlier the
 * same day: "the read-only rendering is now reached from **three places: a
 * document row**, a company row a reader may not edit, and **a company row whose
 * list could not be read**."*
 *
 *  - **A company row a reader may not edit** — {@link ProjectOfInterestCell}'s
 *    `g.disabled` branch. Correct, and the only clause of the three that was.
 *  - **A document row** — true when written and falsified by the dialog that
 *    landed the same afternoon: a document row reaches this ONLY when its list
 *    is EMPTY, because a non-empty one now renders a button.
 *  - **A company row whose list could not be read** — never reached this at all.
 *    That branch returns `CountryCell`'s muted `orDash` span directly, and
 *    always did.
 *
 * ⚠️ **Both errors point the same way: they overstate what depends on this
 * helper**, so a reader restyling it either over-constrains the change or
 * "fixes" the button back into `ProjectsText` to match a documented invariant
 * that was never true. **Count the call sites; do not trust this paragraph.**
 *
 * ⚠️ **`truncate`, which is the SAFE half of the pair, not the silent one.** It
 * carries `overflow: hidden` and `text-overflow: ellipsis` as well as
 * `white-space: nowrap`, so an over-long label is cut with an ellipsis inside a
 * `table-fixed` column. A bare `whitespace-nowrap` is the one that breaks
 * silently — it neither truncates nor widens, it paints over the next column,
 * which is exactly what `Awaiting signature` did to `Your last note` (reported
 * 2026-09-03). `block` is what makes the ellipsis apply to a `<span>`.
 *
 * ⚠️ **It takes a `title` already composed**, rather than a projects list, so
 * the two callers keep their own reasons for what goes in it — a refusal
 * sentence joins the list on the company card, and nothing does on the document
 * one, because a cell nobody may edit has no refusal to report.
 */
function ProjectsText({
  projects,
  label,
  title,
}: {
  projects: readonly { id: number; name: string }[];
  label: string;
  title: string | undefined;
}) {
  /*
   * 🔴 **Pills since 2026-09-25 (owner's decision)** — every project, wrapping,
   * no cap and no `+N`; the row grows instead of the tail being cut. The EMPTY
   * set keeps the helper's label (the dash) exactly as before, so the majority
   * empty case renders byte-identically.
   */
  if (projects.length === 0) {
    return (
      <span className="block text-xs text-fg-secondary" title={title}>
        {label}
      </span>
    );
  }
  return (
    <span className="block text-xs text-fg-secondary" title={title}>
      <ValuePills items={companyProjectsPills(projects)} />
    </span>
  );
}

/**
 * The projects a DEAL, QUOTE or INVOICE names on its own line items — read-only.
 *
 * Owner's decision, 2026-09-14: those three cards show the document's projects,
 * not its company's. It renders the `lineProjects` column, declared by
 * `DOCUMENT_CARD_COLUMNS`; the two cards that really are companies keep
 * `projectOfInterest` and {@link ProjectOfInterestCell}. The reversal and its
 * reason are recorded at {@link COLUMN_LABEL}'s two project entries and, in
 * full, on `PartnershipRow.lineProjects`.
 *
 * ## 🔴 IT OPENS A READ-ONLY LIST — owner's decision, 2026-09-14
 *
 * ⚠️ *SUPERSEDED, and it was this cell's headline: "**IT IS READ-ONLY, AND THAT
 * IS THE SAFETY HALF RATHER THAN AN OMISSION** … ⚠️ **No inert control, and no
 * disabled trigger** … the cell is not disabled, it is a value."* The owner
 * reported that the cell did not open on click the way `projectOfInterest`
 * beside it does, and chose {@link DocumentProjectsModal} — a dialog listing
 * every project the document's lines name, each linking to it.
 *
 * 🔴 **"READ-ONLY" NOW MEANS *WRITES NOTHING*, NOT *IS NOT A CONTROL*, AND THE
 * DISTINCTION IS THE WHOLE OF WHY THIS WAS SAFE TO ADD.** The cell is a trigger;
 * nothing behind it has a save, a picker or an `onSave`. ⛔ **Every reason below
 * is about the WRITE and none of them expired** — read them before adding
 * anything to the dialog.
 *
 * ⛔ **There is still no editor to give it.** A line's project is a property of
 * the LINE — chosen per line on the quote, invoice and deal forms, and this
 * column is a DISTINCT summary across them — so there is no field on the document
 * to write and no single control that could express a change to several lines at
 * once.
 *
 * ⛔ **The available editor is the WRONG one, and reaching for it is the defect
 * this cell exists to prevent — UNCHANGED, and it is now one prop away rather
 * than structurally unreachable.** `CompanyProjectsModal` writes the COMPANY's
 * projects of interest, which is a different list. Opening it from a cell
 * showing the document's projects would let somebody untick an item from a list
 * they were never shown — with a `200` and nothing on screen to say so.
 * **Do not wire `onOpenProjects` to this cell.** It was put to the owner as an
 * option on 2026-09-14 and was **not** the one chosen.
 *
 * ⚠️ **`onOpen` and `onOpenProjects` are one word apart and land on different
 * dialogs**, which is the hazard this cell now carries that it did not before.
 * The guard is no longer the type — both are `() => void` — so it is the call
 * site's comment and this paragraph. **Check which modal a handler opens before
 * copying a line from the `projectOfInterest` arm.**
 *
 * ## ⚠️ THE TRIGGER IS WITHHELD ON AN EMPTY OR UNREAD LIST, AND THAT IS NOT TIDINESS
 *
 * A dialog that opens to say nothing is the affordance-with-one-outcome this page
 * has already been burned by — a menu was reported as a DELETED FEATURE for being
 * merely unreachable — and **the empty case here is the MAJORITY**: 613 of 1,088
 * documents, 56%. So an always-clickable cell would be a control that does
 * nothing on more than half the rows on three cards.
 *
 * ⚠️ **So `[]` and `null` both render exactly what they rendered before**, a
 * plain value, and only a non-empty list becomes a button. ⛔ **Do not "fix" the
 * inconsistency by making the dash clickable**: the two states are told apart by
 * whether there is anything to show, which is the only honest test available.
 *
 * ⚠️ **Every non-empty cell is a trigger, including one- and two-project rows
 * that are not truncated at all.** Gating on `length > 2` would make two
 * identical-looking cells behave differently with nothing on screen saying which
 * is which — an invisible rule — and a single long name is truncated by CSS in a
 * 12rem column anyway, so the dialog is a recovery path there too.
 *
 * ## ⚠️ THE EMPTY CELL IS THE MAJORITY CASE AND IS AN ORDINARY STATE
 *
 * Measured 2026-09-14: **613 of 1,088 documents name no project on any line —
 * 56%**. So more than half these cells are an em dash, and that must read as
 * *no line on this document names a project*, never as a fault or a value that
 * failed to load. `companyProjectsLabel([])` is already exactly that glyph, from
 * the one copy in `format.ts`, and it is the same dash `CountryCell` draws for
 * an absent country — two absent cells on one row spelled two ways read as two
 * different states. ⛔ **Do not give it a sentence, a skeleton or a "None"**: the
 * first is helper text, the second claims a request is in flight, and the third
 * is a word this app spells as a dash everywhere else.
 *
 * ## ⚠️ THE `title` IS NOT OPTIONAL HERE — it carries more than it ever has
 *
 * Measured 2026-09-14: **~21% of quotes and ~12% of invoices name 3+ projects,
 * up to 8**, against company sets that max out at 3. So the two-name cap and its
 * `+N` marker fire far more often on these cards than the cap was ever exercised
 * on the company ones — and the marker sits at the TAIL, where an ellipsis eats
 * it first. `companyProjectsTitle` is the only place the full set is readable,
 * which its own header states: **a caller that renders the label without the
 * title has built a cell that silently omits projects.**
 *
 * ⚠️ **The wording helpers are `companyProjectsLabel`'s and
 * `companyProjectsTitle`'s, and reusing them across the two kinds is
 * deliberate.** They are pure over `{id, name}` and decide only the RENDERING —
 * two names then `+N`, the full list joined, the blank-name `#id` fallback, the
 * dash for none — which is identical for both lists and was measured against
 * this same 12rem column. What differs between the two kinds is the HEADING and
 * what an empty cell MEANS, and neither of those is a job for these functions.
 * ⚠️ **If `company-projects.ts` lands document-specific label/title helpers whose
 * wording differs, swap them in here** — this is the one call site to change.
 *
 * ⚠️ **`null` is still not `[]`.** The wire contract is `lineProjects` always
 * present and `[]` when none — `lineProjectsOf` in the hook normalises it with
 * `?? null` and never `?? []` for exactly this reason — so `null` here can only
 * mean the field did not arrive, which happens whenever this app is deployed
 * ahead of the API. That is a different fact from a document whose lines name
 * nothing, and a `?? []` would claim the second for the first on the one column
 * whose whole job is to say what a document covers.
 *
 * ⛔ **AND IT IS NOT A FALLBACK FOR `projectsOfInterest`.** The row still carries
 * the account's list on these three cards; showing it here — for an empty set or
 * for a `null` — would restore the superseded rendering under a heading that
 * denies it is that, which is strictly worse than the rendering it replaced.
 */
function DocumentProjectsCell({ row, onOpen }: { row: PartnershipRow; onOpen: () => void }) {
  /*
   * ⚠️ **Read ONCE, by name, here** — the field is spelled in exactly one place
   * per column, because the two fields are structurally identical and swapping
   * them compiles.
   */
  const projects = row.lineProjects;

  /*
   * ⚠️ **`CountryCell`'s null, reused rather than re-spelled** — same glyph,
   * same muted tone, and a sentence on the `title` saying which of the two
   * absences this is. The cell above it draws the identical thing for its own
   * `null`, which is what stops a row carrying two unread columns reading as one
   * unread and one empty.
   */
  if (projects === null) {
    return (
      <span className="text-fg-subtle" title="No line projects were read for this row.">
        {orDash(null)}
      </span>
    );
  }

  /*
   * ⚠️ **Spread conditionally, never `title={undefined}` by hand.**
   * `companyProjectsTitle` returns `undefined` on an empty set precisely so
   * React omits the attribute — an empty `title` renders a blank tooltip box on
   * hover and reads as a broken one — and `ProjectsText` takes the value
   * straight through so both callers get that behaviour from the one helper.
   */
  const label = companyProjectsLabel(projects);
  const title = companyProjectsTitle(projects);

  /*
   * ⛔ **NOTHING TO SHOW, SO NO TRIGGER** — and this is the majority branch on
   * these three cards, not an edge: 613 of 1,088 documents name no project on
   * any line. A button opening a dialog with nothing in it is the
   * affordance-with-one-outcome this page has already been burned by. It renders
   * byte-identically to what it rendered before the dialog existed.
   */
  if (projects.length === 0) {
    return <ProjectsText projects={projects} label={label} title={title} />;
  }

  /*
   * ⚠️ **THE WHOLE CELL IS THE TRIGGER, and the class string is
   * {@link ProjectOfInterestCell}'s exactly** — deliberately, because the two
   * columns sit in the SAME 12rem slot on five cards of one page, and a reader
   * who met two different hover treatments over one heading would read them as
   * two different kinds of thing. ⚠️ **Byte-identical rather than shared**: a
   * third `ProjectsText`-shaped helper taking a variant is how the two cells
   * would start disagreeing about which of them owns the styling.
   *
   * ⚠️ **A plain `<button>`, the same knowing exception the cell above
   * documents**: `Button` bakes in `justify-center`, which a `className` cannot
   * override — both are `justify-content` utilities, so the emitted stylesheet's
   * own order decides and `center` wins whatever the class string says.
   *
   * ⛔ **`onOpen` IS NOT `onOpenProjects`.** It opens
   * {@link DocumentProjectsModal}, which writes nothing. The company editor is
   * one prop away and is the one mistake here that cannot be undone — see this
   * function's header.
   */
  return (
    <button
      type="button"
      onClick={onOpen}
      title={title}
      className={cx(
        'block w-full rounded-sm py-1 text-left text-xs text-fg-secondary',
        'transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none',
      )}
    >
      <ValuePills items={companyProjectsPills(projects)} />
      {/*
        * What the control DOES, for a reader who cannot see that it is a button.
        * The visible text stays the accessible name — the projects are the
        * answer — so this is appended rather than replacing it. ⚠️ **"see", not
        * "change"**: the sibling cell's wording promises an editor, and this one
        * must not, or a screen-reader user is told a read-only list will let them
        * edit a document.
        */}
      <span className="sr-only"> (see every project on this document)</span>
    </button>
  );
}

/**
 * The invoice's DUE DATE, edited in the column that reports how late it is.
 *
 * 🔴 **THE HEADING SAYS "Overdue by" AND THE EDITOR SETS THE DUE DATE, AND THAT
 * IS DELIBERATE RATHER THAN A MISMATCH TO TIDY.** This page carries the due date
 * in exactly one cell: `overdue` is the only column built from `row.dueDate`, and
 * the owner asked for the due date to be editable. `InlineField`'s `display` prop
 * exists for precisely this — a value that reads as more than its own text — so
 * the READ stays the overdue judgement a reader already knows and the WRITE is
 * the column behind it.
 *
 * ⚠️ **The `label` is "Due date", not the column heading**, so the trigger
 * announces "Edit Due date: …" and the editor is labelled for what it changes.
 * ⛔ Do not "fix" this by renaming the column: the dash in this cell means NOT
 * OVERDUE (which includes settled, and includes no due date at all), and a column
 * headed "Due date" would make that dash read as *no due date* on every settled
 * invoice.
 *
 * 🔴 **IT SENDS `{billDate, dueDate}` TOGETHER — AND THE REASON WRITTEN HERE WAS
 * WRONG, corrected 2026-09-13.** ⚠️ *SUPERSEDED: "The server checks `billDate >
 * dueDate` only when it sees BOTH keys in one body, so a PATCH moving only the
 * due date past a stored bill date succeeds and leaves the invoice inconsistent,
 * silently."*
 *
 * **Measured in `invoices.service.ts`:** `touchesDates` is
 * `input.billDate !== undefined || input.dueDate !== undefined` — an **OR** —
 * and the check that follows runs `assertDateOrder(mergedDates(existing,
 * input))`. So **EITHER key alone is already judged against the STORED other
 * half**, and only a PATCH touching neither date skips the check. The hole this
 * paragraph described never existed on this endpoint.
 *
 * ⚠️ **THE CONTROL IS UNCHANGED AND IS STILL CORRECT — which is exactly why
 * nothing would ever have surfaced the dead reason.** Sending the pair is now
 * belt-and-braces rather than load-bearing: one harmless extra key, and a local
 * mirror of a rule that keeps holding if the server's ever moves. **Right
 * answer, wrong reason, no failing artefact** — the shape this file records
 * against the tax-control passage, and the reason a correction has to be written
 * rather than inferred from the code continuing to work.
 *
 * ⚠️ **IT COST NINE DAYS AND WAS ARBITRATED BETWEEN TWO DOCUMENTS.** Both repos'
 * `CLAUDE.md` argued it from source READS; neither ever quoted the boolean
 * operator the whole dispute turned on. **Name the operator, the column, the
 * mapper — a dispute between two summaries is not settled by a third summary.**
 *
 * ⚠️ **ONE EXTRA REQUEST PER SAVE, and it is not one per row.** `PartnershipRow`
 * carries no bill date — it is not a column on this page — so the save reads the
 * invoice for it. It fires only when somebody actually commits a due date, which
 * is rare. ⚠️ **That read is now a COST rather than a necessity**, so it is the
 * half a later pass may reasonably drop; dropping it means dropping `billDate`
 * from the payload too, and the two go together or not at all.
 *
 * ⚠️ **A FAILED READ REFUSES THE WRITE rather than falling back to the lone
 * key.** A request that did not happen is not evidence about what the invoice
 * holds, and guessing here is exactly the silent inconsistency the pair exists to
 * prevent.
 */
function DueDateCell({
  row,
  writer,
  masters,
}: {
  row: PartnershipRow;
  writer: PartnershipRowWriter;
  masters: StatusMasters;
}) {
  const g = cellGate(writer);

  return (
    <InlineField
      label="Due date"
      type="date"
      value={toDateInputValue(row.dueDate)}
      display={<OverdueCell row={row} masters={masters} extraTitle={g.reason} />}
      /*
       * ⚠️ **`validate` is the SYNCHRONOUS half only.** A part-typed date reads
       * back as `''`, which is indistinguishable from a cleared field — and
       * `dueDate` really is clearable, so only a real-date rule can be applied
       * before the request. The ORDERING rule needs the bill date, which is a
       * request away, so it lives in `onSave` and reports as a refusal there.
       */
      validate={(next) => (next === '' || isRealDate(next) ? null : 'Must be a real date in YYYY-MM-DD form.')}
      onSave={async (next) => {
        const dueDate = next === '' ? null : next;

        let billDate: string | null;
        try {
          billDate = (await api.getInvoice(row.id)).data.billDate;
        } catch {
          writer.onSettled();
          return {
            status: 'error',
            message:
              'Couldn’t read this invoice’s bill date, so the due date was not saved. Try again.',
          };
        }

        /*
         * The same sentence `InlineInvoiceFields` uses, because it is the same
         * rule — and it is a local mirror of the server's, not a substitute for
         * it: the pair travels either way, so a rule that changed on the server
         * still refuses.
         */
        if (dueDate !== null && billDate !== null && billDate > dueDate) {
          writer.onSettled();
          return { status: 'error', message: 'The due date cannot be before the bill date.' };
        }

        return writer.save({
          /*
           * ⚠️ **The key the reader is editing, so a 422 naming EITHER half of
           * the pair lands on the control that is open.** The bill date has no
           * control on this page, so routing to it would mark nothing and inflate
           * a count nobody can act on.
           */
          requestKey: 'dueDate',
          write: () => api.updateInvoice(row.id, { billDate, dueDate }),
          applied: ({ data }) => toDateInputValue(data.dueDate) === next,
          notApplied: 'Due date not saved — the server didn’t apply the change. Try again.',
          push: (envelope) => envelope.xeroPush,
          pushSubject: `Invoice ${recordLabelOf(row)}`,
        });
      }}
      disabled={g.disabled}
    />
  );
}

/**
 * Whether an open task sits anywhere on this account.
 *
 * ⛔ **DERIVED AND READ-ONLY, AND IT IS NOT A COLUMN WAITING FOR AN EDITOR.**
 * Tasks are created, completed and re-dated in the drawer the row already opens
 * from two places; a Yes/No control here could only express "make one up" or
 * "close whatever is open", neither of which is a thing a person means.
 *
 * 🔴 **THREE THINGS THE FLAG IS NOT, AND THE `title` IS WHERE THEY ARE SAID
 * because a heading cannot carry them:**
 *
 *  - **It does not test a DUE DATE.** A task with no date at all and one three
 *    weeks overdue both read `Yes`. So nothing here may promise a future date —
 *    see {@link COLUMN_LABEL.upcomingTask}, which is why the heading says "Open
 *    task" and not the owner's own word for it.
 *  - **It is ACCOUNT-LEVEL IN BOTH DIRECTIONS.** A `Yes` on a quote row means a
 *    task is open somewhere on that ACCOUNT, not on that quote — and a `No` means
 *    nothing is open anywhere on it, not merely on this record.
 *  - **It is VIEWER-INDEPENDENT**, unlike `latestNote` two columns over, whose
 *    blank is scoped to the reader and needs the word "Your" to be sayable at
 *    all. Every reader gets the same answer here, so `No` is a fact about the
 *    account rather than about who is looking.
 *
 * ⚠️ **`Yes`/`No` and never a tick or a colour.** A tick beside an empty cell
 * makes "no open task" and "not read" identical, and this table's em dash already
 * means the second — `hasOpenTask` is a `boolean` with no third state, so the
 * cell must not borrow a glyph that has one. Colour is never the only signal
 * here either; the word carries it.
 */
function UpcomingTaskCell({ row }: { row: PartnershipRow }) {
  const open = row.hasOpenTask;
  return (
    <span
      className={cx('text-xs', open ? 'font-medium text-fg-secondary' : 'text-fg-subtle')}
      title={
        open
          ? 'At least one task is open somewhere on this account. It may have no due date, and it may already be overdue.'
          : 'No task is open anywhere on this account.'
      }
    >
      {open ? 'Yes' : 'No'}
    </span>
  );
}

/**
 * The Deals card's "Quote signed" cell (owner 2026-10-27) — read-only.
 * "Signed <date>" linked to the signed quote (a real `<Link>`), "Signed" alone
 * when the date is unknown, and `—` both when nothing is signed (`null`) and
 * when an older server sent no key (`undefined`). Wording is
 * `quoteSignedLabel`'s, the one copy.
 */
function QuoteSignedCell({ row }: { row: PartnershipRow }) {
  const signed = row.quoteSigned;
  if (signed === undefined || signed === null) {
    return <span className="text-xs text-fg-subtle">{quoteSignedLabel(signed)}</span>;
  }
  return (
    <TextLink
      to={`/quotes/${signed.quoteId}`}
      className="text-xs"
      {...(signed.quoteNumber !== null ? { title: signed.quoteNumber } : {})}
    >
      {quoteSignedLabel(signed)}
    </TextLink>
  );
}

/** The quote-signed funnel's value for one row; `null` (Is empty) = not reported. */
function quoteSignedFilterValue(row: PartnershipRow): 'signed' | 'unsigned' | null {
  if (row.quoteSigned === undefined) return null;
  return row.quoteSigned === null ? 'unsigned' : 'signed';
}

/**
 * The quote-signed funnel's two options, DECLARED so both are offered at a count
 * of zero — the closed-enum case, like {@link openTaskFilterOptions}. Labels are
 * `QUOTE_SIGNED_FILTER_OPTIONS`'; counts are over the loaded rows only.
 */
function quoteSignedFilterOptions(rows: readonly PartnershipRow[]): ColumnFilterOption[] {
  let signed = 0;
  let unsigned = 0;
  for (const row of rows) {
    const value = quoteSignedFilterValue(row);
    if (value === 'signed') signed += 1;
    else if (value === 'unsigned') unsigned += 1;
  }
  return QUOTE_SIGNED_FILTER_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    count: option.value === 'signed' ? signed : unsigned,
  }));
}

/**
 * The Companies card's credit balance — read-only, one figure per currency.
 *
 * `creditHeldOf` is the one place the three states are told apart: a balance
 * this reader cannot see is a subdued dash whose `title` says so (⛔ never *no
 * credit*), no credit is a plain em dash, and held credit is `balanceLabel` with
 * `balanceTitle` on the `title` — load-bearing, because the cell truncates and
 * an ellipsis eats the second and third currencies.
 */
function CreditBalanceCell({ row }: { row: PartnershipRow }) {
  const held = creditHeldOf(row.creditBalances);
  if (held.kind === 'unknown') {
    return (
      <span className="text-xs text-fg-subtle" title={CREDIT_BALANCE_NOT_VISIBLE}>
        {balanceLabel([])}
      </span>
    );
  }
  if (held.kind === 'none') {
    return <span className="text-xs text-fg-subtle">{balanceLabel([])}</span>;
  }
  return (
    <span
      className="block text-xs text-fg-secondary tabular-nums"
      title={balanceTitle(held.balances)}
    >
      {/* One pill per currency, wrapping — never summed (owner's decision 2026-09-25). */}
      <ValuePills items={balancePills(held.balances)} />
    </span>
  );
}

/**
 * A card's per-stage status writes, owned by the PANEL: which rows are saving,
 * the last refusal per row, and the change handler. Keyed by
 * {@link stageStatusWriteKey} — company AND column — so a refusal lands on the
 * one cell that was edited and not on every status column of the row.
 */
interface StageStatusWrites {
  saving: ReadonlySet<string>;
  errors: ReadonlyMap<string, string>;
  onChange: (
    row: PartnershipRow,
    companyId: number,
    column: StageStatusColumn,
    stageStatusId: number | null,
  ) => void;
}

function stageStatusWriteKey(companyId: number, column: StageStatusColumn): string {
  return `${companyId}:${column.stageId}`;
}

/**
 * One `{Stage} status` cell (owner 2026-10-25), rendered by the shared
 * {@link StageStatusColumnCell}.
 *
 * ⚠️ **A dashboard row is not a `Company`**, so the cell is handed the four
 * fields it reads — id, name, stage and stage status — and nothing else. The
 * cell matches by ID only (`stage.id` against the column's `stageId`).
 *
 * ⚠️ **`companyWriter`'s gate**: a stage status is a `d_companies` column written
 * with `PATCH /companies`. Its why-disabled sentence goes on the cell's Tooltip
 * wrapper, never as a line in the grid; a failed status master puts
 * {@link MASTER_UNREAD} there instead of offering a short list.
 */
function RowStageStatusCell({
  row,
  companyId,
  column,
  writer,
  editing,
  writes,
}: {
  row: PartnershipRow;
  companyId: number | null;
  column: StageStatusColumn;
  writer: PartnershipRowWriter;
  editing: PartnershipEditing;
  writes: StageStatusWrites;
}) {
  const canEdit = writer.canEdit && companyId !== null && !editing.stageStatus.failed;
  const disabledReason = !writer.canEdit
    ? (writer.disabledReason ?? null)
    : editing.stageStatus.failed
      ? MASTER_UNREAD
      : null;
  /*
   * ⚠️ The library reads `stage`, `stageStatus`, `name` and passes the object
   * back through `onChange`; nothing else on a `Company` is consulted, so the
   * cast states a shape this cell owns rather than forging a record.
   */
  const company = {
    id: companyId ?? row.id,
    name: recordLabelOf(row),
    stage: row.companyStage,
    stageStatus: row.stageStatus ?? null,
  } as unknown as Company;
  const writeKey = companyId === null ? null : stageStatusWriteKey(companyId, column);

  return (
    <StageStatusColumnCell
      company={company}
      column={column}
      allStatuses={editing.stageStatus.statuses}
      canEdit={canEdit}
      disabledReason={disabledReason}
      saving={writeKey !== null && writes.saving.has(writeKey)}
      error={writeKey === null ? null : (writes.errors.get(writeKey) ?? null)}
      onChange={(_, stageStatusId) => {
        if (companyId === null) return;
        writes.onChange(row, companyId, column, stageStatusId);
      }}
    />
  );
}

/**
 * One row's cells, in the same order as the headings — because they are built
 * from the same list.
 *
 * 🔴 **IT RENDERS THE RECORD AND VALUE CELLS TOO, WHICH `ExtraCells` DID NOT.**
 * *SUPERSEDED 2026-09-12: `ExtraCells`, which drew the middle of the row while
 * the panel hard-coded `<TD><RowIdentity/></TD>` before it and the value cell
 * after it.* Those two positions were a fact about the markup rather than about
 * the column list, so a reader who moved the value column would have moved its
 * HEADING and left its cell where it was — silently, because under `table-fixed`
 * that is a shifted column and not an error.
 *
 * ⚠️ **`verticalAlign="top"` on EVERY cell, still a prop and still not a class.**
 * `TD` bakes in `align-middle` and both are the same CSS property, so a class
 * here would be racing the emitted stylesheet's order. The reason is unchanged:
 * a note wraps to as many lines as it needs, so the row is as tall as its
 * tallest cell, and centred cells would float the figure in the middle of
 * nothing beside a four-line note.
 */
function RowCells({
  row,
  tableKey,
  columnKeys,
  masters,
  statusMaster,
  signatures,
  valueLabel,
  editing,
  frozenSideOf,
  frozenOffsets,
  onOpenActivity,
  onOpenDocumentProjects,
  purchaseHighlights,
  stageStatusColumns,
  stageStatusWrites,
}: {
  row: PartnershipRow;
  /**
   * Which card this row is on.
   *
   * 🔴 **THREE DECISIONS ARE PROPERTIES OF THE CARD AND NOT OF THE ROW**, and
   * each of them answers wrongly if read off the row instead:
   * {@link VALUE_COLUMN_KIND} (a stage or money — never `state.valueLabel`, an
   * admin-editable string), {@link STATUS_COLUMN_KIND} (which master the picker
   * writes — never `row.status?.kind`, which is absent on exactly the row
   * somebody opens the control to fix), and {@link companyIdOf}.
   */
  tableKey: PartnershipTableKey;
  columnKeys: readonly string[];
  masters: StatusMasters;
  /** The status master WITH its load state — see `StatusCell`. */
  statusMaster: FilterMaster;
  signatures: SignatureState;
  /** `null` on the Leads card, which renders no value column at all. */
  valueLabel: string | null;
  /** The two gates, the pickers' masters and the write callbacks. */
  editing: PartnershipEditing;
  /*
   * The SAME handler the row's icon button calls, threaded down rather than a
   * second piece of state. There is ONE `ActivityDrawer` on this page and it is
   * mounted by the page itself; two ways into one sheet is fine, a second sheet
   * is not — five tables of a hundred rows would otherwise be five hundred
   * mounted drawers, each with its own fetch and its own focus trap.
   */
  onOpenActivity: (row: PartnershipRow, focus?: 'notes') => void;
  /*
   * ⛔ **THE READ-ONLY PROJECTS DIALOG, NOT THE COMPANY EDITOR.** One
   * {@link DocumentProjectsModal} for the page, on the same reasoning as the
   * drawer above: five tables of a hundred rows would otherwise be five hundred
   * mounted dialogs. **This is NOT `PartnershipEditing.onOpenProjects`**, which
   * opens `CompanyProjectsModal` and writes the COMPANY's whole set — a
   * different list, irreversibly. They are one word apart and both
   * `(row) => void`, so nothing but the name tells them apart.
   */
  onOpenDocumentProjects: (row: PartnershipRow) => void;
  /*
   * The cycle ids selected in this card's two purchase-history cycle filters —
   * `purchasedCycle` for the products column, `participatedCycle` for the
   * projects column. `[]` when unset, and always `[]` on a card that does not
   * offer the filter, so a stray `?dealsPurchasedCycle=` highlights nothing.
   */
  purchaseHighlights: {
    products: readonly number[];
    projects: readonly number[];
  };
  /** This card's `{Stage} status` columns by their `stageStatus:<id>` key. */
  stageStatusColumns: ReadonlyMap<string, StageStatusColumn>;
  stageStatusWrites: StageStatusWrites;
} & FreezeProps) {
  /*
   * ⚠️ **TWO WRITERS, because two of the columns here write a COMPANY and the
   * rest write the card's own record** — see {@link PartnershipEditing}. Built
   * once per row rather than per cell, so two cells on one row cannot gate
   * differently.
   */
  const companyWriter = rowWriter(editing, editing.company);
  const recordWriter = rowWriter(editing, editing.record);
  const companyId = companyIdOf(row, tableKey);
  /*
   * ⚠️ **Read into a local so `tsc` can narrow it.** An indexed access with a
   * variable key is re-evaluated at every mention, so testing
   * `STATUS_COLUMN_KIND[tableKey] === null` inline narrows nothing and the prop
   * below would still be `… | null`.
   */
  const statusKind = STATUS_COLUMN_KIND[tableKey];

  return (
    <>
      {columnKeys.map((key) => {
        const frozen = frozenPropsFor(key, frozenSideOf, frozenOffsets);

        if (key === RECORD_KEY) {
          return (
            <TD key={key} verticalAlign="top" {...frozen}>
              <RowIdentity row={row} />
            </TD>
          );
        }

        if (key === VALUE_KEY) {
          /*
           * ⚠️ **Still omitted entirely on a card with no value column.** The
           * key is simply absent from `columnKeys` there, so this branch is
           * unreachable on Leads rather than guarded — but the guard stays,
           * because `valueLabel` is what says the column is real and a cell
           * rendered against no `<col>` shifts every column that follows.
           */
          if (valueLabel === null) return null;
          /*
           * 🔴 **THE COMPANY CARDS' VALUE COLUMN IS A STAGE AND IS EDITABLE; THE
           * OTHER THREE CARDS' IS MONEY AND IS NOT.** See the read-only list at
           * the top of the editing section: a document's total is the sum of its
           * LINE ITEMS and the four header columns have two competing writers, so
           * one key per PATCH would leave three durable incoherent states — each
           * visible to a `GET`, copied by convert, and read by Xero.
           *
           * ⚠️ **`VALUE_COLUMN_KIND`, never `valueLabel`.** The heading is
           * admin-facing text and a rename must not turn an amount into a picker.
           */
          if (VALUE_COLUMN_KIND[tableKey] === 'company-stage') {
            return (
              <TD key={key} verticalAlign="top" {...frozen}>
                <CompanyStageCell
                  row={row}
                  companyId={companyId}
                  writer={companyWriter}
                  master={editing.companyStages}
                />
              </TD>
            );
          }
          return (
            <TD
              key={key}
              verticalAlign="top"
              numeric
              className="truncate text-xs"
              title={row.meta ?? undefined}
              {...frozen}
            >
              {orDash(row.meta)}
            </TD>
          );
        }

        /*
         * The per-stage `{Stage} status` columns (owner 2026-10-25) — an explicit
         * branch, because a dynamic key would otherwise reach the `null` arm
         * below and draw an empty cell under its heading.
         */
        if (isStageStatusColumnId(key)) {
          const column = stageStatusColumns.get(key);
          return (
            <TD key={key} verticalAlign="top" {...frozen}>
              {column === undefined ? null : (
                <RowStageStatusCell
                  row={row}
                  companyId={companyId}
                  column={column}
                  writer={companyWriter}
                  editing={editing}
                  writes={stageStatusWrites}
                />
              )}
            </TD>
          );
        }

        /*
         * 🔴 **THIS CHAIN IS NOT EXHAUSTIVE AND ITS LAST ARM IS A BARE `else`, so
         * a key with no branch of its own SILENTLY RENDERS THE OVERDUE CELL under
         * whatever heading it was given.** Nothing errors and `tsc` is green: the
         * cell is present, in the right column, drawing the wrong fact. That is
         * why `upcomingTask` has a branch below rather than being left to fall
         * through — and why the next column added needs one too.
         */
        return (
          <TD key={key} verticalAlign="top" {...frozen}>
            {key === 'status' ? (
              /*
               * ⚠️ **The picker's master is chosen by the CARD, never by the
               * row's tag** — `STATUS_COLUMN_KIND` answers `null` on the three
               * cards that declare no status column at all, and the badge is what
               * renders there if one ever appears without an editor behind it.
               */
              statusKind === null ? (
                <RowStatusBadge status={row.status} masters={masters} />
              ) : (
                <StatusCell
                  row={row}
                  kind={statusKind}
                  writer={recordWriter}
                  masters={masters}
                  master={statusMaster}
                  confirmQuoteStage={editing.confirmQuoteStage}
                />
              )
            ) : key === 'country' ? (
              <CountryCell
                row={row}
                companyId={companyId}
                writer={companyWriter}
                master={editing.countries}
              />
            ) : key === 'source' ? (
              /*
               * ⚠️ **`companyWriter`, like `country` above it** — a source is a
               * column on `d_companies` and is written with `PATCH /companies`,
               * so it gates on `companies.update`. On these two cards that is the
               * same gate the card's own module answers to, which is a
               * COINCIDENCE and not a rule: see {@link EDIT_MODULE}.
               *
               * ⚠️ **Declared only on the two `/companies` cards**, so this arm
               * is unreachable elsewhere — a `source` entry on any other spec
               * would render an empty cell under a heading, because no document
               * row carries the field. The branch exists rather than being left
               * to the `null` arm below for the reason that arm's own note
               * gives: a key with no branch is visibly unfinished, and the next
               * column added needs one too.
               */
              <SourceCell
                row={row}
                companyId={companyId}
                writer={companyWriter}
                master={editing.companySources}
              />
            ) : key === 'companyType' ? (
              /*
               * ⚠️ **`companyWriter`, like `country` and `source` above it** — a
               * type is a column on `d_companies` and is written with
               * `PATCH /companies`, so it gates on `companies.update`. On these
               * two cards that is the same gate the card's own module answers to,
               * which is a COINCIDENCE and not a rule: see {@link EDIT_MODULE}.
               *
               * ⚠️ **Declared only on the two `/companies` cards**, so this arm is
               * unreachable elsewhere — a `companyType` entry on any other spec
               * would render an empty cell under a heading, because no document row
               * carries the field. The branch exists rather than being left to the
               * `null` arm below for the reason that arm's own note gives: a key
               * with no branch silently renders the OVERDUE cell under whatever
               * heading it was given, and nothing errors.
               */
              <CompanyTypeCell
                row={row}
                companyId={companyId}
                writer={companyWriter}
                master={editing.companyTypes}
              />
            ) : key === 'upcomingTask' ? (
              <UpcomingTaskCell row={row} />
            ) : key === 'projectOfInterest' ? (
              /*
               * 🔴 **BACK TO THE TWO `/companies` CARDS ON 2026-09-14 — owner's
               * decision.** ⚠️ *SUPERSEDED: "🔴 **DECLARED ON ALL FIVE CARDS
               * since 2026-09-12** … a deal, quote and invoice row carries its
               * company's list now, and `companyIdOf` is what says which company
               * to write to."* ⚠️ *SUPERSEDED before that, and note that its WORRY
               * is what the new arrangement answers: "**Declared only on the two
               * `/companies` cards** … A `projectOfInterest` entry on any other
               * spec would render this against a deal, quote or invoice row,
               * which carries no such list."*
               *
               * ⚠️ **The three document cards declare `lineProjects` instead** —
               * a different column key over a different field, rendered by
               * {@link DocumentProjectsCell} below. `COMPANY_CARD_COLUMNS` and
               * `DOCUMENT_CARD_COLUMNS` are the one place that is decided, so
               * this arm needs no card test of its own and must not grow one:
               * a `projectOfInterest` entry on a document spec would render the
               * ACCOUNT's list under a heading denying it is, and offer the
               * irreversible write below from a cell that does not describe it.
               */
              <ProjectOfInterestCell
                row={row}
                companyId={companyId}
                /*
                 * ⚠️ **`companyWriter`, not the card's own.** Projects of interest
                 * are a column on `d_companies` and are written with
                 * `PATCH /companies`, so this gates on `companies.update`. On
                 * these two cards that is the same gate the card itself answers
                 * to, which is a COINCIDENCE and not a rule — see
                 * {@link EDIT_MODULE}. ⚠️ *SUPERSEDED 2026-09-14, and only because
                 * the arm narrowed: "on EVERY card … even on the deals card,
                 * whose own records gate on `deals`."*
                 */
                writer={companyWriter}
                onOpen={() => editing.onOpenProjects(row)}
              />
            ) : key === 'lineProjects' ? (
              /*
               * 🔴 **THE THREE DOCUMENT CARDS' PROJECTS COLUMN — owner's
               * decision, 2026-09-14**: what THIS deal, quote or invoice is for,
               * rather than what its account is being chased for.
               *
               * ⛔ **STILL NO `writer`, AND THAT ABSENCE IS STILL STRUCTURAL.**
               * {@link DocumentProjectsCell} does not accept one, so `tsc`
               * refuses a call site that tries to hand this cell an editor.
               *
               * 🔴 **`onOpen` ARRIVED ON 2026-09-14 AND IS NOT THAT.** ⚠️
               * *SUPERSEDED: "**NO `writer` AND NO `onOpen`** … `tsc` refuses a
               * call site that tries to hand this cell the company editor".*
               * The owner reported that the cell did not open on click and chose
               * a **read-only list**; `onOpenDocumentProjects` opens
               * {@link DocumentProjectsModal}, which writes nothing.
               *
               * ⛔ **SO THE TYPE NO LONGER GUARDS THIS ARM AND THIS COMMENT
               * DOES.** `editing.onOpenProjects` — one word away, used by the
               * `projectOfInterest` arm forty lines up — opens
               * `CompanyProjectsModal`, which writes the COMPANY's whole set.
               * Both are `(row) => void`, so handing this arm that one compiles cleanly
               * and lets a reader untick an item from a list this cell never
               * showed them. **Read the cell's header before changing this
               * line.**
               */
              <DocumentProjectsCell
                row={row}
                onOpen={() => onOpenDocumentProjects(row)}
              />
            ) : key === 'accountLead' ? (
              // Read-only: the record's current resolved lead (2026-10-27).
              row.accountLead ? (
                <span className="text-sm text-fg">{row.accountLead.name}</span>
              ) : (
                <span className="text-xs text-fg-subtle">
                  <span aria-hidden="true">—</span>
                  <span className="sr-only">No account lead</span>
                </span>
              )
            ) : key === 'quoteSigned' ? (
              // Read-only; declared on the DEALS card alone (`DEAL_CARD_COLUMNS`).
              <QuoteSignedCell row={row} />
            ) : key === 'creditBalance' ? (
              /*
               * Read-only, and declared on the COMPANIES card alone
               * (`COMPANIES_CARD_COLUMNS`). One figure per currency, never
               * summed; see {@link CreditBalanceCell}.
               */
              <CreditBalanceCell row={row} />
            ) : key === 'productsPurchased' ? (
              /*
               * Read-only, COMPANIES card alone. No `writer`, no `onOpen` — see
               * {@link PurchaseHistoryCell}.
               */
              <PurchaseHistoryCell
                history={row.purchaseHistory}
                kind="products"
                highlightCycleIds={purchaseHighlights.products}
                companyName={recordLabelOf(row)}
              />
            ) : key === 'projectsParticipated' ? (
              <PurchaseHistoryCell
                history={row.purchaseHistory}
                kind="projects"
                highlightCycleIds={purchaseHighlights.projects}
                companyName={recordLabelOf(row)}
              />
            ) : key === 'note' ? (
              <NoteCell row={row} onOpenActivity={() => onOpenActivity(row, 'notes')} />
            ) : key === 'lastNote' ? (
              /*
               * The ACCOUNT's last human note by anyone — the two company cards
               * alone (2026-10-02). Read-only: it is a summary across the
               * account's records, so there is no single field to write.
               * `undefined` (older server) renders a dash, `null` "No notes".
               *
               * Clicking either cell opens the page's one tasks-and-notes
               * drawer on its notes (owner, 2026-10-11) — the same
               * `OpenNotesButton` `/companies` and the sales tracker use, so
               * the three cannot word or style the way in apart. Offered on
               * every state, "No notes" included: the drawer is where the first
               * note is written.
               */
              <OpenNotesButton
                companyName={recordLabelOf(row)}
                onOpen={() => onOpenActivity(row, 'notes')}
              >
                <AccountLastNoteCell note={row.accountLastNote} />
              </OpenNotesButton>
            ) : key === 'lastNoteAt' ? (
              <OpenNotesButton
                companyName={recordLabelOf(row)}
                onOpen={() => onOpenActivity(row, 'notes')}
              >
                <AccountLastNoteDateCell note={row.accountLastNote} />
              </OpenNotesButton>
            ) : key === 'signature' ? (
              /*
               * `row.id` IS the quote id on the quotes card — the row is built
               * from the quote, so there is no second identifier to get wrong.
               * This column is only ever declared on that card; a `signature`
               * entry on any other spec would look up a quote id that is really
               * a company or an invoice id and could match an unrelated
               * envelope.
               */
              <SignatureCell state={signatures} quoteId={row.id} />
            ) : key === 'overdue' ? (
              /*
               * 🔴 **THE DUE DATE IS EDITED HERE, under a heading reading
               * "Overdue by"** — this is the only cell on the page built from
               * `row.dueDate`, and the owner asked for the due date to be
               * editable. `DueDateCell` renders `OverdueCell` as its `display`,
               * so the read is unchanged. See its header for why the column is
               * NOT renamed to match.
               */
              <DueDateCell row={row} writer={recordWriter} masters={masters} />
            ) : (
              /*
               * ⛔ **`null`, NOT A FALL-THROUGH TO THE LAST CELL.** This arm used
               * to be `OverdueCell`, which meant a key added to
               * `PartnershipColumn` without a branch here rendered an invoice's
               * overdue days under somebody else's heading — a real fact in the
               * wrong column, which reads as data rather than as a missing
               * branch. An empty `<TD>` keeps the column count (and therefore the
               * `table-fixed` alignment) while being visibly unfinished.
               */
              null
            )}
          </TD>
        );
      })}
    </>
  );
}

/**
 * Caps the rows scroller at exactly `VISIBLE_ROWS` rendered rows.
 *
 * ⚠️ **Measured, never assumed.** A row is one line when `primary` fits and two
 * when it does not, and the point at which that happens moves with the viewport
 * and with the sidebar collapsing. Any constant is therefore five rows at one
 * width and four-and-a-bit at another, which cuts a row through its own text and
 * looks like a rendering fault.
 *
 * ⚠️ *SUPERSEDED 2026-09-03: the example, which was a long company name "in a
 * HALF-WIDTH card".* The cards are full width now, so that particular name fits
 * where it used to wrap — **and the measurement matters exactly as much**, for a
 * reason the old example obscured by making this look like a narrow-card
 * problem: a row's height also depends on whether it carries a STATUS BADGE and
 * a company line under its name (quotes and invoices do, leads do not), so five
 * rows on the Quotes card and five on the Leads card are measurably different
 * heights at the SAME width. Widening the cards removed one source of variation
 * and this change added another.
 *
 * So the height is the sum of the `<thead>`'s own height and the `offsetHeight`
 * of the first five `<tr>`s, read after layout. The sixth row is then always
 * cut exactly at its top edge, whatever any of them measure.
 *
 * The observer watches the TABLE, not the scroller: the table's height is what
 * the measurement is made of and it does not change when the scroller's cap
 * does, so a resize cannot feed its own result back in. Nothing is measured or
 * capped at five rows or fewer — there is nothing below the fold to reach.
 */
function useFiveRowCap(
  rowCount: number,
  /**
   * 🔴 **THE VISIBLE COLUMN SET, AS A STRING — added 2026-09-12 with the column
   * manager, and the `ResizeObserver` below is NOT a substitute for it.**
   *
   * Hiding or reordering a column changes where every cell WRAPS, and the note
   * column is ten lines tall at its limit — so the first five rows can change
   * height while the TABLE's total height does not move at all (one row gets
   * shorter, a row further down gets taller). The observer watches the table, so
   * that case fires nothing: the cap stays at the old five-row height and cuts
   * the fifth row through its own text, which reads as a rendering fault rather
   * than as a column somebody hid.
   *
   * ⚠️ **A signature, not the array** — this is a dependency, and a fresh array
   * identity per render would re-attach the observer on every keystroke in a
   * funnel.
   */
  layoutSignature: string,
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  /*
   * `useLayoutEffect`, not `useEffect`: the rows arrive from a fetch, so the
   * table would paint at its full height for a frame and then snap to five —
   * a visible jump on every card at once. This is a browser-only SPA, so
   * there is no server render to warn about.
   */
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || rowCount <= VISIBLE_ROWS) {
      setMaxHeight(null);
      return;
    }

    const table = scroller.querySelector('table');
    if (!table) return;

    const measure = () => {
      const rows = table.tBodies[0]?.rows;
      if (!rows || rows.length < VISIBLE_ROWS) return;
      let total = table.tHead?.offsetHeight ?? 0;
      for (let i = 0; i < VISIBLE_ROWS; i += 1) total += rows[i]?.offsetHeight ?? 0;
      // Same number on a re-measure is a no-op render, so this settles rather
      // than oscillating.
      setMaxHeight(total);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(table);
    return () => observer.disconnect();
  }, [rowCount, layoutSignature]);

  return { scrollRef, maxHeight };
}

/**
 * The ordering control. ONE implementation, rendered by the one card component
 * that all five cards are — so there is no way for two cards to word, validate
 * or default the same choice differently.
 *
 * ⚠️ **The options come from `state.sortOptions`, never from a list written
 * here.** They are a property of each endpoint, and this file has now been on
 * both sides of that: it once said `/deals`, `/quotes` and `/invoices` declared
 * no `sort` enum "so theirs cannot" offer A–Z, which was true then and is not
 * now. All five offer A–Z and Z–A; only the three document cards offer "Newest
 * first". **Nothing in this component had to change for that** — which is the
 * whole argument for reading the options off the state: a list written here
 * would have been a second copy of a backend contract, and the day it
 * disagreed the card would render an option that quietly does nothing.
 *
 * ⚠️ **No VISIBLE label, deliberately** — five labelled controls on one dashboard
 * is five repetitions of the word "Sort", and a label here would be the only text
 * on the card that is not a record. The selected option names itself ("Recent
 * activity", "A–Z"), and the accessible name carries the card's own title so a
 * screen reader hears which of the five it is on. That is an accessible name, not
 * helper text.
 *
 * Never disabled — not while loading, not on a failed load. Re-ordering a card
 * that failed is a reasonable thing to try, and a control whose disabled state
 * cannot be explained is worse than one that occasionally repeats a request.
 *
 * ## ✅ A SEARCHABLE `Combobox` SINCE 2026-09-13 — owner's decision
 *
 * *SUPERSEDED: a `ui/Select` with an inline `<option>` list and an `aria-label`
 * on the element itself.* Every non-table-column filter dropdown in this app
 * becomes a searchable `Combobox`; the house shape is `DealsPage`'s filter bank,
 * which is where the `Field label hideLabel` wrapper comes from.
 *
 * ⚠️ **`Field … hideLabel` REPLACES the old `aria-label`, and it is not
 * decoration.** `ComboboxProps` declares no `aria-label`, so an attribute spread
 * at this call site would reach no element and the control would be an unnamed
 * button — the same defect a missing `<label>` gives any other input. The label
 * is `sr-only`, so nothing visible changed and the no-visible-label ruling above
 * is untouched.
 *
 * ⛔ **NO `emptyOptionLabel`, unlike the filter Comboboxes it copies.** Those
 * pickers have a real "no filter" state; an ordering does not — every card is
 * always sorted somehow — so an empty row would be a choice that means nothing,
 * and choosing it would leave `onChange('')` to be resolved back to the default
 * anyway.
 *
 * ## 🔴 THE TRAP: A `Combobox` FALLS BACK TO ITS PLACEHOLDER, WHERE A `<select>`
 * SHOWED THE VALUE
 *
 * A native `<select>` whose `value` matches no `<option>` still renders the raw
 * value; this control renders `placeholder` instead. So a `state.sort` absent
 * from `state.sortOptions` would read as **unsorted over a list that really is
 * ordered** — a wrong claim, silently, with nothing failing.
 *
 * ✅ **VERIFIED SAFE ON 2026-09-13, and it is STRUCTURAL rather than a
 * coincidence that happens to hold today.** The check is a chain of three, and
 * the third is the one that matters:
 *
 *  1. `state.sort` is `sorts[spec.key]`, and `sorts` is built by
 *     `resolvePartnershipSort` for all five cards — never from `searchParams`
 *     raw, so a hand-typed value cannot reach this control unresolved.
 *  2. `resolvePartnershipSort` returns `DEFAULT_SORT` (`activity`) for anything
 *     it does not recognise, rather than passing the raw string through.
 *  3. 🔴 **It validates against `PARTNERSHIP_SORT_OPTIONS[key]`, which is
 *     `Object.fromEntries(TABLES.map((spec) => [spec.key, spec.sortOptions]))` —
 *     literally the SAME ARRAY this control renders.** So the resolver and the
 *     option list cannot drift: a value that survives resolution is a member of
 *     the list by construction, and one that does not becomes `activity`, which
 *     every card carries.
 *
 * ⚠️ **What would break it is a change in `use-partnership-tables.ts`, not
 * here** — a second source for either half, or a resolver validating against the
 * `PartnershipSort` union instead of the card's own list. That last one is the
 * live hazard, because it is the obvious-looking simplification and it typechecks:
 * `?leadsSort=value-asc` would then resolve to `value-asc`, which `COMPANY_SORTS`
 * does not carry, and this trigger would read **blank over a card ordered by
 * `activity`**. ⚠️ **The placeholder is a statement of fact (`Order by`) rather
 * than an ordering**, so even then it would not name an order that is not in
 * force — but it would stop naming the one that is.
 */
function SortControl({
  state,
  onChange,
  stageNameOf,
}: {
  state: PartnershipTableState;
  onChange: (sort: PartnershipSort) => void;
  /** Names a stage for a per-stage status ordering — from the card's columns. */
  stageNameOf: (stageId: number) => string | null;
}) {
  /*
   * ⚠️ **Derived from `state.sortOptions`, exactly as the `<option>` list was.**
   * The orderings a card may offer are a property of the ENDPOINT; a list written
   * here would be a second copy of a backend contract, and the day it disagreed
   * the card would render an option that quietly does nothing.
   */
  const options: { value: string; label: string }[] = state.sortOptions.map((option) => ({
    value: option,
    label: PARTNERSHIP_SORT_LABEL[option],
  }));
  /*
   * 🔴 **A per-stage status ordering (owner 2026-10-25) is set from its COLUMN's
   * funnel and is not in `sortOptions`** — so it is added here as the one extra
   * option, or this `Combobox` would fall back to its placeholder over a card
   * that really is ordered (the trap the doc above records).
   */
  if (parseStageStatusSort(state.sort) !== null) {
    options.push({ value: state.sort, label: partnershipSortLabel(state.sort, stageNameOf) });
  }

  return (
    <div className="w-40 min-w-0">
      <Field label={`Order ${state.title} by`} hideLabel>
        {({ id }) => (
          <Combobox
            id={id}
            value={state.sort}
            onChange={(value) => onChange(value as PartnershipSort)}
            options={options}
            placeholder="Order by"
            searchPlaceholder="Search orderings…"
          />
        )}
      </Field>
    </div>
  );
}

/* --- the filters --------------------------------------------------------- */

/**
 * A master row as a filter needs it. All four masters this page filters by —
 * `/deal-stages`, `/quote-stages`, `/invoice-statuses` and `/countries` — carry
 * an id, a label and an active flag, which is the whole of what a picker reads.
 *
 * ⛔ **The LABEL is for reading and nothing else. Never match on it.** These are
 * admin-editable free text; `"Unpaid"` contains `"paid"` and `"Not Accepted"`
 * contains `"accept"`, and this codebase has been bitten three times by a client
 * deciding what a record means from a name an administrator can change. The
 * filter sends the ID.
 */
interface FilterOption {
  id: number;
  name: string;
  isActive: boolean;
}

/**
 * One master behind one filter, with the three states a picker must be able to
 * tell apart.
 *
 * ⚠️ **`failed` is not `rows.length === 0`.** An empty master and an unread one
 * produce the same empty dropdown, and they mean opposite things — one is "there
 * is nothing to filter by", the other is "we could not find out". The control
 * words them differently and disables only the second.
 */
interface FilterMaster {
  rows: readonly FilterOption[];
  loading: boolean;
  failed: boolean;
}

/**
 * Keeps a chosen value visible when the master does not carry it.
 *
 * ⚠️ **A shared or hand-typed URL can name an id this list does not hold** — a
 * row deleted, or a master that paged short of it. Without this the funnel shows
 * the column as unfiltered while the request IS narrowed, so the rows read as
 * the whole card. Rendering it as `#<id>` keeps the control and the request
 * describing the same thing, which is what the old `<select>` did with its
 * "(not in the list)" option.
 *
 * The same helper, for the same reason, sits on `CompaniesPage`. It is copied
 * rather than shared because the two pages' option shapes are built from
 * different masters and a shared one would need both; if a third caller appears,
 * that is the moment it earns a home in `use-table-filters.ts`.
 */
function withSelectedValues(
  options: ColumnFilterOption[],
  selected: readonly string[],
): ColumnFilterOption[] {
  const known = new Set(options.map((option) => option.value));
  const missing = selected.filter((value) => !known.has(value));
  return missing.length === 0
    ? options
    : [...options, ...missing.map((value) => ({ value, label: `#${value}` }))];
}

/**
 * A project funnel's EMPTINESS value — the ids joined, or `null` for nothing.
 *
 * 🔴 **SHARED BY BOTH PROJECT COLUMNS SINCE 2026-09-14** — `projectOfInterest`
 * on the two company cards and `lineProjects` on the three document ones. One
 * copy because the two funnels must answer Is empty identically: a column where
 * an empty list reads as empty beside one where it does not is two behaviours
 * under one control shape, and a reader moving between cards would not know
 * which they had.
 *
 * ⚠️ **`get` earns its place for the EMPTINESS TEST ALONE** — `matches` is what
 * decides a hit, because the cell is a SET. The joined string is never compared
 * against anything: two projects would produce `"4,9"`, which matches nothing
 * else on the card, and that is why both specs SUPPLY their options rather than
 * letting the hook accumulate distinct `get` values.
 *
 * ⚠️ **`null` covers BOTH an empty list and an unread one** — conflated only
 * here, where both honestly answer "empty", and never in either cell, which must
 * keep them apart. ⛔ **On the document cards Is empty is the MAJORITY**:
 * measured 2026-09-14, **613 of 1,088 documents name no project on any line
 * (56%)**. That is the data, not a broken funnel.
 */
function projectIdsValue(projects: readonly PartnershipRowProject[] | null): string | null {
  return projects === null || projects.length === 0
    ? null
    : projects.map((project) => project.id).join(',');
}

/**
 * Whether a row's projects include any the reader has ticked.
 *
 * ⛔ **`null` MATCHES NOTHING, and it is NOT `?? []`.** A row whose list could
 * not be read has no projects to test, and writing the coalesce here would put
 * the one character this contract forbids into a predicate where it happens to
 * be harmless — which is how it ends up copied to a render where it is not.
 * Rows with no list are reached through Is empty, which is what that control is
 * for.
 *
 * ⚠️ **ANY, not ALL** — ticking a second project can only ever ADD rows, which
 * is the only reading of a multi-select funnel over a set that does not make the
 * second tick behave like a narrowing the first did not.
 *
 * 🔴 **ONE COPY, SHARED BY BOTH PROJECT COLUMNS.** Two predicates one column key
 * apart, over two fields of the identical shape, is precisely where a stray
 * `?? []` or an `every` would land on one card and not the other — and neither
 * would fail, they would simply return a different set of rows under headings a
 * reader compares.
 */
function projectMatches(
  projects: readonly PartnershipRowProject[] | null,
  selected: readonly string[],
): boolean {
  if (projects === null) return false;
  return projects.some((project) => selected.includes(String(project.id)));
}

/**
 * A project funnel's vocabulary, built from the LOADED ROWS.
 *
 * 🔴 **IT SERVES TWO DIFFERENT COLUMNS SINCE 2026-09-14** — `projectOfInterest`
 * on the two `/companies` cards, and `lineProjects` on the other three. ⚠️
 * *SUPERSEDED, the heading: "The Projects-of-interest funnel's vocabulary".* The
 * `projectsOf` accessor is which, and it is required for the reason written at
 * the parameter. Everything below is true of both lists: they are the same
 * `{id, name}` shape, both page-scoped, and neither has a server filter to
 * escalate to.
 *
 * 🔴 **BUILT FROM THE PAGE ON PURPOSE, WHICH IS THE OPPOSITE OF THE RULE THE
 * COUNTRY FUNNEL FOLLOWS — and the two are not in conflict.** A funnel must
 * offer everything it can REACH. The country funnel queries the whole table, so
 * a list accumulated from one page under-offers against rows it could really
 * have found and nothing says so; it therefore reads the `/countries` master.
 * This funnel narrows the loaded rows and nothing else — no endpoint behind
 * these five cards takes a project filter — so the rows ARE its reach, and a
 * list built from them is exactly complete for what the control can do.
 *
 * ⛔ **SO DO NOT "FIX" THIS BY READING THE PROJECTS MASTER.** That would offer
 * every project in the catalogue over a filter that can only ever narrow a
 * hundred rows: most options would answer zero, and picking one would empty the
 * card — which reads as *this lead is pursuing nobody for that project* rather
 * than as *nobody in the rows I fetched is*. Two different facts the control
 * could not tell apart, and the exact defect the country funnel was rewritten
 * to remove, arriving from the other direction.
 *
 * ⚠️ **The COUNTS are per project, so they legitimately sum past the row
 * count** — a company pursued for three projects contributes one to each of
 * three options. Each count answers "how many loaded rows would survive ticking
 * this", which is the only question a count on a funnel is read as.
 * ⚠️ **They over-sum harder on the document cards**: measured 2026-09-14,
 * **~21% of quotes and ~12% of invoices name 3+ projects, up to 8**, against
 * company sets that max out at 3. That is arithmetic working, not a fault — but
 * it is why the counts must stay per-option rather than being normalised into
 * anything that looks like a share of the card.
 *
 * ⚠️ **The label goes through `companyProjectsLabel`, the module that OWNS this
 * wording** — a one-element list is exactly one project's name, and its blank-
 * name fallback (`#id`) comes with it. A local `name.trim() || '#id'` here would
 * be a second copy of a rule that lives in one place on purpose.
 *
 * ⚠️ **Alphabetical, unlike a declared closed vocabulary.** `ColumnSpec.options`
 * preserves the order it is given because an enum usually has a meaning to its
 * sequence; an accumulated list has none, so this sorts the way the hook's own
 * accumulation would have.
 */
function projectFilterOptions(
  rows: readonly PartnershipRow[],
  /*
   * 🔴 **WHICH FIELD, AS AN ACCESSOR, AND IT IS REQUIRED RATHER THAN
   * DEFAULTED — 2026-09-14.** The funnel must accumulate from the SAME list its
   * cell renders, or it offers a company's projects of interest under a heading
   * reading "Projects on this document" and narrows the card on a fact nothing
   * on it displays. **A default would make one of the two fields the silent
   * answer for any caller that forgot**, which is the failure this parameter
   * exists to remove; each arm passes the same accessor to all three of its
   * readers, so the field is spelled once per column.
   */
  projectsOf: (row: PartnershipRow) => readonly PartnershipRowProject[] | null,
): ColumnFilterOption[] {
  const seen = new Map<number, { name: string; count: number }>();
  for (const row of rows) {
    /*
     * ⛔ `null` is "no list to read" and contributes nothing — never `?? []`,
     * which would be indistinguishable from an account pursuing nothing, or from
     * a document whose lines name none. This loop needs neither fact: an unread
     * list offers no options either way, and the rows carrying it are reached
     * through Is empty.
     */
    const held = projectsOf(row);
    if (held === null) continue;
    for (const project of held) {
      const held = seen.get(project.id);
      if (held) {
        held.count += 1;
        continue;
      }
      seen.set(project.id, { name: project.name, count: 1 });
    }
  }
  return Array.from(seen, ([id, { name, count }]) => ({
    value: String(id),
    label: companyProjectsLabel([{ id, name }]),
    count,
  })).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

/**
 * The Open-task funnel's two options, with the split among the loaded rows.
 *
 * ⚠️ **BOTH ARE ALWAYS OFFERED, including at a count of zero** — this is a
 * closed two-valued vocabulary, not an accumulation, and the value a reader
 * opens the funnel to find is precisely the one no row on this page carries. A
 * `0` beside it is the honest answer and it is useful before the tick rather
 * than after: it says the card would empty.
 *
 * ⚠️ **The values are `UpcomingTaskCell`'s own words.** The funnel filters on
 * what the column prints; anything else would be a second vocabulary for one
 * fact.
 *
 * ⛔ **The counts are over the LOADED rows and are not a figure about the
 * account list.** `hasOpenTask` is account-level and this card may hold more
 * rows than it fetched, so neither number may be read, worded or re-used as
 * "how many accounts have an open task".
 */
function openTaskFilterOptions(rows: readonly PartnershipRow[]): ColumnFilterOption[] {
  let open = 0;
  for (const row of rows) if (row.hasOpenTask) open += 1;
  return [
    { value: 'Yes', label: 'Yes', count: open },
    { value: 'No', label: 'No', count: rows.length - open },
  ];
}

/**
 * The credit funnel's value for one row — `'held'`, `'none'`, or `null` when the
 * balance is not available to this reader (which is what *Is empty* matches).
 * ⛔ `null` is never folded into `'none'`: *we could not see it* is not *no
 * credit*.
 */
function creditFilterValue(row: PartnershipRow): 'held' | 'none' | null {
  const held = creditHeldOf(row.creditBalances);
  return held.kind === 'unknown' ? null : held.kind;
}

/**
 * The Companies card's credit funnel options, DECLARED so `No credit` is offered
 * even when every loaded row holds some — the closed-enum case, like
 * {@link openTaskFilterOptions}. Counts are over the loaded rows only.
 */
function creditFilterOptions(rows: readonly PartnershipRow[]): ColumnFilterOption[] {
  let held = 0;
  let none = 0;
  for (const row of rows) {
    const value = creditFilterValue(row);
    if (value === 'held') held += 1;
    else if (value === 'none') none += 1;
  }
  return [
    { value: 'held', label: 'Holds credit', count: held },
    { value: 'none', label: 'No credit', count: none },
  ];
}

const NO_MASTER: FilterMaster = { rows: [], loading: false, failed: false };

/**
 * The selection a heading with no server filter behind it reports.
 *
 * ⚠️ **A shared frozen instance, like `NO_PARTNERSHIP_FILTER` in the hook** — it
 * is read on every render of every non-server heading, and freezing is what makes
 * a `push` onto it a runtime error in development rather than every such column
 * quietly sharing one mutated array.
 */
const EMPTY_FILTER_IDS: readonly number[] = Object.freeze([]);

/**
 * Which filter kind a column HEADING names — `undefined` for a heading that
 * names none.
 *
 * 🔴 **THIS REPLACED THREE PARALLEL TERNARY CHAINS IN `headerFor`, EACH OF WHICH
 * FELL THROUGH TO `status` — 2026-09-14.** Those chains carried a comment saying
 * *"do not reorder these into a chain that lets a fourth kind fall into
 * `status`"*, and the fourth kind then arrived and did exactly that with a green
 * build: a company-type selection would have been sent as the card's STAGE
 * filter, drawing ticks over a master it did not come from. **A comment is not a
 * type.**
 *
 * ⚠️ **Keyed by the COLUMN key, which is not the same set as the filter kinds** —
 * `record`, `note` and the rest name no kind, so this is deliberately partial over
 * an open string domain while its VALUES are closed over
 * {@link PartnershipFilterKind}. A fifth kind is a `tsc` error at the annotation;
 * a column that names none is simply absent.
 *
 * ⚠️ **Naming a kind here is not offering it.** The CARD decides that, through
 * `filterKinds` — see `offersCompanyType` and its neighbours, which is the second
 * half of the test and the one that keeps `scope: 'server'` off an endpoint with
 * no such key.
 */
const COLUMN_FILTER_KIND: Readonly<Record<string, PartnershipFilterKind>> = {
  country: 'country',
  source: 'source',
  companyType: 'companyType',
  status: 'status',
  /*
   * 2026-10-09: the two purchase-history columns filter by CYCLE on the server
   * (`?purchasedCycleId=` / `?participatedCycleId=`), so the funnel offers
   * cycles over a column whose cells name products and projects.
   */
  productsPurchased: 'purchasedCycle',
  projectsParticipated: 'participatedCycle',
};

/**
 * The `lastNoteAt` column's date range — `/companies` `?lastNoteAfter=` /
 * `?lastNoteBefore=`, both inclusive `YYYY-MM-DD` (2026-10-02).
 *
 * ⚠️ **Not a {@link PartnershipFilterKind}** — a pair of dates rather than an
 * id list, with no master behind it — so it is absent from
 * {@link COLUMN_FILTER_KIND} and `headerFor` wires it through its own arm.
 */
const LAST_NOTE_RANGE = {
  date: true,
  minParam: 'lastNoteAfter',
  maxParam: 'lastNoteBefore',
} as const;

/**
 * The URL's two bounds as the funnel's typed range — used only when the hook
 * holds no range of its own (a reload, a shared link, Back), so a half-typed
 * bound in an open popover is never overwritten from the URL.
 */
function lastNoteRangeFromUrl(after: string | null, before: string | null) {
  if (after !== null && before !== null) {
    return after === before
      ? { operator: 'equals' as const, min: after, max: '' }
      : { operator: 'between' as const, min: after, max: before };
  }
  if (after !== null) return { operator: 'atLeast' as const, min: after, max: '' };
  if (before !== null) return { operator: 'atMost' as const, min: '', max: before };
  return undefined;
}

/**
 * The URL query keys carrying one card's last-note range. Card-prefixed like
 * every other filter key here (`?leadsLastNoteAfter=`), so two cards can never
 * share one.
 */
function lastNoteParamOf(key: PartnershipTableKey, bound: 'after' | 'before'): string {
  return `${key}LastNote${bound === 'after' ? 'After' : 'Before'}`;
}

/**
 * The `/countries` master, for the Country filter on the two `/companies` cards.
 *
 * ✅ **NARROWED TO THE COUNTRIES COMPANIES ARE ACTUALLY IN — 2026-09-12, owner's
 * instruction.** `hasCompanies: true` is a real server-side filter
 * (`d_companies.country_id`), shipped in the same day's backend change.
 *
 * Measured before it was built: **415 companies hold 30 distinct countries**
 * against a master of **196**. So the funnel used to offer 166 options that
 * could only ever answer zero rows — and picking one gave an empty card, which
 * reads as *"this lead has no companies in France"* rather than as *"no company
 * anywhere is in France"*. Those are different facts and the control could not
 * tell them apart.
 *
 * ⛔ **THIS IS A FILTER, WHICH IS THE ONLY REASON IT MAY SEND THE KEY.** A
 * filter offers what can MATCH; a picker offers what can be CHOSEN. The country
 * pickers on `CompanyFormModal`, `MasterFormModal` and `CatalogPage` must keep
 * reading the whole master — narrowing one of those makes the 167th country
 * unreachable, so nobody could ever be the first company in a country.
 *
 * ⚠️ **The narrowing is GLOBAL, not scoped to this card's account lead**, and
 * that is the owner's word — *"companies in companies master"*. So a country
 * can be offered here whose companies all belong to somebody else's accounts,
 * and picking it empties the card. The alternative is worse: scoping per card
 * would rebuild the vocabulary out of the rows on screen, which is the
 * accumulate-from-the-page failure this page was rewritten to remove, and it
 * would make the Leads and Companies funnels offer different countries.
 *
 * ⚠️ **`perPage: 250` STAYS** even though 30 rows come back today. It is the
 * endpoint's ceiling, the result is data rather than a constant, and a master
 * that outgrows 50 pages silently — omitting every country past the first fifty
 * with no error at all.
 *
 * ⚠️ **No `isActive` filter, deliberately.** Reads never filter on it, so a
 * company can hold a country that has since been retired — narrowing the master
 * would drop that country from the dropdown while rows carrying it sit in the
 * table, and there would be no way to filter to them. Retired rows are LABELLED
 * at render instead, the same rule `selectablePickerOptions` follows.
 * ⚠️ **`hasCompanies` does NOT subsume this**: a retired country a company is
 * still recorded in survives the new filter and must keep surviving.
 */
function useCountryMaster(enabled: boolean): FilterMaster {
  const [state, setState] = useState<FilterMaster>({ rows: [], loading: enabled, failed: false });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listCountries({ page: 1, perPage: 250, hasCompanies: true }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setState({
          /*
           * ⚠️ `countryName` — the MASTER's spelling. The same field on a
           * company's embedded `country` is `name`, and reaching for the wrong
           * one compiles and prints `undefined`. `src/lib/types.ts` records that
           * this exact confusion shipped once.
           */
          rows: result.data.map((row) => ({
            id: row.id,
            name: row.countryName,
            isActive: row.isActive,
          })),
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (signal.aborted) return;
        setState({ rows: [], loading: false, failed: true });
      });
    return () => controller.abort();
  }, [enabled]);

  return state;
}

/**
 * The `/countries` master as a PICKER needs it — the whole thing.
 *
 * 🔴 **A SECOND REQUEST BESIDE {@link useCountryMaster}, AND THE TWO MUST NEVER
 * BE MERGED.** The funnel above sends `hasCompanies: true`, which is right for a
 * FILTER — it offers what can MATCH, and 166 of the 196 rows could only ever
 * answer zero. A PICKER offers what can be CHOSEN, and narrowing it the same way
 * would make the 167th country unreachable: nobody could ever be the first
 * company in a country. `CLAUDE.md` states that ruling in terms, and
 * `useCountryMaster`'s own header repeats it — this hook is what honours it.
 *
 * ⚠️ **Gated on the WRITE, which is the one case where that is correct.** This
 * repo has the mirror-image defect on record: the staff directory was fetched
 * only for users who could edit, so a reader with no permission got an empty
 * owner picker — indistinguishable from a directory that failed to load. The
 * difference is that this list feeds NO DISPLAY. The country a row holds is on
 * the row (`row.country`); this master is consulted only to populate an editor a
 * reader without the permission never opens. A reader who may not write spends
 * nothing, and sees exactly the same cell either way.
 *
 * ⚠️ **No `isActive` filter**, for the reason every picker here shares: reads
 * never filter on it, so a company can hold a country that has since been
 * retired, and dropping it would leave the record unable to show what it holds.
 * `editorOptions` labels it instead.
 *
 * ⚠️ **`perPage: 250` is the endpoint's ceiling** — 196 rows come back today, and
 * a master that outgrows 50 pages silently, omitting every country past the
 * first fifty with no error at all.
 */
function useCountryPickerMaster(enabled: boolean): FilterMaster {
  const [state, setState] = useState<FilterMaster>({ rows: [], loading: enabled, failed: false });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listCountries({ page: 1, perPage: 250 }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setState({
          // ⚠️ `countryName` — the MASTER's spelling. The same field on a
          // company's embedded `country` is `name`; reaching for the wrong one
          // compiles and prints `undefined`.
          rows: result.data.map((row) => ({
            id: row.id,
            name: row.countryName,
            isActive: row.isActive,
          })),
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (signal.aborted) return;
        setState({ rows: [], loading: false, failed: true });
      });
    return () => controller.abort();
  }, [enabled]);

  return state;
}

/**
 * The `/deal-stages` master, for the Status filter on the Deals card.
 *
 * ⚠️ **A THIRD master, not the quote-stage or invoice-status list.** It carries
 * `isWon`/`isLost` where those carry `isAccepted`/`isSettled`, and an id from
 * one resolves against the other perfectly well — which is exactly why the
 * page's own `StatusMasters` keeps them apart with a tag.
 *
 * ⚠️ **No `active` filter, and `perPage: 100`** — the deal-picker rule this repo
 * already states: `?active=true` gives a clean create dropdown and drops the
 * value a record already holds once that option is retired, and the default of
 * 20 silently truncates.
 */
function useDealStageMaster(enabled: boolean): FilterMaster {
  const [state, setState] = useState<FilterMaster>({ rows: [], loading: enabled, failed: false });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listDealStages({ page: 1, perPage: 100 }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setState({
          rows: result.data.map((row) => ({ id: row.id, name: row.name, isActive: row.isActive })),
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (signal.aborted) return;
        setState({ rows: [], loading: false, failed: true });
      });
    return () => controller.abort();
  }, [enabled]);

  return state;
}

/**
 * The `/cycles` master, for the Companies card's two purchase-history cycle
 * filters (2026-10-09).
 *
 * ⚠️ **Ordered by `startDate` on the server, newest first (matching /companies),
 * never by `id`** — the ids are not
 * in date order (`2022 - 23` holds the highest id), so an id ordering would read
 * as a shuffled calendar. `perPage: 100` is the entity ceiling; there are eight
 * cycles today. A cycle carries no `isActive`, so every row is offered as live.
 */
function useCycleMaster(enabled: boolean): FilterMaster {
  const [state, setState] = useState<FilterMaster>({ rows: [], loading: enabled, failed: false });

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const { signal } = controller;
    api
      .listCycles({ page: 1, perPage: 100, sort: 'startDate', order: 'desc' }, signal)
      .then((result) => {
        if (signal.aborted) return;
        setState({
          rows: result.data.map((row) => ({ id: row.id, name: row.cycleName, isActive: true })),
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (signal.aborted) return;
        setState({ rows: [], loading: false, failed: true });
      });
    return () => controller.abort();
  }, [enabled]);

  return state;
}

/** Every master the five filters read, resolved once for the whole page. */
interface FilterMasters {
  countries: FilterMaster;
  /**
   * `/company-sources`, for the Source filter on the two `/companies` cards.
   *
   * ⚠️ **Built from the SAME read that feeds the picker** — see the import note
   * on `use-company-sources`. The country pair had to be two requests because a
   * FILTER offers what can MATCH (`hasCompanies: true`) while a PICKER offers
   * what can be CHOSEN; `/company-sources` publishes no such narrowing, so one
   * list is correct for both and a second hook would spend a second request on
   * the same five rows.
   *
   * ⚠️ **NO `hasCompanies`-style narrowing exists here and one must not be
   * faked client-side.** Offering only the sources the loaded rows carry would
   * be the accumulate-from-the-page defect this page was rewritten to remove;
   * offering all of them means a source nobody on this reader's accounts has is
   * offered and empties the card, which is the same accepted cost `country`
   * carries and the funnel's option counts are what state it.
   */
  companySources: FilterMaster;
  /**
   * `/company-types`, for the Company type filter on the two `/companies` cards.
   *
   * ⚠️ **Built from the SAME read that feeds the picker**, exactly as
   * `companySources` above is and for the identical reason: the country pair had
   * to be two requests because a FILTER offers what can MATCH (`hasCompanies:
   * true`) while a PICKER offers what can be CHOSEN; `/company-types` publishes no
   * such narrowing, so one list is correct for both.
   *
   * ⚠️ **NO `hasCompanies`-style narrowing exists here and one must not be faked
   * client-side.** Offering only the types the loaded rows carry would be the
   * accumulate-from-the-page defect this page was rewritten to remove.
   */
  companyTypes: FilterMaster;
  /**
   * `/cycles`, for the two purchase-history cycle filters on the Companies card
   * (2026-10-09). The whole master, ordered by START DATE — never by `id`, whose
   * order is production's counter and not the calendar — and never accumulated
   * from the loaded rows.
   */
  cycles: FilterMaster;
  dealStages: FilterMaster;
  quoteStages: FilterMaster;
  invoiceStatuses: FilterMaster;
}

/**
 * Which master a given card's filter reads.
 *
 * 🔴 **BY THE CARD's KEY, because "status" is THREE DIFFERENT MASTERS.** A deal
 * stage, a quote stage and an invoice status are three tables with three flag
 * sets, and an id from any of them is a plain integer that resolves against the
 * other two without complaint. Picking the master by the card is what stops a
 * dropdown offering quote stages to a filter that sends `stageId` to `/deals`.
 */
/**
 * The kinds answered by ONE master whichever card asks — `null` marks the one
 * that is not.
 *
 * 🔴 **EXHAUSTIVE, AND THAT IS THE POINT — 2026-09-14.** *SUPERSEDED: an
 * `if/else` chain ending in a bare `return NO_MASTER`.* That shape made a FOURTH
 * kind fall through to an EMPTY master, which opens a funnel with **zero
 * options** — indistinguishable from "no company types exist", which is exactly
 * the reading a reader would take on a sparse master. Nothing errored, `tsc` was
 * green, and the control looked finished.
 *
 * ⚠️ **`null` is a DECLARED "per-card", never an omission.** `status` is THREE
 * masters — a deal stage, a quote stage and an invoice status are three tables
 * whose ids are all plain integers and resolve against one another without
 * complaint — so it cannot be answered here and says so, rather than being left
 * out and reaching a fallback.
 */
const KIND_MASTER: {
  readonly [K in PartnershipFilterKind]: keyof FilterMasters | null;
} = {
  country: 'countries',
  source: 'companySources',
  companyType: 'companyTypes',
  status: null,
  participatedCycle: 'cycles',
  purchasedCycle: 'cycles',
  /*
   * ⚠️ **`null`, and not a per-card status master either**: the per-stage status
   * funnels are answered PER COLUMN — each lists only its own stage's statuses
   * — in `filterHeaderFor`'s stage arm, which never reaches `masterFor`. No
   * column key maps to this kind in `COLUMN_FILTER_KIND`.
   */
  stageStatus: null,
};

/**
 * Which `status` master a card reads — `null` for the two that have none.
 *
 * ⚠️ **EXHAUSTIVE over the CARDS**, so a sixth card is a `tsc` error here rather
 * than a silently empty funnel. The two `/companies` cards are `null` because
 * they declare no `status` kind at all — see `PartnershipFilterKind`, where the
 * reason is the lead/non-lead partition owning `stageId` — and that `null` is a
 * statement rather than a gap.
 */
const STATUS_MASTER: { readonly [K in PartnershipTableKey]: keyof FilterMasters | null } = {
  leads: null,
  companies: null,
  deals: 'dealStages',
  quotes: 'quoteStages',
  invoices: 'invoiceStatuses',
};

function masterFor(
  key: PartnershipTableKey,
  kind: PartnershipFilterKind,
  masters: FilterMasters,
): FilterMaster {
  const shared = KIND_MASTER[kind];
  if (shared !== null) return masters[shared];
  const perCard = STATUS_MASTER[key];
  /*
   * Reachable only for `status` on the two `/companies` cards, which declare no
   * such kind. Returning an empty master rather than throwing means a spec that
   * gained the wrong kind renders an inert control instead of taking the page
   * down — the half of the old chain's behaviour worth keeping.
   */
  return perCard === null ? NO_MASTER : masters[perCard];
}

/*
 * `FILTER_ALL_LABEL` ("All countries" / "All statuses") and `FILTER_KIND_LABEL`
 * went with `FilterControl` on 2026-09-12. A funnel has no "everything" OPTION
 * to word — no ticks IS everything — and it names itself from the column
 * heading, so both maps had nothing left to say.
 *
 * ⚠️ **The funnel's accessible name is `Filter <column>`, so five cards now
 * carry five buttons reading "Filter Record".** That ambiguity is not new and is
 * not made worse: the old `SortHeader` rendered a button labelled bare
 * "Record" on all five. Each table carries a `<caption>` naming its card, which
 * is what a screen reader announces around it. If it ever needs closing, close
 * it in `FilterableTH` for every list page at once rather than by re-introducing
 * a per-card label map here.
 */

/*
 * 🔴 **`FilterControl` WAS HERE AND IS DELETED — owner's instruction,
 * 2026-09-12: the filter moved into the column heading as a funnel.**
 *
 * Three of its rulings did NOT go with it, and each is now carried by the
 * `headerFor` wrapper in `PartnershipTablePanel` rather than being lost:
 *
 *  - **A retired option is LABELLED, never hidden.** Reads do not filter on
 *    `isActive`, so records sit on retired rows, and dropping one from the list
 *    would leave them unreachable by any filter value with nothing saying so.
 *  - **A value the master does not carry still renders, as itself.** A shared
 *    URL can name an id the list does not hold; `withSelectedValues` draws it as
 *    `#<id>` so the control and the request keep describing the same thing. The
 *    old `<select>` had the sharper version of this bug — an unmatched value
 *    silently showed the FIRST option, so the control read "All countries" over
 *    a narrowed request.
 *  - **Disabled ONLY when the master could not be READ**, and never when a value
 *    is already chosen, or a reader who can see the card is narrowed has no way
 *    to clear it. An unread master, an empty one and a permission denial must
 *    not look identical.
 *
 * ⚠️ **What it did that the funnel does NOT: render a bare "Clear the country
 * filter" link when the master failed WITH a filter set.** The funnel covers
 * that case by staying enabled with the chosen ids shown as `#<id>`, so the way
 * out is still one click — but if a future change disables a funnel whose column
 * is filtered, this is the dead end it recreates.
 */

/* --- creating a record from a card --------------------------------------- */

/** The permission module and button wording behind each card's **+**. */
const CREATE_SPEC: Record<
  PartnershipCreate,
  { module: ModuleSlug; label: string }
> = {
  /*
   * ⚠️ **"New company" on BOTH `/companies` cards, including Leads — the module's
   * own word, not the card's.** The form lets the person choose the stage, so a
   * record created from the Leads card is not necessarily a lead; calling the
   * button "New lead" would promise a stage this page does not set. See
   * `PartnershipCreate` for why seeding one was refused.
   */
  company: { module: 'companies', label: 'New company' },
  deal: { module: 'deals', label: 'New deal' },
  quote: { module: 'quotes', label: 'New quote' },
  invoice: { module: 'invoices', label: 'New invoice' },
};

function PlusIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg {...iconProps('h-4 w-4')}>
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M4 20h16" />
    </svg>
  );
}

/*
 * The company cards' columns the `/companies/export` endpoint knows, keyed by
 * the CARD's column id (2026-10-18). `record` is the company name and `value`
 * is the company stage on both company cards. A visible column absent from this
 * map is dropped from the request: an id the export does not know is a 422.
 * `upcomingTask` is the NEXT open task, not the export's `lastTask`, so it is
 * dropped rather than mapped.
 */
const EXPORT_COLUMN_OF: Partial<Record<string, string>> = {
  [RECORD_KEY]: 'name',
  [VALUE_KEY]: 'stage',
  country: 'country',
  source: 'source',
  companyType: 'companyType',
  projectOfInterest: 'projectOfInterest',
  productsPurchased: 'productsPurchased',
  projectsParticipated: 'projectsParticipated',
  creditBalance: 'creditBalance',
  lastNote: 'lastNote',
  lastNoteAt: 'lastNoteAt',
};

function exportColumnsOf(visibleKeys: readonly string[]): string[] {
  const columns: string[] = [];
  for (const key of visibleKeys) {
    // A per-stage status column's key IS its export id (`stageStatus:<id>`).
    const column = EXPORT_COLUMN_OF[key] ?? stageStatusExportColumn(key) ?? undefined;
    if (column !== undefined && !columns.includes(column)) columns.push(column);
  }
  return columns;
}

/* --- the per-ROW create menu, which is a different thing from the card's --- */

/**
 * The row-level "+" on a card, already filtered per module by the page.
 *
 * ⚠️ **This is NOT the card's own "+" and the two answer different questions.**
 * `CREATE_SPEC` above is *"a new record of the kind this card lists"*, and it
 * opens a modal on this page. This one is *"a record raised AGAINST the row you
 * are looking at"* — a quote for that company, an invoice from that deal — and
 * it is the same menu the module list carries, mounted from the one copy in
 * `@/components/records/RecordCreateMenu`.
 *
 * ⚠️ **THREE cards have one now, and INVOICES still does not.** *SUPERSEDED
 * 2026-09-04, owner's instruction: "Only two cards have one … Quotes and
 * Invoices carry none: `/quotes` and `/invoices` have no row create menu to
 * mirror … **Do not invent one here** — a dashboard is the worst place to
 * originate a convention two module pages do not have."* The Quotes half of that
 * is spent: the owner asked for convert on a quote row's "+".
 *
 * ⚠️ **The originate-no-convention objection is NOT spent, and it is why the
 * Quotes arm carries no create targets.** `/quotes` already offers convert on
 * every row it renders — table cell, stacked card and board card, each a
 * dedicated icon button opening `InvoiceFormModal` in place — so this menu
 * mirrors a capability the module list has, in the affordance this dashboard
 * has. It does not invent one. **The INVOICES card stays absent**: what a "+" on
 * an invoice row should raise is still undecided, and `/invoices` still has
 * nothing to mirror.
 *
 * ✅ **LEADS CARRIES IT NOW — owner's instruction, 2026-09-13.** *SUPERSEDED:
 * "⚠️ **Leads is deliberately absent too, and this one IS open.** A Leads row is
 * a company exactly as a Companies row is, so the menu would work; the owner
 * asked for Companies and Deals and this does what was asked. Adding it is one
 * `case` below."* It is gated on the **same `companyRowTargets`** as the
 * Companies card and hands the same `{ id: row.id, name: row.primary }`,
 * because a Leads row **is** a company — which this file already depends on
 * twice over: `companyIdOf` answers `row.id` on both `/companies` cards rather
 * than reading `row.companyId`, and `EDIT_MODULE` declares `leads: 'companies'`.
 * The two cards are a PARTITION of one table (`nonLeadStageFilter` derives one
 * from the other), so a menu that is correct on one is correct on the other, and
 * gating them separately would be a second answer to that split.
 */

/**
 * The menu's MODULE to the flow's KIND.
 *
 * ⛔ **Two vocabularies for one thing, one letter apart, and NEITHER SIDE FAILS
 * IF THEY ARE CONFUSED.** `CompanyCreateTarget['module']` is a `ModuleSlug` —
 * plural, `'quotes'` — because it gates on `ability.canCreate`;
 * `CompanyProjectLinesKind` is a document kind — singular, `'quote'` — because
 * it names what is being raised. Written as an exhaustive `Record` rather than a
 * cast or a trailing-`s` trim, so a fourth company create target cannot arrive
 * without declaring what it seeds.
 */
const PROJECT_LINES_KIND: Record<CompanyCreateTarget['module'], CompanyProjectLinesKind> = {
  deals: 'deal',
  quotes: 'quote',
  invoices: 'invoice',
};

type RowCreate =
  | {
      kind: 'company';
      targets: readonly CompanyCreateTarget[];
      /**
       * Handle a target on THIS page instead of navigating — the projects-of-
       * interest line flow. Asked per module, and `true` means *taken*: every
       * item it answers `false` for navigates exactly as it always has. Omitted
       * entirely, all three navigate.
       *
       * ⚠️ **It hangs off the UNION rather than being a prop on
       * `PartnershipTablePanel`.** `rowCreate` is already threaded page → panel
       * → menu, so the callback travels with the arm it belongs to and cannot
       * reach a card whose menu has no use for it. A parallel prop would be a
       * second thing to keep in step with this arm, and the way that drift
       * shows up is SILENCE: a handler that is passed and never fires.
       */
      onSelect?: (row: PartnershipRow, module: CompanyCreateTarget['module']) => boolean;
    }
  | { kind: 'deal'; targets: readonly DealCreateTarget[] }
  /*
   * ⚠️ **No `targets`, and that is the shape rather than an omission.** The two
   * arms above carry a per-MODULE list because each of their items is gated on
   * `ability.canCreate(module)`. Convert is gated on the ROLE —
   * `ability.canConvertQuote()` — which is one question for the whole menu, so
   * `rowCreateFor` answers it once and this arm exists or it does not. Giving it
   * a `targets` list would put a `ModuleSlug` back in reach of a `canCreate`
   * filter that is the wrong question; see `ConvertQuoteMenu`.
   */
  | { kind: 'quote' };

/**
 * ⚠️ **`row.primary` is the record's NAME on every arm** — the company name on a
 * company row, the deal name on a deal row, and on a quote row the subject with
 * its Xero quote number appended when it has one (see `PartnershipRow.primary`).
 * It is passed as a name, never as a record: no menu reads anything else, which
 * is what lets a dashboard row stand in for a `Company`, a `Deal` or a `Quote`.
 */
function RowCreateMenu({ rowCreate, row }: { rowCreate: RowCreate; row: PartnershipRow }) {
  if (rowCreate.kind === 'company') {
    /*
     * Lifted out so the arrow below closes over a value `tsc` has already
     * narrowed, rather than re-reading an optional member inside a callback.
     */
    const handleSelect = rowCreate.onSelect;
    return (
      <CreateForCompanyMenu
        company={{ id: row.id, name: row.primary }}
        targets={rowCreate.targets}
        /*
         * 🔴 **A DIRECT PROP WITH AN `undefined` BRANCH, NEVER A CONDITIONAL
         * SPREAD.** `{...(x ? { onSelect } : {})}` type-checks even against a
         * component that declares no such prop, so a rename or a removal on the
         * far side drops it in silence — green build, green lint, and a control
         * that simply never fires, which is an affordance nobody would think to
         * go looking for. Spelled out, `tsc` names the prop. The rule is stated
         * at `SalesPursuitCard.tsx`, which mounts this same menu.
         *
         * ⚠️ `CreateForCompanyMenu` declares `onSelect` OPTIONAL, so passing
         * `undefined` is its documented "every item navigates" case rather than
         * an absence to be worked around.
         */
        onSelect={handleSelect === undefined ? undefined : (module) => handleSelect(row, module)}
      />
    );
  }
  if (rowCreate.kind === 'quote') {
    return <ConvertQuoteMenu quote={{ id: row.id, name: row.primary }} />;
  }
  return (
    <CreateFromDealMenu deal={{ id: row.id, dealName: row.primary }} targets={rowCreate.targets} />
  );
}

/**
 * The row's quote-PDF control (owner 2026-10-25): on the Quotes card, the
 * preview of THAT quote at every stage; on the Deals card, the picker over the
 * deal's quotes. Both open the page's ONE viewer (`useQuotePdfPreview`).
 *
 * ⚠️ **Nullable per card and already resolved against `ability` by the page**
 * — the same shape as `rowCreate` and `onOpenContacts`, because it is what
 * `actionButtons` counts. A card handed `null` renders no button and reserves
 * no width for one.
 *
 * `quoteNumber` is the row's `documentNumber` (the EQ number, quote rows
 * only); `null` falls back to "Quote" in the viewer's title.
 */
interface PdfPreviewRow {
  kind: 'quote' | 'deal';
  onPreview: (target: QuotePdfPreviewTarget) => void;
}

function RowPdfPreview({ pdfPreview, row }: { pdfPreview: PdfPreviewRow; row: PartnershipRow }) {
  if (pdfPreview.kind === 'quote') {
    return (
      <PreviewQuotePdfButton
        variant="row"
        target={{
          id: row.id,
          quoteNumber: row.documentNumber ?? null,
          subject: recordLabelOf(row),
          companyName: row.secondary,
        }}
        onPreview={pdfPreview.onPreview}
      />
    );
  }
  return (
    <DealQuotesPdfMenu
      variant="row"
      dealId={row.id}
      dealName={recordLabelOf(row)}
      onPreview={pdfPreview.onPreview}
    />
  );
}

/**
 * The row's contacts button — a people icon with the company's contact count,
 * tooltip "Contacts" — opening the page's {@link CompanyContactsManagerModal}
 * on the company this row belongs to. Owner's decision 2026-10-16, replacing
 * the 2026-09-17 mail button.
 *
 * ⛔ **UNGATED, AND THAT IS DELIBERATE RATHER THAN AN OVERSIGHT.** Reading a
 * company's contacts is open to every authenticated role, so this is offered to
 * everyone the ROW is offered to — the same standing the tasks-and-notes button
 * beside it has. The dialog gates its own WRITES on `ability`. ⚠️ **Do not reach
 * for the card's `edit` gates to narrow it**: `companyEditGate` answers
 * `companies.update`, and gating a READ on a WRITE is a defect this codebase
 * already records.
 *
 * ⚠️ **The count is `row.contactCount`, and a `null` shows NO figure, never
 * "0"** — `null` is a server that did not send the field. The button keeps its
 * `w-14` either way so the rows stay aligned; see `CONTACTS_BUTTON_EXTRA_REM`.
 *
 * ⚠️ **`companyIdOf`, never `row.id`.** It is `row.id` on the two cards this is
 * mounted on today, so the two expressions agree — but they agree by virtue of
 * what those cards are, not by rule, and the function is the one place that
 * distinction is written down. Spelling `row.id` here would be a second copy of
 * a rule that has a first copy forty lines up.
 *
 * ⚠️ **A `null` company WITHHOLDS the button rather than disabling it.** Not
 * reachable from Leads or Companies — a row there IS a company — so this is what
 * keeps the control honest if it is ever handed to a card where `row.companyId`
 * can be absent. A disabled button would need a sentence saying why, and this
 * page routes a why-disabled sentence to the cell's `title`, which nothing hovers
 * on a control that looks inert. **An affordance whose only outcome is nothing is
 * worse than no affordance.**
 */
function RowContactsButton({
  row,
  tableKey,
  onOpen,
}: {
  row: PartnershipRow;
  tableKey: PartnershipTableKey;
  onOpen: (company: ContactsCompany) => void;
}) {
  const companyId = companyIdOf(row, tableKey);
  if (companyId === null) return null;

  /*
   * ⚠️ **`recordLabelOf`, the same helper the record cell and the activity
   * sheet name a row with** — so the dialog's heading reads exactly what the
   * reader clicked beside, and its own doc is explicit that a second copy is
   * what drifts.
   *
   * ⚠️ **NOT `row.primary`, and the difference is live rather than
   * hypothetical**: that helper trims and falls back to `Record #<id>`, so a
   * company whose name is blank or whitespace gives the field an empty string.
   * Passing it straight through would open a dialog headed by nothing, on the
   * one row where the reader most needs to be told which company they are
   * about to write to.
   */
  const name = recordLabelOf(row);
  const count = row.contactCount;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="w-14"
      leadingIcon={<UsersIcon />}
      tooltip="Contacts"
      /*
       * ⚠️ **NAMED with the row's company, because this cell holds three ghost
       * buttons and a screen reader meets them on every row.** "Contacts" alone
       * would announce indistinguishable controls; the visible tooltip stays the
       * owner's one word.
       */
      aria-label={count === null ? `Contacts at ${name}` : `Contacts at ${name} (${count})`}
      onClick={() => onOpen({ id: companyId, name })}
    >
      {count !== null && <span className="tabular-nums">{count}</span>}
    </Button>
  );
}

/**
 * The half of {@link PartnershipEditing} the PAGE resolves, per card.
 *
 * ⚠️ **The two members it omits are panel-LOCAL and cannot come from the page**:
 * `onWriteNotice` writes a banner that has to live inside the card the row is on,
 * and `onSettled` re-measures that card's five-row cap. A page-level version of
 * either would put one card's message over another's rows.
 */
type PartnershipEditAccess = Omit<PartnershipEditing, 'onSettled' | 'onWriteNotice'>;

function PartnershipTablePanel({
  state,
  masters,
  filterMasters,
  signatures,
  canCreate,
  rowCreate,
  edit,
  onSortChange,
  onFilterChange,
  onLastNoteRangeChange,
  onCreate,
  onOpenActivity,
  onOpenContacts,
  onOpenDocumentProjects,
  pdfPreview,
  exportScope,
  scopedToOthers,
}: {
  state: PartnershipTableState;
  masters: StatusMasters;
  filterMasters: FilterMasters;
  signatures: SignatureState;
  /** Already resolved against `ability` by the page — see `CREATE_SPEC`. */
  canCreate: boolean;
  /**
   * The two write gates, the pickers' masters and the page-level callbacks —
   * already resolved against `ability` for THIS card, exactly as `canCreate` is.
   */
  edit: PartnershipEditAccess;
  /**
   * The per-ROW create menu, or `null` where this card offers none — see
   * `RowCreate`. Already filtered per module by the page, and already `null`
   * when that filter left nothing, so this is the whole test.
   */
  rowCreate: RowCreate | null;
  onSortChange: (key: PartnershipTableKey, sort: PartnershipSort) => void;
  onFilterChange: (
    key: PartnershipTableKey,
    kind: PartnershipFilterKind,
    ids: readonly number[],
  ) => void;
  /**
   * Writes the two company cards' last-note date range into the URL — a pair
   * of `YYYY-MM-DD` bounds, `null` for "no bound". Separate from
   * `onFilterChange` because a date range is not an id list.
   */
  onLastNoteRangeChange: (
    key: PartnershipTableKey,
    after: string | null,
    before: string | null,
  ) => void;
  onCreate: (state: PartnershipTableState) => void;
  onOpenActivity: (row: PartnershipRow, focus?: 'notes') => void;
  /**
   * Opens the page's {@link CompanyContactsManagerModal} on this row's company,
   * or `null` where this card offers no contacts button at all.
   *
   * 🔴 **NULLABLE, AND THE NULL IS THE SCOPE — the same shape as `rowCreate`
   * above and for the same reason.** The owner asked for this on Leads and
   * Companies; the page decides which cards get a handler (see
   * `CONTACTS_CARD_KEYS`), and a card handed `null` renders no button and reserves
   * no room for one. **Narrowing the prop to those two keys instead would put
   * the scope in this component's shape**, where the reason for it cannot be
   * read — the mistake `onOpenDocumentProjects` directly below declines to make
   * in the other direction.
   *
   * ⚠️ **It is what `actionButtons` and `actionExtraRem` count**, so a card
   * that stops offering this must stop reserving the width in the same change;
   * both read this one prop, so they cannot come apart.
   *
   * ⚠️ **It takes the RESOLVED company, not the row — unlike `onOpenProjects`,
   * which takes a row and a card key and resolves at the modal.** The modal's
   * own prop is `{id, name} | null`, so handing over the resolved pair makes the
   * page's state exactly the modal's prop and leaves no state that can be "open
   * on a row with no company". The refetch after a contact add or delete is
   * the page's (both company cards, on close — see `contactsChangedRef`), so no
   * card key is needed. **The row is where {@link companyIdOf} can answer**, so
   * that is where the withholding decision belongs — see
   * {@link RowContactsButton}.
   */
  onOpenContacts: ((company: ContactsCompany) => void) | null;
  /**
   * Opens the page's read-only {@link DocumentProjectsModal}.
   *
   * ⚠️ **Passed on all five cards though only three can reach it.** The two
   * company cards declare `projectOfInterest` rather than `lineProjects`, so the
   * cell that calls this is never rendered there — but narrowing the prop would
   * encode the CURRENT column split into this component's shape, and the split
   * lives in `COMPANY_CARD_COLUMNS` / `DOCUMENT_CARD_COLUMNS`, where the reason
   * for it can be read.
   */
  onOpenDocumentProjects: (row: PartnershipRow) => void;
  /**
   * The row's quote-PDF control, or `null` where this card offers none — see
   * {@link PdfPreviewRow}. Counted by `actionButtons`, like `rowCreate`.
   */
  pdfPreview: PdfPreviewRow | null;
  /**
   * The lead scope this card's Export is scoped to (the page's, 2026-10-27), or
   * `null` where the card offers no Export — a non-company card, a reader
   * without `companies.export`, or a session that has not resolved. Resolved by
   * the page, like `canCreate`.
   */
  exportScope: PartnershipLeadScope | null;
  /**
   * The page-level Account lead control names anyone but the signed-in person
   * alone (2026-10-27), so an empty card says "these accounts", not "your".
   */
  scopedToOthers: boolean;
}) {
  const headingId = useId();
  const lowerTitle = state.title.toLowerCase();
  const hasRows = state.rows.length > 0;

  /* --- the write half ----------------------------------------------------- *
   *
   * 🔴 **A MESSAGE THAT HAS TO OUTLIVE THE REFETCH.** A landed write bumps this
   * card's reload token, and `usePartnershipTables` answers `PENDING` the moment
   * its stamp changes — so the card returns to its skeleton and every
   * `InlineField` on it is unmounted, taking any notice it was holding. Almost
   * nothing needs to survive that: the value visibly changes, which is the
   * acknowledgement. **A failed Xero push does**, because the write succeeded and
   * the customer's document is nonetheless not in the accountant's system, and
   * nothing else on any screen in this app would ever say so.
   *
   * ⚠️ **It is dismissible rather than self-clearing.** The panel does not
   * unmount on a refetch, so a banner with no way out would sit over the card
   * until the reader left the page — and a stale claim about Xero is worse than
   * none. ⛔ It must not be cleared by the next successful save either: two
   * documents can fail to push, and the second save is not evidence about the
   * first.
   */
  const [writeNotice, setWriteNotice] = useState<string | null>(null);
  /*
   * ⚠️ **Bumped on EVERY settled save, landed or not, and folded into the
   * five-row cap's signature below.** `useFiveRowCap` observes the TABLE, so a
   * row that gains a status line while another loses one leaves the table's total
   * height unmoved and fires nothing — and the cap then cuts the fifth row
   * through its own text, which reads as a rendering fault rather than as a
   * message somebody just dismissed. The observer is not a substitute for this,
   * for the same reason the column signature beside it is not.
   */
  const [editNonce, setEditNonce] = useState(0);

  /*
   * The card's Export (2026-10-18): the same `/companies` params `load` sends,
   * from the one builder, minus paging — so the file is this card's list.
   */
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportKey = isCompanyCardKey(state.key) ? state.key : null;
  const canExportCard = exportScope !== null && exportKey !== null;

  const editing: PartnershipEditing = {
    ...edit,
    onWriteNotice: setWriteNotice,
    onSettled: () => setEditNonce((current) => current + 1),
  };

  /*
   * The card's own DECLARED column set — what this card would show a reader who
   * has arranged nothing. `columnKeys` below turns it into the one ordered list,
   * and `layout.visibleKeys` after that is what actually renders.
   *
   * ⚠️ *SUPERSEDED 2026-09-03: "`minWidthRem` is `null` on the three cards that
   * carry no extra columns, and those render exactly as they did before this
   * change."* The note column landed on all five, so all five DECLARE a minimum
   * and all five pin their action cell — ⚠️ **and since 2026-09-12 that is a
   * statement about the default arrangement and not about the card**: a reader
   * who hides every extra column takes their own copy of that card back to
   * `null`. `minWidthRem` and `pinActions` moved down beside the layout for
   * exactly that reason; the derivation between them is unchanged.
   */
  const { extraColumns } = state;
  const hasValueColumn = state.valueLabel !== null;

  /*
   * 🔴 **THE PER-STAGE STATUS COLUMNS — owner 2026-10-25.** One column per
   * stage THIS card holds (`state.stageStatusStageIds`: the Lead stage on Leads,
   * the active non-lead stages on Companies, `null` on the document cards), out
   * of the page's one read of the stage and status masters. Nothing renders
   * while those masters load or after they fail — the list is simply empty.
   *
   * ⚠️ Memoised on the hook's Set identity, which it keeps stable while the ids
   * are unchanged, and on the page's memoised column list.
   */
  const cardStageIds = state.stageStatusStageIds;
  const allStageStatusColumns = edit.stageStatus.columns;
  const stageStatusColumns = useMemo<readonly StageStatusColumn[]>(
    () =>
      cardStageIds === null
        ? []
        : allStageStatusColumns.filter((column) => cardStageIds.has(column.stageId)),
    [cardStageIds, allStageStatusColumns],
  );
  const stageStatusColumnsByKey = useMemo<ReadonlyMap<string, StageStatusColumn>>(
    () => new Map(stageStatusColumns.map((column) => [column.id, column])),
    [stageStatusColumns],
  );
  const offersStageStatus = state.filterKinds.includes('stageStatus');

  /*
   * The status writes — saving flags and refusals keyed per company AND column
   * (see `stageStatusWriteKey`), held by the PANEL because a landed write
   * reloads the rows and a refusal has to outlive nothing but the row.
   */
  const [stageStatusSaving, setStageStatusSaving] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [stageStatusErrors, setStageStatusErrors] = useState<ReadonlyMap<string, string>>(
    () => new Map<string, string>(),
  );
  const setStageStatusError = (writeKey: string, message: string | null) => {
    setStageStatusErrors((current) => {
      if (message === null && !current.has(writeKey)) return current;
      const next = new Map(current);
      if (message === null) next.delete(writeKey);
      else next.set(writeKey, message);
      return next;
    });
  };

  /**
   * Sets one company's stage status from a `{Stage} status` cell.
   *
   * ⚠️ **The same flow `/companies` runs** (`CompaniesPage.changeStageStatus`):
   * a status that needs a follow-up date or a reason asks first, through the
   * page's ONE `useStageStatusChange` dialog (a cancel changes nothing and sends
   * nothing); then ONE `PATCH /companies/:id {stageStatusId, ...extra}`, and the
   * response's `data.stageStatus?.id` compared to what was asked — a `200` is
   * not evidence the change happened. A landed write reloads BOTH company cards
   * (`onSaved`), as the Stage cell does.
   */
  const changeStageStatus = async (
    row: PartnershipRow,
    companyId: number,
    column: StageStatusColumn,
    nextStatusId: number | null,
  ) => {
    const previous = row.stageStatus ?? null;
    if ((previous?.id ?? null) === nextStatusId) return;
    const statuses = edit.stageStatus.statuses;
    const nextStatus =
      nextStatusId === null ? null : (statuses.find((status) => status.id === nextStatusId) ?? null);
    // The status BEFORE the change, by id; one the master no longer lists is
    // left out (unknown), never guessed.
    const currentStatus =
      previous === null
        ? null
        : (statuses.find((status) => status.id === previous.id) ?? undefined);

    const extra = await edit.requestStageStatusChange({
      companyName: recordLabelOf(row),
      status: nextStatus,
      // The row's stored follow-up date seeds the dialog, as on `/companies`.
      currentFollowUpOn: row.followUpOn ?? null,
      ...(currentStatus === undefined ? {} : { currentStatus }),
    });
    if (extra === null) return;

    const writeKey = stageStatusWriteKey(companyId, column);
    setStageStatusError(writeKey, null);
    setStageStatusSaving((current) => new Set(current).add(writeKey));
    try {
      const { data } = await api.updateCompany(companyId, {
        stageStatusId: nextStatusId,
        ...extra,
      });
      if ((data.stageStatus?.id ?? null) !== nextStatusId) {
        setStageStatusError(
          writeKey,
          'Stage status not saved — the server didn’t apply the change. Try again.',
        );
        return;
      }
      edit.onSaved();
    } catch (caught) {
      let message: string;
      if (caught instanceof ApiError && caught.status === 403) {
        message = `${
          edit.company.disabledReason ?? 'Your account no longer has permission to change this.'
        } Nothing was saved.`;
      } else if (caught instanceof ApiError) {
        message =
          caught.fieldErrors.stageStatusId ??
          caught.fieldErrors.followUpOn ??
          caught.fieldErrors.stageStatusReason ??
          caught.formMessage ??
          caught.message;
      } else {
        message =
          caught instanceof Error && caught.message !== ''
            ? caught.message
            : 'Could not reach the server. Check your connection and try again.';
      }
      setStageStatusError(writeKey, message);
    } finally {
      setStageStatusSaving((current) => {
        const next = new Set(current);
        next.delete(writeKey);
        return next;
      });
      setEditNonce((current) => current + 1);
    }
  };

  const stageStatusWrites: StageStatusWrites = {
    saving: stageStatusSaving,
    errors: stageStatusErrors,
    onChange: (row, companyId, column, stageStatusId) =>
      void changeStageStatus(row, companyId, column, stageStatusId),
  };

  /** Names a stage for a per-stage status ordering, from this card's columns. */
  const stageNameOf = (stageId: number): string | null =>
    stageStatusColumns.find((column) => column.stageId === stageId)?.stageName ?? null;

  /* --- the column funnels ------------------------------------------------- *
   *
   * 🔴 **THIS REPLACED A `<select>` IN THE TOOLBAR — owner's instruction,
   * 2026-09-12: *"remove the country filter dropdown and give the column level
   * filter boxes proper how they are in other modules"*.**
   *
   * ⚠️ **`columnKeys` is the ONE column list this card DECLARES, and
   * `layout.visibleKeys` below is that list ARRANGED** — the `ColumnSpec`s and
   * the funnels read the first, every rendered part of the table reads the
   * second. *SUPERSEDED 2026-09-12: "feeding the `ColumnSpec`s, the head and —
   * through `extraColumns` and `hasValueColumn` — the `<colgroup>` and the width
   * minimum", which described four derivations of one list rather than one.*
   * Under `table-fixed` a head with one more cell than the colgroup declares
   * does not squeeze: it shifts every following column and the browser pads the
   * shortfall at the end, which is exactly what the value column's removal had
   * to be built around — and what a reader reordering the table would now do on
   * every render.
   */
  /*
   * ⚠️ **Only the STATUS master is resolved up here now — 2026-09-14.**
   * *SUPERSEDED: a `countryMaster` and a `sourceMaster` beside it.* `headerFor`
   * resolves its own master from the ONE `kind` it looked up, so three
   * pre-resolved locals would be three more things to keep in step with that
   * lookup — which is the shape the three ternary chains had and the reason
   * `companyType` fell through them. This one survives because two OTHER readers
   * need it: the `status` column spec's label resolver and the row's status cell.
   */
  const statusMaster = masterFor(state.key, 'status', filterMasters);
  const offersCountry = state.filterKinds.includes('country');
  /*
   * ⚠️ **The CARD's own `filterKinds`, never "does the column render".** The two
   * agree on this page today — `source` is declared on exactly the two cards
   * whose endpoint takes `sourceId` — and reading the column list instead would
   * attach `scope: 'server'` to a funnel the moment somebody rendered the column
   * anywhere else, which is a param silently stripped or a 422.
   */
  const offersSource = state.filterKinds.includes('source');
  /*
   * ⚠️ **The CARD's own `filterKinds`, never "does the column render"** — the same
   * ruling `offersSource` above states, and it binds here for the same reason:
   * `companyTypeId` exists on `/companies` and on no other list schema, so
   * rendering the column anywhere else must not attach `scope: 'server'` to its
   * funnel.
   */
  const offersCompanyType = state.filterKinds.includes('companyType');
  const offersStatus = state.filterKinds.includes('status');
  // The Companies card alone — see `COMPANIES_FILTERS` in the hook.
  const offersPurchasedCycle = state.filterKinds.includes('purchasedCycle');
  const offersParticipatedCycle = state.filterKinds.includes('participatedCycle');
  /*
   * The cycles each purchase-history cell highlights: the ids in this card's own
   * cycle filters, and `[]` on a card that does not offer them — gated on
   * `filterKinds` like the funnels, so a stray URL key highlights nothing.
   */
  const purchaseHighlights = {
    products: offersPurchasedCycle ? state.filter.purchasedCycle : EMPTY_FILTER_IDS,
    projects: offersParticipatedCycle ? state.filter.participatedCycle : EMPTY_FILTER_IDS,
  };

  /*
   * 🔴 **EXPLICIT PLACEMENT for the per-stage status columns (owner
   * 2026-10-25): directly AFTER the Stage value column.** The hook marks a
   * `'stageStatuses'` slot inside `extraColumns`; it is dropped here and the
   * `stageStatus:<id>` keys appended after `VALUE_KEY` instead, so each status
   * sits beside the stage it belongs to. Leads has no value column, so there
   * they land at the same tail — where its Stage column would be.
   */
  const columnKeys = useMemo<string[]>(
    () => [
      RECORD_KEY,
      ...extraColumns.filter((column) => column !== 'stageStatuses'),
      ...(hasValueColumn ? [VALUE_KEY] : []),
      ...stageStatusColumns.map((column) => column.id),
    ],
    [extraColumns, hasValueColumn, stageStatusColumns],
  );

  /* --- the column manager ------------------------------------------------- *
   *
   * ✅ **Order, hidden set and the two frozen edges, stored against the ACCOUNT
   * — one controller per card.** See `columnLayoutScope` for why five scopes and
   * not one, and `LOCKED_COLUMN_KEYS` for why `record` is the only thing a
   * reader may not touch.
   *
   * ⚠️ **`columnKeys` is still THE column list; `layout.visibleKeys` is that
   * list arranged.** Everything below — the `<colgroup>`, the header, the body
   * cells and the skeleton — walks the arranged one, which is the point of the
   * change: they used to walk four separate re-derivations of the declared one.
   * The `ColumnSpec`s and `useTableFilters` keep reading `columnKeys` itself, so
   * a hidden column's FILTER is still live rather than silently cleared — hiding
   * a column arranges the table, it does not widen the fetch.
   */
  const layout = useColumnLayout(columnLayoutScope(state.key), columnKeys, LOCKED_COLUMN_KEYS);
  /*
   * ⚠️ **Destructured, and the two hooks below depend on THESE rather than on
   * `layout`.** The controller object is rebuilt every render while both members
   * are memoised inside it, so depending on the object would disconnect and
   * re-attach the freeze observer on every keystroke in a funnel.
   */
  const { visibleKeys, frozenSideOf: storedFrozenSideOf } = layout;

  async function runExport() {
    if (exportScope === null || exportKey === null) return;
    setExporting(true);
    setExportError(null);
    try {
      const params = await companyCardListParams(
        exportKey,
        exportScope,
        state.sort,
        state.filter,
        new AbortController().signal,
      );
      const file = await api.exportCompanies({
        ...params,
        columns: exportColumnsOf(visibleKeys),
      });
      saveBlob(file.filename, file.blob);
    } catch (caught) {
      setExportError(
        caught instanceof Error ? caught.message : 'The export could not be produced.',
      );
    } finally {
      setExporting(false);
    }
  }

  /*
   * 🔴 **THE RECORD COLUMN IS FROZEN LEFT ON EVERY CARD, BY THE PAGE — owner's
   * instruction 2026-09-13.** See {@link PAGE_FROZEN_COLUMN_KEYS} for why this
   * is the page's decision and not a default the reader can clear.
   *
   * ⚠️ **WRAPPED HERE, AT THE ONE PLACE BOTH CONSUMERS READ IT.** The cells get
   * it through {@link FreezeProps} and the measurer gets it as an argument, so a
   * wrapper applied to only one of the two would freeze a column whose offset was
   * never measured, or measure an offset nothing sticks at. Both are silent: the
   * cell is present, in the right column, at the wrong place.
   *
   * ⚠️ **`useCallback` because {@link useFrozenOffsets} depends on this
   * identity** — a fresh function per render rebuilds its `ResizeObserver` on
   * every keystroke in a funnel, which is the cost the destructure above exists
   * to avoid in the first place.
   *
   * ⚠️ **The record column's own offset is always `0` and needs no arithmetic**,
   * because it is the only left-frozen column and `useFrozenOffsets` accumulates
   * widths over left-frozen cells alone. That holds even if a reader drags
   * another column ahead of it — `record` is locked against being moved itself,
   * but nothing stops a key being dropped at index 0, and the column that lands
   * there simply scrolls underneath rather than displacing the frozen one.
   *
   * ⚠️ **The `<col>` for this column still carries NO width, and must not gain
   * one.** Sticky positioning is applied after layout, so a width-less `<col>`
   * absorbing the surplus and a sticky cell are independent — see
   * `RECORD_COLUMN_MIN_REM` for what declaring a width would cost the Deals card.
   *
   * ⚠️ **STATED COST, because it is arithmetic rather than a guess: on a card
   * narrower than about 500px the frozen column leaves almost nothing to scroll
   * into.** The record column is `tableWidth − the declared columns`, i.e. ~304px
   * at a card's minimum — roughly half a 576px card at 1440 (useful), 56px of
   * daylight on the ~360px cards the two-up grid produces at exactly `lg`, and
   * wider than the viewport itself at 320. Hiding extra columns shrinks it, and
   * hiding all of them drops the minimum entirely (see `tableMinWidthRem`), which
   * is the reader's way out. A viewport breakpoint would NOT express this — the
   * cards halve at `lg`, so the narrowest card and the narrowest phone sit on
   * opposite sides of any breakpoint that could be chosen.
   */
  const frozenSideOf = useCallback(
    (key: string): 'left' | 'right' | undefined =>
      key === RECORD_KEY ? 'left' : storedFrozenSideOf(key),
    [storedFrozenSideOf],
  );

  /*
   * ✅ **THE COLUMN MANAGER NEEDED NO ARM FOR THE 2026-09-14 PROJECTS SPLIT, and
   * that is the return on it being two column KEYS rather than one column read
   * two ways.** `COLUMN_LABEL` is keyed by column and each card declares one of
   * the two, so the manager and the table heading cannot name a column
   * differently — where a per-card heading would have had to be resolved here as
   * well, and a card whose heading read "Projects on this document" while its
   * manager offered "Project of interest" reads as two columns, one of which
   * cannot be found. `VALUE_KEY` above is the one genuinely per-card label.
   */
  const columnLabelOf = (key: string): string =>
    key === RECORD_KEY
      ? 'Record'
      : key === VALUE_KEY
        ? (state.valueLabel ?? 'Value')
        : isPartnershipColumn(key)
          ? COLUMN_LABEL[key]
          : // A per-stage status column names itself `{Stage} status`.
            (stageStatusColumnsByKey.get(key)?.label ?? key);

  /*
   * ⚠️ **BOTH follow the VISIBLE set, and they have to move together.** A
   * minimum computed from the declared columns beside a `pinActions` computed
   * from the visible ones — or the reverse — reserves width the colgroup does
   * not declare, and under `table-fixed` that draws one column on top of
   * another with nothing erroring. `tableMinWidthRem`'s own parameter doc
   * records what that cost the last time these two derivations disagreed.
   *
   * So a reader who hides every extra column takes the minimum to `null` and
   * unpins the action cell. That is correct rather than a regression: with no
   * minimum the table cannot scroll sideways, and a pinned cell on a table with
   * nothing to scroll draws a hairline down the middle of the card.
   */
  /*
   * 🔴 **COUNTED ONCE, HERE, AND HANDED TO ALL THREE CONSUMERS** — the table
   * minimum below, and both `PartnershipColgroup` call sites (the skeleton's
   * and the loaded table's).
   *
   * ⚠️ **It used to be the predicate `rowCreate !== null`, spelled out at each
   * of those three call sites**, and that is exactly the arrangement that
   * produced the 2026-09-04 overflow: the colgroup was taught one answer and
   * the minimum another. Three spellings of one fact is three chances to
   * disagree, and under `table-fixed` the disagreement is an overflow rather
   * than an error — see `actionColumnRem`.
   *
   * **The activity button is the `1` and is on every row of every card**; the
   * other two are conditional, and each is already `null` when the reader may
   * not have it, so this follows what RENDERS rather than what the card
   * declares. ⛔ **The three terms must stay in step with the cell below** — a
   * button added there without a term here reserves no room for itself.
   */
  const actionButtons =
    1 +
    (rowCreate !== null ? 1 : 0) +
    (onOpenContacts !== null ? 1 : 0) +
    // The quote-PDF control (2026-10-25) — see `PdfPreviewRow`.
    (pdfPreview !== null ? 1 : 0);
  // The contacts button carries a count and is drawn wider — see `CONTACTS_BUTTON_EXTRA_REM`.
  const actionExtraRem = onOpenContacts !== null ? CONTACTS_BUTTON_EXTRA_REM : 0;

  const minWidthRem = tableMinWidthRem(visibleKeys, actionButtons, actionExtraRem);
  const pinActions = minWidthRem !== null;

  /*
   * ⚠️ **THIS CARD RENDERS THREE DIFFERENT HEADER ROWS OVER ITS LIFETIME** — the
   * skeleton's, the loaded table's, and none at all while the empty state shows.
   *
   * ✅ *SUPERSEDED 2026-09-12: a `headRowNonce`, bumped from a callback ref on
   * arrival and carried in `headKeys`' dependency list, because
   * `useFrozenOffsets` attached its `ResizeObserver` inside an effect keyed on
   * its ARGUMENTS rather than on the node — so the observer stayed bound to a
   * row that had been unmounted, and a later viewport resize moved nothing while
   * two frozen columns overlapped by the difference.* **The hook tracks the row
   * ELEMENT itself now**, so a plain ref is enough and the nonce is gone with
   * the `eslint-disable` it needed. The hazard it guarded is unchanged and is
   * still real — it is just no longer this page's to hold.
   */
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /*
   * ⚠️ **The keys as the header row RENDERS them — the managed ones plus the
   * actions lane** — because `useFrozenOffsets` walks this list against the
   * row's children BY INDEX. Drop the actions key and the hook measures one cell
   * short; worse, its width stops being counted, so a right-frozen column lands
   * underneath it instead of beside it.
   */
  const headKeys = useMemo(() => [...visibleKeys, ACTIONS_KEY], [visibleKeys]);

  /*
   * ⚠️ **The measurer's answer for the actions lane differs from the cells'** —
   * see `frozenPropsFor`. Right-frozen here, so its width counts; unfrozen
   * there, so it keeps `pinned`. That substitution is the hook's own now, which
   * is why `frozenSideOf` goes in unwrapped: see {@link PINNED_COLUMN_KEYS}.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    headKeys,
    frozenSideOf,
    PINNED_COLUMN_KEYS,
  );

  /*
   * ⚠️ **`scope: 'server'` is attached only where the card's own `filterKinds`
   * says the ENDPOINT takes the key**, never merely where the column is
   * rendered. The deals card shows no country column and `/deals` declares no
   * `countryId`; a spec that claimed otherwise would draw a funnel that narrows
   * nothing, which is the "it looks like it worked" failure this page already
   * guards against for its sort options.
   *
   * ⚠️ **Every other column is PAGE-scoped and that is honest rather than
   * second-best.** The card fetches `PER_PAGE` (100) rows and shows five above
   * the fold, so a page funnel narrows the whole fetched set — exact whenever
   * `total <= 100`, a sample above it, and `ColumnFilter`'s own footnote is what
   * says which. There is no server filter to reach for: no endpoint here filters
   * on a record's name, a due date, an envelope or a note.
   */
  const columns = useMemo<ColumnSpec<PartnershipRow>[]>(() => {
    const specs: ColumnSpec<PartnershipRow>[] = [
      { key: 'record', label: 'Record', get: (row) => row.primary },
    ];
    for (const column of extraColumns) {
      if (column === 'country') {
        specs.push({
          key: 'country',
          label: COLUMN_LABEL.country,
          /*
           * ⚠️ **The row carries the country's NAME and the filter sends its
           * ID.** That is why `headerFor` always supplies `options` from the
           * `/countries` master: the hook's fallback accumulates values out of
           * `get`, and an accumulated NAME written into `?leadsCountry=` is a
           * 422. `get` earns its place for the Is-empty / Is-not-empty test,
           * which is page-scoped on every column because no endpoint here can
           * ask for "no country recorded".
           */
          get: (row) => row.country,
          ...(offersCountry
            ? { scope: 'server' as const, param: 'countryId', multi: true }
            : {}),
        });
      } else if (column === 'source') {
        specs.push({
          key: 'source',
          label: COLUMN_LABEL.source,
          /*
           * ⚠️ **The row carries the source's NAME and the filter sends its ID**
           * — exactly as `country` above does. That is why `headerFor` ALWAYS
           * supplies `options` from the `/company-sources` master: the hook's
           * fallback accumulates values out of `get`, and an accumulated NAME
           * written into `?leadsSource=` is a 422 naming the key. `get` earns its
           * place for the Is-empty / Is-not-empty test, which is page-scoped on
           * every column because no endpoint here can ask for "no source
           * recorded".
           *
           * ⚠️ **Is-empty is worth MORE on this column than on its neighbour**,
           * and it is the only way to reach the blank rows at all: `sourceId IN
           * (...)` is UNKNOWN for a NULL, so no value of the server filter can
           * return them — and here that is most of the table.
           */
          get: (row) => row.source,
          /*
           * ⛔ **`multi: true`, and ONLY because `/companies` parses CSV.**
           * `?sourceId=1,2` is a real OR there; the same spelling on any other
           * list or board in this app is a 422 "expected number, received NaN".
           * That is why this flag sits beside `param` under one `offersSource`
           * guard rather than being declared unconditionally — see the `status`
           * arm below, which has no `multi` for exactly the opposite reason.
           */
          ...(offersSource ? { scope: 'server' as const, param: 'sourceId', multi: true } : {}),
        });
      } else if (column === 'companyType') {
        specs.push({
          key: 'companyType',
          label: COLUMN_LABEL.companyType,
          /*
           * ⚠️ **The row carries the type's NAME and the filter sends its ID** —
           * exactly as `country` and `source` above do, and `headerFor` ALWAYS
           * supplies `options` from the `/company-types` master for that reason:
           * the hook's fallback accumulates values out of `get`, and an
           * accumulated NAME written into `?leadsCompanyType=` is a 422 naming the
           * key. `get` earns its place for the Is-empty / Is-not-empty test, which
           * is page-scoped on every column because no endpoint here can ask for
           * "no type recorded".
           *
           * ⚠️ **Is-empty is worth as much here as on `source`, and for the same
           * reason: it is the only way to reach the blank rows at all.**
           * `company_type_id IN (...)` is UNKNOWN for a NULL, so no value of the
           * server filter returns them — and here that is most of the table.
           */
          get: (row) => row.companyType,
          /*
           * ⛔ **`param: 'companyTypeId'`, and the SORT beside it is
           * `?sort=companyType` with no suffix.** This is the one pair on the page
           * that differs by a suffix rather than a word, which makes it the easiest
           * to transpose; `.strict()` turns either mistake into a 422 that takes
           * the whole card out rather than a no-op.
           *
           * ⛔ **`multi: true`, and ONLY because `/companies` parses CSV** — the
           * third key on that endpoint that does. The same spelling on any other
           * list or board in this app is a 422 "expected number, received NaN",
           * which is why this sits beside `param` under one `offersCompanyType`
           * guard rather than being declared unconditionally.
           */
          ...(offersCompanyType
            ? { scope: 'server' as const, param: 'companyTypeId', multi: true }
            : {}),
        });
      } else if (column === 'status') {
        specs.push({
          key: 'status',
          label: COLUMN_LABEL.status,
          /*
           * ⚠️ **`.ref.id`, not `.id`.** `PartnershipRowStatus` is a DISCRIMINATED
           * pair — `{kind: 'quote-stage' | 'invoice-status', ref}` — because a
           * quote stage id and an invoice status id are two masters whose ids are
           * both plain integers and resolve against each other without
           * complaint. The `kind` is not needed here: the card already picked its
           * own master through `masterFor`, so a row on this card can only carry
           * the one this column formats against.
           */
          get: (row) => row.status?.ref.id ?? null,
          format: (value) =>
            statusMaster.rows.find((row) => String(row.id) === value)?.name ?? `#${value}`,
          /*
           * ✅ **`multi` since 2026-10-28** — `/deals`, `/quotes` and `/invoices`
           * now parse a CSV `stageId` / `statusId`, so several ticks are one
           * server OR (`stageParams` / `statusParams`). ⚠️ *SUPERSEDED: "NO
           * `multi` … Several ticks therefore send NOTHING and the card narrows
           * the loaded rows itself."*
           */
          ...(offersStatus ? { scope: 'server' as const, param: 'statusId', multi: true } : {}),
        });
      } else if (column === 'signature') {
        specs.push({
          key: 'signature',
          label: COLUMN_LABEL.signature,
          /*
           * Two states and no more: this quote is in the outstanding set, or it
           * is not. The column asks `outstanding: true`, so a completed,
           * declined or voided envelope is absent from it — which is why the
           * heading says "Awaiting" and why this must not invent a value for
           * one. Until the read is `ready` every row answers `null`, and the
           * funnel is marked `loading` rather than offering an empty list.
           */
          get: (row) =>
            signatures.kind === 'ready' && signatures.byQuoteId.has(row.id) ? 'Awaiting' : null,
        });
      } else if (column === 'overdue') {
        specs.push({ key: 'overdue', label: COLUMN_LABEL.overdue, get: (row) => row.dueDate });
      } else if (column === 'projectOfInterest') {
        /*
         * ✅ **IT HAS A PAGE-SCOPED FUNNEL SINCE 2026-09-13 — owner's
         * instruction.** ⚠️ *SUPERSEDED, and every word of it was true when
         * written: "⛔ **NO SPEC, DELIBERATELY — this is how the column gets no
         * funnel** … A `ColumnSpec` here with no `scope` is a PAGE-scoped funnel
         * by default, which would build its vocabulary out of the hundred rows
         * already fetched — the accumulate-from-the-loaded-page failure this page
         * exists to have removed. There is no server filter to attach `scope:
         * 'server'` to."*
         *
         * 🔴 **THE HALF THAT WAS MEASURED IS UNCHANGED AND STILL BINDING: there
         * is NO server filter, and `?projectOfInterestId=` is REFUSED on all four
         * endpoints — deliberately, not pending.** So this funnel narrows the
         * rows already loaded and nothing else, and `scope` stays at its `page`
         * default. ⛔ **Never give it `scope: 'server'`**; there is no param for
         * `param` to name, and the hook would then emit one that is silently
         * stripped or 422s.
         *
         * ⚠️ **WHAT CHANGED IS WHICH FAILURE APPLIES, NOT WHETHER IT WAS REAL.**
         * The accumulate-from-the-page defect is a mismatch between what a filter
         * can REACH and what it can OFFER: the country funnel queries the whole
         * table, so an option list built from one page under-offers against rows
         * the filter could really have found, and nothing says so. A PAGE-scoped
         * funnel cannot have that gap — the vocabulary and the population it acts
         * on are the SAME hundred rows, so the list is exactly complete for what
         * the control can do. What remains is that the card may hold more rows
         * than it loaded, and `scopeNote` below is where that is said.
         *
         * ⚠️ **`get` returns a JOINED STRING and is NOT what decides a match** —
         * see `matches`. It earns its place for the emptiness test alone, which
         * is page-scoped on every column here because no endpoint exposes an
         * is-null filter. `null` covers BOTH an empty list (`[]`) and *there was
         * no list to read* — conflated only here, where both honestly answer
         * "empty", and never in the cells, which must keep them apart.
         *
         * ⚠️ **Is empty is now the MAJORITY on three of the five cards.**
         * Measured 2026-09-14: **613 of 1,088 documents name no project on any
         * line (56%)**, so that option routinely matches more rows than every
         * project option put together. That is the data, not a broken funnel —
         * ⛔ do not hide the option, reorder it, or word it as an exception.
         */
        /*
         * 🔴 **THE FIELD IS NAMED ONCE PER ARM, IN AN ACCESSOR ITS THREE READERS
         * SHARE — 2026-09-14.** Since the projects split there are two fields of
         * the IDENTICAL shape one column key apart, so `get`, `options` and
         * `matches` naming the field themselves is three chances to disagree:
         * a `matches` reading `lineProjects` under this spec would narrow the
         * card on a fact no cell on it displays, with a `200`, a plausible option
         * list and counts that look right. ⛔ Do not spell a field inside any of
         * the three. The `lineProjects` arm below is the same shape.
         */
        const projectsOf = (row: PartnershipRow): readonly PartnershipRowProject[] | null =>
          row.projectsOfInterest;
        specs.push({
          key: 'projectOfInterest',
          label: COLUMN_LABEL.projectOfInterest,
          get: (row) => projectIdsValue(projectsOf(row)),
          /*
           * ⚠️ **SUPPLIED, never accumulated, because the cell is a SET.** The
           * hook's page-scoped fallback counts distinct `get` values, which here
           * are joined id strings — so a company pursued for two projects would
           * offer `"4,9"` as one option that matches nothing else on the card.
           */
          options: projectFilterOptions(state.rows, projectsOf),
          matches: (row, selected) => projectMatches(projectsOf(row), selected),
        });
      } else if (column === 'lineProjects') {
        /*
         * 🔴 **THE THREE DOCUMENT CARDS' PROJECTS FUNNEL — owner's decision,
         * 2026-09-14.** Identical machinery to `projectOfInterest` above over a
         * DIFFERENT FIELD: `row.lineProjects`, the projects this deal, quote or
         * invoice names on its own line items.
         *
         * ⛔ **PAGE-SCOPED, AND THERE IS NOTHING TO ESCALATE IT TO.** `scope`
         * stays at its default: no list behind these three cards takes a
         * line-project filter of this shape. ⚠️ **`?lineProjectId=` on `/quotes`
         * and `/invoices` is a REAL filter and is NOT this** — it narrows which
         * DOCUMENTS come back, where this column reports what each one names, so
         * wiring one to the other would answer a different question under this
         * heading. The hook's `PartnershipColumn` doc says so in terms.
         *
         * ⚠️ **Is empty is the MAJORITY option here, and that is the data.**
         * Measured 2026-09-14: **613 of 1,088 documents name no project on any
         * line (56%)**, so it routinely matches more rows than every project
         * option put together. ⛔ Do not hide it, reorder it, or word it as an
         * exception — and no empty-state copy may imply the values are on their
         * way, the same ruling `travel` carries on `/client-deliverables`.
         */
        const projectsOf = (row: PartnershipRow): readonly PartnershipRowProject[] | null =>
          row.lineProjects;
        specs.push({
          key: 'lineProjects',
          label: COLUMN_LABEL.lineProjects,
          get: (row) => projectIdsValue(projectsOf(row)),
          // Supplied rather than accumulated, for the reason on the arm above:
          // the hook's fallback would offer a joined id string as one option.
          options: projectFilterOptions(state.rows, projectsOf),
          matches: (row, selected) => projectMatches(projectsOf(row), selected),
        });
      } else if (column === 'productsPurchased' || column === 'projectsParticipated') {
        /*
         * The Companies card's purchase-history funnels.
         *
         * ⚠️ *SUPERSEDED 2026-10-09: "⛔ **PAGE-SCOPED, never `scope: 'server'`**:
         * no endpoint filters on purchase history."* Backend `32057ed` added
         * `?purchasedCycleId=` and `?participatedCycleId=` (CSV cycle ids) on
         * `/companies`, so these funnels now filter by CYCLE on the SERVER and no
         * longer narrow the loaded rows. Their options are the `/cycles` master,
         * supplied by `headerFor` through `masterFor` — never accumulated from the
         * rows, whose cells name products and projects rather than cycles.
         *
         * `get` survives for the page-scoped Is-empty test alone, as on every
         * other server-scoped column here.
         */
        const kind = column === 'productsPurchased' ? 'products' : 'projects';
        const itemsOf = (row: PartnershipRow): readonly PartnershipRowProject[] | null =>
          purchasedItemsOf(row.purchaseHistory, kind);
        const offersCycle =
          column === 'productsPurchased' ? offersPurchasedCycle : offersParticipatedCycle;
        specs.push({
          key: column,
          label: COLUMN_LABEL[column],
          get: (row) => projectIdsValue(itemsOf(row)),
          ...(offersCycle
            ? {
                scope: 'server' as const,
                param: column === 'productsPurchased' ? 'purchasedCycleId' : 'participatedCycleId',
                multi: true,
              }
            : {}),
        });
      } else if (column === 'upcomingTask') {
        /*
         * ✅ **IT HAS A PAGE-SCOPED FUNNEL SINCE 2026-09-13 — owner's
         * instruction.** ⚠️ *SUPERSEDED: "⛔ **NO SPEC, DELIBERATELY — this is how
         * the column gets no funnel** … A `ColumnSpec` here with no `scope` is a
         * PAGE-scoped funnel by default, which over a two-valued derived column
         * narrows toward a POPULATION the card has only a prefix of."*
         *
         * 🔴 **THE MEASUREMENT BEHIND IT STANDS: `?hasOpenTask=` IS A 422 ON ALL
         * FOUR ENDPOINTS.** The flag is derived from the account's tasks and is a
         * column on none of them, so there is nothing for `scope: 'server'` to
         * name and there is no deploy that would change that. ⛔ Do not add one.
         *
         * ⚠️ **THE OLD OBJECTION IS ANSWERED BY THE OPTIONS, NOT DISMISSED.** It
         * was that a two-valued funnel narrows toward a POPULATION — so a reader
         * ticking `Yes` on a card holding more rows than it fetched reads the
         * result as *these are the accounts with open tasks* rather than *these
         * are the ones among the first hundred*. Both options are therefore
         * DECLARED and both carry a COUNT over the loaded rows, so the split is
         * on screen before anything is ticked, and `scopeNote` says which rows it
         * ran over. **That is what makes the narrowing legible without a sentence
         * beside the control.**
         *
         * ⚠️ **The values are the CELL's own words, `Yes` and `No`** — see
         * `UpcomingTaskCell`. A funnel offering anything else would filter on a
         * vocabulary the column never prints.
         *
         * ⚠️ **`get` never returns `null`, so Is empty can never match here** and
         * that is correct rather than a gap: `hasOpenTask` is a `boolean` with no
         * third state, and an emptiness option that always answers nothing would
         * be a control with one outcome.
         */
        specs.push({
          key: 'upcomingTask',
          label: COLUMN_LABEL.upcomingTask,
          get: (row) => (row.hasOpenTask ? 'Yes' : 'No'),
          /*
           * ⚠️ **DECLARED, so `No` is offered even when every loaded row is
           * `Yes`.** The accumulated fallback would drop whichever value the page
           * happens not to hold — and the state a reader opens this funnel to
           * find is exactly the one that is missing from the rows in front of
           * them. This is the closed-enum case `ColumnSpec.options` exists for.
           */
          options: openTaskFilterOptions(state.rows),
        });
      } else if (column === 'quoteSigned') {
        /*
         * The Deals card's quote-signed funnel (owner 2026-10-27) — PAGE-SCOPED
         * over the loaded rows, the `upcomingTask` / `creditBalance` shape:
         * Signed / Not signed, declared with counts. ⚠️ `/deals` does take
         * `?quoteSigned=signed|unsigned`, but this page's server-filter path is
         * id lists only (`PartnershipFilter`), so it is not wired here yet.
         */
        specs.push({
          key: 'quoteSigned',
          label: COLUMN_LABEL.quoteSigned,
          get: quoteSignedFilterValue,
          options: quoteSignedFilterOptions(state.rows),
        });
      } else if (column === 'creditBalance') {
        /*
         * The Companies card's credit funnel — PAGE-SCOPED, because `/companies`
         * takes no credit filter, so `scope` stays at its default and there is
         * no `param` to name. It narrows HOLDS / DOES NOT HOLD credit and never a
         * figure: a funnel over amounts would compare one currency's balance
         * against another's. A balance this reader cannot see answers `null`, so
         * it falls to *Is empty* and never to *No credit*.
         */
        specs.push({
          key: 'creditBalance',
          label: COLUMN_LABEL.creditBalance,
          get: creditFilterValue,
          options: creditFilterOptions(state.rows),
        });
      } else if (column === 'note') {
        /*
         * ⚠️ **Filterable, never SORTABLE.** `latestNote` is viewer-scoped, so
         * ordering by it would return the same card differently for two people
         * with nothing on screen saying so — a defect this page has already had
         * removed. Narrowing it is a different act: it acts on the rows THIS
         * reader can see, which is what the "Your" in the heading already says.
         *
         * 🔴 **THIS ARM IS EXPLICIT AND MUST NOT BE COLLAPSED BACK INTO A
         * TRAILING `else`.** This chain once ended in a CATCH-ALL that pushed the
         * `note` spec, so any column added to `extraColumns` without an arm here
         * silently pushed a SECOND spec under the key `note` — double-counted in
         * `activeCount`, options built twice, and the header still looking right
         * because `headerFor` takes the first match. **Nothing would have
         * failed.** ⚠️ So a column with no arm of its own gets no spec — which is
         * the safe default — but it then has no heading either: `useTableFilters`
         * returns a bare `{label: key}` and the RAW KEY is painted as the column
         * heading.
         */
        specs.push({
          key: 'note',
          label: COLUMN_LABEL.note,
          get: (row) => row.latestNote?.body ?? null,
        });
      } else if (column === 'lastNote') {
        /*
         * The account-wide note, PAGE-scoped — no endpoint filters on a note's
         * text or author. `get` is the AUTHOR (or "Not visible to you"), which
         * gives the funnel a small, honest vocabulary; a body as an option would
         * offer whole paragraphs. ⚠️ *SUPERSEDED 2026-10-09: "⛔ No sort: the
         * ordering lives on the DATE column beside it."* The heading now toggles
         * that same `lastNoteAt` ordering — see `SORTABLE_COLUMN.lastNote`.
         */
        specs.push({
          key: 'lastNote',
          label: COLUMN_LABEL.lastNote,
          get: (row) => accountLastNoteFilterText(row.accountLastNote),
        });
      } else if (column === 'lastNoteAt') {
        /*
         * 🔴 **A SERVER date range, `?lastNoteAfter=` / `?lastNoteBefore=`** —
         * inclusive `YYYY-MM-DD`, UTC days — on `/companies`. ⚠️ **The URL is the
         * store, not the hook**: `headerFor` reads the bounds back from the card's
         * filter and writes them through `onLastNoteRangeChange`; the hook's own
         * `serverParams` for this column are never read by this page. `get` earns
         * its place for the page-scoped Is-empty test (accounts with no note).
         */
        specs.push({
          key: 'lastNoteAt',
          label: COLUMN_LABEL.lastNoteAt,
          get: (row) => accountLastNoteDate(row.accountLastNote),
          range: LAST_NOTE_RANGE,
        });
      } else if (column === 'accountLead') {
        /*
         * The resolved account lead (2026-10-27) — PAGE-scoped by name. The
         * server-side narrowing is the page-level Account lead control, which
         * scopes all five cards at once; this funnel only narrows the loaded rows.
         */
        specs.push({
          key: 'accountLead',
          label: COLUMN_LABEL.accountLead,
          get: (row) => row.accountLead?.name ?? null,
        });
      }
    }
    if (state.valueLabel !== null) {
      specs.push({ key: 'value', label: state.valueLabel, get: (row) => row.meta });
    }
    /*
     * The per-stage status columns (owner 2026-10-25) — one spec each, or the
     * heading would paint the raw `stageStatus:<id>` key. `get` is the status
     * name only when the company is IN that column's stage, so Is empty on a
     * column also matches the companies of every other stage (their cell is a
     * dash). The id funnel is the server's shared `?stageStatusId=` list, read
     * and written in `filterHeaderFor`'s stage arm; ⚠️ `multi` because
     * `/companies` parses CSV.
     */
    for (const column of stageStatusColumns) {
      specs.push({
        key: column.id,
        label: column.label,
        get: (row) =>
          row.companyStage?.id === column.stageId ? (row.stageStatus?.name ?? null) : null,
        ...(offersStageStatus
          ? { scope: 'server' as const, param: 'stageStatusId', multi: true }
          : {}),
      });
    }
    return specs;
    /*
     * ⚠️ **`state.rows` is a dependency because TWO specs build their option
     * lists from it** — the projects funnel's whole vocabulary, and the open-task
     * funnel's counts. It is safe in the hook's accumulation effect, which runs
     * over `columns` but touches `scope: 'server'` columns only and returns
     * `prev` unchanged otherwise, so a fresh `columns` identity per load costs
     * one pass and cannot cascade.
     */
  }, [
    extraColumns,
    state.valueLabel,
    state.rows,
    offersCountry,
    offersSource,
    offersCompanyType,
    offersStatus,
    offersPurchasedCycle,
    offersParticipatedCycle,
    statusMaster.rows,
    signatures,
    stageStatusColumns,
    offersStageStatus,
  ]);

  /*
   * ⚠️ **A stable `memoryId` per card (2026-10-25).** Without one the remembered
   * funnels and sort are keyed by a hash of the column keys, and since the
   * per-stage status columns those keys change once per mount (the columns
   * arrive with the masters) and again whenever an admin edits the stage
   * statuses — so the memory was lost or restored late. The old hash key
   * cannot be carried over: an id is stored as `table-id-<id>`, a hash as
   * `table-<hash>`, so the two never meet; a reader's existing card memory is
   * dropped once.
   */
  const tableFilters = useTableFilters(state.rows, columns, { memoryId: `up-${state.key}` });

  /*
   * The rows the card actually draws.
   *
   * 🔴 **TWO NARROWINGS, AND ONLY ONE OF THEM IS THE HOOK'S.** `visibleRows`
   * applies the page-scoped funnels. The status arm is ours, because the hook
   * cannot do it: its own `selected` for that column is deliberately kept empty
   * (the URL is the store), so it has nothing to filter on. Without this, ticking
   * a second status would draw two ticks over an unnarrowed list — the funnel
   * saying one thing and the rows another, with a `200` and nothing failing.
   */
  const rows = useMemo(() => {
    const pageRows = tableFilters.visibleRows;
    if (!statusPageFiltered(state.filter)) return pageRows;
    const wanted = new Set(state.filter.status);
    return pageRows.filter((row) => row.status !== null && wanted.has(row.status.ref.id));
  }, [tableFilters.visibleRows, state.filter]);

  /*
   * ⚠️ **TWO "are there rows" questions, and collapsing them gets one of the two
   * branches wrong.** `hasRows` asks whether the card HAS DATA and decides
   * whether a refetch shows skeletons or the rows already on screen — driving it
   * from the filtered set would blank a card to skeletons every time a funnel
   * hid everything. `hasVisibleRows` asks whether anything survives the funnels
   * and decides table-versus-empty-state.
   */
  const hasVisibleRows = rows.length > 0;
  /*
   * Whether the funnels are why the card looks empty. `hasRows && !hasVisibleRows`
   * is the whole test: data arrived and nothing survived the narrowing.
   */
  const emptiedByFilters = hasRows && !hasVisibleRows;

  /**
   * One column's header props — the funnel, its options, and the sort that
   * belongs to the same column.
   *
   * ⚠️ **The two server columns do NOT keep their value in the hook; the URL
   * does.** `useTableFilters` starts from `useState({})` and never reads the
   * query string, so a filter moved wholly into it would break every
   * `?leadsCountry=` link and lose the narrowing on reload and on Back. The
   * hook keeps the EMPTINESS half — page-scoped on every column, because no
   * endpoint here exposes an is-null filter — and `selected` stays empty there
   * so it can never emit a second, competing `countryId`. The identical
   * arrangement is on `CompaniesPage`, which documents it at its Stage column.
   */
  const filterHeaderFor = (key: string): FilterableTHProps => {
    /*
     * ✅ **EVERY HEADING ON THIS TABLE CARRIES A FUNNEL AGAIN — 2026-09-13,
     * owner's instruction.** Two early returns stood here, each supplying a bare
     * `{label}` so `projectOfInterest` and `upcomingTask` rendered a heading and
     * no funnel. ⚠️ *SUPERSEDED: "⛔ **NO FUNNEL ON THE PROJECT-OF-INTEREST
     * COLUMN, AND THAT IS THE DECISION — every other heading on this table
     * carries one** … Offering nothing is the honest answer until `/companies`
     * takes a project filter on the wire"* and *"⛔ **NO FUNNEL ON THE OPEN-TASK
     * COLUMN EITHER, and for a different reason from the one above** … a reader
     * ticking "Yes" on a card whose `total` is larger than its fetched page would
     * read the result as "these are the accounts with open tasks" when it is
     * "these are the ones among the first hundred"."*
     *
     * 🔴 **NEITHER ARGUMENT WAS DELETED. Both moved to the `ColumnSpec` arms in
     * `columns` above**, where the spec that answers them now lives — that is the
     * one place a reader asking "why is this funnel shaped like this" will
     * actually be standing. Read them there before changing either spec: the
     * measurements they rest on (no `?projectOfInterestId=`, `?hasOpenTask=` is a
     * 422) are unchanged and still forbid `scope: 'server'` on both.
     *
     * ⚠️ **The special cases had to GO, not merely stop firing.** `headerFor`
     * takes the FIRST match, so an early return left in place beside a real spec
     * would draw the heading and silently discard the funnel, the options and the
     * sort — with `activeCount` still counting a filter nobody could see.
     */
    const props = tableFilters.headerFor(key);
    const filter = props.filter;
    if (!filter) return props;

    /*
     * 🔴 **A `{Stage} status` column (owner 2026-10-25) — its own arm, because
     * its funnel and its sort are both per COLUMN rather than per kind.**
     *
     *  - **Options are ONLY this stage's statuses** (active, in `sortOrder`),
     *    plus any chosen id of this stage that has since been retired, labelled.
     *  - **All columns share ONE `?…StageStatus=` list**; this funnel reads its
     *    own stage's slice (`columnSelection`) and writes only that slice back
     *    (`replaceColumnSelection`), leaving other stages' ids in place. The
     *    server ORs them — a company is in one stage, so AND would be empty.
     *  - **Sort is the per-stage pair** (`sort=stageStatus&sortStageId=<id>`),
     *    offered where the card says it honours it (`stageStatusSortable`).
     */
    const stageColumn = stageStatusColumnsByKey.get(key);
    if (stageColumn !== undefined) {
      const allStatuses = edit.stageStatus.statuses;
      const chosen = offersStageStatus
        ? columnSelection(stageColumn, state.filter.stageStatus, allStatuses)
        : [];
      const offered = new Set(stageColumn.statuses.map((status) => status.id));
      const options: ColumnFilterOption[] = [
        ...stageColumn.statuses.map((status) => ({ value: String(status.id), label: status.name })),
        ...chosen
          .filter((id) => !offered.has(id))
          .map((id) => {
            const master = allStatuses.find((status) => status.id === id);
            return {
              value: String(id),
              label: master === undefined ? `#${id}` : `${master.name} (retired)`,
            };
          }),
      ];
      const sortPair = stageStatusSortPair(stageColumn.stageId);
      const stageDirection: 'asc' | 'desc' | null =
        state.sort === sortPair.asc ? 'asc' : state.sort === sortPair.desc ? 'desc' : null;
      return {
        ...props,
        filter: {
          ...filter,
          ...(offersStageStatus
            ? {
                options,
                loading: false,
                disabled: edit.stageStatus.failed && chosen.length === 0,
                value: { selected: chosen.map(String), emptiness: filter.value.emptiness },
              }
            : {}),
          onChange: (next: ColumnFilterState) => {
            if (offersStageStatus) {
              const ids = next.selected
                .map(Number)
                .filter((id) => Number.isSafeInteger(id) && id > 0);
              onFilterChange(
                state.key,
                'stageStatus',
                replaceColumnSelection(stageColumn, state.filter.stageStatus, ids, allStatuses),
              );
              // The hook keeps only the emptiness half, as on every server column.
              filter.onChange({ selected: [], emptiness: next.emptiness });
              return;
            }
            filter.onChange(next);
          },
          ...(state.stageStatusSortable
            ? {
                sort: {
                  direction: stageDirection,
                  onSort: (next: 'asc' | 'desc') =>
                    changeSort(next === 'asc' ? sortPair.asc : sortPair.desc),
                },
              }
            : {}),
        },
      };
    }

    /*
     * ⚠️ **Sort comes from the CARD, never from the hook.** `ColumnSpec.sortKey`
     * is left off every spec above precisely so the hook renders no sort UI of
     * its own: this card's ordering is a single `PartnershipSort` in the URL,
     * not a `{key, direction}` pair, and two mechanisms writing one ordering is
     * how a caret and a dropdown come to disagree.
     *
     * ⚠️ **Offered only where the CARD's `sortOptions` carries the pair**, which
     * is what keeps the funnel and the ordering dropdown showing the same set —
     * the derivation `SortHeader` used to make, moved rather than dropped.
     */
    /*
     * ⚠️ *SUPERSEDED 2026-09-13: `key === 'record' ? 'record' : SORTABLE_COLUMN[key
     * as PartnershipColumn]`.* The ternary existed because `record` is not a
     * {@link PartnershipColumn}, and it quietly meant the OTHER non-member —
     * `value` — could never name a pair however hard `HEADER_SORTS` tried. Both
     * keys are in the table now, so this is one lookup and the answer for every
     * heading is in one place.
     */
    const pair = sortPairFor(key, state.key);
    const sortable = pair !== undefined && state.sortOptions.includes(pair.asc);
    const direction: 'asc' | 'desc' | null =
      pair === undefined || !sortable
        ? null
        : state.sort === pair.asc
          ? 'asc'
          : state.sort === pair.desc
            ? 'desc'
            : null;

    /*
     * 🔴 **THE KIND IS LOOKED UP, NOT CHAINED — 2026-09-14, and this is the fix
     * the old comment here asked for in prose.** *SUPERSEDED: three ternary chains
     * (`master`, `chosen` and `kind`) that each ENDED on `status`, under a warning
     * reading "⛔ Do not reorder these into a chain that lets a fourth kind fall
     * into `status`".*
     *
     * ⚠️ **A COMMENT IS NOT A TYPE, and the fourth kind arrived.** Adding
     * `companyType` to the union left all three chains compiling and silently
     * filing it under `status`: the funnel would have sent a **type** id as the
     * card's STAGE filter — both are plain integers, so the server answers `200`
     * with a wrongly narrowed list — and drawn its ticks over a master the
     * selection never came from. Nothing errors; `tsc` is green.
     *
     * ✅ **`COLUMN_FILTER_KIND` is exhaustive over {@link PartnershipFilterKind}**,
     * so a FIFTH kind is a `tsc` error at that constant rather than a wrong list
     * on screen. It is the same correction `filterParamOf` already received, where
     * a two-branch ternary over three kinds would have made `?leadsSource=` and
     * `?leadsCountry=` one key.
     */
    const columnKind = COLUMN_FILTER_KIND[key] ?? null;
    /*
     * ⚠️ **The COLUMN names a kind; the CARD decides whether it offers it.** Both
     * halves are needed: without the second, attaching `scope: 'server'` to a card
     * whose endpoint has no such key is a param silently stripped or a 422.
     *
     * ⛔ **ASKED OF `filterKinds` DIRECTLY, never through a chain of per-kind
     * booleans.** A `columnKind === 'country' ? offersCountry : …` ladder is the
     * same shape as the three this change removed — exhaustive today and
     * fall-through tomorrow — and there is no reason to rebuild one here when the
     * card's own list answers the question for every kind at once. The four
     * `offers*` locals survive for the `columns` memo, where each is needed
     * SEPARATELY to decide one spec's `scope`.
     */
    const kind =
      columnKind !== null && state.filterKinds.includes(columnKind) ? columnKind : null;
    const isStatus = kind === 'status';
    /*
     * ⚠️ **Read through the one resolved `kind`, so the three can no longer
     * disagree with each other.** They were three independent chains and a member
     * added to one and missed in another would have drawn a funnel over the right
     * master with the wrong ticks in it.
     */
    const master = kind === null ? NO_MASTER : masterFor(state.key, kind, filterMasters);
    const chosen = kind === null ? EMPTY_FILTER_IDS : state.filter[kind];
    /*
     * 🔴 **The last-note date range — the URL is the store, as for every server
     * filter on this page.** The hook keeps the TYPED range so a half-entered
     * bound is not snapped back under the reader; when it holds none (a reload,
     * a shared link, Back) the funnel is seeded from the card's URL-backed
     * filter, so the drawn bounds always describe the request. ⚠️ Declared only
     * on the two `/companies` cards, the only endpoint taking these keys.
     */
    const isLastNoteRange = key === 'lastNoteAt' && extraColumns.includes('lastNoteAt');
    const lastNoteValue: ColumnFilterState | null = isLastNoteRange
      ? filter.value.range !== undefined
        ? filter.value
        : {
            ...filter.value,
            range: lastNoteRangeFromUrl(state.filter.lastNoteAfter, state.filter.lastNoteBefore),
          }
      : null;

    return {
      ...props,
      filter: {
        ...filter,
        ...(lastNoteValue !== null ? { value: lastNoteValue } : {}),
        ...(kind !== null
          ? {
              /*
               * ⚠️ **Options are ALWAYS supplied — even empty, even while the
               * master is loading — so the hook's accumulated list can never
               * apply.** `ColumnSpec.options` replaces accumulation entirely,
               * which is what this needs: `get` returns a country NAME, a source
               * NAME, a type NAME and a status ID, and an accumulated name written
               * into the URL as an id is a 422 naming the key.
               *
               * A retired row is LABELLED, never hidden: reads do not filter on
               * `isActive`, so rows can sit on a retired value and dropping it
               * from the list would leave them unreachable by any filter.
               */
              options: withSelectedValues(
                master.rows.map((row) => ({
                  value: String(row.id),
                  label: row.isActive ? row.name : `${row.name} (retired)`,
                })),
                chosen.map(String),
              ),
              loading: master.loading,
              /*
               * ⚠️ **Disabled only when the master could not be READ and nothing
               * is chosen.** A card already narrowed must stay clearable, or the
               * reader is left with a short table, a heading that does not say
               * why, and no way back. "We could not read the countries" and
               * "there are no countries" are different facts and only a disabled
               * control with a reason distinguishes them.
               */
              disabled: master.failed && chosen.length === 0,
              value: { selected: chosen.map(String), emptiness: filter.value.emptiness },
              /*
               * 🔴 **The status funnel tells the truth about WHERE it ran.** One
               * tick goes to the server; two or more cannot (`?stageId=1,2` is a
               * 422 on these three endpoints) so the card narrows the rows it
               * has. Saying nothing would leave a reader believing a
               * hundred-row window is the whole account list.
               */
              ...(isStatus && statusPageFiltered(state.filter)
                ? {
                    scopeNote: `Filtering the ${state.rows.length} rows loaded — this list takes one status at a time.`,
                  }
                : {}),
            }
          : {}),
        /*
         * 🔴 **THE TWO NEW FUNNELS SAY WHERE THEY RAN, in the same voice the
         * status funnel above uses — 2026-09-13.** Both narrow the loaded rows
         * and neither can do anything else: `?projectOfInterestId=` is refused on
         * all four endpoints and `?hasOpenTask=` is a 422. The card fetches
         * `PER_PAGE` rows and its `total` can be larger — the quotes card was
         * measured at **137 records with 100 loaded** — so a reader ticking a
         * value here is looking at a window, not at the account list.
         *
         * ⛔ **THIS IS CURRENTLY NOT RENDERED, AND THAT IS NOT A BUG TO FIX
         * HERE.** `ColumnFilter` stopped drawing its scope footnote on 2026-09-05
         * under the owner's helper-text ruling; `scope`, `pageRowCount` and
         * `scopeNote` are all still accepted and deliberately unread, and that
         * component says in terms not to delete the props to chase the dead
         * render. **So this is passed for the same reason the status note beside
         * it is** — restoring the note later must be a render change in one
         * component, never a contract change across every caller. ⚠️ **Do not
         * "make it visible" by putting a sentence next to the control**: that
         * reverses an owner ruling from a call site.
         *
         * ⚠️ **What carries the limitation on screen instead is the COUNTS** —
         * both funnels' options are built from the loaded rows and show how many
         * would survive, which is why `openTaskFilterOptions` offers a zero
         * rather than dropping the option.
         */
        ...(key === 'projectOfInterest'
          ? {
              scopeNote: `Filtering the ${state.rows.length} rows loaded — no list here filters on a company's projects.`,
            }
          : {}),
        /*
         * ⚠️ **ITS OWN SENTENCE, naming ITS OWN field — 2026-09-14.** The
         * document cards' projects column is a different column over a different
         * list, and a note explaining the limit of a filter must not describe
         * the other one. **Neither has a server filter**: `?projectOfInterestId=`
         * is refused on all four endpoints, and no list behind these three takes
         * a line-project filter of this shape — ⚠️ `?lineProjectId=` on `/quotes`
         * and `/invoices` narrows which DOCUMENTS come back, which is a different
         * question from what each one names. So both stay page-scoped.
         */
        ...(key === 'lineProjects'
          ? {
              scopeNote: `Filtering the ${state.rows.length} rows loaded — no list here filters on a document's line projects.`,
            }
          : {}),
        ...(key === 'upcomingTask'
          ? {
              scopeNote: `Filtering the ${state.rows.length} rows loaded — no list here filters on whether a task is open.`,
            }
          : {}),
        ...(key === 'creditBalance'
          ? {
              scopeNote: `Filtering the ${state.rows.length} rows loaded — no list here filters on credit held.`,
            }
          : {}),
        ...(key === 'quoteSigned'
          ? { scopeNote: `Filtering the ${state.rows.length} rows loaded.` }
          : {}),
        /*
         * ⚠️ *SUPERSEDED 2026-10-09:* `productsPurchased` and
         * `projectsParticipated` carried a "Filtering the N rows loaded — no list
         * here filters on …" note. Both filter by cycle on the server now, so
         * the note would be false and is gone.
         */
        onChange: (next: ColumnFilterState) => {
          /*
           * ⚠️ **`kind !== null` is what NARROWS it for `onFilterChange`, not just
           * a gate.** The old three-boolean test left `kind` a plain
           * `PartnershipFilterKind` that was `status` by fall-through, so this call
           * would have filed a company-type selection under the card's status
           * param. Now the one resolved value is both the guard and the argument.
           */
          if (kind !== null) {
            const ids = next.selected
              .map(Number)
              .filter((id) => Number.isSafeInteger(id) && id > 0);
            onFilterChange(state.key, kind, ids);
            // The hook keeps only the emptiness half, so it can never emit a
            // competing value alongside the one `load` sends.
            filter.onChange({ selected: [], emptiness: next.emptiness });
            return;
          }
          if (isLastNoteRange) {
            /*
             * `rangeBounds` is the one gate between a bound box and a request:
             * an incomplete or impossible date yields `null`, so only a sendable
             * bound reaches the URL. Written only when it MOVED, so typing into a
             * box that has not yet formed a date does not rewrite the URL.
             */
            const bounds = rangeBounds(next.range, LAST_NOTE_RANGE);
            if (
              bounds.min !== state.filter.lastNoteAfter ||
              bounds.max !== state.filter.lastNoteBefore
            ) {
              onLastNoteRangeChange(state.key, bounds.min, bounds.max);
            }
          }
          filter.onChange(next);
        },
        ...(sortable && pair !== undefined
          ? {
              sort: {
                direction,
                onSort: (next: 'asc' | 'desc') => changeSort(next === 'asc' ? pair.asc : pair.desc),
              },
            }
          : {}),
      },
    };
  };

  /*
   * The money value column (deals, quotes, invoices) is right-aligned with its
   * cells, so figures line up on the decimal point. The company cards' value
   * column is a STAGE, told apart by `VALUE_COLUMN_KIND`, and stays left.
   * Alignment only — no width moves.
   */
  const headerFor = (key: string): FilterableTHProps => {
    const props = filterHeaderFor(key);
    return key === VALUE_KEY && VALUE_COLUMN_KIND[state.key] === 'money'
      ? { ...props, numeric: true }
      : props;
  };

  /*
   * ⚠️ ONE way out of this card for an ordering change, taken by the dropdown
   * and by every clickable column heading. Both write the same `?<card>Sort=`
   * param, so the caret and the selected option are two renderings of one value
   * and cannot drift apart.
   */
  const changeSort = (sort: PartnershipSort) => onSortChange(state.key, sort);

  /*
   * ⚠️ **Both measure the RENDERED rows, not the fetched ones.** A funnel that
   * leaves three rows must leave a card three rows tall with no scroller —
   * sizing from `state.rows` would cap a three-row card at five-rows-high of
   * empty space and announce a scroll region with nothing below the fold.
   */
  const { scrollRef, maxHeight } = useFiveRowCap(
    rows.length,
    /*
     * ⚠️ **The column arrangement AND the edit nonce.** Both change a row's
     * height without necessarily changing the TABLE's, which is the one case the
     * `ResizeObserver` inside the hook cannot see — see `editNonce` above.
     */
    `${visibleKeys.join('|')}#${editNonce}`,
  );
  const scrolls = rows.length > VISIBLE_ROWS;
  /*
   * Whether the last row is already in view. The fade below is a claim that
   * there is more underneath it, so it has to stop being made once there is
   * not — a cue that never goes away stops being read as a cue.
   */
  const [atEnd, setAtEnd] = useState(false);
  /*
   * ⚠️ A card only takes the wheel once you have clicked into it.
   *
   * Five scrollable cards on one page means the wheel keeps landing on one of
   * them, and the PAGE stops moving — you aim for the page and a card eats the
   * gesture. So at rest a card does not scroll at all and the wheel belongs to
   * the document; clicking or tabbing into one hands it the wheel, and clicking
   * anywhere else hands it back.
   */
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!active) return;
    const release = (event: MouseEvent | FocusEvent) => {
      /*
       * The card is found FROM the scroller rather than held in a second ref:
       * `Panel` renders a `<section>` and spreads its props, but does not
       * forward a ref, and widening a primitive used in twenty-odd places to
       * save one lookup here is not the trade.
       */
      const panel = scrollRef.current?.closest('section') ?? null;
      const target = event.target;
      if (!panel || !(target instanceof Node) || panel.contains(target)) return;
      setActive(false);
      /*
       * Returned to the top on release, so a card at rest always shows the
       * same five rows. Left mid-scroll it would sit showing rows 2 to 6 with
       * no scrollbar moving, which reads as a rendering fault rather than as a
       * position somebody chose.
       */
      const scroller = scrollRef.current;
      if (scroller) scroller.scrollTop = 0;
      setAtEnd(false);
    };
    document.addEventListener('mousedown', release, true);
    document.addEventListener('focusin', release, true);
    return () => {
      document.removeEventListener('mousedown', release, true);
      document.removeEventListener('focusin', release, true);
    };
  }, [active, scrollRef]);
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget;
    // A pixel of slack: fractional row heights mean the arithmetic rarely
    // lands exactly, and a fade flickering at the very bottom is worse than
    // one that clears a moment early.
    setAtEnd(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  };

  return (
    <Panel
      className="overflow-hidden"
      onMouseDown={scrolls ? () => setActive(true) : undefined}
      onFocusCapture={scrolls ? () => setActive(true) : undefined}
    >
      <PanelHeader
        id={headingId}
        title={state.title}
        actions={
          <div className="flex items-center gap-3">
            {!state.loading && !state.error && (
              <span className="tabular text-xs text-fg-muted">
                {pluralise(state.total, 'record')}
              </span>
            )}
            {/*
              * ⚠️ **A react-router `<Link>`, NOT `<Button href>`.** `Button`'s
              * anchor form renders a plain `<a href>`, which for a route inside
              * this SPA is a full document load: the whole bundle re-boots and
              * `/auth/me` runs again, to reach a page React Router could have
              * rendered instantly. A `<Button onClick={navigate}>` would fix
              * that and lose middle-click and open-in-new-tab, which on a "show
              * me the rest of these" affordance is the wrong half to give up.
              * `TextLink` is the house inline link (a `<Link>` underneath).
              *
              * ⚠️ **UNCONDITIONAL — it renders while the card is loading and
              * while it has failed.** Both are exactly when somebody wants the
              * full list: a card that could not load is a reason to go to the
              * module, not a reason to hide the way there.
              *
              * ⚠️ **The link is SCOPED to the same person the card is** (see
              * `listHref`), so the list it opens shows the same records. The
              * Companies card is the one exception and its counts will not
              * agree — its own filter excludes leads and that filter is derived
              * per fetch, so there is nothing to put in a URL; `listHref`
              * records why rather than inventing a second copy of it.
              */}
            <TextLink
              to={state.listHref}
              className="text-xs whitespace-nowrap"
              aria-label={`Show all ${lowerTitle}`}
            >
              Show all
            </TextLink>
          </div>
        }
      />

      {/*
        * ⚠️ Its OWN row, not `PanelHeader`'s `actions` slot. `actions` is
        * `shrink-0`, so a 160px select plus the record count eats into the
        * title, and "Companies (excluding leads)" is the only thing
        * distinguishing that card from the Leads card — the one element that
        * must not be sacrificed. This is the same filter-row idiom `FunnelPanel`
        * above already uses, and it wraps.
        *
        * ⚠️ **THE MEASUREMENT THIS RESTED ON HAS MOVED AND THE ROW HAS NOT —
        * read this before "tidying" the control back into `actions`.**
        * *SUPERSEDED 2026-09-03: "at the `lg` two-column step — where each card
        * is ~350px wide — … leaves the title about 80px and truncates it to
        * 'Companies (exc…'."* The cards are full width now, so at `lg` that
        * particular squeeze no longer happens and the old justification reads as
        * spent. **It is not.** The narrow case simply moved down the
        * breakpoints: below `sm` the card is the viewport, and a `shrink-0`
        * select beside a `shrink-0` count is what truncates the title there
        * instead. Right answer, superseded reason — which is exactly the shape
        * that gets a working control changed because its comment no longer
        * matches the width somebody measured at.
        */}
      {/*
        * 🔴 **THE FILTER `<select>` IS GONE FROM THIS ROW — owner's instruction,
        * 2026-09-12.** It now lives in the column's own heading, as a funnel, the
        * way every list page in this app already works. *SUPERSEDED: "**The
        * filters sit in the SAME row as the ordering, not in a second one.** They
        * are the same kind of thing — controls over what this card shows … **A
        * card renders only the filters its ENDPOINT declares** (see
        * `filterKinds`), so this is a map rather than two fixed slots".*
        *
        * ⚠️ **The `filterKinds` half of that reasoning did NOT go with the
        * control — it moved into `columns`**, where `scope: 'server'` is attached
        * only for a kind this card's endpoint really takes. A funnel drawn over a
        * key `load` does not send would appear to narrow and would not, which is
        * the failure that rule exists to prevent, and it is unchanged.
        *
        * ⚠️ **The ordering dropdown STAYS, and it is not redundant beside the
        * funnels.** Two of its options — "Recent activity" and "Newest first" —
        * are orderings of the whole card with no column to hang off, so removing
        * it would take away the card's default ordering and leave no way back to
        * it. Both write the same `?<card>Sort=` param, so the funnel's arrows and
        * the selected option are two renderings of one value.
        */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2.5">
        <SortControl state={state} onChange={changeSort} stageNameOf={stageNameOf} />
        {/*
          * ⚠️ **IN THIS ROW, NOT IN `PanelHeader`'s `actions` SLOT — and that is
          * the same correction the ordering control and the create button above
          * already record.** `actions` is `shrink-0` and already holds the record
          * count and the Show all link; a third child there squeezes the title,
          * and "Companies (excluding leads)" is the only thing distinguishing
          * that card from the Leads card. This row WRAPS, so a narrow card
          * stacks the control instead of truncating the one word that says which
          * of the five you are looking at.
          *
          * ⚠️ **One controller per card, so the trigger's "N hidden" badge is
          * about THIS card** — see `columnLayoutScope`. Five cards, five
          * arrangements, five badges.
          *
          * Rendered while the card is loading and while it has failed, for the
          * same reason `SortControl` is never disabled: arranging a card that
          * did not load is a reasonable thing to do, and a control that goes
          * inert for the first second is one the reader learns not to trust.
          */}
        {/*
          * ⚠️ **`lockReasonOf` because the shared fallback is about ROW ACTIONS
          * and this page locks its RECORD column** — see `columnLockReason`.
          *
          * 🔴 **`pinnedKeys` NAMES THE RECORD COLUMN — corrected 2026-09-13.**
          * *SUPERSEDED: "⛔ **No `pinnedKeys`, deliberately.** The actions lane is
          * the only pinned column here and it is not in the managed list, so the
          * manager never renders a row for it and has nothing to omit freeze
          * controls from."* That reasoning is untouched and is still why
          * `ACTIONS_KEY` is absent from the list — what changed is that `record`
          * now has its edge fixed by the page too, and it IS in the managed list,
          * so the manager would otherwise draw two freeze buttons over a column
          * that answers to neither. See {@link PAGE_FROZEN_COLUMN_KEYS}.
          *
          * ⚠️ **The manager still reads the CONTROLLER's `frozenSideOf`, which
          * knows nothing about the page's freeze** — and that is harmless only
          * because the controls it would drive are omitted. Restoring them
          * without also teaching the controller would put a pair of buttons on
          * screen showing the wrong state.
          */}
        <ColumnManager
          layout={layout}
          labelOf={columnLabelOf}
          lockedKeys={LOCKED_COLUMN_KEYS}
          lockReasonOf={columnLockReason}
          pinnedKeys={PAGE_FROZEN_COLUMN_KEYS}
        />
        {/* 2026-10-18 (owner): the two company cards export `companies.export`. */}
        {canExportCard && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<DownloadIcon />}
            loading={exporting}
            onClick={() => void runExport()}
          >
            Export
          </Button>
        )}
        {/*
          * 🔴 **THE CREATE BUTTON LIVES IN THIS ROW, NOT IN `PanelHeader`'s
          * `actions` SLOT — and that is a correction, not a preference.**
          *
          * `actions` is `shrink-0`. The comment above this row records that the
          * ordering control was moved OUT of it for exactly that reason: a
          * `shrink-0` control beside a `shrink-0` record count squeezes the
          * title, and below `sm` the card is the viewport. That slot already
          * holds the count and the Show all link; a third child would truncate
          * the one word that says which of the five cards you are looking at.
          * This row WRAPS, so a narrow card stacks the button instead.
          *
          * ⚠️ **`ml-auto` puts it at the trailing edge on a wide card and does
          * nothing once the row wraps**, which is the behaviour wanted at both
          * ends — a primary action reads as belonging to the card, not to the
          * filter beside it.
          *
          * ⚠️ **ABSENT, never disabled, when the reader may not create** — the
          * same shape `/quotes`, `/deals` and `/companies` use for their own New
          * buttons, whose markup this copies. Five disabled create controls on
          * one page are five dead ends.
          *
          * ⚠️ **This is NOT the button removed from the Accounts dashboard on
          * 2026-09-02, and re-adding it there is not implied.** That one lived
          * in a SHARED header and needed each section to register an opener on
          * mount — a chain whose removal is documented at length in
          * `AccountsDashboardPage`. Every card here owns its own state, so there
          * is no opener to register and nothing shared to go stale.
          */}
        {canCreate && (
          <Button
            className="ml-auto"
            variant="primary"
            size="sm"
            leadingIcon={<PlusIcon />}
            onClick={() => onCreate(state)}
          >
            {CREATE_SPEC[state.create].label}
          </Button>
        )}
      </div>

      {/* Rendered independently, not `if/else`: a failed refresh that still
        * holds previously fetched rows must show BOTH the failure and the
        * stale rows. */}
      {state.error && <LoadFailure title={lowerTitle} message={state.error} />}

      {exportError !== null && (
        <Notice
          tone="danger"
          role="alert"
          className="m-3"
          title={<>The export didn&rsquo;t download.</>}
          action={
            <Button variant="ghost" size="sm" onClick={() => setExportError(null)}>
              Dismiss
            </Button>
          }
        >
          <span className="min-w-0 [overflow-wrap:anywhere]">{exportError}</span>
        </Notice>
      )}

      {/*
        * ⚠️ **A WARNING, not a failure, and the tone is the claim.** The write
        * LANDED — the record in EMERGE is correct — and only the push to Xero did
        * not, so a danger-toned block reading like `LoadFailure` above would tell
        * the reader their edit was lost. "Nothing was supposed to happen" and
        * "something failed to happen" must never share a sentence, and neither
        * must "it saved" and "it did not".
        */}
      {writeNotice !== null && (
        <Notice
          tone="warning"
          role="alert"
          className="m-3"
          /*
            * ⚠️ **Dismissible, because nothing else clears it.** The panel
            * survives the refetch that follows the write, so without this the
            * message would outlive the situation it describes.
            */
          action={
            <Button variant="ghost" size="sm" onClick={() => setWriteNotice(null)}>
              Dismiss
            </Button>
          }
        >
          <span className="min-w-0 [overflow-wrap:anywhere]">{writeNotice}</span>
        </Notice>
      )}

      <div aria-busy={state.loading || undefined}>
        {state.loading && !hasRows ? (
          <TableWrap>
            {/* Same layout as the loaded table below, so the card does not
              * re-flow its columns the moment rows arrive — which is also why
              * the minimum width is applied here and not only there. */}
            <Table className="w-full table-fixed" style={minTableWidthStyle(minWidthRem)}>
              <caption className="sr-only">Loading {lowerTitle}.</caption>
              {/*
                * ⚠️ **`table-fixed` PLUS a colgroup, and both are load-bearing.**
                * This table used a 520px minimum, wider than the card it sat in
                * — so the action cell sat past the right edge and only appeared
                * if you swiped the card sideways. A control you have to discover
                * by swiping is not a control.
                *
                * With the widths declared, the record column takes whatever is
                * left and its text truncates instead of pushing the others out
                * of view. `auto` layout cannot do that: it sizes columns to
                * their CONTENT, so one long company name widens the table and
                * re-creates the overflow.
                *
                * 🔴 **FOUR COLUMNS ON THREE CARDS AND SIX ON THE OTHER TWO —
                * owner's instruction, 2026-09-03, twice in one day.**
                * ⚠️ *SUPERSEDED, earlier the same day: "**THREE COLUMNS ON
                * THREE CARDS AND FIVE ON THE OTHER TWO**"* — the "Your last
                * note" column then landed on all five, so the three that had
                * no extra column at all now have one. ⚠️ *SUPERSEDED: "**STILL
                * THREE COLUMNS after the status badge landed**, and that was the
                * decision rather than an omission … At 320px there is no room
                * for a fourth column here without either crushing the record
                * column or re-creating exactly the overflow described above."*
                *
                * **That arithmetic has not been overturned and the swiping
                * defect above is not being re-introduced blind.** The quotes and
                * invoices cards genuinely do not fit at 320px, so they declare a
                * minimum width and scroll sideways below it — and their action
                * column is PINNED, which is what makes the cell the old note
                * called unreachable stay reachable. See `tableMinWidthRem` and
                * `pinActions`. ⚠️ *SUPERSEDED: "The three cards with no extra
                * columns are untouched: no minimum, nothing to scroll, nothing
                * pinned."* There are none left — every card declares a minimum
                * and pins its action cell, which is what makes a note column
                * affordable on the two that were already at five.
                *
                * The widths themselves are in `COLUMN_REM` and are rendered by
                * `PartnershipColgroup` — one component for this table and the
                * loaded one, because two copies that must agree is precisely
                * what drifts.
                */}
              <PartnershipColgroup
                columnKeys={visibleKeys}
                actionButtons={actionButtons}
                actionExtraRem={actionExtraRem}
              />
              {/*
                * ⚠️ The headings are LIVE on the skeleton too, and deliberately.
                * Re-ordering a card that is still loading is a reasonable thing
                * to do — the request in flight is aborted and replaced — and a
                * header that is inert for the first second is a control the
                * reader learns not to trust. Same rule as `SortControl`.
                */}
              <PartnershipHead
                columnKeys={visibleKeys}
                headerFor={headerFor}
                pinActions={pinActions}
                headRowRef={headRowRef}
                frozenSideOf={frozenSideOf}
                frozenOffsets={frozenOffsets}
              />
              <RowsSkeleton
                columnKeys={visibleKeys}
                pinActions={pinActions}
                frozenSideOf={frozenSideOf}
                frozenOffsets={frozenOffsets}
              />
            </Table>
          </TableWrap>
        ) : hasVisibleRows ? (
          /*
           * ⚠️ A plain div and not `TableWrap`, which is the same element but
           * takes only `className` — no ref to measure through, no `tabIndex`,
           * no `aria-label`, no `style`. The house rule is to fix the primitive
           * rather than work around it at the call site; widening `TableWrap`
           * is the right change and is out of this file's scope, so this is a
           * knowing exception rather than an oversight. Its `w-full
           * overflow-x-auto` is reproduced verbatim below.
           *
           * ⚠️ The scrollbar is VISIBLE. It was hidden first, on the
           * instruction that a card should not carry one — and a clipped list
           * with no scrollbar is indistinguishable from a list that ended, so
           * the same card was reported as broken three times. A fade was added
           * and did not settle it either. The chrome is the affordance people
           * look for; the fade stays as a second, softer hint.
           *
           * `overscroll-contain` stops a flick at the end of the list from
           * carrying on into the page behind the card.
           *
           * ⚠️ A scroll region that cannot be focused hides its rows from
           * anyone not using a mouse, so it takes `tabIndex={0}` and an
           * accessible name — a keyboard reader tabs to it and arrows down the
           * rows below the fold. Both are conditional on there BEING rows below
           * the fold: an announced landmark and a tab stop on a container that
           * cannot scroll is a promise of content that is already all visible.
           */
          <div className="relative">
          <div
            ref={scrollRef}
            onScroll={scrolls ? onScroll : undefined}
            className={cx(
              'w-full overflow-x-auto',
              // Only an ACTIVE card scrolls; at rest the wheel is the page's.
              scrolls && (active ? 'overflow-y-auto overscroll-contain' : 'overflow-y-hidden'),
              // The panel clips, so the global focus outline is drawn inside
              // the scroller rather than 1px outside it, where its left and
              // right edges would be cut off.
              'focus-visible:[outline-offset:-2px]',
            )}
            {...(maxHeight === null ? {} : { style: { maxHeight } })}
            {...(scrolls
              ? { tabIndex: 0, role: 'region', 'aria-label': `${state.title}, scrollable` }
              : {})}
          >
            <Table className="w-full table-fixed" style={minTableWidthStyle(minWidthRem)}>
              <caption className="sr-only">
                {state.title}
                {scrolls ? ` — ${VISIBLE_ROWS} of ${rows.length} rows in view.` : ''}
              </caption>
              {/*
                * ⚠️ **`table-fixed` PLUS a colgroup, and both are load-bearing.**
                * This table used a 520px minimum, wider than the card it sat in
                * — so the action cell sat past the right edge and only appeared
                * if you swiped the card sideways. A control you have to discover
                * by swiping is not a control.
                *
                * With the widths declared, the record column takes whatever is
                * left and its text truncates instead of pushing the others out
                * of view. `auto` layout cannot do that: it sizes columns to
                * their CONTENT, so one long company name widens the table and
                * re-creates the overflow.
                *
                * 🔴 **FOUR COLUMNS ON THREE CARDS AND SIX ON THE OTHER TWO —
                * owner's instruction, 2026-09-03, twice in one day.**
                * ⚠️ *SUPERSEDED, earlier the same day: "**THREE COLUMNS ON
                * THREE CARDS AND FIVE ON THE OTHER TWO**"* — the "Your last
                * note" column then landed on all five, so the three that had
                * no extra column at all now have one. ⚠️ *SUPERSEDED: "**STILL
                * THREE COLUMNS after the status badge landed**, and that was the
                * decision rather than an omission … At 320px there is no room
                * for a fourth column here without either crushing the record
                * column or re-creating exactly the overflow described above."*
                *
                * **That arithmetic has not been overturned and the swiping
                * defect above is not being re-introduced blind.** The quotes and
                * invoices cards genuinely do not fit at 320px, so they declare a
                * minimum width and scroll sideways below it — and their action
                * column is PINNED, which is what makes the cell the old note
                * called unreachable stay reachable. See `tableMinWidthRem` and
                * `pinActions`. ⚠️ *SUPERSEDED: "The three cards with no extra
                * columns are untouched: no minimum, nothing to scroll, nothing
                * pinned."* There are none left — every card declares a minimum
                * and pins its action cell, which is what makes a note column
                * affordable on the two that were already at five.
                *
                * The widths themselves are in `COLUMN_REM` and are rendered by
                * `PartnershipColgroup` — one component for this table and the
                * loaded one, because two copies that must agree is precisely
                * what drifts.
                */}
              <PartnershipColgroup
                columnKeys={visibleKeys}
                actionButtons={actionButtons}
                actionExtraRem={actionExtraRem}
              />
              {/* Sticky only when there is something to scroll past — see
                * `STICKY_HEAD`. */}
              <PartnershipHead
                columnKeys={visibleKeys}
                headerFor={headerFor}
                pinActions={pinActions}
                sticky={scrolls}
                headRowRef={headRowRef}
                frozenSideOf={frozenSideOf}
                frozenOffsets={frozenOffsets}
              />
              <TBody>
                {rows.map((row) => (
                  <TR key={row.id} interactive>
                    {/* ⚠️ **The record cell and the value cell are INSIDE this
                      * component now.** They were hard-coded here, first and
                      * second-last, which is a fact about the markup and not
                      * about the column list — so a reader who moved either one
                      * would have moved its HEADING and left its cell where it
                      * was. See `RowCells`. */}
                    <RowCells
                      row={row}
                      /*
                       * ⚠️ **The card's own key, because three of the cells'
                       * decisions are properties of the CARD and not of the
                       * row** — which master a status picker writes, whether the
                       * value column is a stage or money, and which company a
                       * company-owned column belongs to. See `RowCells`.
                       */
                      tableKey={state.key}
                      columnKeys={visibleKeys}
                      masters={masters}
                      /*
                       * The SAME master the funnel above reads, with its load
                       * state — so the picker in a cell and the filter in its
                       * heading can never offer different lists.
                       */
                      statusMaster={statusMaster}
                      signatures={signatures}
                      valueLabel={state.valueLabel}
                      editing={editing}
                      frozenSideOf={frozenSideOf}
                      frozenOffsets={frozenOffsets}
                      onOpenActivity={onOpenActivity}
                      onOpenDocumentProjects={onOpenDocumentProjects}
                      purchaseHighlights={purchaseHighlights}
                      stageStatusColumns={stageStatusColumnsByKey}
                      stageStatusWrites={stageStatusWrites}
                    />
                    {/*
                      * The control sits in its OWN cell, a sibling of the link
                      * rather than a wrapper around it — read from the markup,
                      * not assumed: `TR interactive` styles a hover and carries
                      * no click handler, and the only link is inside
                      * `RowIdentity`. So there is no row-level target for a
                      * click here to swallow and nothing to stop propagating.
                      *
                      * Ungated: the task and note routers carry no role guard,
                      * so this is offered to everyone the row is offered to.
                      *
                      * ⚠️ **PINNED on the cards that scroll sideways, and only
                      * those.** `TD`'s `pinned` is `sticky right-0`, so it can
                      * only ever hold the LAST column — which this is, and which
                      * is exactly why the extra columns could be added at all.
                      * Without it a card narrower than its own table leaves this
                      * button off the right edge, reachable only by swiping the
                      * table sideways, which is a defect this page has already
                      * had reported once.
                      */}
                    <TD className="w-px" pinned={pinActions}>
                      {/*
                        * ⚠️ **The "+" and the contacts button sit BESIDE the
                        * activity button, not instead of it**, and all three
                        * stay in the one pinned cell rather than taking a
                        * column each — a second action column would be a second
                        * thing for `tableMinWidthRem` to reserve and only the
                        * LAST column can be pinned (`TD`'s `pinned` is `sticky
                        * right-0`), so the activity button would have gone back
                        * off the right edge, which is a defect this page has
                        * had reported.
                        *
                        * ⚠️ *SUPERSEDED 2026-09-17: "**The "+" sits BESIDE the
                        * activity button**, and BOTH stay in the one pinned
                        * cell".* The mail button made it three on two of the
                        * five cards; the reasoning is unchanged and is now
                        * load-bearing for one more control.
                        *
                        * `justify-end` so the group hangs off the right edge of
                        * the cell exactly as the single button did, and the
                        * rows below a card with fewer controls do not read as
                        * misaligned against one that has more.
                        */}
                      <div className="flex items-center justify-end gap-1">
                        {/*
                          * ⚠️ **FIRST, so that nothing already here moves.** The
                          * group is `justify-end` in a cell that grew by exactly
                          * one button — so a control added at the LEFT leaves the
                          * "+" and the activity button at the same distance from
                          * the right edge they have always had, on the two cards
                          * that gain this and on the three that do not. Added
                          * last, or in the middle, it would shift the activity
                          * button on two cards of five and the rows would read as
                          * misaligned against each other.
                          *
                          * ⚠️ **`onOpenContacts &&`, not a card-key test.** Which
                          * cards carry this is the PAGE's decision (see
                          * `CONTACTS_CARD_KEYS`), and it is the same prop
                          * `actionButtons` counted — so the button and the room
                          * reserved for it cannot disagree.
                          */}
                        {onOpenContacts && (
                          <RowContactsButton
                            row={row}
                            tableKey={state.key}
                            onOpen={onOpenContacts}
                          />
                        )}
                        {/*
                          * The quote-PDF control (2026-10-25), left of the "+"
                          * for the same reason the contacts button is first:
                          * nothing already here moves. Counted by `actionButtons`.
                          */}
                        {pdfPreview && <RowPdfPreview pdfPreview={pdfPreview} row={row} />}
                        {rowCreate && <RowCreateMenu rowCreate={rowCreate} row={row} />}
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          leadingIcon={<ActivityIcon />}
                          aria-label={`Tasks and notes for ${recordLabelOf(row)}`}
                          title={`Tasks and notes for ${recordLabelOf(row)}`}
                          onClick={() => onOpenActivity(row)}
                        />
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
          {/*
            * ⚠️ The only sign that a sixth row exists.
            *
            * The scrollbar is hidden by request, so a card holding six records
            * draws five and looks like a list that simply ends — the mechanism
            * works and nobody can tell. This fades the last visible row into
            * the card, which reads as "continues below" without adding chrome.
            *
            * `pointer-events-none` is load-bearing: it sits ON TOP of the
            * bottom row, and without it the last row's link and its task
            * button would be unclickable along that strip.
            *
            * `aria-hidden` because it is decoration — the scroll region is
            * already named and announced, and the caption states how many rows
            * of how many are in view.
            */}
          {scrolls && !atEnd && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface to-transparent"
            />
          )}
          </div>
        ) : state.error ? null : (
          /*
           * 🔴 **A CARD EMPTIED BY ITS OWN FUNNELS MUST NOT SAY THE ACCOUNT HAS
           * NOTHING ON IT.** Before the funnels the only way to reach this state
           * was genuinely having no records, so one sentence covered it. Now a
           * reader can hide every row from the header and meet "No leads on your
           * accounts" — which is false, and reads as data having vanished rather
           * than as a filter they set two clicks ago.
           *
           * ⚠️ **This is the documented exception to the no-helper-text rule, not
           * a carve-out from it**: it names a state the reader cannot otherwise
           * see (an `EmptyState` description is required to say what to do next)
           * and it gives the way back rather than advice.
           */
          <EmptyState
            icon={<InboxIcon className="h-4 w-4" />}
            title={
              emptiedByFilters
                ? `No ${lowerTitle} match the filters on this card`
                : scopedToOthers
                  ? `No ${lowerTitle} on these accounts`
                  : EMPTY_TITLE[state.scopedBy](lowerTitle)
            }
            {...(emptiedByFilters
              ? { description: 'Clear a column filter from its heading to see the rest.' }
              : {})}
          />
        )}
      </div>

      {/*
        * ⚠️ **THE ONE THING SCROLLING CANNOT TELL YOU: that the scroll ENDS
        * BEFORE THE DATA DOES.**
        *
        * `PER_PAGE` is 100 — the endpoints' own ceiling — and there is no
        * second-page fetch, so a card whose `total` is larger holds a prefix.
        * Everything else on the card describes it perfectly and none of it says
        * so: the header reads "340 records", the rows scroll, the fade clears at
        * the bottom, and somebody who has just scrolled to the end of a hundred
        * rows has every reason to believe they have seen all three hundred and
        * forty. That is the shape this codebase keeps recording — a surface that
        * renders identically whether it is complete or truncated.
        *
        * ⚠️ **This is NOT helper text and a sweep must not delete it.** It states
        * a fact about the DATA that the reader cannot otherwise see, which is the
        * documented exception — the same standing as a `Combobox` footnote
        * reading "Showing the first 200 of 400". It gives no advice and explains
        * no control.
        *
        * Both figures come from the response: `rows.length` is what was given,
        * `total` is the server's own count for the whole filtered table. Neither
        * is derived from `PER_PAGE`, so a page size that changes on either side
        * cannot make this sentence wrong.
        */}
      {!state.loading && !state.error && state.total > state.rows.length && (
        <p className="m-0 border-t border-border px-4 py-2 text-xs">
          <TextLink to={state.listHref}>Show all</TextLink>
        </p>
      )}
    </Panel>
  );
}

/*
 * The header stays put while the rows move under it — a scrolled table whose
 * header has left is a column of figures with nothing saying what they are.
 *
 * ⚠️ The rule under it is an INSET SHADOW, not a border. `Table` is
 * `border-collapse: collapse`, where a border is painted at the grid line and
 * belongs to the table rather than to the row, so `TR`'s own `border-b` stays
 * behind while the header travels and the header scrolls with no edge under
 * it. Same mechanism, and the same reason, as `Table`'s pinned cells; the
 * hairline is `var(--hairline)` and not `var(--color-border)` because the
 * `--color-*` names live in `@theme inline` and are not emitted at runtime.
 *
 * 🔴 **`z-20`, RAISED FROM `z-10` ON 2026-09-03, AND THE OLD VALUE WAS A REAL
 * COLLISION THE MOMENT THE ACTION COLUMN WAS PINNED.**
 *
 * ⚠️ *SUPERSEDED: "`z-10` — one rung below `z-20`, which is the ceiling for page
 * content and belongs to `Table`'s pinned header cell. Equal z-index is settled
 * by document order, so a header sharing the pinned cell's number would be a
 * coin toss."*
 *
 * That reasoning was right about the mechanism and was written when nothing on
 * this page was pinned. `TD`'s `pinned` is `sticky right-0 z-10` — so a pinned
 * BODY cell and this header were on the SAME rung, and `<tbody>` comes after
 * `<thead>` in document order, which is the half of the old note that decides
 * it: the action cell would have painted straight over the header as the rows
 * scrolled under it. Exactly the failure `src/index.css`'s ladder records —
 * "equal z-index is not a tie the later element loses, it is a tie the later
 * element WINS" — and it is silent: the cell is present, correctly positioned,
 * carrying the z-index it was given, and drawn in the wrong place.
 *
 * ⚠️ **`z-20` is the CEILING for page content, not a spare rung, and this is
 * still inside it.** App chrome starts at 30. The pinned header cell's own
 * `z-20` is unaffected: it is a DESCENDANT of this element, which is `sticky`
 * with a `z-index` and therefore a stacking context, so that number orders it
 * against its sibling `<th>`s and never against anything outside the header.
 */
const STICKY_HEAD = 'sticky top-0 z-20 shadow-[inset_0_-1px_0_0_var(--hairline)]';

/*
 * ⚠️ The second column's heading comes from `state.valueLabel` and is never
 * written here. The cell under it holds a different KIND of thing per table —
 * money on deals, quotes and invoices, a stage name on companies and leads —
 * and a heading hard-coded in this file would typecheck and render perfectly
 * while calling a stage a value.
 *
 * The third column carries the row's tasks-and-notes control and is
 * deliberately unlabelled: `TH`'s empty cell would announce as a blank column
 * header, so the name is on each button instead.
 */
/**
 * The two orderings one clickable header toggles between.
 *
 * ⚠️ **A header is a shortcut into the card's EXISTING ordering state, not a
 * second one.** Clicking one writes the same `?<card>Sort=` param the dropdown
 * writes, so the two controls cannot disagree, a click survives a reload, and
 * there is nothing to keep in step. The alternative — a local sort state on the
 * table — would be a second authority beside the URL, which this codebase has
 * been bitten by before.
 *
 * ⛔ **`record` maps to `az`/`za`, and the COLUMN each endpoint sorts is NOT
 * decided here.** It is `name` on `/companies`, `dealName` on `/deals` and
 * `subject` on the other two, written down once in `AZ_SORT_KEY` — see
 * `use-partnership-tables.ts`. This file names orderings; that file names
 * columns.
 */
const HEADER_SORTS = {
  record: { asc: 'az', desc: 'za' },
  status: { asc: 'status-asc', desc: 'status-desc' },
  /*
   * ⚠️ **Offered by the two `/companies` cards ALONE** — `SortHeader` renders a
   * plain heading unless the card's own `sortOptions` carries the pair, so the
   * three document cards get an inert heading here without a branch on the card
   * key. That derivation is what stops the header and the dropdown offering
   * different things.
   */
  country: { asc: 'country-asc', desc: 'country-desc' },
  /*
   * 🔴 **THE SOURCE PAIR WAS OWED, NOT NEW — wired 2026-09-14.** `?sort=source`
   * has been a member of `CompanyListSort` since the source column shipped, and
   * this dashboard drew the funnel and never the caret: a capability live at both
   * ends and missing in the middle, for a day, with nothing able to fail on it.
   * **Six separate maps had to gain an entry for that header to sort**, and not
   * one of them is typechecked against another — which is why `SORTABLE_COLUMN`
   * below stopped being `Partial` in the same change.
   */
  source: { asc: 'source-asc', desc: 'source-desc' },
  /* The company-type pair, 2026-09-14, shipped with its column. */
  companyType: { asc: 'company-type-asc', desc: 'company-type-desc' },
  /*
   * 🔴 **THE THREE PAIRS BELOW SHIPPED 2026-09-13 — owner's instruction — AND
   * THE ENDPOINTS COULD NOT EXPRESS ANY OF THEM THE DAY BEFORE.** `/deals` and
   * `/quotes` gained `amount` and `openTask`; `/invoices` gained `amount`,
   * `dueDate` and `openTask`; `/companies` gained `openTask`.
   *
   * ⛔ **NOTHING HERE DECIDES WHICH CARD MAY OFFER ONE.** That is
   * `state.sortOptions`, read in `headerFor`, and it is the whole of the safety:
   * a pair named here that a card's own list does not carry draws no sort UI and
   * claims no `aria-sort`. **Do not add a second guard beside the column** — two
   * mechanisms deciding one thing is how a funnel and a dropdown come to offer
   * different orderings.
   */
  value: { asc: 'value-asc', desc: 'value-desc' },
  overdue: { asc: 'overdue-asc', desc: 'overdue-desc' },
  task: { asc: 'task-asc', desc: 'task-desc' },
  /* The account's last note by date, 2026-10-02 — the two company cards. */
  lastNote: { asc: 'last-note-asc', desc: 'last-note-desc' },
  /*
   * 2026-10-09 (backend `32057ed`). `stage` is the Companies card's Value column
   * — a company STAGE, so it cannot reuse `value`, which is `amount` on the
   * document cards; `sortPairFor` picks it through `VALUE_COLUMN_KIND`. The
   * four set columns order by their COUNT of distinct members.
   */
  stage: { asc: 'stage-asc', desc: 'stage-desc' },
  interestCount: { asc: 'interest-count-asc', desc: 'interest-count-desc' },
  participatedCount: { asc: 'participated-count-asc', desc: 'participated-count-desc' },
  purchasedCount: { asc: 'purchased-count-asc', desc: 'purchased-count-desc' },
  lineProjects: { asc: 'line-projects-asc', desc: 'line-projects-desc' },
  /* 2026-10-27 — the Deals card's "Quote signed" column, by signed date. */
  quoteSigned: { asc: 'quote-signed-asc', desc: 'quote-signed-desc' },
  /* 2026-10-27 — `?sort=accountLead` on all four lists, by lead name, no lead last. */
  accountLead: { asc: 'account-lead-asc', desc: 'account-lead-desc' },
} as const satisfies Record<string, { asc: PartnershipSort; desc: PartnershipSort }>;

/**
 * Every key that can appear as a column heading and is allowed to name a sort
 * pair — ⚠️ **NOT just the "extra" ones.** *SUPERSEDED 2026-09-13: "Which extra
 * columns can be sorted at all — and it is THREE SHORT OF ALL OF THEM,
 * deliberately."* `record` used to be a ternary at the call site and `value`
 * could not be expressed at all, because both are column KEYS that are not
 * members of {@link PartnershipColumn}. Both are in the table now, so the one
 * question "can this heading sort, and by what" has exactly one place to look.
 *
 * 🔴 **VALUE, OVERDUE AND OPEN TASK JOINED IT ON 2026-09-13 — owner's
 * instruction — and each rests on a backend change of the same day**, not on a
 * re-reading of what was already there.
 *
 * ⛔ **`value` IS THE TRAP ON THIS PAGE AND THE GUARD IS NOT HERE.** The two
 * `/companies` cards render a Value column too and it holds a company STAGE, not
 * money — they are told apart by {@link VALUE_COLUMN_KIND} and **never** by
 * `state.valueLabel`, an admin-facing string. Sending `?sort=amount` to
 * `/companies` is a **422 on a `.strict()` schema that takes the whole card
 * out**, which reads as a flaky endpoint rather than as a wrong key. What
 * prevents it is that `COMPANY_SORTS` carries no `value-asc`, so `headerFor`'s
 * `state.sortOptions.includes(pair.asc)` test answers false and no sort UI is
 * drawn. **That derivation is the guard; do not add a `VALUE_COLUMN_KIND` branch
 * beside it**, which would be a second authority over one decision.
 *
 * ⚠️ **`overdue` is INVOICES-ONLY and is not symmetry waiting to be finished** —
 * `d_quotes` has no due-date column at all, so `INVOICE_SORTS` alone carries the
 * pair and the same `sortOptions` test keeps it off every other card.
 *
 * ⚠️ **`upcomingTask` is the only pair besides A–Z that all five endpoints can
 * express**, which is exactly the property `note` one column over does not have.
 *
 * ## Three columns are still absent, for three different reasons
 *
 * ⛔ **None of them is "not built yet", and a later pass must not close them by
 * symmetry.** The backend pins all three as refusals, so adding one there fails
 * loudly rather than silently here.
 *
 *  - **`note`** ⛔ **may never order anything.** `latestNote` is VIEWER-SCOPED,
 *    so the same card would come back in a different order for two people with a
 *    200 and nothing on screen saying why. ⚠️ It sits ONE ROW from `upcomingTask`
 *    in the same popover and only one of the two returns the same list to
 *    everybody — which is the whole reason the task pair could be built and this
 *    one could not. See `COLUMN_LABEL.note`.
 *  - **`signature`** is a `/docusign/envelopes?outstanding=true` read joined to
 *    the rows IN THE BROWSER. There is no column and no key, so there is nothing
 *    for `?sort=` to name.
 *  - **`projectOfInterest`** is a SET per row, so there is no single value to
 *    order by. ⚠️ It gained a FUNNEL on 2026-09-13 and did not gain a sort, and
 *    the two are different questions: `ColumnSpec.matches` can ask "is this
 *    project among them", where an ordering would have to pick one member to
 *    rank by and no such choice is defensible.
 *
 * ✅ **`country` IS SORTABLE NOW — 2026-09-12, and it is the one absentee whose
 * reason really was "not built yet".** *SUPERSEDED 2026-09-03: "The `country`
 * column landed on the two `/companies` cards and is the fifth absentee, for a
 * fifth reason: **`/companies` declares `sort: z.enum(['name','createdAt',
 * 'updatedAt'])` and no country member** — measured against the deployed schema
 * that day, not inferred."* That measurement was honest and is now history:
 * backend `0e30700` added the fourth member, ordering on `co.country_name`, and
 * this heading gained its `aria-sort` with it.
 *
 * ⚠️ **Note which KIND of claim expired.** The other absentees are absent for
 * structural reasons — no such column, derived in the browser, viewer-scoped —
 * and none of those can be closed by a deploy. This one was a fact about
 * somebody else's enum on a particular day, which is the only shape here that a
 * change in the other repo can falsify. **Re-measure a claim of that shape
 * before quoting it; leave the structural ones alone.**
 *
 * 🔴 **THREE OF THE FIVE BULLETS THAT STOOD HERE WERE CLOSED BY A DEPLOY ON
 * 2026-09-13, and the paragraph above is what predicted which three.**
 * ⚠️ *SUPERSEDED, in full: "🔴 **`status` and `country` are here, and the three
 * absentees are absent for three different reasons — none of them 'not built
 * yet':** — **`signature`** is not a column on any endpoint … — **`overdue`** is
 * DERIVED — `dueDate` against today, and against `is_settled` on the
 * `/invoice-statuses` master. `/invoices` sorts by `billDate`, which is a
 * different column and not this one under another name. — **`note`** ⛔ **may
 * never order anything.** It is VIEWER-SCOPED … — **`upcomingTask`** is DERIVED
 * from the account's tasks and is a column on no endpoint, so there is nothing
 * for `?sort=` to name — the same reason `signature` is absent. ⚠️ **And it is
 * two-valued**, so even a page-scoped ordering would only ever partition the
 * fetched hundred into two blocks, which reads as a ranking and is not one. —
 * the **value** column: no endpoint here declares a `grandTotal` sort … **Do not
 * add a sortable value header on the strength of that comment** — it would send
 * a key that is a 422 on a `.strict()` schema, which shows up as one card
 * failing to load beside four that look perfect."*
 *
 * ⚠️ **READ WHAT WAS WRONG WITH EACH, BECAUSE THEY FAILED DIFFERENTLY.**
 *
 *  - **`overdue`** and the **value** column were EXHAUSTIVE NEGATIVES ABOUT
 *    SOMEBODY ELSE'S ENUM — "no endpoint here declares" — measured honestly on
 *    2026-09-03 and falsified by a backend change ten days later. Exactly the
 *    shape the `country` paragraph above names as the only one a deploy can
 *    close. ⚠️ The value bullet's WARNING was right and is kept: `grandTotal` is
 *    still not a member, and the pair that shipped is **`amount`** — so a reader
 *    acting on that comment in `types.ts` would still send a 422. **The key
 *    changed, the caution did not.**
 *  - **`upcomingTask`** made TWO claims and only one of them was about the wire.
 *    *"A column on no endpoint"* was true and is now false — `openTask` is a real
 *    sort member on all four. *"Two-valued, so an ordering partitions rather than
 *    ranks"* is STILL TRUE and was never a reason to refuse it: the server
 *    partitions, `PARTNERSHIP_SORT_LABEL` says so by calling it "Open task
 *    first"/"last" rather than naming a rank, and a partition is what somebody
 *    grouping their week by open work actually wants. **A true objection was
 *    carried as though it settled a question it did not answer.**
 *  - **`signature`** and **`note`** were STRUCTURAL and are untouched — they are
 *    in the list above this one, where they belong.
 *
 * ⚠️ **The non-sortable headers therefore carry NO `aria-sort` AT ALL — not
 * even `none`.** `aria-sort="none"` announces a column that *can* be sorted and
 * currently is not, which is a promise of a control that will never exist here.
 * The same rule `FilterableTH` follows on the list pages.
 *
 * ⛔ **BOTH PROJECT COLUMNS ARE ABSENT, AND STRUCTURALLY — `projectOfInterest`
 * and, since 2026-09-14, `lineProjects`.** A cell holding a SET has no single
 * value to order by, so there is nothing for `?sort=` to name and nothing a
 * page-scoped ordering could rank either. That is the `signature` and `note`
 * kind of absence, **not** the `overdue` kind: ⚠️ **no deploy can close it**, and
 * a backend session adding a sort member would not change the answer here. The
 * hook's `PartnershipColumn` doc records the same refusal from the wire's side.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, the paragraph directly above.* "No deploy can close
 * it" was wrong: backend `32057ed` orders every set-valued column by its COUNT
 * of distinct members, which is one value per row. Both project columns, and
 * the Companies card's two purchase-history columns, sort now. The reasoning
 * against ranking by a chosen MEMBER still stands; a count is not that.
 */
/**
 * 🔴 **EXHAUSTIVE SINCE 2026-09-14, AND THE `null`s ARE THE POINT.**
 * *SUPERSEDED: `Partial<Record<…>>` with the five unsortable columns simply left
 * out.*
 *
 * **`Partial` was right about the DATA and wrong about the PROCESS.** Some of
 * these columns genuinely cannot be sorted — a set has no single value to order
 * by, an envelope state is in no table — so a total map of pair NAMES was never
 * available, and dropping `Partial` outright is not the fix. What `Partial` also
 * bought was silence: a column that SHOULD sort and was forgotten looked
 * identical to one that cannot, and **that is exactly what happened to `source`**,
 * which shipped filterable-and-not-sortable over a wire that had supported
 * `?sort=source` all along. Nothing failed, because an omission from a `Partial`
 * record is not an error anywhere.
 *
 * ✅ **So the omission is now DELIBERATE rather than possible**: every column has
 * an entry, and a column that cannot sort says `null` in its own words. A new
 * member of {@link PartnershipColumn} is a `tsc` error here until somebody states
 * which it is — which is the one question this map exists to answer.
 *
 * ⚠️ **`null` MEANS "STRUCTURALLY CANNOT", NOT "NOT WIRED YET".** Each one below
 * carries its reason, and they are not interchangeable: a reason of the
 * *no such column* kind cannot be closed by a deploy, where `source`'s absence
 * could have been closed by a line. **Read the reason before turning a `null`
 * into a pair** — and if a backend change really does make one sortable, the
 * comment is what says whether that is plausible.
 */
const SORTABLE_COLUMN: {
  readonly [K in PartnershipColumn | typeof RECORD_KEY | typeof VALUE_KEY]:
    | keyof typeof HEADER_SORTS
    | null;
} = {
  /*
   * ⚠️ **`record` and `value` are NOT {@link PartnershipColumn} members** — they
   * are the two fixed columns every card draws, declared outside `extraColumns`.
   * `record` used to be a ternary at the call site and `value` had no way in at
   * all, which is why the Value heading could not sort before 2026-09-13.
   */
  [RECORD_KEY]: 'record',
  status: 'status',
  country: 'country',
  /*
   * 🔴 **WIRED 2026-09-14, and it was the omission this map's shape was changed
   * to prevent** — see the doc above. The wire member predates the caret.
   */
  source: 'source',
  companyType: 'companyType',
  [VALUE_KEY]: 'value',
  overdue: 'overdue',
  upcomingTask: 'task',
  /*
   * ⛔ **STRUCTURAL — a `/docusign/envelopes?outstanding=true` read joined to the
   * rows IN THE BROWSER.** There is no column and no key, so there is nothing for
   * `?sort=` to name and no deploy can change that.
   */
  signature: null,
  /*
   * ⛔ **STRUCTURAL, AND THE ONE THAT MAY NEVER BE CLOSED EVEN IF IT COULD BE.**
   * `latestNote` is VIEWER-SCOPED, so the same card would come back in a different
   * order for two people with a `200` and nothing on screen saying why. ⚠️ It sits
   * ONE ROW from `upcomingTask` in the same popover and only one of the two
   * returns the same list to everybody — which is the whole reason the task pair
   * could be built and this one could not.
   */
  note: null,
  /*
   * ✅ **The account-wide note IS sortable — by its DATE, on the date column.**
   * It is viewer-INDEPENDENT (anyone's note, the same for every reader), which is
   * exactly what `note` above is not. `/companies` `?sort=lastNoteAt`; accounts
   * with no note sort last both ways. The TEXT column beside it carries no sort:
   * one ordering, one heading.
   */
  lastNoteAt: 'lastNote',
  /*
   * ⚠️ *SUPERSEDED 2026-10-09: `lastNote: null` — "The TEXT column beside it
   * carries no sort: one ordering, one heading."* Owner's ruling: every
   * filterable heading offers Ascending/Descending, and the account's last note
   * sorts by its DATE. So both headings toggle the one `lastNoteAt` ordering —
   * the same value, never a second one.
   */
  lastNote: 'lastNote',
  /*
   * ⚠️ *SUPERSEDED 2026-10-09: `null` — "⛔ **STRUCTURAL — a SET per row, so
   * there is no single value to order by.** … an ordering would have to pick one
   * member to rank by and no such choice is defensible."* That was right about
   * picking a MEMBER and is not what was built: backend `32057ed` orders a set
   * by its COUNT of distinct members, which is one value per row and needs no
   * member chosen.
   */
  projectOfInterest: 'interestCount',
  /* ⚠️ *SUPERSEDED 2026-10-09: `null`, "STRUCTURAL"* — the count, as above. */
  lineProjects: 'lineProjects',
  /* `/deals` `?sort=quoteSigned` (2026-10-27); unsigned deals sort last. */
  quoteSigned: 'quoteSigned',
  /*
   * ⛔ **STRUCTURAL — one figure PER CURRENCY, and `/companies` takes no credit
   * sort.** Ordering would have to rank a GBP balance against a USD one, which
   * is the cross-currency comparison a balance must never be reduced to.
   */
  creditBalance: null,
  /*
   * The per-stage status SLOT is never a heading. Each `stageStatus:<id>` column
   * that replaces it sorts through its own per-stage pair (`stageStatusSortPair`),
   * wired in `filterHeaderFor`'s stage arm rather than through this map.
   */
  stageStatuses: null,
  /*
   * ⚠️ *SUPERSEDED 2026-10-09: `null` — "Sets per row, so no single value to
   * order by."* Both order by their COUNT of distinct members, scoped
   * server-side to the card's cycle filter on that column when one is set.
   */
  productsPurchased: 'purchasedCount',
  projectsParticipated: 'participatedCount',
  accountLead: 'accountLead',
};

/**
 * The sort pair one heading toggles, or `undefined` for a heading that names
 * none.
 *
 * ⚠️ **`Object.hasOwn` rather than a cast, and the cast is what it replaces.**
 * *SUPERSEDED: `SORTABLE_COLUMN[key as PartnershipColumn | typeof RECORD_KEY |
 * typeof VALUE_KEY]`.* That assertion told `tsc` a plain string was always a
 * member — harmless while the map was `Partial` and reading `undefined`, and a
 * LIE once the map became total: the type then says every lookup yields a value
 * while a stray key still yields `undefined` at runtime. Testing membership keeps
 * the answer honest without an assertion, and it is the same shape
 * `isPartnershipColumn` already uses against `COLUMN_REM`.
 */
function sortPairFor(
  key: string,
  tableKey: PartnershipTableKey,
): (typeof HEADER_SORTS)[keyof typeof HEADER_SORTS] | undefined {
  /*
   * 2026-10-09: the Value heading names a DIFFERENT pair per card kind. On the
   * document cards it is money (`value` → `amount`); on the Companies card it is
   * a company STAGE (`stage`). Told apart by `VALUE_COLUMN_KIND`, never by the
   * admin-facing `valueLabel`. This chooses WHICH pair; whether the card may
   * offer it is still `state.sortOptions`, read in `headerFor` — the one guard.
   */
  if (key === VALUE_KEY && VALUE_COLUMN_KIND[tableKey] === 'company-stage') {
    return HEADER_SORTS.stage;
  }
  if (!Object.hasOwn(SORTABLE_COLUMN, key)) return undefined;
  const pairName = SORTABLE_COLUMN[key as keyof typeof SORTABLE_COLUMN];
  return pairName === null ? undefined : HEADER_SORTS[pairName];
}

/*
 * 🔴 **`SortCaret` AND `SortHeader` WERE HERE AND ARE DELETED — 2026-09-12.**
 * The ordering moved INTO the column's funnel popover, which is where every
 * other list page in this app puts it, so a clickable heading with a caret
 * beside it would be a second control over the same value.
 *
 * Two of their rulings survive in `headerFor`, and both would be easy to lose:
 *
 *  - **Whether a column is sortable is DERIVED from the card's own
 *    `sortOptions`, never declared beside the column.** That list is a property
 *    of the endpoint and already drives the ordering dropdown, so the two cannot
 *    offer different things. A funnel whose sort arrows `load` does not
 *    translate looks like it worked, and the reader concludes the data is wrong
 *    rather than the control.
 *  - **`aria-sort` only where the API can really sort.** `ColumnFilter` omits
 *    its sort UI entirely when `sort` is absent, and `FilterableTH` claims no
 *    `aria-sort` then either — so an unsortable heading still promises nothing.
 *
 * ⚠️ **One behaviour genuinely changed and it is worth knowing: there is no
 * longer a click TARGET on the heading text itself.** Sorting is two actions
 * now — open the funnel, choose a direction — where it used to be one click on
 * the label. That is the trade the owner asked for, and it is what every other
 * table here already does; it is not an oversight to be "fixed" by making the
 * label clickable as well, which would give one column two sort controls that
 * disagree about what a second click does.
 */

/**
 * The header row — one {@link FilterableTH} per column, funnel and all.
 *
 * 🔴 **IT TAKES A COLUMN LIST AND A `headerFor`, NOT THE CARD'S SORT STATE, AND
 * THAT IS THE POINT OF THE REWRITE.** The sort used to be a `SortHeader` here
 * and the filter a `<select>` in the toolbar above, so the two controls over one
 * column lived in two components and neither knew about the other. Both are
 * inside the column's own popover now, built by one function in the panel — the
 * arrangement every list page in this app already uses.
 *
 * ⚠️ **The `columnKeys` handed here are `layout.visibleKeys` — the ONE arranged
 * list the `<colgroup>`, the body cells and the skeleton also walk**, so a
 * column cannot appear in one and not another and cannot appear at a different
 * INDEX in one than in another. *SUPERSEDED 2026-09-12: "built ONCE in the panel
 * and drives the `ColumnSpec`s, the `<colgroup>` and this row" — which was true
 * of this row and of the specs, and was never true of the colgroup, the body or
 * the skeleton: those three re-derived their own sequence from
 * `state.extraColumns`.* Under `table-fixed` that disagreement does not fail
 * loudly: a header with no `<col>` shifts every column after it and the browser
 * pads the shortfall at the end, which is the defect the value column's removal
 * already documented.
 */
/*
 * ✅ **`PartnershipHeaderCell` WAS HERE AND IS DELETED — 2026-09-12.** It was a
 * local copy of `FilterableTH`'s frozen branch, composing `TH` and
 * `ColumnFilter` by hand, because `FilterableTH` forwarded no
 * `frozen`/`frozenOffset` and `ColumnFilter.tsx` was not this page's to change.
 * **It forwards both now**, so the workaround is gone rather than kept beside
 * the thing it worked around — which is what its own note asked for.
 *
 * ⚠️ **The `aria-sort` rule survives the deletion unchanged, and it survives
 * because the copy was faithful**: `FilterableTH` claims a sort state only where
 * `filter.sort` is present, so an unsortable heading still promises nothing —
 * not even `aria-sort="none"`. That was the one behaviour the local copy existed
 * to preserve, and it was preserved by copying the shared component's own
 * ternary. Deleting the copy therefore changes no markup.
 */

function PartnershipHead({
  columnKeys,
  headerFor,
  pinActions,
  sticky,
  headRowRef,
  frozenSideOf,
  frozenOffsets,
}: {
  columnKeys: readonly string[];
  headerFor: (key: string) => FilterableTHProps;
  pinActions: boolean;
  sticky?: boolean;
  /**
   * The header row, handed to `useFrozenOffsets`, which measures the RENDERED
   * width of each cell.
   *
   * ⚠️ **Measured rather than summed out of `COLUMN_REM`, even though this table
   * IS `table-fixed` with a real `<colgroup>` — because two of its columns have
   * no `rem` to sum.** The RECORD column carries no width at all (it absorbs the
   * surplus, which is the whole reason a long school name truncates instead of
   * pushing the action cell off the edge), so the left offset of anything after
   * it is unknowable from the constants; and the ACTION lane takes the
   * primitives' `max-sm` touch bump to `w-10`, which `ACTION_COLUMN_*_REM`
   * deliberately does not declare. Arithmetic would be exact on three of the
   * five column kinds and confidently wrong on the other two.
   *
   * ✅ *SUPERSEDED 2026-09-12: "A CALLBACK ref, not a `RefObject` — this card
   * mounts a different header row for its skeleton and for its loaded table, and
   * the panel has to know when that node changes so the measuring effect
   * re-attaches to it."* **A plain `RefObject`**: the card still mounts two
   * different rows, but `useFrozenOffsets` compares the row ELEMENT itself
   * between renders, so nothing here has to notice the swap and report it.
   */
  headRowRef: React.RefObject<HTMLTableRowElement | null>;
} & FreezeProps) {
  return (
    <THead className={cx(sticky && STICKY_HEAD)}>
      {/*
        * ✅ *SUPERSEDED 2026-09-12: a raw `<tr>` carrying `TR`'s class list
        * verbatim, "for ONE reason: `TRProps` declares no `ref`".* **`TR` takes
        * one now**, so the second copy of those classes — which nothing would
        * have kept in step — is gone.
        */}
      <TR ref={headRowRef}>
        {columnKeys.map((key) => (
          /*
           * ⚠️ **`wrap` on every one, which is why `FilterableTH` grew the
           * prop.** These cards are `table-fixed` with a `<colgroup>`, so a
           * heading can neither widen its column nor be allowed to overflow it —
           * `Awaiting signature` painted over `Your last note` once already
           * (reported 2026-09-03) and the two read as one run-on word. The
           * primitive's default truncation would cut that heading to `Awaiting
           * s…`, which names nothing; wrapping costs one header line on the
           * cards that need it and nothing on the others.
           */
          <FilterableTH
            key={key}
            {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
            {...headerFor(key)}
            wrap
          />
        ))}
        {/*
          * ⚠️ **`pinned`, never `frozen`** — `pinned` IS `frozen="right"` at
          * offset zero, it is what fifteen pages already say, and the two carry
          * `box-shadow` literals that cannot merge on one cell. This lane still
          * counts as right-frozen inside `useFrozenOffsets`, which the panel
          * hands `PINNED_COLUMN_KEYS` for exactly that, so any OTHER
          * right-frozen column is offset PAST it rather than underneath it.
          */}
        <TH className="w-px" pinned={pinActions}>
          <span className="sr-only">Tasks and notes</span>
        </TH>
      </TR>
    </THead>
  );
}

/* --- page ---------------------------------------------------------------- */

/*
 * `ActivityDrawer` takes a target whether or not it is open, so a closed sheet
 * needs something to hold. Id `0` matches no record and the drawer renders
 * nothing while `open` is false; it exists so the prop is never a lie about a
 * real record — the same placeholder the other list pages pass.
 */
const CLOSED_TARGET: ActivityTarget = { type: 'company', id: 0 };

/**
 * The query-string key carrying one card's ordering.
 *
 * Five controls on one page means five params, which is the honest cost of
 * putting them in the URL at all.
 *
 * ⚠️ **Why the URL and not `useState`.** Not for shareability — every table
 * here is scoped by `accountLeadId` to the signed-in person, so a colleague
 * opening this link sees THEIR accounts and the URL can never describe what
 * another reader sees. What it buys is that a reload, a Back out of a record
 * and a bookmark all keep the arrangement, and that the arrangement lives in
 * exactly one place. The alternative — `useState`, or `localStorage` — loses it
 * on reload with nothing to restore from, or sets up a second authority beside
 * the URL that this codebase has been bitten by before.
 */
function sortParamOf(key: PartnershipTableKey): string {
  return `${key}Sort`;
}

/**
 * The query-string key carrying one card's filter.
 *
 * ⚠️ **Keyed by CARD AND KIND, so a card that offers several filters needs no
 * change here** — `?leadsCountry=44`, `?leadsSource=2,3`, `?quotesStatus=3`.
 * Sharing one param per card would have made the second filter a rewrite rather
 * than a line in a spec. ✅ **That promise was tested and held on 2026-09-14**,
 * when the two `/companies` cards became the first to offer two kinds at once:
 * the suffix map below gained a row and nothing else moved.
 *
 * 🔴 **A MAP RATHER THAN A TERNARY, and that is the change.** *SUPERSEDED:
 * `` `${key}${kind === 'status' ? 'Status' : 'Country'}` ``.* A two-branch test
 * over a union of three silently files a THIRD kind under the second's param —
 * so `?leadsSource=` and `?leadsCountry=` would have been one key, and ticking a
 * source would have cleared the country with a `200` and nothing failing. The
 * `Record` is exhaustive, so a fourth kind is a `tsc` error here rather than a
 * collision on the wire.
 *
 * ⚠️ **The suffix is capitalised because it is glued to a camelCase card key**
 * (`leads` + `Source`), and these strings are what appear in a URL somebody
 * shares — so they are as much a contract as the wire keys and must not be
 * renamed to tidy them.
 *
 * ⚠️ **Why the URL rather than `useState`** — the same reasoning `sortParamOf`
 * records, and it applies more strongly to a filter: a reader who narrows a card,
 * opens a record and presses Back must land on the card they left. `useState`
 * loses that with nothing to restore from.
 */
const FILTER_PARAM_SUFFIX: Record<PartnershipFilterKind, string> = {
  status: 'Status',
  country: 'Country',
  source: 'Source',
  /*
   * ⚠️ **`CompanyType`, not `Type`** — these strings appear in a URL somebody
   * shares, so a short form would be a second spelling of a field whose whole
   * contract is the long word (see `Company.companyType` in `types.ts`). It also
   * keeps `?leadsCompanyType=` unmistakable beside `?leadsCountry=`, which is the
   * pair a reader scanning a link would otherwise conflate.
   */
  companyType: 'CompanyType',
  /*
   * 2026-10-09 — `?companiesParticipatedCycle=` and `?companiesPurchasedCycle=`.
   * Named for the CYCLE, because the ids in them are cycle ids and not the
   * project or product ids the column cells show.
   */
  participatedCycle: 'ParticipatedCycle',
  purchasedCycle: 'PurchasedCycle',
  /*
   * 2026-10-25 — `?leadsStageStatus=` / `?companiesStageStatus=`: ONE shared
   * status-id list per card, edited slice by slice by every `{Stage} status`
   * column's funnel.
   */
  stageStatus: 'StageStatus',
};

function filterParamOf(key: PartnershipTableKey, kind: PartnershipFilterKind): string {
  return `${key}${FILTER_PARAM_SUFFIX[kind]}`;
}

/*
 * The Account lead control's two non-person options (2026-10-27). Option
 * values only — the URL spelling is the hook's (`serialisePartnershipLeadScope`).
 * Neither can collide with a user id, which is always digits.
 */
const LEAD_OPTION_ALL = 'all';
const LEAD_OPTION_UNASSIGNED = 'none';

export function UniversityPartnershipsPage() {
  const { user, ability } = useAuth();

  /*
   * The permission check. A hidden sidebar row is an optimisation and never a
   * guarantee — the URL is typeable, and a permission set can change under an
   * access token that lasts over five days — so the page answers for itself.
   *
   * ⚠️ This DEGRADES OPEN — see `NAV_SURFACE_MODULE` in `@/lib/permissions`.
   * Only an explicit `none` scope denies; an unseeded catalogue, an older API
   * or a failed `/permissions/me` all leave the page visible.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook. Feeding `null` to `usePartnershipTables` is what
   * actually stops the work: five table fetches would otherwise fire for a
   * reader who is about to be shown a single panel, and the hook already takes
   * a null id and holds, which is how it waits for `/auth/me`.
   *
   * `FunnelPanel` owns its own window picker and therefore calls `useMyTargets`
   * itself; on the refusal path it is not rendered at all, so its reads never
   * start. It is handed the signed-in id and holds on a null in the same way.
   *
   * ⚠️ *SUPERSEDED 2026-10-27: `scopedTo`, the signed-in id handed to the hook.*
   * The hook takes `leadScope` (below) now; `viewerId` is what stays.
   */
  const canRead = ability.canRead(NAV_SURFACE_MODULE.dashboardUniversityPartnerships);
  const viewerId = canRead ? (user?.id ?? null) : null;
  /*
   * 🔴 **WHO MAY SWITCH THE ACCOUNT LEAD SCOPE (owner 2026-10-27).** Only a
   * reader whose companies read scope is `all` (or a super admin). Everybody
   * else is always "me" and the `leads` URL key is ignored — the server would
   * narrow an `own` reader's rows anyway, and the control would then promise
   * other people's accounts it cannot show.
   */
  // Its own permission (owner 2026-10-27): Super Admin only by default, opened
  // to other sets on the permission-set screen. Without it the dashboard is the
  // viewer's own and any `leads` in the URL is ignored.
  const canChooseLeads = ability.canRead(NAV_SURFACE_MODULE.upLeadSwitcher);

  // Above the refusal's early return, so the hook order never changes.
  const layout = usePageLayout('university-partnerships', PAGE_LAYOUT_DEFAULTS);

  /*
   * ⚠️ `useSearchParams()` with NO argument. Its first parameter is a DEFAULT
   * INIT, not options — `useSearchParams({ replace: true })` injects a literal
   * `replace=true` into the query string and then into everything parsed out of
   * it. The `{ replace: true }` belongs on the setter, below.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * The page-level Account lead scope (owner 2026-10-27): `?leads=` absent = the
   * signed-in person, `all`, or `5,9,none`. Read for a switcher only; `null`
   * while the session or the permission has not resolved, which holds the hook.
   */
  const leadsParam = canChooseLeads ? searchParams.get(PARTNERSHIP_LEADS_PARAM) : null;
  const leadScope: PartnershipLeadScope | null = useMemo(
    () => (viewerId === null ? null : parsePartnershipLeadScope(leadsParam, viewerId)),
    [leadsParam, viewerId],
  );
  /*
   * Exactly one lead → that person's funnel and commission (`me` included);
   * several, Unassigned or All → the organisation, commission strip hidden.
   */
  const singleLeadId: number | null =
    leadScope === null
      ? null
      : leadScope.kind === 'me'
        ? (leadScope.ids[0] ?? null)
        : leadScope.kind === 'leads' && leadScope.ids.length === 1 && !leadScope.includeUnassigned
          ? (leadScope.ids[0] ?? null)
          : null;
  const funnelOrganisation = leadScope !== null && singleLeadId === null;
  const scopedToSelf = singleLeadId !== null && singleLeadId === viewerId;

  /*
   * The control's options: All, Unassigned, then every user in the
   * `account_lead` group. Gated on the switcher, so nobody else pays for
   * `/users`. The signed-in person and any id the URL names are merged in when
   * the group does not hold them, so a chip always shows a name.
   */
  const leadStaff = useUserOptions(canRead && canChooseLeads, true);
  const leadOptions = useMemo<MultiComboboxOption[]>(() => {
    const people = leadStaff.byGroup.account_lead.users.map((person) => ({
      value: String(person.id),
      label: person.name,
    }));
    const known = new Set(people.map((option) => option.value));
    const extra: number[] = [];
    if (user && !known.has(String(user.id))) {
      people.unshift({ value: String(user.id), label: user.name });
      known.add(String(user.id));
    }
    for (const id of leadScope?.kind === 'leads' ? leadScope.ids : []) {
      if (!known.has(String(id))) extra.push(id);
    }
    for (const id of extra) {
      const person = leadStaff.users.find((candidate) => candidate.id === id);
      people.push({ value: String(id), label: person?.name ?? `User #${id}` });
    }
    return [
      { value: LEAD_OPTION_ALL, label: 'All' },
      { value: LEAD_OPTION_UNASSIGNED, label: 'Unassigned' },
      ...people,
    ];
  }, [leadStaff.byGroup, leadStaff.users, user, leadScope]);

  const leadValues: string[] =
    leadScope === null
      ? []
      : leadScope.kind === 'all'
        ? [LEAD_OPTION_ALL]
        : [
            ...leadScope.ids.map(String),
            ...(leadScope.includeUnassigned ? [LEAD_OPTION_UNASSIGNED] : []),
          ];

  /*
   * ⚠️ **Built from the CURRENT params**, like every other writer here, and the
   * default ("me", or an emptied selection) is an ABSENT key. Ticking All
   * replaces the selection; ticking anything while All is selected drops All.
   */
  const changeLeads = (values: string[]) => {
    if (viewerId === null) return;
    const hadAll = leadValues.includes(LEAD_OPTION_ALL);
    let next: PartnershipLeadScope;
    if (values.includes(LEAD_OPTION_ALL) && !hadAll) {
      next = allLeadScope();
    } else {
      const chosen = values.filter((value) => value !== LEAD_OPTION_ALL);
      const ids = chosen
        .filter((value) => value !== LEAD_OPTION_UNASSIGNED)
        .map(Number)
        .filter((id) => Number.isSafeInteger(id) && id > 0);
      const includeUnassigned = chosen.includes(LEAD_OPTION_UNASSIGNED);
      next =
        ids.length === 0 && !includeUnassigned
          ? meLeadScope(viewerId)
          : ids.length === 1 && ids[0] === viewerId && !includeUnassigned
            ? meLeadScope(viewerId)
            : { kind: 'leads', ids, includeUnassigned };
    }
    const params = new URLSearchParams(searchParams);
    const serialised = serialisePartnershipLeadScope(next);
    if (serialised === null) params.delete(PARTNERSHIP_LEADS_PARAM);
    else params.set(PARTNERSHIP_LEADS_PARAM, serialised);
    setSearchParams(params, { replace: true });
  };
  const leadsControlId = useId();

  /*
   * Resolved per card, because the option lists still differ — `?companiesSort=
   * newest` names an ordering `/companies` cannot express, and
   * `resolvePartnershipSort` falls it back to the default rather than selecting
   * an option the request will not honour. Recomputed each render — five string
   * lookups off a value that only changes when the URL does.
   *
   * ⚠️ **The example here used to be `?dealsSort=az`, and that link now WORKS**
   * rather than falling back: `/deals` gained `sort`, so a URL somebody shared
   * while the option was inert has started doing what it always said. That is
   * the intended outcome and not a break — but it is worth naming, because a
   * silent fallback is exactly the mechanism that let such a link exist at all.
   */
  const sorts: PartnershipSorts = {
    leads: resolvePartnershipSort('leads', searchParams.get(sortParamOf('leads'))),
    deals: resolvePartnershipSort('deals', searchParams.get(sortParamOf('deals'))),
    quotes: resolvePartnershipSort('quotes', searchParams.get(sortParamOf('quotes'))),
    invoices: resolvePartnershipSort('invoices', searchParams.get(sortParamOf('invoices'))),
    companies: resolvePartnershipSort('companies', searchParams.get(sortParamOf('companies'))),
  };

  /*
   * ⚠️ Built from the CURRENT params rather than from `sorts`, so changing one
   * card cannot rewrite another's key — including a key this page resolved away
   * from because it was unusable. Somebody who arrives on a link carrying
   * `?dealsSort=az` and then re-orders the Quotes card keeps their (inert) deals
   * param exactly as they received it; silently rewriting somebody's URL as a
   * side effect of an unrelated control is how a shared link stops matching
   * what was shared.
   *
   * The DEFAULT is written as an ABSENT key, not as `=activity`. A URL nobody
   * has touched stays clean, and "no param" and "the default" are then the same
   * state rather than two spellings of it that have to be kept in agreement.
   *
   * `{ replace: true }` per the house rule: re-ordering a card is adjusting a
   * view, not navigating, so it must not put a Back-button step between the
   * reader and the page they came from.
   */
  const changeSort = (key: PartnershipTableKey, sort: PartnershipSort) => {
    const next = new URLSearchParams(searchParams);
    if (isDefaultPartnershipSort(sort)) next.delete(sortParamOf(key));
    else next.set(sortParamOf(key), sort);
    setSearchParams(next, { replace: true });
  };

  /*
   * Every card's filters, resolved from the URL the same way the orderings are.
   *
   * ⚠️ **BOTH members are read for every card, including the one it does not
   * offer**, and that is deliberate rather than lazy. `load` sends only the key
   * its own spec declares (see `PartnershipFilterKind`), so a stray
   * `?leadsStatus=3` is inert — where branching here on `filterKinds` would put
   * a second copy of that decision in a second file, and the two would have to
   * be kept in step for the rest of the page's life.
   *
   * ⚠️ **`useMemo` because this object is a dependency of five effects.** A
   * fresh object every render would refetch all five cards on every render of
   * this page. The key is the raw query string, so it changes exactly when the
   * URL does.
   */
  /*
   * ⚠️ **Read from the query STRING, not from the `searchParams` object — the
   * house rule, and here it is what makes the memo work at all.** Depending on
   * the object risks a fresh identity per render, which would rebuild this on
   * every render and refetch all five cards; the string changes exactly when the
   * URL does. Parsing it back into a `URLSearchParams` inside the memo is what
   * keeps the dependency honest rather than declared-and-unread.
   */
  const paramString = searchParams.toString();
  const filters: PartnershipFilters = useMemo(() => {
    const params = new URLSearchParams(paramString);
    const read = (key: PartnershipTableKey) => ({
      status: resolvePartnershipFilterIds(params.get(filterParamOf(key, 'status'))),
      country: resolvePartnershipFilterIds(params.get(filterParamOf(key, 'country'))),
      /*
       * ⚠️ Read for EVERY card, including the three that do not offer it — see
       * the note above. `load` sends only the key its own spec declares, so a
       * stray `?dealsSource=2` is inert rather than a 422 on a `.strict()`
       * schema that has no such key.
       */
      source: resolvePartnershipFilterIds(params.get(filterParamOf(key, 'source'))),
      /*
       * ⚠️ Read for EVERY card, including the three that do not offer it — the
       * same rule `source` above states. ⛔ **A member left out here is NOT a
       * silent no-op: a shared `?leadsCompanyType=2` link would load the card
       * UNFILTERED with an empty funnel**, so the link shows a different set than
       * the person who sent it saw, and nothing on screen says so.
       *
       * ✅ **`tsc` catches the omission today** — this object is assigned into a
       * `PartnershipFilters`, so a missing member is a type error rather than a
       * dead link. That is a property of the annotation on the memo, not of this
       * helper; ⛔ do not widen the memo's type to `Record<…, unknown>` or drop
       * the annotation, which would make the next member's absence silent.
       */
      companyType: resolvePartnershipFilterIds(params.get(filterParamOf(key, 'companyType'))),
      /*
       * The two purchase-history cycle filters (2026-10-09). Read for every card
       * like the members above; only the Companies card sends them.
       */
      participatedCycle: resolvePartnershipFilterIds(
        params.get(filterParamOf(key, 'participatedCycle')),
      ),
      purchasedCycle: resolvePartnershipFilterIds(params.get(filterParamOf(key, 'purchasedCycle'))),
      // The per-stage status columns' one shared list (2026-10-25).
      stageStatus: resolvePartnershipFilterIds(params.get(filterParamOf(key, 'stageStatus'))),
      /*
       * The account's last-note date range (2026-10-02). Read for every card like
       * the members above; only the two `/companies` cards send it, so a stray
       * `?dealsLastNoteAfter=` is inert. A malformed date reads as absent.
       */
      lastNoteAfter: resolvePartnershipDateBound(params.get(lastNoteParamOf(key, 'after'))),
      lastNoteBefore: resolvePartnershipDateBound(params.get(lastNoteParamOf(key, 'before'))),
    });
    return {
      leads: read('leads'),
      deals: read('deals'),
      quotes: read('quotes'),
      invoices: read('invoices'),
      companies: read('companies'),
    };
  }, [paramString]);

  /*
   * ⚠️ **The same "built from the CURRENT params" rule the ordering follows** —
   * changing one card's filter must not rewrite another's key. Clearing writes
   * an ABSENT key rather than an empty value: `?quotesStatus=` would round-trip
   * back through `resolvePartnershipFilterId` as `null` anyway, so the two are
   * one state, and a URL nobody has narrowed stays clean.
   */
  const changeFilter = (
    key: PartnershipTableKey,
    kind: PartnershipFilterKind,
    ids: readonly number[],
  ) => {
    const next = new URLSearchParams(searchParams);
    /*
     * ⚠️ **An EMPTY selection deletes the key rather than writing an empty
     * value**, exactly as the single-id version did: `?quotesStatus=` round-trips
     * back through `resolvePartnershipFilterIds` as `[]` anyway, so the two are
     * one state and a URL nobody has narrowed stays clean.
     */
    if (ids.length === 0) next.delete(filterParamOf(key, kind));
    else next.set(filterParamOf(key, kind), ids.join(','));
    setSearchParams(next, { replace: true });
  };

  /*
   * The last-note date range — the same "built from the CURRENT params" and
   * "absent key, never an empty value" rules `changeFilter` follows, for a pair
   * of `YYYY-MM-DD` bounds rather than an id list.
   */
  const changeLastNoteRange = (
    key: PartnershipTableKey,
    after: string | null,
    before: string | null,
  ) => {
    const next = new URLSearchParams(searchParams);
    if (after === null) next.delete(lastNoteParamOf(key, 'after'));
    else next.set(lastNoteParamOf(key, 'after'), after);
    if (before === null) next.delete(lastNoteParamOf(key, 'before'));
    else next.set(lastNoteParamOf(key, 'before'), before);
    setSearchParams(next, { replace: true });
  };

  /*
   * One refetch nonce per card, bumped after a record is created from it.
   *
   * ⚠️ **Per card, not one for the page** — the reasoning is on the hook's own
   * `reloadTokens` parameter. Creating a quote must not blank the other four
   * cards to skeletons for four requests that cannot have changed.
   *
   * ⚠️ **`useMemo`-free but STABLE, because it is a plain state object** — it
   * only ever changes when `bumpReload` replaces it, so the five effects that
   * read one member each are not re-run by an unrelated render.
   */
  const [reloadTokens, setReloadTokens] = useState<Record<PartnershipTableKey, number>>({
    leads: 0,
    deals: 0,
    quotes: 0,
    invoices: 0,
    companies: 0,
  });

  /*
   * 🔴 **THE PER-STAGE STATUS COLUMNS' MASTERS, READ ONCE FOR THE PAGE (owner
   * 2026-10-25).** The stage and stage-status masters behind every
   * `{Stage} status` column on the Leads and Companies cards. One read here,
   * every stage's columns, and each card narrows them to the stages it holds
   * (`state.stageStatusStageIds`) — rather than one `useStageStatusColumns` per
   * card, which would read `/company-stages` once per card. Gated on `canRead`
   * like every read on this page; NOT on `companies.update`, because the
   * columns DISPLAY for every reader and only their cells write.
   */
  const stageStatus = useStageStatusColumns({ enabled: canRead });
  // The page's ONE status dialog (date / reason) — mounted once, below.
  const { request: requestStageStatusChange, dialog: stageStatusDialog } = useStageStatusChange();

  /*
   * Each card's stage ids, as last reported by the hook — state, not a ref, so
   * the pruning below re-runs once a card reports (an effect syncs it).
   */
  const [cardStageIds, setCardStageIds] = useState<
    Partial<Record<PartnershipTableKey, ReadonlySet<number> | null>>
  >({});

  /*
   * ⚠️ **ONLY the card's CURRENT columns' ids reach the hook**
   * (`stageStatusFilterForColumns`), so a stale id — a stage that lost its
   * column, or another card's stage — is neither sent nor counted as active.
   * Unchanged while the masters load or before the card has reported its
   * stages. `filters` is returned as is when nothing was pruned.
   *
   * ⚠️ **HELD — the ids go out unchanged — in three more cases (2026-10-25):**
   *   - the card has not reported yet: `undefined`, or an EMPTY set, which is
   *     what the hook reports for the Companies card before its first load
   *     (its private `NO_STAGE_IDS`). Pruning against that pruned every id, sent
   *     one unfiltered request, then refetched. A card that genuinely holds no
   *     stage has no status columns either, so holding there prunes nothing a
   *     reader could have chosen;
   *   - `null` (a card with no stage status columns) — unchanged as before;
   *   - the stage or status master FAILED: an unread master is not evidence an
   *     id is stale, and pruning showed a URL-filtered card unfiltered with no
   *     chip to say so.
   */
  const hookFilters: PartnershipFilters = useMemo(() => {
    let changed = false;
    const next = { ...filters };
    for (const key of Object.keys(filters) as PartnershipTableKey[]) {
      const ids = cardStageIds[key];
      const columnsOfCard =
        ids === undefined || ids === null || ids.size === 0
          ? null
          : stageStatus.columns.filter((column) => ids.has(column.stageId));
      const pruned = stageStatusFilterForColumns(
        filters[key].stageStatus,
        columnsOfCard ?? [],
        stageStatus.statuses,
        stageStatus.loading || stageStatus.failed || columnsOfCard === null,
      );
      if (pruned !== filters[key].stageStatus) {
        changed = true;
        next[key] = { ...filters[key], stageStatus: pruned };
      }
    }
    return changed ? next : filters;
  }, [
    filters,
    cardStageIds,
    stageStatus.columns,
    stageStatus.statuses,
    stageStatus.loading,
    stageStatus.failed,
  ]);

  // `leadScope` is null until /auth/me resolves; the hook takes the null and holds.
  const tables = usePartnershipTables(leadScope, sorts, hookFilters, reloadTokens);

  /*
   * Adopt each card's reported stage ids DURING render (React's "adjust state
   * when a value changes" pattern) — compared by identity, which the hook keeps
   * stable while the ids are unchanged, so this settles after one extra pass.
   */
  if (tables.some((table) => cardStageIds[table.key] !== table.stageStatusStageIds)) {
    const next = { ...cardStageIds };
    for (const table of tables) next[table.key] = table.stageStatusStageIds;
    setCardStageIds(next);
  }

  /*
   * The two masters behind the quote-stage and invoice-status badges.
   *
   * ⚠️ **Gated on `canRead`, the same test that stops the five table fetches.**
   * A reader who is about to be shown a single refusal panel should not spend two
   * requests on reference lists nothing will render — the same reasoning that
   * feeds `null` to `usePartnershipTables` above rather than returning early.
   *
   * ⚠️ Deliberately NOT gated on `scopedTo`. These lists are the same for
   * everybody and are not scoped to a person, so there is nothing to wait for
   * `/auth/me` to tell us — gating them on the user would leave the first paint
   * of rows badged from the name fallback and then re-tone them a moment later,
   * which is a visible flicker on a colour that means something.
   *
   * `loading` and `failed` are deliberately unread; see `StatusMasters` for why
   * a failed master costs a tone and never a label.
   */
  const stageMaster = useQuoteStageOptions(canRead);
  const statusMaster = useInvoiceStatusOptions(canRead);
  const masters: StatusMasters = {
    stages: stageMaster.stages,
    statuses: statusMaster.statuses,
  };

  /*
   * The four masters the five filter controls read.
   *
   * ⚠️ **TWO NEW REQUESTS FOR THE PAGE, NOT FIVE** — `/deal-stages` and
   * `/countries`. The quote-stage and invoice-status lists are the SAME two the
   * badges already fetch above, reused rather than fetched again: a second copy
   * would double the requests and could disagree with the badge sitting in the
   * same row as the control filtering it.
   *
   * ⚠️ Gated on `canRead`, like every other read on this page — a reader about
   * to be shown a single refusal panel spends nothing.
   *
   * ⚠️ **`failed` from the two shared hooks is threaded through, and it matters
   * more here than it does for a badge.** A failed master costs a badge its
   * TONE and nothing else, so `StatusMasters` deliberately ignores it; a failed
   * master costs a FILTER every option it has, which the control has to say
   * rather than render as an empty dropdown.
   */
  const countryMaster = useCountryMaster(canRead);
  const dealStageMaster = useDealStageMaster(canRead);
  const cycleMaster = useCycleMaster(canRead);

  /*
   * The `/company-sources` master — owner's instruction, 2026-09-14.
   *
   * 🔴 **ONE READ FOR BOTH THE FUNNEL AND THE PICKER, which is why it is gated on
   * `canRead` and not on `canUpdateCompanies`.** The two country hooks below are
   * two requests because a FILTER and a PICKER need different lists there
   * (`hasCompanies: true` versus the whole master); `/company-sources` publishes
   * no such narrowing, so one list is exactly right for both.
   *
   * ⛔ **So it must NOT be gated on the write, and that is the mirror image of the
   * defect this repo has on record.** The staff directory was once fetched only
   * for users who could edit, leaving a read-only reader with an empty picker —
   * indistinguishable from a directory that failed to load. Here the list feeds a
   * FUNNEL every reader may use, so gating it on `companies.update` would give a
   * read-only reader a Source filter with no options in it. The accepted cost is
   * that such a reader spends one request on a five-row master.
   */
  const companySources = useCompanySources(canRead);
  /*
   * ⚠️ **The same rows projected for the funnel.** `FilterMaster` is the shape
   * every funnel on this page reads; the PICKER takes `companySources` itself,
   * because `companySourceOptions` needs the real `ReferenceRecord`s — see
   * `PartnershipEditing.companySources`.
   */
  const companySourceMaster: FilterMaster = {
    rows: companySources.records,
    loading: companySources.loading,
    failed: companySources.failed,
  };

  /*
   * The `/company-types` master — owner's instruction, 2026-09-14.
   *
   * 🔴 **ONE READ FOR BOTH THE FUNNEL AND THE PICKER, gated on `canRead` and NOT
   * on `canUpdateCompanies`** — the identical arrangement `companySources` above
   * carries, and for the identical reason. ⛔ Gating it on the write would give a
   * read-only reader a Company type filter with no options in it, which is the
   * staff-directory defect this repo has on record: a READ gated on a WRITE, where
   * an empty picker is indistinguishable from a master that failed to load. The
   * accepted cost is one request on a small master for such a reader.
   */
  const companyTypes = useCompanyTypes(canRead);
  /*
   * ⚠️ **The same rows projected for the funnel.** `FilterMaster` is the shape
   * every funnel on this page reads; the PICKER takes `companyTypes` itself,
   * because `companyTypeOptions` needs the real `ReferenceRecord`s — see
   * `PartnershipEditing.companyTypes`.
   */
  const companyTypeMaster: FilterMaster = {
    rows: companyTypes.records,
    loading: companyTypes.loading,
    failed: companyTypes.failed,
  };

  /* --- what the reader may write ------------------------------------------ *
   *
   * 🔴 **RESOLVED HERE AND THREADED DOWN AS BOOLEANS, exactly as `canCreate`
   * already is.** `ability` is in scope at this component and nowhere else on
   * this page, which is what stops two cells answering the same question
   * differently — and per MODULE rather than once for the page, because a
   * permission set can grant companies and refuse invoices.
   */
  const editGateFor = (module: ModuleSlug): EditGate => {
    const canEdit = ability.canUpdate(module);
    return {
      canEdit,
      /*
       * ⚠️ **Only when they may NOT.** A sentence under a live control would be
       * helper text; under a dead one it is the documented exception, because a
       * bare `disabled` makes a permission refusal, a derived column and an
       * ordinary value look identical.
       */
      disabledReason: canEdit
        ? undefined
        : (denialReason(ability, 'update', module) ??
          'Your account does not have permission to change this.'),
    };
  };
  /*
   * ⚠️ **`companies` GATES THREE COLUMNS ON ALL FIVE CARDS** — the country, the
   * company stage and the projects of interest all write a COMPANY however they
   * are reached, so a deal row's projects answer to `companies.update` and not to
   * `deals.update`. See `EDIT_MODULE`.
   */
  const companyEditGate = editGateFor('companies');
  const canUpdateCompanies = canRead && companyEditGate.canEdit;

  /*
   * The two masters the EDITORS pick from.
   *
   * ⚠️ **Gated on the WRITE, which is correct here and is the mirror image of a
   * defect this repo has on record.** Neither list feeds a DISPLAY — a row
   * carries its own country name and its own stage name — so a reader who may not
   * write spends nothing and sees exactly the same cells. The defect to avoid is
   * gating a list a READER needs: the staff directory was once fetched only for
   * users who could edit, so a read-only reader got an empty owner picker,
   * indistinguishable from a directory that failed to load.
   *
   * ⛔ **`useCountryPickerMaster` is NOT `useCountryMaster` above and the two may
   * not be merged** — a filter offers what can MATCH and narrows to
   * `hasCompanies`; a picker offers what can be CHOSEN and must not, or nobody
   * could ever be the first company in a country.
   */
  const countryPickerMaster = useCountryPickerMaster(canUpdateCompanies);
  const companyStages = useCompanyStages(canUpdateCompanies);
  const companyStageMaster: FilterMaster = {
    rows: companyStages.records,
    loading: companyStages.loading,
    failed: companyStages.failed,
  };

  /*
   * The projects-of-interest picker's candidates and its cycle pills.
   *
   * 🔴 **READ ONCE FOR THE WHOLE PAGE, NEVER PER ROW-OPEN.** `/projects` and
   * `/cycles` fetched when a modal opens would be one request per row against a
   * budget shared across the office — on a page whose five cards already fetch a
   * hundred rows each. The modal is handed the result. ⚠️ *SUPERSEDED: "a
   * 120-per-60s budget."* It is **12,000/60s** (corrected 2026-09-17); what
   * refuses a per-row read is its SHAPE, not the size of the bucket.
   *
   * ⚠️ **Gated on `companies.update`, not on a general "may write anything"**:
   * this field is only ever written with `PATCH /companies`, so that is the one
   * permission that can open the modal at all.
   */
  const projectOptions = useProjectOptions(canUpdateCompanies);
  const projectCycleScopes = useCycleScopeIds(canUpdateCompanies);
  const filterMasters: FilterMasters = {
    countries: countryMaster,
    companySources: companySourceMaster,
    companyTypes: companyTypeMaster,
    cycles: cycleMaster,
    dealStages: dealStageMaster,
    quoteStages: {
      rows: stageMaster.stages,
      loading: stageMaster.loading,
      failed: stageMaster.failed,
    },
    invoiceStatuses: {
      rows: statusMaster.statuses,
      loading: statusMaster.loading,
      failed: statusMaster.failed,
    },
  };

  /*
   * Who is waiting on a signature, for every quote on the page at once.
   *
   * ⚠️ **ONE request for the whole page, not one per row** — the reasoning is on
   * `OUTSTANDING_PER_PAGE` in `@/lib/outstanding-envelopes` and is the same
   * argument `/quotes` makes: `GET /docusign/envelopes` filters by a SINGLE
   * `quoteId`, so asking per row would cost one request per quote on screen —
   * and this page's cards now fetch up to 100 rows each, against a budget shared
   * by everyone behind the office NAT. **Five cards × `PER_PAGE` rows is the
   * objection: a per-row lookup on a paginated list is the wrong SHAPE, and it
   * grows with the page size rather than with the page count.**
   *
   * ⚠️ *SUPERSEDED: "against a 120-per-60s budget … It would spend the whole
   * office's allowance on one dashboard load."* The limiter is **12,000/60s**
   * (corrected 2026-09-17), so one load no longer exhausts the bucket and that
   * sentence is withdrawn rather than rephrased — it was arithmetic, and the
   * arithmetic was off by a factor of a hundred. **The batching stands on the
   * shape**; the `429` storm this app was once burnt by, where the refusals read
   * as a dead session and redirected everybody to `/login`, really happened.
   *
   * ⚠️ Gated on `canRead`, the same test that stops the five table fetches and
   * the two masters above. A reader about to be shown a single refusal panel
   * should not spend a request on a column nothing will render.
   *
   * The second argument is a re-run token. Nothing on this page SENDS anything
   * for signature — it is five read-only tables — so there is nothing to
   * invalidate and it is a constant. On `/quotes` it is bumped after a send or a
   * void, which are the only two things that change the outstanding set.
   */
  const [signatures] = useOutstandingEnvelopes(canRead, 0);

  /*
   * ⚠️ ONE sheet for the whole page, not one per row. Five tables of fifteen
   * rows would otherwise be seventy-five mounted drawers, each with its own
   * fetch and its own focus trap. A row only says WHICH record it is for; the
   * row itself is held rather than its id, so the heading always has a name
   * behind it and the target comes from the row's own `activityTarget` — a
   * deal row opens the deal's tasks, a company row the company's.
   */
  const [activityFor, setActivityFor] = useState<PartnershipRow | null>(null);
  /*
   * Where the sheet opens. `'notes'` from the "Your last note" cell, so the
   * drawer scrolls to the notes; `undefined` from every other trigger, which
   * keeps their behaviour exactly as it was. Set in the same handler as the
   * row, so the two cannot disagree about which open they describe.
   */
  const [activityFocus, setActivityFocus] = useState<'notes' | undefined>(undefined);
  const openActivity = (row: PartnershipRow, focus?: 'notes') => {
    setActivityFocus(focus);
    setActivityFor(row);
  };

  /*
   * Which create modal is open, or `null` for none.
   *
   * ⚠️ **ONE piece of state for all five cards, holding the KIND rather than a
   * boolean per modal.** Four booleans would let two modals be open at once —
   * two focus traps fighting, which this repo already records as a real defect
   * on nested modals — and the union makes that unrepresentable.
   */
  const [createFor, setCreateFor] = useState<PartnershipCreate | null>(null);

  /*
   * Which row's projects of interest are being edited, and which card it is on.
   *
   * ⚠️ **ONE modal for the whole page, holding the ROW** — the same shape as
   * `activityFor` above, and for the same reason: five cards of a hundred rows
   * would otherwise be five hundred mounted dialogs, each with its own focus
   * trap and its own copy of the project list.
   *
   * ⚠️ **The CARD is held beside the row because the row does not carry it**, and
   * the save has to know which card to refetch. A `PartnershipRow` is
   * deliberately card-agnostic — the same company appears on several — so this is
   * the one place the pairing exists.
   *
   * 🔴 **ONLY THE TWO `/companies` CARDS CAN FILL THIS SINCE 2026-09-14**, and
   * the conclusion survives a correction to the reason. ⚠️ *SUPERSEDED, later
   * the same day: "{@link DocumentProjectsCell} is read-only and **takes no
   * `onOpen`**, so a deal, quote or invoice row can no longer reach this
   * state."* **That cell IS a trigger now** — the owner reported it as not
   * opening and chose a read-only list — but it fills
   * {@link documentProjectsFor} below, never this. **Why** is on that cell: this
   * modal writes the COMPANY's whole set. Opened from a cell showing the document's line projects, the reader's untick would
   * land on a list they were never shown.
   *
   * ⛔ **So what keeps the two apart is no longer the TYPE.** It was structural
   * while the cell accepted no handler at all; it is now a naming distinction
   * between two `(row) => void` props threaded side by side, and the guard is
   * the comment at the `lineProjects` arm.
   *
   * ⚠️ **`tableKey` is NOT narrowed to those two, deliberately.** It feeds
   * {@link companyIdOf} and the per-card refetch, both of which are total over
   * {@link PartnershipTableKey}; narrowing the type would encode the CURRENT set
   * of openers into the state's shape, so restoring an editor on a card whose
   * subject matches would be a type change rather than a call-site one. The rule
   * lives in the cells, where the reason for it can be read.
   */
  const [projectsFor, setProjectsFor] = useState<{
    row: PartnershipRow;
    tableKey: PartnershipTableKey;
  } | null>(null);

  /*
   * Which DOCUMENT row's line projects are being read in full — owner's
   * decision, 2026-09-14, on their report that the column did not open on click.
   *
   * ⚠️ **ONE modal for the whole page**, the same rule as `projectsFor`,
   * `activityFor` and the four create forms: five cards of a hundred rows would
   * otherwise be five hundred mounted dialogs, each with its own focus trap.
   *
   * ⛔ **IT IS NOT `projectsFor` AND MUST NEVER BE COLLAPSED INTO IT**, however
   * alike the two look. That one opens `CompanyProjectsModal`, which WRITES the
   * company's whole set. This one opens a read-only list of a DIFFERENT field. Two
   * states, two dialogs, two subjects; the only thing they share is a shape.
   *
   * ⚠️ **The ROW alone, with no `tableKey`.** `projectsFor` carries the card
   * because its save has to know which card to refetch and `companyIdOf` needs it
   * to resolve a company. Nothing here writes, so there is no refetch and no
   * company to resolve — the row carries `lineProjects` and its own label, which
   * is everything the dialog reads. ⛔ **Do not add the card "for symmetry"**: an
   * unread field on a state object is the next thing somebody consults.
   */
  const [documentProjectsFor, setDocumentProjectsFor] = useState<PartnershipRow | null>(null);

  /*
   * Which company's contacts are open in the contacts manager — owner's
   * decision 2026-10-16 (the read-only mail list of 2026-09-17 until then).
   *
   * ⚠️ **ONE modal for the whole page**, the same rule as `projectsFor`,
   * `documentProjectsFor`, `activityFor` and the four create forms. It is not a
   * cost-saving: this page mounts the two `/companies` cards at a hundred rows
   * each, and **a modal per row would show one set of contacts per row while two
   * rows can name one company** — the failure `CompanyProjectsModal` records,
   * where a per-row dialog left every sibling row stating the old set.
   *
   * 🔴 **THE RESOLVED COMPANY, NOT THE ROW — the one state on this page that
   * does not hold a row, and the reason is worth reading before it is
   * "harmonised" with its four neighbours.** The modal's own prop is
   * `{id, name} | null`, and `companyIdOf` can in principle answer `null`; a row
   * here would make "open, on a row with no company" a representable state that
   * the mount would then have to render as *closed*, which is a dialog that
   * cannot be opened and cannot be seen not to open. Holding the pair instead
   * makes this state EXACTLY the prop, so `company={contactsFor}` needs no
   * reconciliation at all and the withholding happens at the button, where the
   * row is — see {@link RowContactsButton}.
   *
   * ⚠️ *SUPERSEDED 2026-10-16: "Nothing here writes, so nothing refetches."*
   * The manager adds and deletes contacts, which moves the row's count. See
   * `contactsChangedRef`.
   */
  const [contactsFor, setContactsFor] = useState<ContactsCompany | null>(null);
  /*
   * Whether the open manager added or deleted a contact. ⚠️ **The refetch waits
   * for CLOSE** rather than running on every `onChanged`: a refetch blanks the
   * card underneath to skeletons, and doing that behind an open dialog per edit
   * is noise. On close, both company cards reload (`reloadAfterEdit` — Leads
   * and Companies are a partition), which brings the fresh `contactCount` back.
   * Inline field edits, primary and group changes do not move the count, so they
   * do not set this.
   */
  const contactsChangedRef = useRef(false);

  /*
   * Which company's projects of interest are being turned into a document's
   * line items, and as what kind — or `null` for none.
   *
   * ⚠️ **ONE flow for the whole page**, the same rule as the drawer above, the
   * four create forms and the projects modal: five cards of a hundred rows each
   * mounting their own would be five hundred focus traps, and this one performs
   * its own reads.
   *
   * ⚠️ **`null` while closed, never a sentinel `0`.** The same reasoning
   * `CompanyProjectsModal`'s `companyId` carries below — a closed flow names no
   * record at all, where an id that happens to match nothing today is the weaker
   * guarantee.
   *
   * ⚠️ **It holds the REQUEST, not a row**, unlike `projectsFor` and
   * `activityFor`: the flow fetches the company itself and reads only `id` and
   * `name`, so handing it a `PartnershipRow` would hand it fifty columns it must
   * not consult and would make the flow card-aware.
   */
  const [projectLinesFor, setProjectLinesFor] = useState<CompanyProjectLinesRequest | null>(null);

  /*
   * The pending accepted-or-declined quote stage, if a reader is being asked
   * about one.
   *
   * ⚠️ **It carries the `resolve` of the save's own promise** — see
   * {@link QuoteStageConfirm}, which explains why that is the only mechanism
   * `InlineField` leaves available and what it costs.
   */
  const [stageConfirm, setStageConfirm] = useState<QuoteStageConfirm | null>(null);

  const confirmQuoteStage = (request: Omit<QuoteStageConfirm, 'resolve'>) =>
    new Promise<boolean>((resolve) => setStageConfirm({ ...request, resolve }));

  /*
   * ⚠️ **`resolve` is called OUTSIDE the state updater, deliberately.** A
   * resolver fired inside `setState`'s callback is a side effect in a function
   * React may run twice, and a save that has already settled would then be told
   * again — harmless on a promise, and exactly the habit that stops being
   * harmless the day the callback does something else.
   *
   * ⚠️ **Every exit settles it.** A dialog dismissed by Escape, by the scrim or
   * by Cancel all reach `onClose`, so there is no path that leaves the save's
   * promise pending and the field reading "Saving…" for ever.
   */
  const settleStageConfirm = (confirmed: boolean) => {
    stageConfirm?.resolve(confirmed);
    setStageConfirm(null);
  };

  /**
   * Writes one company's projects of interest, for the modal.
   *
   * ⛔ **A WHOLE-SET REPLACE: `[]` CLEARS.** `projectIdsOfInterest` is not a
   * merge, which is why the cell refuses to open this at all when the row's list
   * was never read — an editor over an unread set would show nothing ticked and
   * destroy everything the company holds, with a `200`. See
   * `ProjectOfInterestCell`.
   *
   * ⚠️ **Reads the response back.** A `200` is not evidence the change happened:
   * strictness answers *the key was not recognised*, and this answers the
   * different question — *recognised and not applied*.
   */
  const saveProjectsOfInterest = async (
    companyId: number,
    projectIds: number[],
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    const tableKey = projectsFor?.tableKey ?? null;
    try {
      // ⛔ ONE explicit literal key.
      const { data } = await api.updateCompany(companyId, {
        projectIdsOfInterest: projectIds,
      });

      /*
       * ⚠️ **Compared as SETS, never as sequences.** The server returns the
       * company's projects in its own order, and the modal holds them in the
       * order the reader ticked; comparing as lists would report every save as
       * unapplied the moment the two disagreed.
       *
       * ⚠️ **No `?? []` on `data.projectsOfInterest`.** If a server were to omit
       * the key this throws and is reported as a failure, which is the honest
       * outcome — coalescing would turn an unread response into the confident
       * claim that every project was removed, on the one field whose whole job is
       * to say what is being pursued.
       */
      const saved = new Set(data.projectsOfInterest.map((project) => project.id));
      const wanted = new Set(projectIds);
      if (saved.size !== wanted.size || [...wanted].some((id) => !saved.has(id))) {
        return {
          ok: false,
          message:
            'Projects of interest not saved — the server didn’t apply the change. Try again.',
        };
      }

      if (tableKey !== null) reloadAfterEdit(tableKey);
      return { ok: true };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * ⚠️ A 403 carries no `details`, so it can never be a field error. It can
         * arrive even though the gate said otherwise, because a role can change
         * under an access token that lasts over five days — the gate is the
         * optimisation, this is the guarantee.
         */
        if (caught.status === 403) {
          return {
            ok: false,
            message: `${
              companyEditGate.disabledReason ??
              'Your account no longer has permission to change this company.'
            } Nothing was saved.`,
          };
        }
        /*
         * Ordered by SPECIFICITY, never by which shape arrived first: the 422
         * naming the key, then the form-level sentence a `.strict()` rejection
         * lands on, then the server's own message — which on a 422 is only
         * "Validation failed".
         */
        return {
          ok: false,
          message:
            caught.fieldErrors.projectIdsOfInterest ?? caught.formMessage ?? caught.message,
        };
      }
      return {
        ok: false,
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  /*
   * Refetch the cards a newly created record could have landed on.
   *
   * 🔴 **A COMPANY REFRESHES BOTH `/companies` CARDS, AND THAT IS NOT
   * BELT-AND-BRACES.** The form lets the person choose the stage, so a new
   * company lands on Leads or on Companies depending on a value this page never
   * sees — and reading it back to decide would be a second copy of the
   * lead/non-lead partition that `nonLeadStageFilter` exists to have only one
   * of. Refreshing both is one extra request and cannot be wrong; guessing is
   * one request and is silently wrong whenever the guess misses, leaving the
   * person looking at a card that does not show what they just created.
   *
   * The other three map one-to-one, and the mapping is derived from the specs
   * rather than written down — so a sixth card cannot arrive without it.
   */
  /*
   * The row "+" menus' targets, gated per MODULE and resolved once for the page
   * rather than per card — unlike `canCreate` above, which is per card because
   * each card creates a different kind of record. These two lists are the same
   * wherever they are mounted: what a company can have raised against it does
   * not depend on which card the row is on.
   *
   * ⚠️ Empty collapses to `null`, so a reader who may create none of them gets
   * no trigger at all rather than one opening an empty popover — and
   * `tableMinWidthRem` stops reserving width for it in the same step.
   */
  const companyRowTargets = useMemo(
    () => COMPANY_CREATE_TARGETS.filter((target) => ability.canCreate(target.module)),
    [ability],
  );
  const dealRowTargets = useMemo(
    () => DEAL_CREATE_TARGETS.filter((target) => ability.canCreate(target.module)),
    [ability],
  );
  /*
   * 🔴 **THE QUOTES CARD'S GATE, AND IT IS NOT `ability.canCreate(...)` LIKE THE
   * TWO ABOVE IT.** `POST /quotes/{id}/convert` is `requireRole(...WRITE_ROLES)`,
   * so the ROLE decides. `permissions.ts` reconciles that with
   * `/permissions/me`, and the two disagree in BOTH directions here: the seeded
   * `basic-user` set once carried no `quotes.convert` while the route took any
   * write role, so `canCreate('invoices')` would hide a control the server
   * honours — and a set granting invoice creation to a `viewer` would offer one
   * it refuses. `ROLE_AUTHORITATIVE` keeps the permission list out of it.
   *
   * ⚠️ It is one question for the whole menu rather than a per-item filter,
   * which is why there is no `quoteRowTargets` list beside the two above.
   */
  const canConvertQuote = ability.canConvertQuote();

  /**
   * Open the project-lines flow for a company row — or decline it, and let the
   * menu item navigate.
   *
   * 🔴 **RETURNING `false` IS THE WHOLE FALLBACK, AND IT IS WHY THIS IS
   * ADDITIVE.** A row with nothing to seed keeps the menu's existing
   * `?companyId=N&new=1` navigation byte for byte — the item's own `href`, the
   * same one `/companies` follows. Taking the item and opening a flow over an
   * empty list would be a dialog that can only say "nothing here", reached from
   * a control that used to work.
   *
   * ⛔ **`projectsOfInterest === null` IS NOT "no projects".** It is *never
   * read*, or a row carrying no company at all, and an empty array is a company
   * somebody has decided holds none. Both decline here — but they are tested as
   * two facts because a `?? []` FOLDS them, and this page permits that coalesce
   * in exactly one place (`CompanyProjectsModal`'s `projects`, below) and only
   * because `ProjectOfInterestCell` refuses to open on a `null` first. There is
   * no such guard in front of this, so there is no `??` here.
   *
   * ⚠️ **The two words are mapped, never assumed equal** — see
   * `PROJECT_LINES_KIND`.
   *
   * ⚠️ **It never touches `setCreateFor`.** That state drives the four
   * card-level create modals, and a flow that also opened one would be two
   * focus traps — the case `createFor` is a union precisely to make
   * unrepresentable.
   */
  const openProjectLines = (row: PartnershipRow, module: CompanyCreateTarget['module']): boolean => {
    const projects = row.projectsOfInterest;
    if (projects === null) return false;
    if (projects.length === 0) return false;
    setProjectLinesFor({
      kind: PROJECT_LINES_KIND[module],
      /*
       * ⚠️ `row.id` is the COMPANY on both `/companies` cards — `companyIdOf`'s
       * own reasoning, and what `CreateForCompanyMenu` has been handed here
       * since it landed. This arm is only ever reached from those two cards.
       */
      company: { id: row.id, name: row.primary },
    });
    return true;
  };

  /*
   * ⚠️ **Keyed on the card, and BOTH `/companies` cards get one now** —
   * *SUPERSEDED 2026-09-13, owner's instruction: "the two `/companies` cards do
   * NOT both get one — Leads is out of scope; see `RowCreate`."* Written as a
   * switch rather than a lookup so that a sixth card cannot silently acquire or
   * miss a menu: the default is "no menu", which is the safe answer for a card
   * whose module list has none to mirror.
   *
   * ⚠️ **`invoices` is what the default is now protecting** — not Quotes, and no
   * longer Leads; see `RowCreate`. Nothing is raised from an invoice and nothing
   * on `/invoices` offers one, so the absence there is still the decision.
   */
  const rowCreateFor = (key: PartnershipTableKey): RowCreate | null => {
    /*
     * ⚠️ **ONE arm for both company cards, on the one `companyRowTargets`
     * list.** They are a partition of one table, so a menu correct on one is
     * correct on the other; two arms would be two places for the gate and the
     * handler to drift.
     */
    if (key === 'leads' || key === 'companies') {
      return companyRowTargets.length > 0
        ? { kind: 'company', targets: companyRowTargets, onSelect: openProjectLines }
        : null;
    }
    if (key === 'deals') {
      return dealRowTargets.length > 0 ? { kind: 'deal', targets: dealRowTargets } : null;
    }
    if (key === 'quotes') {
      return canConvertQuote ? { kind: 'quote' } : null;
    }
    return null;
  };

  /**
   * Refetch the named cards.
   *
   * ⚠️ **The ONE way this page forces a refetch**, shared by the create modals
   * and by every inline edit — so "which cards could this have changed" is
   * answered per caller and the mechanism is not duplicated.
   */
  const bumpReload = (keys: readonly PartnershipTableKey[]) => {
    setReloadTokens((current) => {
      const next = { ...current };
      for (const key of keys) next[key] = current[key] + 1;
      return next;
    });
  };

  const reloadAfterCreate = (create: PartnershipCreate) => {
    bumpReload(tables.filter((table) => table.create === create).map((table) => table.key));
  };

  /**
   * Refetch after an inline edit LANDED, so the row re-ranks.
   *
   * 🔴 **THE ORDERING IS SERVER-SIDE AND THIS PAGE HOLDS PURE SERVER STATE.** The
   * backend folds a record's own `updated_at` into `latest_activity_at`, so an
   * edit moves the row under the default "Recent activity" sort — and there is no
   * local patch layer here to nudge, by design. Only a refetch can show it.
   *
   * 🔴 **AN EDIT ON EITHER `/companies` CARD RELOADS BOTH, and that is not
   * belt-and-braces.** Leads and Companies are a PARTITION on `stageId`
   * (`nonLeadStageFilter` derives one from the other), so a company that stops
   * being a lead has to leave one card and arrive on the other — and this
   * callback is one per CARD, not one per column, so it cannot know whether the
   * stage was what moved.
   *
   * ⚠️ **Reloading both is one extra request and cannot be wrong; guessing is one
   * request and is silently wrong whenever the guess misses**, leaving the reader
   * looking at a card that still lists a company it no longer holds. That is
   * `reloadAfterCreate`'s own reasoning, arriving on the write path.
   *
   * ⚠️ **THE ACCEPTED COST, STATED: the other cards go stale.** A company's
   * projects of interest ride on its deal, quote and invoice rows too, so editing
   * them from the Deals card leaves the Companies card showing the old list until
   * something reloads it. Bumping all five would fix that and would blank five
   * cards to skeletons on every cell save — too much for a fact that is one
   * refresh from correct.
   */
  const reloadAfterEdit = (key: PartnershipTableKey) => {
    bumpReload(key === 'leads' || key === 'companies' ? ['leads', 'companies'] : [key]);
  };

  /**
   * What every create modal does on a successful save: close, then refetch.
   *
   * ⚠️ **It does NOT navigate to the new record.** The module pages do, because
   * creating there is the start of working on that record; here it is topping up
   * a dashboard, and throwing the reader off a page holding five cards' worth of
   * chosen orderings and filters would be a worse answer than leaving them where
   * they are with the row now in front of them. The row's own link is one click.
   */
  const finishCreate = (create: PartnershipCreate) => {
    setCreateFor(null);
    reloadAfterCreate(create);
  };

  /*
   * The quote PDF viewer (owner 2026-10-25): ONE host for the page, opened from
   * the Quotes card's rows and the Deals card's picker. Mark Sent or "Send to
   * Xero and preview" changes a quote's stage, so the Quotes card refetches —
   * that card only, by the `reloadAfterEdit` reasoning above.
   */
  const pdfPreview = useQuotePdfPreview({ onChanged: () => reloadAfterEdit('quotes') });
  const navigate = useNavigate();
  const offersPdfPreview = canPreviewQuotePdf(ability);
  const pdfPreviewFor = (key: PartnershipTableKey): PdfPreviewRow | null => {
    if (!offersPdfPreview) return null;
    if (key === 'quotes') return { kind: 'quote', onPreview: pdfPreview.open };
    if (key === 'deals') return { kind: 'deal', onPreview: pdfPreview.open };
    return null;
  };

  // Below every hook, so the early return cannot change the hook order.
  if (!canRead) {
    return (
      <>
        <PageHeader title="University Partnerships" />
        <Panel className="min-w-0">
          <EmptyState
            title="This dashboard isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title="University Partnerships"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {/*
              * The page-level Account lead scope (owner 2026-10-27): all five
              * cards and the funnel. Offered only to a reader who may switch it.
              */}
            {canChooseLeads && leadScope !== null && (
              <div className="flex min-w-0 items-center gap-2">
                <label htmlFor={leadsControlId} className="shrink-0 text-xs text-fg-muted">
                  Account lead
                </label>
                <div className="w-56 max-w-full max-sm:w-48">
                  <MultiCombobox
                    id={leadsControlId}
                    values={leadValues}
                    onChange={changeLeads}
                    options={leadOptions}
                    loading={leadStaff.loading || leadStaff.groupsLoading}
                    maxVisibleChips={1}
                    summaryNoun={{ singular: 'lead', plural: 'leads' }}
                    maxSelected={50}
                    {...(leadStaff.failed
                      ? { footnote: 'Couldn’t load the account leads.' }
                      : leadStaff.hasMore
                        ? { footnote: 'Showing the first 100 staff.' }
                        : {})}
                  />
                </div>
              </div>
            )}
            <LayoutToolbar layout={layout} />
          </div>
        }
      />

      {/*
        * The layout manager (owner 2026-10-20): the funnel and the five cards
        * are its six sections, all `full` by default, so the grid below renders
        * exactly the old single column until somebody presses Edit layout.
        * ⚠️ `[&>*]:min-w-0` is passed on — the reason is in the note below.
        */}
      <LayoutGrid
        layout={layout}
        className="[&>*]:min-w-0"
        render={{
          targets: () => (
            <FunnelPanel
              userId={funnelOrganisation ? viewerId : singleLeadId}
              isSelf={funnelOrganisation || scopedToSelf}
              organisation={funnelOrganisation}
            />
          ),
          'follow-ups': () =>
            ability.canRead('trackers-prospect-follow-ups') && ability.canRead('companies') ? (
              <FollowUpsPanel />
            ) : null,
          ...Object.fromEntries(
            tables.map((state) => [
              state.key,
              () => (
        /*
          * 🔴 **ONE CARD PER ROW AT EVERY WIDTH — owner's instruction,
          * 2026-09-03.** *SUPERSEDED: `lg:grid-cols-2`, "Two tables per row from
          * `lg`, one below … The fifth table sits alone, deliberately NOT
          * spanned."* There is no pairing left to preserve and no odd card out,
          * so the note about the fifth sitting alone went with the second column
          * rather than being left to read as a description of a layout that no
          * longer exists.
          *
          * ⚠️ **`[&>*]:min-w-0` SURVIVES THE CHANGE AND IS STILL LOAD-BEARING —
          * do not delete it as leftover from the two-column layout.** A grid item
          * defaults to `min-width: auto`, which is a floor of its CONTENT's
          * minimum size, and that is true of a single-column track exactly as it
          * was of two: without it a table wider than the viewport widens its own
          * track and pushes the PAGE into horizontal scroll, instead of scrolling
          * inside its own container. One column makes that LESS likely to fire,
          * not impossible — and this repo has the resulting page-level overflow
          * on record as a real defect. Kept on the container rather than on
          * `Panel`, so a sixth table cannot arrive without it.
          *
          * `grid` is kept over `flex flex-col` for that reason alone: the idiom,
          * the `gap`, and the `min-w-0` handling are one thing that has been got
          * right once. (2026-10-20: the grid is `LayoutGrid` now, and the
          * selector is handed to it as `className`.)
          */
            <PartnershipTablePanel
              key={state.key}
              state={state}
              masters={masters}
              filterMasters={filterMasters}
              signatures={signatures}
              /*
               * ⚠️ Resolved HERE, per card, from the module the card's create
               * target names — never from the card's own key, and never once
               * for the page. `ability.canCreate` is per module: a permission
               * set can grant companies and refuse invoices, and a single
               * page-level test would then show a New invoice button that
               * collects a 403.
               */
              canCreate={ability.canCreate(CREATE_SPEC[state.create].module)}
              exportScope={
                isCompanyCardKey(state.key) && ability.canExport('companies') ? leadScope : null
              }
              scopedToOthers={leadScope !== null && !scopedToSelf}
              /*
               * ⚠️ **A DIFFERENT question from `canCreate` above, on purpose.**
               * That one asks whether this card's own "+" may make a record of
               * the kind the card lists; this asks what may be raised AGAINST a
               * row. They resolve different modules and are wired separately so
               * neither can be read as implying the other.
               *
               * ⚠️ **SPLIT SINCE 2026-09-13 — some of these items navigate and
               * some open a flow HERE.** *SUPERSEDED: "**These items NAVIGATE —
               * they open nothing on this page, so they do not go through
               * `finishCreate`/`reloadAfterCreate`.**"*
               *
               * **The Deals and Quotes cards still navigate, and that is not an
               * omission.** The Deals menu must land on the deal's own page
               * (`/deals/{id}?create=…`) because a create form cannot carry a
               * deal's basket — a line is `POST /quotes/{id}/products` and
               * cannot exist before the record does — and mounting a
               * company-locked form here instead is exactly the behaviour
               * `/deals` reverted once already. Nothing is created while this
               * page is mounted for those two, so there is nothing to refetch;
               * coming back to the dashboard remounts it and the record is there.
               *
               * **The two `/companies` cards are the exception**, and only for a
               * row that has projects of interest to seed: `openProjectLines`
               * takes the item and `CompanyProjectLinesFlow` creates the
               * document while this page is still mounted — so THAT path does
               * refetch, through `reloadAfterCreate` at the flow's own mount
               * below. A row with nothing to seed falls through to the same
               * navigation as before. ⚠️ **It does not go through
               * `finishCreate`**: that also clears `createFor`, which belongs to
               * the four card-level modals and not to this flow.
               */
              rowCreate={rowCreateFor(state.key)}
              /*
               * ⚠️ **A THIRD question again, and the two gates inside it are not
               * one.** `company` is `companies.update` and governs the country,
               * the stage and the projects of interest — the three columns that
               * write a COMPANY however they are reached. `record` is the card's
               * own module and governs the quote stage, the invoice status and
               * the invoice due date. They coincide on the two `/companies` cards
               * by coincidence, not by rule: a permission set can grant companies
               * and refuse invoices.
               */
              edit={{
                company: companyEditGate,
                record: editGateFor(EDIT_MODULE[state.key]),
                countries: countryPickerMaster,
                companyStages: companyStageMaster,
                /*
                 * ⚠️ **The hook's own state, NOT `companySourceMaster`** — the
                 * picker needs the real `ReferenceRecord`s that
                 * `companySourceOptions` reads, where the funnel takes the lossy
                 * `FilterMaster` projection. One read, two shapes; see
                 * `PartnershipEditing.companySources`.
                 */
                companySources,
                /*
                 * ⚠️ **The hook's own state, NOT `companyTypeMaster`** — the same
                 * one-read-two-shapes arrangement `companySources` directly above
                 * carries: the picker needs the real `ReferenceRecord`s that
                 * `companyTypeOptions` reads, where the funnel takes the lossy
                 * `FilterMaster` projection.
                 */
                companyTypes,
                // The per-stage status columns and the page's one status dialog.
                stageStatus: {
                  columns: stageStatus.columns,
                  statuses: stageStatus.statuses,
                  failed: stageStatus.failed,
                },
                requestStageStatusChange,
                onSaved: () => reloadAfterEdit(state.key),
                onOpenProjects: (row) => setProjectsFor({ row, tableKey: state.key }),
                confirmQuoteStage,
              }}
              onSortChange={changeSort}
              onFilterChange={changeFilter}
              onLastNoteRangeChange={changeLastNoteRange}
              onCreate={(card) => setCreateFor(card.create)}
              onOpenActivity={openActivity}
              /*
               * ⚠️ **`null` on the three document cards, which is what puts the
               * scope in ONE place** — `CONTACTS_CARD_KEYS`, where the owner's
               * decision and the case against widening it can both be read. The
               * card takes the prop at face value: it renders no button and
               * reserves no width for one.
               *
               * ⚠️ **`setContactsFor` DIRECTLY, with no arrow.** The handler's
               * argument is already the modal's prop — see `contactsFor` — so
               * there is nothing to resolve here.
               */
              onOpenContacts={CONTACTS_CARD_KEYS.includes(state.key) ? setContactsFor : null}
              /*
               * ⚠️ **The ROW alone, with no `tableKey` beside it** — unlike
               * `onOpenProjects` above, which pairs them because its save has to
               * know which card to refetch and which company to write to. This
               * dialog writes nothing, so there is nothing to refetch and no
               * company to resolve: the row carries its own `lineProjects` and
               * its own label, which is the whole of what the dialog reads.
               */
              onOpenDocumentProjects={setDocumentProjectsFor}
              pdfPreview={pdfPreviewFor(state.key)}
            />
              ),
            ] as const),
          ),
        }}
      />

      {/*
        * ⚠️ **Mounted at the PAGE, not inside a card.** One of each, whichever
        * card opened it — five cards each mounting their own would be five
        * copies of some of the largest components in this app, and the two
        * `/companies` cards would hold two company forms that could both be
        * open. Each returns `null` while closed, so nothing renders until one is
        * asked for.
        *
        * ⚠️ **`onSaved` and `onClose` are DIFFERENT ACTIONS and only one
        * refetches.** Cancelling created nothing, so a reload there would be a
        * request that can only return what is already on screen. `onSaved`
        * closes and refetches through `finishCreate`, which is also what keeps
        * the "which cards could this have landed on" decision in one place.
        */}
      <CompanyFormModal
        open={createFor === 'company'}
        onClose={() => setCreateFor(null)}
        onSaved={() => finishCreate('company')}
      />
      <DealFormModal
        open={createFor === 'deal'}
        onClose={() => setCreateFor(null)}
        onSaved={() => finishCreate('deal')}
      />
      {/*
        * ⚠️ **No `onXeroPush` and no `onLinesSaved` on either document form,
        * deliberately.** Both are optional, and this page has nothing to do with
        * either event: it renders no Xero panel to refresh and no line grid to
        * report into. Passing a handler that only refetched the card would claim
        * the dashboard knows something about a push that it does not.
        */}
      <QuoteFormModal
        open={createFor === 'quote'}
        onClose={() => setCreateFor(null)}
        onSaved={() => finishCreate('quote')}
        // "Create and preview" (2026-10-25) leaves for the quote page, which opens the PDF.
        onCreatedPreview={(q) => navigate(quotePreviewHref(q.id))}
      />
      {pdfPreview.element}
      {/* The `{Stage} status` cells' date / reason dialog — ONE for the page. */}
      {stageStatusDialog}
      <InvoiceFormModal
        open={createFor === 'invoice'}
        onClose={() => setCreateFor(null)}
        onSaved={() => finishCreate('invoice')}
      />

      <ActivityDrawer
        open={activityFor !== null}
        onClose={() => setActivityFor(null)}
        target={activityFor?.activityTarget ?? CLOSED_TARGET}
        recordLabel={activityFor ? recordLabelOf(activityFor) : ''}
        focus={activityFocus}
        viewerId={user?.id ?? null}
        viewerTimeZone={viewerZone(user?.timezone)}
      />

      {/*
        * ⚠️ **ONE modal for the page, whichever card and whichever row opened
        * it** — the same rule as the drawer above and the four create forms. The
        * project list and the cycle index it picks from are read ONCE here and
        * handed in; a modal that fetched them itself would be one `/projects` and
        * one `/cycles` per row-open.
        *
        * ⚠️ **`companyId` is `null` while closed, not `0`** — the modal declares
        * it nullable precisely so a closed dialog names no record at all. `0`
        * would be a company id that happens to match nothing today, which is the
        * weaker guarantee: `ActivityDrawer`'s `CLOSED_TARGET` has to use one only
        * because its own prop is not nullable.
        *
        * ⚠️ **`disabledReason` is passed even when `undefined`, because the modal
        * declares it REQUIRED as `string | undefined`.** The prop is "why not, if
        * not" — a conditional spread would let a later edit drop it silently and
        * leave a read-only dialog with no explanation at all, which is the state
        * a `disabledReason` exists to prevent.
        */}
      <CompanyProjectsModal
        open={projectsFor !== null}
        onClose={() => setProjectsFor(null)}
        companyId={projectsFor === null ? null : companyIdOf(projectsFor.row, projectsFor.tableKey)}
        companyName={projectsFor ? recordLabelOf(projectsFor.row) : ''}
        /*
         * ⛔ **`?? []` IS SAFE HERE AND NOWHERE ELSE ON THIS PAGE, because the
         * cell refuses to open on a `null`.** `ProjectOfInterestCell` renders no
         * trigger when the list was never read — a whole-set replace over an
         * unread set destroys it — so this coalesce is only ever reached while
         * the modal is closed and the value is unused. ⚠️ **If that guard is ever
         * removed, this becomes the bug it is guarding against**: an empty list
         * the reader would save as "no projects".
         */
        projects={projectsFor?.row.projectsOfInterest ?? []}
        projectOptions={projectOptions.projects}
        cycleScopes={projectCycleScopes}
        optionsLoading={projectOptions.loading}
        /*
         * ⚠️ **TRUE ON EVERY OPEN TODAY, and that is a measurement rather than a
         * defensive flag.** `/projects` caps `perPage` at 100 — the server's own
         * ceiling, so there is no larger number to reach for — and there are 113
         * active projects, so roughly thirteen are simply not offered. The modal
         * has to say so: a project missing from a picker is indistinguishable
         * from a project that does not exist.
         */
        optionsTruncated={projectOptions.hasMore}
        optionsFailed={projectOptions.failed}
        canEdit={companyEditGate.canEdit}
        disabledReason={companyEditGate.disabledReason}
        onSave={saveProjectsOfInterest}
      />

      {/*
        * The company's contacts manager — owner's decision 2026-10-16.
        *
        * ⚠️ **ONE for the page, whichever card and whichever row opened it** —
        * the same rule as every dialog above, and for the same reason: the two
        * `/companies` cards carry a hundred rows each, so a dialog per row is
        * two hundred mounted focus traps and two hundred contact reads.
        *
        * ⚠️ **The "two rows, one company" hazard `CompanyProjectsModal` records
        * does NOT arise here.** Leads and Companies partition on stage, so a
        * company is on exactly one row of one card.
        *
        * ⛔ **NO GATE FROM THE PAGE, and the absence is the decision.** The
        * modal gates its own writes on `ability` per action (contacts.* and
        * companies.update for the primary). **Do not add `companyEditGate` here
        * for symmetry with its neighbour**: that gate answers
        * `companies.update`, and most of this dialog is `contacts.*`.
        *
        * ⚠️ **Refetch on CLOSE, and only after an add or delete** — see
        * `contactsChangedRef`.
        */}
      <CompanyContactsManagerModal
        company={contactsFor}
        onClose={() => {
          setContactsFor(null);
          if (contactsChangedRef.current) {
            contactsChangedRef.current = false;
            reloadAfterEdit('companies');
          }
        }}
        onChanged={() => {
          contactsChangedRef.current = true;
        }}
      />

      {/*
        * 🔴 **THE DOCUMENT'S OWN LINE PROJECTS, LISTED IN FULL — owner's
        * decision, 2026-09-14**, on their report that the column did not open on
        * click the way the one above it does.
        *
        * ⛔ **IT TAKES NO GATE, NO WRITER AND NO `onSave`, AND THAT IS THE WHOLE
        * DIFFERENCE FROM THE DIALOG DIRECTLY ABOVE IT.** It reads a field that is
        * a consequence of the document's line items; there is nothing on a
        * document to write, and the editor that does exist writes the COMPANY's
        * list — a different one, irreversibly. **Do not hand this
        * `companyEditGate`, `saveProjectsOfInterest`, or anything else from the
        * block above.** The absence of a permission gate is not an oversight:
        * every reader of this page may already read these names in the cell.
        *
        * ⚠️ **`?? []` IS SAFE HERE FOR THE SAME REASON IT IS ON THE MODAL ABOVE,
        * AND FOR NO OTHER.** `DocumentProjectsCell` renders no trigger at all on
        * a `null` or an empty list, so this coalesce is only ever evaluated while
        * the dialog is closed and the value is unused — and the modal ANDs its
        * own `open` with a non-empty list as a second guard. ⚠️ **If that cell
        * guard is ever removed this becomes the `?? []` the whole column
        * forbids**: an unread list rendered as "this document names no project".
        */}
      <DocumentProjectsModal
        open={documentProjectsFor !== null}
        onClose={() => setDocumentProjectsFor(null)}
        documentLabel={documentProjectsFor ? recordLabelOf(documentProjectsFor) : ''}
        projects={documentProjectsFor?.lineProjects ?? []}
      />

      {/*
        * 🔴 **THE "+" ON A COMPANY ROW RAISES A DOCUMENT WITH LINE ITEMS SEEDED
        * FROM THAT COMPANY'S PROJECTS OF INTEREST — owner's instruction,
        * 2026-09-13.** Reached from both `/companies` cards, for whichever of
        * Deal, Quote and Invoice the reader may create.
        *
        * ⚠️ **ONE mount for the page**, the same rule as everything above it. It
        * returns `null` while `request` is `null` and it performs its own reads
        * ONLY while open — including the company itself, which is why it is
        * handed `{id, name}` and nothing else. **Nothing is fetched per
        * row-open by this page.**
        *
        * ⚠️ **`onSaved` and `onClose` are DIFFERENT ACTIONS and only one
        * refetches.** Cancelling created nothing, so a reload there could only
        * return what is already on screen — the same split the four create
        * modals draw.
        *
        * ⛔ **`onSaved` is NOT `finishCreate`.** That helper also clears
        * `createFor`, which belongs to the four card-level create modals above;
        * from here it would close a form this flow never opened. What is wanted
        * is the other half alone — `reloadAfterCreate`, which is where "which
        * cards could this have landed on" is decided, so that mapping is not
        * duplicated here either.
        *
        * ⚠️ **`onSaved` DOES NOT CLOSE, because the flow closes ITSELF** — it
        * calls `onSaved()` and then its own `close()`, which is this `onClose`.
        * Nulling the request here as well would be a second close path racing
        * the flow's own, and it is the one that would be wrong the day the flow
        * grows a step that outlives the save.
        */}
      <CompanyProjectLinesFlow
        request={projectLinesFor}
        onClose={() => setProjectLinesFor(null)}
        onSaved={() => {
          /*
           * ⚠️ Guarded rather than `projectLinesFor!`: the flow only ever fires
           * this while open, so the `null` arm is unreachable — and asserting
           * that would be this page's one claim `tsc` could not keep.
           */
          if (projectLinesFor !== null) reloadAfterCreate(projectLinesFor.kind);
        }}
      />

      {/*
        * 🔴 **ONE CONFIRMATION, FOR AN ACCEPTING OR DECLINING QUOTE STAGE —
        * owner's decision, 2026-09-12.** Mounted at the page for the same reason
        * everything else here is: a dialog per row would be one focus trap per
        * row. It is fed by {@link QuoteStageConfirm}, whose `resolve` settles the
        * save's own promise.
        *
        * ⚠️ **`tone="primary"`, NOT `danger`.** Nothing is being destroyed —
        * accepting a quote is an ordinary and desirable act. The dialog is here
        * because the consequence is ONE-WAY in a system this page does not show,
        * which is a different thing from a dangerous one, and painting it red
        * would train the reader to dismiss it.
        */}
      <ConfirmDialog
        open={stageConfirm !== null}
        onClose={() => settleStageConfirm(false)}
        onConfirm={() => settleStageConfirm(true)}
        tone="primary"
        title={
          stageConfirm === null
            ? ''
            : `Move ${stageConfirm.quoteLabel} to ${stageConfirm.stageName}?`
        }
        confirmLabel={stageConfirm === null ? 'Confirm' : `Move to ${stageConfirm.stageName}`}
        description={
          stageConfirm === null ? null : (
            <div className="grid gap-2">
              {/*
                * ⚠️ **WHAT ACTUALLY HAPPENS, not "are you sure".** Each sentence
                * names a consequence the reader cannot see from this page — a
                * confirmation that only restates the action is one people learn
                * to dismiss.
                *
                * ⚠️ **The stage is NOT named as "Accepted" or "Declined"** — the
                * dialog says the stage's own name, because `is_accepted` is
                * seeded on BOTH `Accepted` AND `Invoiced` and the reader may well
                * be choosing the second. Wording it as the flag would tell them
                * they were doing something they were not.
                */}
              {/*
                * The two sales-tracker sentences that stood here ("this quote's
                * card moves to Booked / Lost", "cards move forward only") were
                * removed on 2026-10-12: the tracker is built on deals now and a
                * quote stage moves no tracker row.
                */}
              {/*
                * ⚠️ **Hedged, because the push is genuinely conditional and a
                * flat promise would be wrong on the ordinary case.** A quote with
                * no line items, or on a company with no Xero link, is SKIPPED —
                * which is a normal outcome and not a failure. Saying "it will be
                * sent to Xero" would be a claim the response often contradicts.
                */}
              <p className="m-0">
                Saving also sends the quote to Xero, unless it has no line items or its company is
                not linked there.
              </p>
            </div>
          )
        }
      />
    </div>
  );
}
